import * as React from 'react';
import React__default, { createElement, createContext, useContext, useState, useRef, useEffect, useCallback, useReducer, useMemo, forwardRef } from 'react';
import { useTranslation, initReactI18next, I18nextProvider } from 'react-i18next';
import styled, { styled as styled$1, css, createGlobalStyle, ThemeProvider } from 'styled-components';
import { FaChevronRight } from 'react-icons/fa';
import { create } from 'zustand';
import { v4 } from 'uuid';
import { renderToString } from 'react-dom/server';
import { z } from 'zod';
import { FiEdit2, FiDownload } from 'react-icons/fi';
import { MdDelete, MdErrorOutline, MdVisibility, MdVisibilityOff, MdOutlineCheckCircle, MdExpandMore } from 'react-icons/md';
import { createPortal } from 'react-dom';
import { HexColorPicker } from 'react-colorful';
import { GrCircleQuestion } from 'react-icons/gr';
import { TbAlertCircleFilled } from 'react-icons/tb';
import { CgRemove } from 'react-icons/cg';
import QRCodeStyling from 'qr-code-styling';
import i18n from 'i18next';

const defaultQRStyle = {
    qrStyleType: "standard",
    backgroundColor: "#FFFFFF",
    dotsStyle: "square",
    dotsColor: "#000000",
    cornerStyle: "rounded-dot",
    cornerBorderColor: "#000000",
    cornerBackgroundColor: "#000000",
    logoUrl: "",
    frameStyle: "none",
    frameColor: "#404040",
    frameBackgroundColor: "#FFFFFF",
    frameTextColor: undefined,
    frameText: "SCAN ME",
};

const formatToValidUrl = (url) => {
    if (!url)
        return "";
    return (url === null || url === void 0 ? void 0 : url.includes("http")) ? url : "https://" + url;
};
const generateQRReference = () => {
    const uuid = v4();
    const reference = uuid.substring(0, uuid.indexOf('-')).toUpperCase();
    return reference;
};

const defaultUrlData = {
    qrType: "url-static",
    qrStyle: defaultQRStyle,
    qrData: {
        typeName: "URL",
        qrName: "",
        url: "",
    },
    reference: generateQRReference(),
};

const defaultVCardData = {
    qrType: "vCard",
    qrStyle: defaultQRStyle,
    qrData: {
        typeName: "vCard",
        qrName: "",
        address: "",
        email: "",
        mobilePhone: "",
        website: "",
        city: "",
        companyName: "",
        country: "",
        fax: "",
        fullName: "",
        landlinePhone: "",
        profileImage: "",
        numeration: "",
        profession: "",
        state: "",
        zipCode: "",
        socialNetworks: [],
        summary: "",
        themeColor: {
            backgroundColor: "#087191",
            buttonColor: "#9EE4FA",
        },
    },
    reference: generateQRReference(),
};

const defaultWifiData = {
    qrType: "wifi",
    qrStyle: defaultQRStyle,
    qrData: {
        typeName: "Wifi",
        qrName: "",
        qrNetworkHidden: false,
        qrNetworkName: "",
        qrNetworkPassword: "",
        qrNetworkSecurityType: "",
    },
    reference: generateQRReference(),
};

const buildEmptyQRData = (type) => {
    return {
        qrType: type,
        reference: "",
        qrStyle: defaultQRStyle,
        qrData: {},
    };
};
const getQRDataByType = (type) => {
    switch (type) {
        case "url-static":
            return defaultUrlData;
        case "vCard":
            return defaultVCardData;
        case "wifi":
            return defaultWifiData;
        default:
            // TODO: Add more types
            return buildEmptyQRData(type);
    }
};

const DEFAULT_QR_TYPE = "url-static";
const DEFAULT_QR_DATA = getQRDataByType(DEFAULT_QR_TYPE);

const fieldToQRDataPath = {
    // Fields for qrData.qrStyle
    dotsColor: "qrStyle.dotsColor",
    backgroundColor: "qrStyle.backgroundColor",
    cornerStyle: "qrStyle.cornerStyle",
    dotsStyle: "qrStyle.dotsStyle",
    cornerBorderColor: "qrStyle.cornerBorderColor",
    cornerBackgroundColor: "qrStyle.cornerBackgroundColor",
    logoUrl: "qrStyle.logoUrl",
    frameStyle: "qrStyle.frameStyle",
    frameColor: "qrStyle.frameColor",
    frameBackgroundColor: "qrStyle.frameBackgroundColor",
    frameTextColor: "qrStyle.frameTextColor",
    frameText: "qrStyle.frameText",
    // Any other is a field for qrData
};

function setNestedValue(obj, path, value) {
    const keys = path.split(".");
    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!(key in current) || typeof current[key] !== "object") {
            current[key] = {};
        }
        current = current[key];
    }
    current[keys[keys.length - 1]] = value;
}

const useQRStore = create()((set) => {
    // initial state (in order to separate the actions from the state, we can change this if we want)
    const initialState = {
        selectedType: DEFAULT_QR_TYPE,
        qrData: DEFAULT_QR_DATA,
        formValues: {},
        formErrors: {},
        isLoading: false,
        isValidForm: false,
    };
    // actions
    const setSelectedType = (type) => set({ selectedType: type });
    const setQRData = (data) => {
        return set(() => ({
            qrData: JSON.parse(JSON.stringify(data)),
            formValues: Object.assign({}, data.qrData),
            formErrors: {},
            isValidForm: false,
        }));
    };
    const setFormValue = (name, value) => set((state) => {
        const newFormValues = Object.assign(Object.assign({}, state.formValues), { [name]: value });
        if (state.qrData) {
            const newQRData = Object.assign({}, state.qrData);
            const fieldPath = fieldToQRDataPath[name];
            if (fieldPath) {
                setNestedValue(newQRData, fieldPath, value);
            }
            else {
                const qrDataFieldPath = `qrData.${name}`;
                setNestedValue(newQRData, qrDataFieldPath, value);
            }
            return {
                formValues: newFormValues,
                qrData: newQRData,
            };
        }
        else {
            return {
                formValues: newFormValues,
            };
        }
    });
    const setFormError = (name, errors) => set((state) => {
        const newFormErrors = Object.assign({}, state.formErrors);
        if (errors && errors.length > 0) {
            newFormErrors[name] = errors;
        }
        else {
            delete newFormErrors[name];
        }
        const isValidForm = Object.values(newFormErrors).every((errs) => errs.length === 0);
        return {
            formErrors: newFormErrors,
            isValidForm,
        };
    });
    const resetForm = () => set({
        formValues: {},
        formErrors: {},
        isValidForm: false,
        qrData: null,
        isLoading: false,
    });
    const setLoading = (loading) => set({ isLoading: loading });
    const setIsValidForm = (isValid) => set({ isValidForm: isValid });
    return Object.assign(Object.assign({}, initialState), { setSelectedType,
        setQRData,
        setFormValue,
        setFormError,
        resetForm,
        setLoading,
        setIsValidForm });
});

const DEFAULT_SERVICE_CONFIG = {
    apiUrl: "",
    cloudinary: {
        apiUrl: "https://api.cloudinary.com/v1_1",
        cloudName: "",
        uploadPreset: "",
    },
};
const validateConfig = (config) => {
    const missingFields = [];
    if (!config.apiUrl) {
        missingFields.push("apiUrl");
    }
    if (!config.cloudinary.cloudName) {
        missingFields.push("cloudinary.cloudName");
    }
    if (!config.cloudinary.uploadPreset) {
        missingFields.push("cloudinary.uploadPreset");
    }
    if (missingFields.length > 0) {
        throw new Error(`QREditor: Missing required configuration fields: ${missingFields.join(", ")}`);
    }
    return config;
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

class APIService {
    constructor(store) {
        this.store = store;
    }
    fetchWithLoading(url, options, errorMessage) {
        return __awaiter(this, void 0, void 0, function* () {
            this.store.getState().setLoading(true);
            try {
                const response = yield fetch(url, options);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return yield response.json();
            }
            catch (error) {
                console.error(errorMessage, error);
                throw new Error(errorMessage);
            }
            finally {
                this.store.getState().setLoading(false);
            }
        });
    }
    post(url, data, authorization) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.fetchWithLoading(url, {
                method: "POST",
                headers: {
                    Accept: "*/*",
                    "Content-Type": "application/json",
                    Authorization: authorization ? `Bearer ${authorization}` : "",
                },
                body: JSON.stringify(data),
            }, "Failed to submit data");
        });
    }
}

class CloudinaryService extends APIService {
    constructor(store, apiUrl, cloudName, uploadPreset) {
        super(store);
        this.uploadUrl = `${apiUrl}/${cloudName}/image/upload`;
        this.uploadPreset = uploadPreset;
    }
    uploadImage(imageData) {
        return __awaiter(this, void 0, void 0, function* () {
            const formData = new FormData();
            formData.append("file", imageData);
            formData.append("upload_preset", this.uploadPreset);
            const response = yield this.fetchWithLoading(this.uploadUrl, {
                method: "POST",
                body: formData,
            }, "Failed to upload image");
            return response.secure_url;
        });
    }
}

const processQRSvg = (qrSvgData) => __awaiter(void 0, void 0, void 0, function* () {
    if (qrSvgData instanceof Blob) {
        return yield qrSvgData.text();
    }
    else if (Buffer && Buffer.isBuffer(qrSvgData)) {
        return qrSvgData.toString("utf-8");
    }
    else if (typeof qrSvgData === "string") {
        if (qrSvgData.startsWith("data:image/svg+xml;base64,")) {
            return atob(qrSvgData.split(",")[1]);
        }
        return qrSvgData;
    }
    throw new Error("Unsupported QR code data format");
});
const renderFrameToSVG = (FrameComponent, props) => {
    return renderToString(createElement(FrameComponent, props));
};
const composeFrameWithQR = (frameConfig, qrSvgString, frameText, frameColor, frameBackgroundColor, frameTextColor) => __awaiter(void 0, void 0, void 0, function* () {
    const frameSvgString = renderFrameToSVG(frameConfig.Component, {
        frameText,
        frameColor,
        frameBackgroundColor,
        frameTextColor,
    });
    const parser = new DOMParser();
    const frameDoc = parser.parseFromString(frameSvgString, "image/svg+xml");
    const processedQRSvg = yield processQRSvg(qrSvgString);
    const qrDoc = parser.parseFromString(processedQRSvg, "image/svg+xml");
    const qrRoot = qrDoc.documentElement;
    let svgWidth = parseFloat(qrRoot.getAttribute("width") || "300");
    let svgHeight = parseFloat(qrRoot.getAttribute("height") || "300");
    const viewBoxAttr = qrRoot.getAttribute("viewBox");
    if (viewBoxAttr) {
        const [_vbX, _vbY, vbW, vbH] = viewBoxAttr.split(" ").map(Number);
        svgWidth = vbW;
        svgHeight = vbH;
    }
    const qrPlaceholder = frameDoc.getElementById(frameConfig.qrPlaceholderId);
    if (qrPlaceholder) {
        const x = parseFloat(qrPlaceholder.getAttribute("x") || "0");
        const y = parseFloat(qrPlaceholder.getAttribute("y") || "0");
        const placeholderWidth = parseFloat(qrPlaceholder.getAttribute("width") || "100");
        const placeholderHeight = parseFloat(qrPlaceholder.getAttribute("height") || "100");
        const parent = qrPlaceholder.parentNode;
        parent === null || parent === void 0 ? void 0 : parent.removeChild(qrPlaceholder);
        const gNode = frameDoc.createElementNS("http://www.w3.org/2000/svg", "g");
        const scaleX = placeholderWidth / svgWidth;
        const scaleY = placeholderHeight / svgHeight;
        const scale = Math.min(scaleX, scaleY);
        gNode.setAttribute("transform", `translate(${x}, ${y}) scale(${scale})`);
        while (qrRoot.firstChild) {
            gNode.appendChild(qrRoot.firstChild);
        }
        parent === null || parent === void 0 ? void 0 : parent.appendChild(gNode);
    }
    return new XMLSerializer().serializeToString(frameDoc.documentElement);
});
const getFrameTextColorBasedOnFrameType = (frameType) => {
    if (!frameType) {
        return "#FFFFFF";
    }
    switch (frameType) {
        case "white":
        case "museum":
        case "cafe":
        case "envelope":
        case "board":
            return "#000000";
        default:
            return "#FFFFFF";
    }
};
const getFrameColorBasedOnFrameType = (frameType) => {
    if (!frameType) {
        return "#FFFFFF";
    }
    switch (frameType) {
        case "envelope":
            return "#FFFFFF";
        default:
            return "#404040";
    }
};

class ConvertersService {
    constructor(cloudinaryService) {
        this.cloudinaryService = cloudinaryService;
    }
    transformQRStyle(style) {
        return __awaiter(this, void 0, void 0, function* () {
            let imageUrl = "";
            if (style.logoUrl) {
                imageUrl = yield this.cloudinaryService.uploadImage(style.logoUrl);
            }
            return {
                styleType: "STANDARD",
                dotsStyle: style.dotsStyle || "square",
                dotsColor: style.dotsColor || "#000000",
                backgroundColor: style.backgroundColor || "#FFFFFF",
                cornerStyle: style.cornerStyle || "square",
                cornerBorderColor: style.cornerBorderColor || "#000000",
                cornerBackgroundColor: style.cornerBackgroundColor || "#000000",
                imageUrl,
                frameStyle: style.frameStyle || "none",
                frameColor: style.frameColor || getFrameColorBasedOnFrameType(style.frameStyle),
                frameBackgroundColor: style.frameBackgroundColor || "#FFFFFF",
                frameTextColor: style.frameTextColor || getFrameTextColorBasedOnFrameType(style.frameStyle),
                frameText: style.frameText,
            };
        });
    }
    transformThemeColor(theme) {
        return {
            background: theme.backgroundColor,
            button: theme.buttonColor,
        };
    }
    transformQRContent(data) {
        return __awaiter(this, void 0, void 0, function* () {
            const qrData = data.qrData;
            switch (qrData.typeName) {
                case "URL":
                    return {
                        "qr-name": qrData.qrName || "Your QR Code",
                        "field-url": formatToValidUrl(qrData.url),
                    };
                case "Wifi":
                    return {
                        "qr-name": qrData.qrName || "Your QR Code",
                        "network-name": qrData.qrNetworkName,
                        "network-password": qrData.qrNetworkSecurityType === "nopass" ? "" : qrData.qrNetworkPassword,
                        "network-security-type": qrData.qrNetworkSecurityType === "nopass" ? undefined : qrData.qrNetworkSecurityType,
                        "network-hidden-password": qrData.qrNetworkHidden,
                    };
                case "vCard": {
                    let imageUrl = "";
                    if (qrData.profileImage) {
                        imageUrl = yield this.cloudinaryService.uploadImage(qrData.profileImage);
                    }
                    const social = {};
                    qrData.socialNetworks.forEach((network) => {
                        if (network.value) {
                            social[network.id] = network.value;
                        }
                    });
                    return {
                        "qr-name": qrData.qrName || "Your QR Code",
                        color: this.transformThemeColor({
                            backgroundColor: qrData.themeColor.backgroundColor,
                            buttonColor: qrData.themeColor.buttonColor,
                        }),
                        "info-image": imageUrl,
                        "info-full-name": qrData.fullName,
                        "info-email": qrData.email,
                        "info-mobile-phone": qrData.mobilePhone,
                        "info-landline-phone": qrData.landlinePhone,
                        "info-fax": qrData.fax,
                        "info-website": formatToValidUrl(qrData.website),
                        "info-company-name": qrData.companyName,
                        "info-profession": qrData.profession,
                        "info-summary": qrData.summary,
                        "info-address": qrData.address,
                        "info-numeration": qrData.numeration,
                        "info-zip-code": qrData.zipCode,
                        "info-city": qrData.city,
                        "info-state": qrData.state,
                        "info-country": qrData.country,
                        social,
                    };
                }
                default:
                    throw new Error(`Unsupported QR type: ${qrData.typeName}`);
            }
        });
    }
}

const getWindow = () => {
    return typeof window !== "undefined" ? window : null;
};

const getAuthCookie = () => {
    const cookieString = document.cookie || "";
    const cookies = cookieString.split(";").map((cookie) => cookie.trim());
    const authCookie = cookies.find((cookie) => cookie.startsWith("Authorization="));
    if (!authCookie) {
        return undefined;
    }
    return decodeURIComponent(authCookie.split("=")[1]);
};
const setCookie = (name, value, domain, expiration, path = "/") => {
    const encodedValue = encodeURIComponent(value);
    const expiresValue = "";
    const domainValue = domain ? `; domain=${domain}` : "";
    document.cookie = `${name}=${encodedValue}${expiresValue}${domainValue}; path=${path}`;
};
const getCookieDomain = () => {
    const window = getWindow();
    if (!window)
        return "";
    const { hostname } = window.location;
    const domainWithoutPort = hostname.split(":")[0];
    if (domainWithoutPort === "localhost") {
        return domainWithoutPort;
    }
    const parts = domainWithoutPort.split(".");
    if (parts.length <= 2) {
        return domainWithoutPort;
    }
    return parts.slice(parts.length - 2).join(".");
};
const setDownloadQRCookie = (qrCodeReference) => {
    const cookieName = "downloadQR";
    const reference = JSON.stringify({ qrCodeReference });
    const cookieDomain = getCookieDomain();
    setCookie(cookieName, reference, cookieDomain);
};
const isTokenExpired = (token) => {
    if (!token)
        return true;
    try {
        const [, payload] = token.split(".");
        const decodedPayload = JSON.parse(atob(payload));
        const expirationDate = new Date(decodedPayload.exp * 1000);
        return expirationDate < new Date();
    }
    catch (e) {
        return true;
    }
};

const EXCLUDED_SUBDOMAINS = ["test"];
class QRCodeService extends APIService {
    constructor(store, apiUrl, convertersService) {
        super(store);
        this.apiUrl = apiUrl;
        this.convertersService = convertersService;
    }
    submitQRCode(data, style) {
        return __awaiter(this, void 0, void 0, function* () {
            const transformedContent = yield this.convertersService.transformQRContent(data);
            const transformedStyle = yield this.convertersService.transformQRStyle(style);
            const qrCodeReference = generateQRReference();
            const authorization = isTokenExpired(getAuthCookie()) ? undefined : getAuthCookie();
            const payload = {
                type: data.qrType,
                content: transformedContent,
                style: transformedStyle,
                reference: qrCodeReference,
            };
            return this.post(`${this.apiUrl}${authorization ? "/me" : ""}/qrcode`, payload, authorization);
        });
    }
    navigateToAppSignup(navigateToSubdomain, reference) {
        const window = getWindow();
        if (!window)
            return;
        const { hostname } = window.location || {};
        let targetUrl;
        if (hostname.includes("localhost")) {
            const port = window.location.port || "8001";
            targetUrl = `http://localhost:${port}/signup`;
        }
        else if (/^(stage|[a-zA-Z0-9]+)\./.test(hostname)) {
            let subdomain = hostname.split(".")[0];
            const domain = hostname.split(".").slice(1).join(".");
            if (EXCLUDED_SUBDOMAINS.includes(subdomain)) {
                subdomain = "stage";
            }
            if (navigateToSubdomain) {
                subdomain = subdomain === "www" ? "app" : `${subdomain}-app`;
            }
            targetUrl = `https://${subdomain}.${domain}/signup`;
        }
        else {
            targetUrl = `https://${navigateToSubdomain ? "app." : ""}${hostname}/signup`;
        }
        window.location.href = targetUrl;
    }
}

const createServices = (config) => {
    var _a, _b;
    const finalConfig = Object.assign(Object.assign(Object.assign({}, DEFAULT_SERVICE_CONFIG), config), { cloudinary: Object.assign(Object.assign(Object.assign({}, DEFAULT_SERVICE_CONFIG === null || DEFAULT_SERVICE_CONFIG === void 0 ? void 0 : DEFAULT_SERVICE_CONFIG.cloudinary), config === null || config === void 0 ? void 0 : config.cloudinary), { apiUrl: ((_a = config === null || config === void 0 ? void 0 : config.cloudinary) === null || _a === void 0 ? void 0 : _a.apiUrl) ||
                ((_b = DEFAULT_SERVICE_CONFIG === null || DEFAULT_SERVICE_CONFIG === void 0 ? void 0 : DEFAULT_SERVICE_CONFIG.cloudinary) === null || _b === void 0 ? void 0 : _b.apiUrl) ||
                "https://api.cloudinary.com/v1_1" }) });
    validateConfig(finalConfig);
    const cloudinaryService = new CloudinaryService(useQRStore, finalConfig.cloudinary.apiUrl, finalConfig.cloudinary.cloudName, finalConfig.cloudinary.uploadPreset);
    const convertersService = new ConvertersService(cloudinaryService);
    const qrCodeService = new QRCodeService(useQRStore, finalConfig.apiUrl, convertersService);
    return {
        cloudinaryService,
        convertersService,
        qrCodeService,
    };
};

const ServicesContext = createContext(null);
const ServicesProvider = ({ children, config }) => {
    const services = createServices(config);
    return React__default.createElement(ServicesContext.Provider, { value: services }, children);
};
const useServices = () => {
    const context = useContext(ServicesContext);
    if (!context) {
        throw new Error("useServices must be used within a ServicesProvider");
    }
    return context;
};

const URLRegex = "^(?:https?:\\/\\/(?:www\\.)?|https:(?:\\/\\/)?)?(www\\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)";
const HEXRegex = "^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$";
const urlValidation = (pattern, message) => z.string().regex(RegExp(URLRegex), "The entered URL is not valid.");
const maxLengthValidation = (maxLength, message) => z
    .string()
    .max(maxLength || 50, `The text maximum length is ${maxLength || 50} characters.`);
const minMaxLengthValidation = (minLength, maxLength) => z
    .string()
    .min(minLength, "Required field")
    .max(maxLength, `The text maximum length is ${maxLength} characters.`);
const validateRequiredAndHex = (pattern, message) => z.string().superRefine((val, ctx) => {
    const regExpToUse = RegExp(HEXRegex);
    const errorMsg = "The color is not valid";
    if (!val.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Required field",
        });
        return;
    }
    if (!regExpToUse.test(val)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: errorMsg,
        });
    }
});

const URLCompactFormSchema = () => {
    return {
        displayName: "URL",
        fields: [
            {
                type: "text",
                name: "url",
                label: "Website URL",
                required: true,
                placeholder: "https://www.surfershop.com/sale",
                dataQA: "url-input",
                initialValue: "",
                onBlurValidate: () => urlValidation(),
                onChangeValidate: () => urlValidation(),
                onSubmitValidate: () => urlValidation(),
            },
        ],
    };
};
const URLExtendedFormSchema = () => {
    return {
        displayName: "URL",
        fields: [
            {
                type: "text",
                name: "url",
                label: "Website URL",
                required: true,
                placeholder: "https://www.surfershop.com/sale",
                initialValue: "",
                onBlurValidate: () => urlValidation(),
                onChangeValidate: () => urlValidation(),
                onSubmitValidate: () => urlValidation(),
            },
        ],
    };
};

const createCssProp = (tokenObj, prefix, parentKey = "") => {
    const cssProps = [];
    Object.entries(tokenObj).forEach(([key, value]) => {
        const currentKey = parentKey ? `${parentKey}-${key}` : key;
        if (typeof value === "object" && value !== null) {
            const nestedProps = createCssProp(value, prefix, currentKey);
            cssProps.push(nestedProps);
        }
        else {
            const variableKey = prefix === "--" ? `${prefix}${currentKey}` : `${prefix}-${currentKey}`;
            cssProps.push(`${variableKey}: ${value}`);
        }
    });
    return cssProps.join(";") + ";";
};
function getCssVariable(...args) {
    return `var(--qe-${args.join("-")})`;
}
function getFont(property) {
    return getCssVariable(property);
}
function getColor(colorName, shade) {
    return getCssVariable("color", colorName, shade);
}
function getBreakpoint(breakpoint) {
    return getCssVariable("breakpoints", breakpoint);
}
// BORDER
function getBorderWidth(size) {
    return getCssVariable("border", "width", size);
}
function getBorderRadius(size) {
    return getCssVariable("border", "radius", size);
}
function getSpacing(size) {
    return getCssVariable("spacing", size);
}
function getPadding(size) {
    return `padding: ${getSpacing(size)}`;
}
function getMarginY(size) {
    return `margin-top: ${getSpacing(size)}; margin-bottom: ${getSpacing(size)}`;
}
function getMarginTop(size) {
    return `margin-top: ${getSpacing(size)}`;
}
function getMarginBottom(size) {
    return `margin-bottom: ${getSpacing(size)}`;
}
function getPaddingX(size) {
    return `padding-left: ${getSpacing(size)}; padding-right: ${getSpacing(size)}`;
}
function getPaddingY(size) {
    return `padding-top: ${getSpacing(size)}; padding-bottom: ${getSpacing(size)}`;
}
function getPaddingTop(size) {
    return `padding-top: ${getSpacing(size)}`;
}
function getPaddingLeft(size) {
    return `padding-left: ${getSpacing(size)}`;
}
function mediaQuery(minWidth) {
    return `@media (min-width: ${minWidth})`;
}
function responsive(styles) {
    const cssRules = [];
    if (styles.mobile) {
        cssRules.push(styles.mobile);
    }
    if (styles.tablet) {
        cssRules.push(`
      ${mediaQuery(getBreakpoint("tablet"))} {
        ${styles.tablet}
      }
    `);
    }
    if (styles.desktop) {
        cssRules.push(`
      ${mediaQuery(getBreakpoint("desktop"))} {
        ${styles.desktop}
      }
    `);
    }
    return cssRules.join("\n");
}
function responsiveMargin(sizes, direction) {
    return responsive({
        mobile: `margin${`-${direction}` }: ${getSpacing(sizes.mobile)};`,
        tablet: sizes.tablet && `margin${`-${direction}` }: ${getSpacing(sizes.tablet)};`,
        desktop: sizes.desktop && `margin${`-${direction}` }: ${getSpacing(sizes.desktop)};`,
    });
}

const AddressGridContainer = styled.div `
  display: grid;
  ${responsive({
    mobile: `
      grid-template-columns: 1fr;
      gap: ${getSpacing("size-00")};
    `,
    tablet: `
      grid-template-columns: 1fr 1fr 1fr;
      gap: ${getSpacing("size-05")};
    `,
})}
`;
const AddressFirstRow = styled.div `
  display: grid;
  ${responsive({
    mobile: `
      grid-template-columns: 1fr;
      gap: ${getSpacing("size-00")};
    `,
    tablet: `
      grid-template-columns: 2fr 1fr 1fr;
      gap: ${getSpacing("size-05")};
    `,
})}
`;
const AddressSecondRow = styled.div `
  display: grid;
  ${responsive({
    mobile: `
      grid-template-columns: 1fr;
      gap: ${getSpacing("size-00")};
    `,
    tablet: `
      grid-template-columns: 1fr 1fr 1fr;
      gap: ${getSpacing("size-05")};
    `,
})}
`;

const FacebookIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none" },
        React__default.createElement("path", { fill: "#1877F2", d: "M0 16c0 8.837 7.163 16 16 16s16-7.163 16-16S24.837 0 16 0 0 7.163 0 16z" }),
        React__default.createElement("path", { fill: "#FFF", d: "M24 16c0-4.4-3.6-8-8-8s-8 3.6-8 8c0 4 2.9 7.3 6.7 7.9v-5.6h-2V16h2v-1.8c0-2 1.2-3.1 3-3.1.9 0 1.8.2 1.8.2v2h-1c-1 0-1.3.6-1.3 1.2V16h2.2l-.4 2.3h-1.9V24c4-.6 6.9-4 6.9-8z" }))));
const TwitterIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none" },
        React__default.createElement("path", { fill: "#1DA1F2", d: "M0 16c0 8.837 7.163 16 16 16s16-7.163 16-16S24.837 0 16 0 0 7.163 0 16z" }),
        React__default.createElement("path", { fill: "#FFF", d: "M24 11c-.6.3-1.2.4-1.9.5.7-.4 1.2-1 1.4-1.8-.6.4-1.3.6-2.1.8-.6-.6-1.5-1-2.4-1-2.1 0-3.7 2-3.2 4-2.7-.1-5.1-1.4-6.8-3.4-.9 1.5-.4 3.4 1 4.4-.5 0-1-.2-1.5-.4 0 1.5 1.1 2.9 2.6 3.3-.5.1-1 .2-1.5.1.4 1.3 1.6 2.3 3.1 2.3-1.2.9-3 1.4-4.7 1.2 1.5.9 3.2 1.5 5 1.5 6.1 0 9.5-5.1 9.3-9.8.7-.4 1.3-1 1.7-1.7z" }))));
const InstagramIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none" },
        React__default.createElement("path", { fill: "#000", d: "M0 16c0 8.837 7.163 16 16 16s16-7.163 16-16S24.837 0 16 0 0 7.163 0 16z" }),
        React__default.createElement("path", { fill: "#FFF", d: "M16 9.2h3.4c.8 0 1.2.2 1.5.3.4.2.7.3 1 .6.3.3.5.6.6 1 .1.3.2.7.3 1.5v6.8c0 .8-.2 1.2-.3 1.5-.2.4-.3.7-.6 1-.3.3-.6.5-1 .6-.3.1-.7.2-1.5.3h-6.8c-.8 0-1.2-.2-1.5-.3-.4-.2-.7-.3-1-.6-.3-.3-.5-.6-.6-1-.1-.3-.2-.7-.3-1.5v-6.8c0-.8.2-1.2.3-1.5.2-.4.3-.7.6-1 .3-.3.6-.5 1-.6.3-.1.7-.2 1.5-.3H16zm0-1.5h-3.4c-.9 0-1.5.2-2 .4s-1 .5-1.5 1-.7.9-1 1.5c-.2.5-.3 1.1-.4 2v6.8c0 .9.2 1.5.4 2s.5 1 1 1.5.9.7 1.5 1c.5.2 1.1.3 2 .4h6.8c.9 0 1.5-.2 2-.4s1-.5 1.5-1 .7-.9 1-1.5c.2-.5.3-1.1.4-2v-6.8c0-.9-.2-1.5-.4-2s-.5-1-1-1.5-.9-.7-1.5-1c-.5-.2-1.1-.3-2-.4H16z" }),
        React__default.createElement("path", { fill: "#FFF", d: "M16 11.7c-2.4 0-4.3 1.9-4.3 4.3s1.9 4.3 4.3 4.3 4.3-1.9 4.3-4.3-1.9-4.3-4.3-4.3zm0 7.1c-1.5 0-2.8-1.2-2.8-2.8 0-1.5 1.2-2.8 2.8-2.8 1.5 0 2.8 1.2 2.8 2.8 0 1.5-1.3 2.8-2.8 2.8zm4.4-6.2a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" }))));
const LinkedinIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none" },
        React__default.createElement("path", { fill: "#2867B2", d: "M0 16c0 8.837 7.163 16 16 16s16-7.163 16-16S24.837 0 16 0 0 7.163 0 16z" }),
        React__default.createElement("path", { fill: "#FFF", d: "M11.6 24H8.2V13.3h3.4V24zM9.9 11.8C8.8 11.8 8 11 8 9.9 8 8.8 8.9 8 9.9 8c1.1 0 1.9.8 1.9 1.9 0 1.1-.8 1.9-1.9 1.9zM24 24h-3.4v-5.8c0-1.7-.7-2.2-1.7-2.2s-2 .8-2 2.3V24h-3.4V13.3h3.2v1.5c.3-.7 1.5-1.8 3.2-1.8 1.9 0 3.9 1.1 3.9 4.4V24h.2z" }))));
const DribbbleIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none" },
        React__default.createElement("path", { fill: "#EA4C89", d: "M0 16c0 8.837 7.163 16 16 16s16-7.163 16-16S24.837 0 16 0 0 7.163 0 16z" }),
        React__default.createElement("path", { fill: "#FFF", d: "M16 8c-4.416 0-8 3.584-8 8s3.584 8 8 8c4.4 0 8-3.584 8-8s-3.6-8-8-8zm5.28 3.68a6.862 6.862 0 0 1 1.552 4.256c-.224-.048-2.48-.496-4.752-.224-.048-.112-.096-.24-.144-.352a18.66 18.66 0 0 0-.448-.992c2.496-1.008 3.648-2.496 3.792-2.688zM16 9.184c1.728 0 3.328.656 4.528 1.712-.128.176-1.152 1.552-3.584 2.464-1.12-2.064-2.368-3.744-2.544-4a7.428 7.428 0 0 1 1.6-.176zm-2.912.64c.176.24 1.392 1.936 2.528 3.952-3.2.848-6.016.832-6.32.832a6.914 6.914 0 0 1 3.792-4.784zm-3.92 6.192v-.208c.288.016 3.616.048 7.024-.976.192.384.384.768.56 1.168-.08.032-.176.048-.272.08-3.52 1.136-5.392 4.24-5.552 4.496a6.755 6.755 0 0 1-1.76-4.56zM16 22.832a6.813 6.813 0 0 1-4.192-1.44c.128-.256 1.504-2.928 5.36-4.272.016-.016.032-.016.048-.016.96 2.496 1.36 4.576 1.456 5.184a6.758 6.758 0 0 1-2.672.544zm3.808-1.168c-.064-.416-.432-2.416-1.328-4.864 2.144-.336 4.016.224 4.256.288a6.79 6.79 0 0 1-2.928 4.576z" }))));
const FlickrIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none" },
        React__default.createElement("path", { fill: "#FFF", stroke: "#D9D9D9", "stroke-width": ".5", d: "M16 .25a15.7 15.7 0 0 1 11.137 4.613A15.7 15.7 0 0 1 31.75 16a15.7 15.7 0 0 1-4.613 11.137A15.7 15.7 0 0 1 16 31.75a15.7 15.7 0 0 1-11.137-4.613A15.7 15.7 0 0 1 .25 16 15.7 15.7 0 0 1 4.863 4.863 15.7 15.7 0 0 1 16 .25z" }),
        React__default.createElement("path", { fill: "#FF0084", d: "M24.2 16c0 2.1-1.7 3.8-3.8 3.8-2.1 0-3.8-1.7-3.8-3.8 0-2.1 1.7-3.8 3.8-3.8 2.1 0 3.8 1.7 3.8 3.8z" }),
        React__default.createElement("path", { fill: "#0063DC", d: "M15.5 16c0 2.1-1.7 3.8-3.8 3.8-2.1 0-3.8-1.7-3.8-3.8 0-2.1 1.7-3.8 3.8-3.8 2 0 3.8 1.7 3.8 3.8z" }))));
const GithubIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none", "fill-rule": "evenodd" },
        React__default.createElement("path", { fill: "#231E1B", "fill-rule": "nonzero", d: "M0 16c0 8.837 7.163 16 16 16s16-7.163 16-16S24.837 0 16 0 0 7.163 0 16z" }),
        React__default.createElement("path", { fill: "#FFF", d: "M15.976 8.192A7.977 7.977 0 0 0 8 16.168c0 3.523 2.3 6.507 5.431 7.584.392.049.538-.196.538-.392v-1.37c-2.201.49-2.69-1.076-2.69-1.076-.343-.93-.881-1.175-.881-1.175-.734-.489.049-.489.049-.489.782.049 1.223.832 1.223.832.734 1.223 1.86.88 2.3.685.048-.538.293-.88.489-1.076-1.762-.196-3.621-.881-3.621-3.964 0-.88.294-1.566.832-2.153-.05-.146-.343-.978.098-2.055 0 0 .685-.196 2.201.832.636-.196 1.322-.245 2.007-.245s1.37.098 2.006.245c1.517-1.028 2.201-.832 2.201-.832.44 1.077.147 1.909.098 2.104a3.16 3.16 0 0 1 .832 2.153c0 3.083-1.86 3.719-3.62 3.915.293.244.538.733.538 1.467v2.202c0 .196.146.44.538.392A7.984 7.984 0 0 0 24 16.168c-.049-4.404-3.62-7.976-8.024-7.976z" }))));
const LineIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none", "fill-rule": "evenodd" },
        React__default.createElement("circle", { cx: "16", cy: "16", r: "16", fill: "#06C755" }),
        React__default.createElement("g", { "fill-rule": "nonzero", transform: "translate(8 8)" },
            React__default.createElement("path", { fill: "#FFF", d: "M16.99 6.894C16.99 3.093 13.18 0 8.495 0 3.811 0 0 3.093 0 6.894c0 3.408 3.026 6.262 7.104 6.801.277.06.654.183.748.419.086.215.057.552.028.768l-.12.727c-.038.215-.172.84.735.459.907-.381 4.892-2.88 6.674-4.933 1.231-1.35 1.821-2.722 1.821-4.241z" }),
            React__default.createElement("path", { fill: "#06C755", d: "M14.163 9.089h-2.388a.16.16 0 0 1-.16-.16V5.224a.16.16 0 0 1 .16-.159h2.388a.16.16 0 0 1 .16.16v.602a.16.16 0 0 1-.16.16h-1.622v.624h1.622a.16.16 0 0 1 .16.16v.608a.16.16 0 0 1-.16.16h-1.622v.625h1.622a.16.16 0 0 1 .16.16v.601a.16.16 0 0 1-.16.165zm-8.827 0a.16.16 0 0 0 .159-.16v-.601a.16.16 0 0 0-.16-.16h-1.62V5.223a.16.16 0 0 0-.16-.159H2.95a.16.16 0 0 0-.159.16v3.704c0 .088.071.16.16.16h2.388l-.002.002z" }),
            React__default.createElement("rect", { width: "1", height: "4.032", x: "6.008", y: "5.057", fill: "#06C755", rx: ".122" }),
            React__default.createElement("path", { fill: "#06C755", d: "M10.873 5.057h-.602a.16.16 0 0 0-.159.16v2.201l-1.696-2.29a.096.096 0 0 0-.013-.017l-.01-.01-.009-.007H8.38l-.01-.006h-.004l-.009-.005h-.674a.16.16 0 0 0-.16.16V8.95a.16.16 0 0 0 .16.159h.603a.16.16 0 0 0 .159-.16V6.727l1.698 2.293a.16.16 0 0 0 .042.041l.01.007h.004l.008.004h.025a.193.193 0 0 0 .043.005h.598a.16.16 0 0 0 .16-.159V5.219a.16.16 0 0 0-.16-.162z" })))));
const RedditIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none" },
        React__default.createElement("circle", { cx: "16", cy: "16", r: "16", fill: "#FF4500" }),
        React__default.createElement("path", { fill: "#FFF", d: "M26.106 16a2.212 2.212 0 0 0-3.747-1.585c-1.512-1.09-3.594-1.795-5.914-1.876l1.007-4.74 3.293.7a1.585 1.585 0 1 0 .161-.773l-3.671-.78a.392.392 0 0 0-.469.303l-1.132 5.29c-2.347.065-4.468.763-5.994 1.876a2.21 2.21 0 1 0-2.435 3.622 4.385 4.385 0 0 0-.052.679c0 3.395 3.96 6.162 8.846 6.162 4.884 0 8.848-2.76 8.848-6.162 0-.223-.018-.445-.052-.665A2.212 2.212 0 0 0 26.105 16zM10.94 17.584a1.585 1.585 0 1 1 3.169 0 1.585 1.585 0 0 1-3.17 0zm8.828 4.172c-1.08 1.075-3.145 1.159-3.753 1.159-.61 0-2.674-.084-3.751-1.159a.41.41 0 0 1 .58-.58c.678.68 2.132.922 3.168.922 1.037 0 2.49-.242 3.17-.921a.412.412 0 0 1 .586.579zm-.29-2.587a1.585 1.585 0 1 1 0-3.17 1.585 1.585 0 0 1 0 3.17z" }))));
const SkypeIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none" },
        React__default.createElement("path", { fill: "#0078D7", d: "M0 16c0 8.837 7.163 16 16 16s16-7.163 16-16S24.837 0 16 0 0 7.163 0 16z" }),
        React__default.createElement("path", { fill: "#FFF", d: "M16 20.8c-2.7 0-3.9-1.4-3.9-2.4 0-.2.1-.5.3-.6.2-.2.4-.2.6-.2 1.1 0 .8 1.7 3 1.7 1.1 0 1.7-.7 1.7-1.3 0-.4-.2-.8-.9-1l-2.4-.6c-1.9-.5-2.2-1.6-2.2-2.5 0-2.1 1.9-2.8 3.7-2.8 1.6 0 3.6.9 3.6 2.1 0 .5-.4.8-1 .8-1 0-.8-1.4-2.8-1.4-1 0-1.5.5-1.5 1.1 0 .6.8.9 1.4 1l1.8.4c1.9.4 2.4 1.6 2.4 2.6 0 1.9-1.3 3.1-3.8 3.1zm7.4-3.5c.1-.4.1-.9.1-1.3 0-2.2-1-4.3-2.7-5.8-1.7-1.5-4-2-6.2-1.7-.6-.3-1.4-.5-2.2-.5-1.6 0-3.1.9-3.9 2.3-.8 1.4-.8 3.1 0 4.5-.4 2.4.3 4.9 2.1 6.7 1.7 1.7 4.2 2.5 6.6 2.1.7.4 1.4.6 2.2.6 1.6 0 3.1-.9 3.9-2.3.8-1.5.9-3.2.1-4.6z" }))));
const SnapchatIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none", "fill-rule": "evenodd" },
        React__default.createElement("circle", { cx: "16", cy: "16", r: "16", fill: "#FCFC02" }),
        React__default.createElement("path", { fill: "#000", "fill-rule": "nonzero", d: "M24.996 19.914c-.126-.348-.37-.536-.648-.686-.052-.028-.099-.056-.14-.075l-.255-.127c-.864-.46-1.54-1.033-2.005-1.72a3.904 3.904 0 0 1-.343-.61c-.043-.113-.038-.178-.01-.24a.383.383 0 0 1 .113-.117c.15-.099.3-.197.404-.263.183-.122.334-.216.428-.282.352-.249.6-.512.756-.808a1.6 1.6 0 0 0 .08-1.334c-.235-.62-.818-1-1.527-1-.15 0-.296.014-.446.046l-.118.029c.005-.423-.004-.87-.042-1.311-.132-1.546-.676-2.354-1.24-2.997a4.962 4.962 0 0 0-1.26-1.015c-.854-.488-1.827-.737-2.883-.737-1.057 0-2.025.249-2.88.737-.475.268-.897.61-1.26 1.015-.563.643-1.103 1.456-1.24 2.997-.037.442-.046.888-.042 1.31l-.117-.028a2.016 2.016 0 0 0-.442-.047c-.709 0-1.291.386-1.526 1.001-.17.437-.141.92.08 1.334.155.296.404.56.756.808.094.066.24.16.427.282.099.066.245.16.39.254a.409.409 0 0 1 .127.126c.028.062.033.127-.014.25a3.744 3.744 0 0 1-.338.596c-.456.667-1.114 1.236-1.95 1.691-.441.235-.902.39-1.099.92-.146.4-.052.851.32 1.236.136.141.295.263.47.357.361.198.746.353 1.15.46.085.024.16.057.23.104.137.118.118.296.296.56.09.135.207.253.339.347.38.263.808.277 1.258.296.41.014.87.033 1.4.206.221.07.447.212.71.376.634.39 1.498.921 2.95.921 1.451 0 2.32-.536 2.96-.925.262-.16.488-.301.7-.372.525-.173.99-.192 1.4-.206.45-.02.878-.033 1.258-.296.16-.113.291-.254.385-.423.132-.22.127-.376.25-.484a.642.642 0 0 1 .215-.099 5.202 5.202 0 0 0 1.165-.465c.184-.098.353-.23.494-.385l.004-.004c.367-.376.456-.818.31-1.203z" }),
        React__default.createElement("path", { fill: "#FFF", d: "M23.71 20.604c-.785.433-1.311.386-1.715.649-.348.22-.141.7-.39.874-.31.21-1.222-.015-2.396.375-.973.32-1.588 1.245-3.335 1.245-1.748 0-2.35-.92-3.336-1.245-1.174-.39-2.085-.16-2.395-.375-.25-.174-.047-.653-.39-.874-.41-.263-.93-.216-1.715-.649-.503-.277-.216-.446-.052-.526 2.847-1.376 3.303-3.504 3.322-3.664.023-.192.051-.343-.16-.535-.202-.188-1.104-.747-1.358-.921-.413-.291-.596-.578-.46-.935.094-.244.324-.338.564-.338.075 0 .15.01.225.023.456.099.897.325 1.15.39.034.01.062.014.095.014.136 0 .183-.07.174-.225-.028-.498-.099-1.466-.02-2.372.109-1.245.508-1.865.987-2.41.23-.263 1.306-1.4 3.369-1.4 2.062 0 3.138 1.132 3.368 1.395.479.545.878 1.165.986 2.41.08.907.01 1.874-.023 2.372-.01.165.037.226.174.226a.326.326 0 0 0 .094-.014c.253-.061.695-.292 1.15-.39a.92.92 0 0 1 .226-.024c.24 0 .47.094.564.338.136.357-.047.644-.46.935-.25.174-1.152.733-1.358.921-.212.193-.184.343-.16.536.019.16.474 2.287 3.321 3.664.17.08.451.253-.047.53z" }))));
const TikTokIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none" },
        React__default.createElement("path", { fill: "#000", d: "M0 16C0 7.163 7.163 0 16 0s16 7.163 16 16-7.163 16-16 16S0 24.837 0 16z" }),
        React__default.createElement("path", { fill: "#25F4EE", d: "M14.498 14.3v-.623a4.847 4.847 0 0 0-3.428 8.774 4.83 4.83 0 0 1-1.302-3.303 4.852 4.852 0 0 1 4.73-4.848z" }),
        React__default.createElement("path", { fill: "#25F4EE", d: "M14.616 21.362c1.191 0 2.166-.95 2.208-2.132l.004-10.56h1.928a3.779 3.779 0 0 1-.06-.67h-2.633l-.003 10.561a2.215 2.215 0 0 1-2.209 2.132c-.37 0-.718-.092-1.028-.256a2.198 2.198 0 0 0 1.793.925zm7.742-9.108v-.588a3.624 3.624 0 0 1-1.995-.594 3.659 3.659 0 0 0 1.995 1.182z" }),
        React__default.createElement("path", { fill: "#FE2C55", d: "M20.363 11.072a3.641 3.641 0 0 1-.9-2.403h-.704a3.65 3.65 0 0 0 1.604 2.403zm-6.515 5.19c-1.22 0-2.213.993-2.213 2.214 0 .85.484 1.59 1.188 1.96a2.192 2.192 0 0 1-.42-1.291c0-1.221.993-2.214 2.213-2.214.227 0 .448.039.654.103v-2.691a4.92 4.92 0 0 0-.654-.046c-.04 0-.075.003-.114.003v2.065a2.226 2.226 0 0 0-.655-.103z" }),
        React__default.createElement("path", { fill: "#FE2C55", d: "M22.358 12.254V14.3a6.264 6.264 0 0 1-3.663-1.178v5.357a4.855 4.855 0 0 1-4.848 4.852 4.827 4.827 0 0 1-2.777-.88A4.839 4.839 0 0 0 14.616 24a4.855 4.855 0 0 0 4.847-4.852v-5.357a6.264 6.264 0 0 0 3.663 1.178v-2.633c-.266 0-.522-.029-.768-.082z" }),
        React__default.createElement("path", { fill: "#FFF", d: "M18.695 18.48v-5.358a6.264 6.264 0 0 0 3.663 1.178v-2.046a3.659 3.659 0 0 1-1.995-1.182 3.681 3.681 0 0 1-1.608-2.403h-1.927l-.004 10.561a2.215 2.215 0 0 1-2.208 2.132c-.74 0-1.39-.366-1.796-.921a2.222 2.222 0 0 1-1.188-1.962c0-1.22.992-2.214 2.212-2.214.228 0 .448.04.654.103v-2.064a4.846 4.846 0 0 0-3.428 8.148 4.83 4.83 0 0 0 2.777.879 4.858 4.858 0 0 0 4.848-4.852z" }))));
const TripadvisorIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none" },
        React__default.createElement("circle", { cx: "16", cy: "16", r: "16", fill: "#34E0A1" }),
        React__default.createElement("path", { fill: "#000", d: "m25.855 12.843 1.926-2.096h-4.27a13.339 13.339 0 0 0-15.009 0H4.221l1.926 2.096a5.887 5.887 0 1 0 7.967 8.668L16 23.565l1.886-2.052a5.889 5.889 0 1 0 7.968-8.67zm-15.742 8.334a3.984 3.984 0 1 1 0-7.967 3.984 3.984 0 0 1 0 7.967zm5.888-4.098c0-2.622-1.908-4.871-4.421-5.834a11.497 11.497 0 0 1 8.842 0c-2.515.963-4.42 3.212-4.42 5.834zm5.885 4.098a3.984 3.984 0 1 1 0-7.967 3.984 3.984 0 0 1 0 7.967zm0-6.072a2.088 2.088 0 1 0 0 4.177 2.088 2.088 0 0 0 0-4.177zm-9.685 2.088a2.088 2.088 0 1 1-4.18 0 2.088 2.088 0 0 1 4.18 0z" }))));
const TumblrIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none" },
        React__default.createElement("path", { fill: "#001935", d: "M0 16c0 8.837 7.163 16 16 16s16-7.163 16-16S24.837 0 16 0 0 7.163 0 16z" }),
        React__default.createElement("path", { fill: "#FFF", d: "M17.388 24c-2.41 0-4.198-1.235-4.198-4.198V15.06H11v-2.57c2.41-.623 3.415-2.692 3.525-4.49h2.501v4.078h2.913v2.983h-2.913v4.128c0 1.235.623 1.667 1.617 1.667h1.417V24h-2.672z" }))));
const VimeoIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none" },
        React__default.createElement("path", { fill: "#1AB7EA", d: "M0 16c0 8.837 7.163 16 16 16s16-7.163 16-16S24.837 0 16 0 0 7.163 0 16z" }),
        React__default.createElement("path", { fill: "#FFF", d: "m8 12.8.7.9s1.3-1.1 1.8-.5c.4.5 2.2 6.9 2.7 8.1.5 1 1.9 2.4 3.4 1.4s6.5-5.2 7.4-10.3c.9-5-6-4-6.7.4 1.7-1.1 2.7.4 1.7 2.2-.9 1.7-1.8 2.9-2.2 2.9-.4 0-.8-1.2-1.3-3.2-.5-2.2-.5-6-2.7-5.5-2.1.4-4.8 3.6-4.8 3.6z" }))));
const VkontakteIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none" },
        React__default.createElement("path", { fill: "#07F", d: "M32 16c0-8.837-7.163-16-16-16S0 7.163 0 16s7.163 16 16 16 16-7.163 16-16z" }),
        React__default.createElement("path", { fill: "#FFF", d: "M16.711 21.333c-5.466 0-8.583-3.497-8.711-9.326h2.75c.087 4.275 2.101 6.083 3.7 6.457v-6.457h2.58v3.685c1.575-.157 3.238-1.839 3.797-3.692h2.574a6.89 6.89 0 0 1-1.232 2.669 7.437 7.437 0 0 1-2.269 1.987 7.823 7.823 0 0 1 2.587 1.927A7.217 7.217 0 0 1 24 21.333h-2.837c-.608-1.77-2.126-3.146-4.132-3.332v3.332h-.32z" }))));
const WebIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none", "fill-rule": "evenodd" },
        React__default.createElement("path", { fill: "#000", "fill-rule": "nonzero", d: "M32 16c0-8.837-7.163-16-16-16S0 7.163 0 16s7.163 16 16 16 16-7.163 16-16z" }),
        React__default.createElement("path", { fill: "#FFF", d: "M20.895 15.506c-.04-1.46-.25-2.859-.614-4.103l.323-.04c.677-.092 1.212-.205 1.592-.337.146-.05.27-.104.377-.163a8.303 8.303 0 0 1 1.758 4.643h-3.436zm1.618 5.707a2.683 2.683 0 0 0-.317-.132c-.38-.131-.915-.244-1.592-.336-.114-.015-.232-.03-.353-.044.382-1.27.604-2.707.644-4.207h3.436a8.304 8.304 0 0 1-1.818 4.719zm-3.886 2.71c.34-.406.652-.891.932-1.452.129-.26.249-.53.36-.813l.18.02c.897.102 1.413.22 1.705.314a8.36 8.36 0 0 1-3.177 1.93zm-1.23-.155c-.295.25-.597.42-.903.507V21.48c.842.01 1.656.04 2.394.09-.068.158-.14.311-.213.46-.375.752-.805 1.337-1.279 1.738zm-.903-7.274h3.413c-.042 1.481-.27 2.889-.66 4.111a41.741 41.741 0 0 0-2.753-.112v-4zm0-4.88a41.54 41.54 0 0 0 2.786-.115c.37 1.198.586 2.568.627 4.007h-3.413v-3.891zm0-3.89c.306.089.608.258.902.508.474.401.904.986 1.279 1.738.09.181.176.37.257.565-.75.052-1.58.083-2.438.092V7.725zm5.392 2.365c-.274.096-.8.228-1.788.34l-.139.016a10.37 10.37 0 0 0-.4-.916 7.357 7.357 0 0 0-.932-1.451 8.36 8.36 0 0 1 3.259 2.01zm-6.38.538a41.966 41.966 0 0 1-2.438-.092c.081-.195.167-.384.257-.565.375-.752.805-1.337 1.279-1.738.294-.25.596-.42.902-.507v2.902zm0 4.88h-3.413c.04-1.44.256-2.81.627-4.008.842.066 1.79.106 2.786.116v3.891zm0 4.986a42.11 42.11 0 0 0-2.753.112c-.39-1.222-.618-2.63-.66-4.111h3.413v3.999zm0 3.782a2.45 2.45 0 0 1-.902-.507c-.474-.401-.904-.986-1.279-1.738a9.067 9.067 0 0 1-.213-.46 42.08 42.08 0 0 1 2.394-.09v2.795zm-5.31-2.283c.292-.094.808-.212 1.705-.314l.18-.02c.111.282.231.554.36.813.28.56.592 1.046.932 1.451a8.36 8.36 0 0 1-3.177-1.93zm-.71-.78a8.304 8.304 0 0 1-1.817-4.718h3.436c.04 1.5.262 2.937.644 4.207-.121.014-.24.029-.353.044-.677.092-1.212.205-1.592.336a2.687 2.687 0 0 0-.317.132zm-.06-10.349c.107.059.232.112.378.163.38.132.915.245 1.592.336.104.014.212.028.323.04-.364 1.245-.575 2.644-.614 4.104H7.67a8.303 8.303 0 0 1 1.758-4.643zm3.947-2.785a7.35 7.35 0 0 0-.932 1.451c-.144.29-.278.597-.4.916l-.14-.015c-.988-.113-1.513-.245-1.787-.341a8.36 8.36 0 0 1 3.26-2.011zM24.6 12.367a9.3 9.3 0 0 0-2-2.967A9.301 9.301 0 0 0 16 6.667 9.301 9.301 0 0 0 9.4 9.4 9.302 9.302 0 0 0 6.667 16 9.301 9.301 0 0 0 9.4 22.6a9.3 9.3 0 0 0 6.6 2.733 9.3 9.3 0 0 0 6.6-2.734A9.3 9.3 0 0 0 25.333 16c0-1.26-.247-2.482-.733-3.633z" }))));
const XingIcon = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32" },
    React__default.createElement("g", { fill: "none", "fill-rule": "evenodd" },
        React__default.createElement("circle", { cx: "16", cy: "16", r: "16", fill: "#006064" }),
        React__default.createElement("g", { "fill-rule": "nonzero" },
            React__default.createElement("path", { fill: "#FFF", d: "M11.319 20.238H8.435a.425.425 0 0 1-.376-.195.438.438 0 0 1 0-.44l3.065-5.373c.003-.008.003-.01 0-.017l-1.95-3.354c-.082-.159-.094-.316-.017-.44.072-.119.218-.18.392-.18h2.885c.442 0 .66.283.802.538l1.984 3.436-3.114 5.47c-.147.266-.356.555-.787.555z" }),
            React__default.createElement("path", { fill: "#CDDC39", d: "m25.081 7.296-6.386 11.291c-.003.008-.003.014 0 .019l4.066 7.431c.08.16.084.322.007.445-.075.119-.208.185-.383.185h-2.881c-.44 0-.663-.293-.805-.551l-4.1-7.518c.205-.361 6.418-11.381 6.418-11.381.154-.277.343-.55.772-.55h2.916c.173 0 .31.065.383.185.075.123.072.283-.007.444z" })))));

const getSocialMediaIcon = (socialMedia) => {
    switch (socialMedia) {
        case "facebook":
            return React__default.createElement(FacebookIcon, null);
        case "twitter":
            return React__default.createElement(TwitterIcon, null);
        case "instagram":
            return React__default.createElement(InstagramIcon, null);
        case "linkedin":
            return React__default.createElement(LinkedinIcon, null);
        case "tiktok":
            return React__default.createElement(TikTokIcon, null);
        case "snapchat":
            return React__default.createElement(SnapchatIcon, null);
        case "dribbble":
            return React__default.createElement(DribbbleIcon, null);
        case "flickr":
            return React__default.createElement(FlickrIcon, null);
        case "github":
            return React__default.createElement(GithubIcon, null);
        case "line":
            return React__default.createElement(LineIcon, null);
        case "reddit":
            return React__default.createElement(RedditIcon, null);
        case "skype":
            return React__default.createElement(SkypeIcon, null);
        case "tripadvisor":
            return React__default.createElement(TripadvisorIcon, null);
        case "tumblr":
            return React__default.createElement(TumblrIcon, null);
        case "vimeo":
            return React__default.createElement(VimeoIcon, null);
        case "vkontakte":
            return React__default.createElement(VkontakteIcon, null);
        case "web":
            return React__default.createElement(WebIcon, null);
        case "xing":
            return React__default.createElement(XingIcon, null);
    }
};

const socialMediaValidators = {
    validators: {
        onBlurValidate: () => z
            .string()
            .regex(RegExp(URLRegex), "The entered value is not valid")
            .optional()
            .or(z.literal("")),
        onChangeValidate: () => z
            .string()
            .regex(RegExp(URLRegex), "The entered value is not valid")
            .optional()
            .or(z.literal("")),
        onSubmitValidate: () => z
            .string()
            .regex(RegExp(URLRegex), "The entered value is not valid")
            .optional()
            .or(z.literal("")),
    },
};
const VCardCompactFormSchema = (formContextProps) => {
    return {
        displayName: "vCard",
        fields: [
            {
                type: "text",
                name: "fullName",
                label: "Full Name",
                dataQA: "full-name-input",
                required: true,
                maxLength: 100,
                placeholder: "e.g. Johanna Smith",
                onBlurValidate: () => minMaxLengthValidation(1, 100),
                onChangeValidate: () => minMaxLengthValidation(1, 100),
                onSubmitValidate: () => minMaxLengthValidation(1, 100),
            },
            {
                type: "accordion",
                name: "vCardInformation",
                dataQA: "vcard-information-accordion",
                title: "vCard Information",
                id: "information-accordion",
                fields: [
                    // The profile image is no possible in a static form. Only add when making the api call
                    {
                        type: "fileUploader",
                        name: "profileImage",
                        dataQA: "profile-image-input",
                        isProfile: true,
                        fileConfig: {
                            type: "image",
                            maxSize: 2 * 1024 * 1024,
                            subTypes: ["jpg", "jpeg", "png"],
                        },
                    },
                    {
                        type: "text",
                        name: "email",
                        dataQA: "email-input",
                        placeholder: "e.g. youremail@domain.com",
                        label: "Email",
                        onBlurValidate: () => z.string().email("Email not valid").optional().or(z.literal("")),
                        onChangeValidate: () => z.string().email("Email not valid").optional().or(z.literal("")),
                        onSubmitValidate: () => z.string().email("Email not valid").optional().or(z.literal("")),
                    },
                    {
                        type: "group",
                        id: "phoneNumbers",
                        styledComponent: AddressGridContainer,
                        fields: [
                            {
                                type: "text",
                                name: "mobilePhone",
                                dataQA: "mobile-phone-input",
                                label: "Mobile Phone",
                                placeholder: "e.g. (123)-123-123-123",
                            },
                            {
                                type: "text",
                                name: "landlinePhone",
                                dataQA: "landline-phone-input",
                                label: "Landline",
                                placeholder: "e.g. (123)-123-123-123",
                            },
                            {
                                type: "text",
                                name: "fax",
                                dataQA: "fax-input",
                                label: "Fax",
                                placeholder: "e.g. (123)-123-123-123",
                            },
                        ],
                    },
                    {
                        type: "group",
                        id: "companyName",
                        styledComponent: AddressGridContainer,
                        fields: [
                            {
                                type: "text",
                                name: "website",
                                dataQA: "website-input",
                                label: "Website",
                                placeholder: "e.g. www.yourwebadress.com",
                                onBlurValidate: () => urlValidation(),
                                onChangeValidate: () => urlValidation(),
                                onSubmitValidate: () => urlValidation(),
                            },
                            {
                                type: "text",
                                name: "companyName",
                                dataQA: "company-name-input",
                                label: "Company name",
                                placeholder: "e.g. Workplace Yoga",
                            },
                            {
                                type: "text",
                                name: "profession",
                                dataQA: "profession-input",
                                label: "Profession",
                                placeholder: "e.g. Yoga teacher",
                            },
                        ],
                    },
                    {
                        type: "text",
                        name: "summary",
                        maxLength: 250,
                        dataQA: "summary-input",
                        label: "Summary",
                        placeholder: "e.g. I offer on-site yoga classes to promote wellness, reduce stress and improve productivity.",
                        onBlurValidate: () => maxLengthValidation(250),
                        onChangeValidate: () => maxLengthValidation(250),
                        onSubmitValidate: () => maxLengthValidation(250),
                    },
                ],
            },
            {
                type: "accordion",
                name: "companyLocation",
                dataQA: "company-location-accordion",
                title: "Location",
                id: "location-accordion",
                fields: [
                    {
                        type: "group",
                        id: "addressOne",
                        styledComponent: AddressFirstRow,
                        fields: [
                            {
                                type: "text",
                                name: "address",
                                label: "Address",
                                dataQA: "address-street-input",
                                placeholder: "e.g. High Street",
                                maxLength: 70,
                                onBlurValidate: () => maxLengthValidation(70),
                                onChangeValidate: () => maxLengthValidation(70),
                                onSubmitValidate: () => maxLengthValidation(70),
                            },
                            {
                                type: "text",
                                name: "numeration",
                                label: "Number",
                                dataQA: "address-numeration-input",
                                placeholder: "e.g. 10",
                                maxLength: 40,
                                onBlurValidate: () => maxLengthValidation(40),
                                onChangeValidate: () => maxLengthValidation(40),
                                onSubmitValidate: () => maxLengthValidation(40),
                            },
                            {
                                type: "text",
                                name: "zipCode",
                                dataQA: "address-zipcode-input",
                                label: "ZIP code",
                                placeholder: "e.g. 12348",
                                maxLength: 40,
                                onBlurValidate: () => maxLengthValidation(40),
                                onChangeValidate: () => maxLengthValidation(40),
                                onSubmitValidate: () => maxLengthValidation(40),
                            },
                        ],
                    },
                    {
                        type: "group",
                        id: "addressTwo",
                        styledComponent: AddressSecondRow,
                        fields: [
                            {
                                type: "text",
                                name: "city",
                                label: "City",
                                dataQA: "address-city-input",
                                placeholder: "e.g. High Street",
                                maxLength: 70,
                                onBlurValidate: () => maxLengthValidation(70),
                                onChangeValidate: () => maxLengthValidation(70),
                                onSubmitValidate: () => maxLengthValidation(70),
                            },
                            {
                                type: "text",
                                name: "state",
                                label: "State",
                                dataQA: "address-state-input",
                                placeholder: "e.g. 10",
                                maxLength: 70,
                                onBlurValidate: () => maxLengthValidation(70),
                                onChangeValidate: () => maxLengthValidation(70),
                                onSubmitValidate: () => maxLengthValidation(70),
                            },
                            {
                                type: "text",
                                name: "country",
                                label: "Country",
                                dataQA: "address-country-input",
                                placeholder: "e.g. 12348",
                                maxLength: 70,
                                onBlurValidate: () => maxLengthValidation(70),
                                onChangeValidate: () => maxLengthValidation(70),
                                onSubmitValidate: () => maxLengthValidation(70),
                            },
                        ],
                    },
                ],
            },
            {
                type: "accordion",
                name: "socialNetworks",
                dataQA: "social-networks-accordion",
                title: "Social networks",
                id: "social-networks-accordion",
                fields: [
                    {
                        type: "socialMediaPicker",
                        label: "Add link to...",
                        dataQA: "social-media-picker",
                        name: "socialNetworks",
                        items: [
                            {
                                id: "facebook",
                                content: getSocialMediaIcon("facebook"),
                            },
                            {
                                id: "instagram",
                                content: getSocialMediaIcon("instagram"),
                            },
                            {
                                id: "twitter",
                                content: getSocialMediaIcon("twitter"),
                            },
                            {
                                id: "dribbble",
                                content: getSocialMediaIcon("dribbble"),
                            },
                            {
                                id: "flickr",
                                content: getSocialMediaIcon("flickr"),
                            },
                            {
                                id: "github",
                                content: getSocialMediaIcon("github"),
                            },
                            {
                                id: "line",
                                content: getSocialMediaIcon("line"),
                            },
                            {
                                id: "linkedin",
                                content: getSocialMediaIcon("linkedin"),
                            },
                            {
                                id: "reddit",
                                content: getSocialMediaIcon("reddit"),
                            },
                            {
                                id: "skype",
                                content: getSocialMediaIcon("skype"),
                            },
                            {
                                id: "snapchat",
                                content: getSocialMediaIcon("snapchat"),
                            },
                            {
                                id: "tiktok",
                                content: getSocialMediaIcon("tiktok"),
                            },
                            {
                                id: "tripadvisor",
                                content: getSocialMediaIcon("tripadvisor"),
                            },
                            {
                                id: "tumblr",
                                content: getSocialMediaIcon("tumblr"),
                            },
                            {
                                id: "vimeo",
                                content: getSocialMediaIcon("vimeo"),
                            },
                            {
                                id: "vkontakte",
                                content: getSocialMediaIcon("vkontakte"),
                            },
                            {
                                id: "web",
                                content: getSocialMediaIcon("web"),
                            },
                            {
                                id: "xing",
                                content: getSocialMediaIcon("xing"),
                            },
                        ],
                        socialMediaMap: {
                            facebook: {
                                id: "facebook",
                                name: "Facebook",
                                icon: "facebook",
                                validators: socialMediaValidators.validators,
                                dataQA: "facebook-url-input",
                            },
                            instagram: {
                                id: "instagram",
                                name: "Instagram",
                                icon: "instagram",
                                validators: socialMediaValidators.validators,
                                dataQA: "instagram-url-input",
                            },
                            twitter: {
                                id: "twitter",
                                name: "Twitter",
                                icon: "twitter",
                                validators: socialMediaValidators.validators,
                                dataQA: "twitter-url-input",
                            },
                            dribbble: {
                                id: "dribbble",
                                name: "Dribbble",
                                icon: "dribbble",
                                validators: socialMediaValidators.validators,
                                dataQA: "dribbble-url-input",
                            },
                            flickr: {
                                id: "flickr",
                                name: "Flickr",
                                icon: "flickr",
                                validators: socialMediaValidators.validators,
                                dataQA: "flickr-url-input",
                            },
                            github: {
                                id: "github",
                                name: "Github",
                                icon: "github",
                                validators: socialMediaValidators.validators,
                                dataQA: "github-url-input",
                            },
                            line: {
                                id: "line",
                                name: "Line",
                                icon: "line",
                                validators: socialMediaValidators.validators,
                                dataQA: "line-url-input",
                            },
                            linkedin: {
                                id: "linkedin",
                                name: "Linkedin",
                                icon: "linkedin",
                                validators: socialMediaValidators.validators,
                                dataQA: "linkedin-url-input",
                            },
                            reddit: {
                                id: "reddit",
                                name: "Reddit",
                                icon: "reddit",
                                validators: socialMediaValidators.validators,
                                dataQA: "reddit-url-input",
                            },
                            skype: {
                                id: "skype",
                                name: "Skype",
                                icon: "skype",
                                validators: socialMediaValidators.validators,
                                dataQA: "skype-url-input",
                            },
                            snapchat: {
                                id: "snapchat",
                                name: "Snapchat",
                                icon: "snapchat",
                                validators: socialMediaValidators.validators,
                                dataQA: "snapchat-url-input",
                            },
                            tiktok: {
                                id: "tiktok",
                                name: "Tiktok",
                                icon: "tiktok",
                                validators: socialMediaValidators.validators,
                                dataQA: "tiktok-url-input",
                            },
                            tripadvisor: {
                                id: "tripadvisor",
                                name: "Tripadvisor",
                                icon: "tripadvisor",
                                validators: socialMediaValidators.validators,
                                dataQA: "tripadvisor-url-input",
                            },
                            tumblr: {
                                id: "tumblr",
                                name: "Tumblr",
                                icon: "tumblr",
                                validators: socialMediaValidators.validators,
                                dataQA: "tumblr-url-input",
                            },
                            vimeo: {
                                id: "vimeo",
                                name: "Vimeo",
                                icon: "vimeo",
                                validators: socialMediaValidators.validators,
                                dataQA: "vimeo-url-input",
                            },
                            vkontakte: {
                                id: "vkontakte",
                                name: "Vkontakte",
                                icon: "vkontakte",
                                validators: socialMediaValidators.validators,
                                dataQA: "vkontakte-url-input",
                            },
                            web: {
                                id: "web",
                                name: "Web",
                                icon: "web",
                                validators: socialMediaValidators.validators,
                                dataQA: "web-url-input",
                            },
                            xing: {
                                id: "xing",
                                name: "Xing",
                                icon: "xing",
                                validators: socialMediaValidators.validators,
                                dataQA: "xing-url-input",
                            },
                        },
                    },
                ],
            },
        ],
    };
};
const VCardExtendedFormSchema = (formContextProps) => {
    return {
        displayName: "vCard",
        fields: [
            {
                type: "text",
                name: "url",
                label: "VCARD",
                required: true,
                placeholder: "Enter URL",
                initialValue: "",
                onBlurValidate: () => z.string().url("Invalid URL"),
                onChangeValidate: () => z.string().url("Invalid URL"),
                onSubmitValidate: () => z.string().url("Invalid URL"),
            },
        ],
    };
};

const WifiGridContainer = styled.div `
  display: grid;
  grid-template-columns: 1fr;
  gap: ${getSpacing("size-04")};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 2fr);
    gap: ${getSpacing("size-05")};
  }

  ${getMarginBottom("size-06")};

  > div {
    ${getMarginBottom("size-02")};
  }

  > div:last-child {
    ${getMarginTop("size-04")};
    display: flex;
    align-items: center;
  }
`;

const WifiCompactFormSchema = (formContextProps) => {
    const { getValue } = formContextProps;
    const isPassword = getValue("qrNetworkSecurityType") !== "nopass";
    return {
        displayName: "Wifi",
        fields: [
            {
                type: "group",
                id: "wifi",
                styledComponent: WifiGridContainer,
                fields: [
                    {
                        type: "text",
                        name: "qrNetworkName",
                        label: "Network name (SSID)",
                        dataQA: "network-name-input",
                        required: true,
                        placeholder: "e.g. centralcafe",
                        initialValue: "",
                        maxLength: 100,
                        onBlurValidate: () => minMaxLengthValidation(1, 100),
                        onChangeValidate: () => minMaxLengthValidation(1, 100),
                        onSubmitValidate: () => minMaxLengthValidation(1, 100),
                    },
                    {
                        type: "password",
                        name: "qrNetworkPassword",
                        required: isPassword,
                        disabled: !isPassword,
                        dataQA: "network-password-input",
                        label: "Network password",
                        maxLength: 100,
                        placeholder: "e.g. mypassword",
                        onBlurValidate: (_, values) => {
                            const isPassword = values["qrNetworkSecurityType"] !== "nopass";
                            return isPassword ? minMaxLengthValidation(1, 100) : z.any();
                        },
                        onChangeValidate: (_, values) => {
                            const isPassword = values["qrNetworkSecurityType"] !== "nopass";
                            return isPassword ? minMaxLengthValidation(1, 100) : z.any();
                        },
                        onSubmitValidate: (_, values) => {
                            const isPassword = values["qrNetworkSecurityType"] !== "nopass";
                            return isPassword ? minMaxLengthValidation(1, 100) : z.any();
                        },
                    },
                    {
                        type: "select",
                        name: "qrNetworkSecurityType",
                        dataQA: "network-security-type-select",
                        label: "Security type",
                        options: [
                            { value: "nopass", label: "No password" },
                            { value: "WEP", label: "WEP" },
                            { value: "WPA", label: "WPA" },
                            { value: "WPA2", label: "WPA2" },
                            { value: "WPA3", label: "WPA3" },
                        ],
                        required: true,
                        placeholder: "Select",
                        tooltipHelper: {
                            item: "help",
                            tooltip: {
                                text: "To check your Wi-Fi security, go to the Wi-Fi icon on the taskbar and click on properties under your network. Scroll down until you find the security type.",
                                position: "top",
                            },
                        },
                        onBlurValidate: () => z.string(),
                        onChangeValidate: () => z.string(),
                        onSubmitValidate: () => z.string(),
                    },
                    {
                        type: "checkbox",
                        dataQA: "network-hidden-checkbox",
                        name: "qrNetworkHidden",
                        label: "Hidden network",
                    },
                ],
            },
        ],
    };
};
const WifiExtendedFormSchema = () => {
    return {
        displayName: "Wifi",
        fields: [
            {
                type: "text",
                name: "url",
                label: "WIFI",
                required: true,
                placeholder: "Enter URL",
                initialValue: "",
                onBlurValidate: () => z.string().url("Invalid URL"),
                onChangeValidate: () => z.string().url("Invalid URL"),
                onSubmitValidate: () => z.string().url("Invalid URL"),
            },
        ],
    };
};

const getFormSchemaByTypeAndMode = (type, mode) => {
    switch (type) {
        case "url-static":
            return mode === "compact" ? URLCompactFormSchema : URLExtendedFormSchema;
        case "vCard":
            return mode === "compact" ? VCardCompactFormSchema : VCardExtendedFormSchema;
        case "wifi":
            return mode === "compact" ? WifiCompactFormSchema : WifiExtendedFormSchema;
        default:
            // TODO: Add other types
            return mode === "compact" ? URLCompactFormSchema : URLExtendedFormSchema;
    }
};

const AccordionContainer = styled.div `
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0px 4.5px 10px 0px #0000000d;
  border: 1px solid ${getColor("neutral", 300)};
  background-color: ${getColor("neutral", "white")};
  font-family: ${getFont("family-primary")};
  scroll-margin-top: 300px;
  ${getPadding("size-05")};
`;
const AccordionHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;
const TitleContainer = styled.div `
  display: flex;
  flex-direction: column;
`;
const Title = styled.span `
  font-weight: 700;
  line-height: 24px;
  font-size: 18px; // TODO: ajustar con las fonts funtions
`;
const Subtitle = styled.span `
  font-weight: 400;
  line-height: 18px;
  font-size: 14px; // TODO: ajustar con las fonts funtions
  color: #666;
  ${getMarginTop("size-03")};
`;
const Content = styled.div `
  display: grid;
  grid-template-rows: ${({ $isExpanded }) => ($isExpanded ? "1fr" : "0fr")};
  transition: 0.1s ease-out;
  font-size: 16px; // TODO: ajustar con las fonts funtions
  overflow: hidden;
  height: ${({ $isExpanded }) => ($isExpanded ? "auto" : "0px")};
`;
const ContentChildren = styled.div `
  min-height: 0;
  overflow: hidden;
  ${getMarginTop("size-06")};
  ${getPaddingTop("size-04")};
`;
const Icon = styled.div `
  display: flex;
  align-self: flex-start;
  border-style: solid;
  border-width: 2px 2px 0 0;
  height: 8px;
  width: 8px;
  transition: all 0.25s ease-in-out;
  transform: ${({ $isExpanded }) => ($isExpanded ? "rotate(-45deg)" : "rotate(135deg)")};
  color: ${getColor("neutral", 900)};
  ${getMarginTop("size-02")};
`;

const Accordion = ({ title, subtitle, isExpanded: isExpandedProp, onToggle, children, className, style, dataQA, id: accordionId, }) => {
    const [isExpandedState, setIsExpandedState] = useState(false);
    const contentRef = useRef(null);
    const isControlled = isExpandedProp !== undefined;
    const isExpanded = isControlled ? isExpandedProp : isExpandedState;
    const handleToggle = () => {
        const newValue = !isExpanded;
        onToggle === null || onToggle === void 0 ? void 0 : onToggle(newValue, accordionId);
        if (!isControlled) {
            setIsExpandedState(newValue);
        }
    };
    return (React__default.createElement(AccordionContainer, { id: accordionId, className: className, "data-qa": dataQA, "$isExpanded": isExpanded, style: style },
        React__default.createElement(AccordionHeader, { "data-qa": `${dataQA}-header`, "$isExpanded": isExpanded, onClick: handleToggle },
            React__default.createElement(TitleContainer, null,
                React__default.createElement(Title, { "data-qa": `${dataQA}-header-title` }, title),
                subtitle && React__default.createElement(Subtitle, { "data-qa": `${dataQA}-header-subtitle` }, subtitle)),
            React__default.createElement(Icon, { "$isExpanded": isExpanded })),
        React__default.createElement(Content, { ref: contentRef, "$isExpanded": isExpanded, "data-qa": `${dataQA}-content` },
            React__default.createElement(ContentChildren, null, children))));
};

const FormContext = createContext(null);
const useFormContext = () => {
    const context = useContext(FormContext);
    if (!context) {
        throw new Error("useFormContext must be used within a Form component!");
    }
    return context;
};

const isFormValid = (errors, values, fields) => {
    const noErrors = Object.values(errors).every((error) => error.length === 0);
    const allRequiredFieldsValid = Object.entries(fields).every(([name, fieldData]) => {
        if (fieldData.required) {
            const value = values[name];
            return value !== undefined && value !== null && value !== "";
        }
        return true;
    });
    return noErrors && allRequiredFieldsValid;
};

const Field = (props) => {
    const { children, name, onBlurValidate, onChangeValidate, onSubmitValidate, listenTo, required } = props;
    const { isSubmitting, values, errors, fields, setValue, getValue, setErrors, registerField, unregisterField, validateField, } = useFormContext();
    const valuesRef = useRef(values);
    const value = values[name];
    const fieldErrors = errors[name] || [];
    const validatorsRef = useRef({
        onBlurValidate,
        onChangeValidate,
        onSubmitValidate,
    });
    useEffect(() => {
        valuesRef.current = values;
    }, [values]);
    useEffect(() => {
        validatorsRef.current = {
            onBlurValidate,
            onChangeValidate,
            onSubmitValidate,
        };
    }, [onBlurValidate, onChangeValidate, onSubmitValidate]);
    useEffect(() => {
        registerField(name, validatorsRef.current, listenTo, required);
        return () => {
            unregisterField(name);
        };
    }, [name, registerField, unregisterField, listenTo, required]);
    const revalidateDependentFields = useCallback((changedFieldName, updatedValues) => {
        Object.entries(fields).forEach(([fieldName, fieldData]) => {
            const { validators, listenTo } = fieldData;
            if (fieldName !== changedFieldName &&
                validators.onChangeValidate &&
                listenTo &&
                listenTo.includes(changedFieldName)) {
                try {
                    const fieldValue = updatedValues[fieldName];
                    const schema = validators.onChangeValidate(fieldValue, updatedValues);
                    schema.parse(fieldValue);
                    setErrors(fieldName, []);
                }
                catch (error) {
                    const errorMessages = error.errors
                        ? error.errors.map((e) => e.message)
                        : [error.message];
                    setErrors(fieldName, errorMessages);
                }
            }
        });
    }, [fields, setErrors]);
    const handleChange = useCallback((newValue) => {
        const updatedValues = Object.assign(Object.assign({}, valuesRef.current), { [name]: newValue });
        setValue(name, newValue);
        if (onChangeValidate) {
            try {
                const schema = onChangeValidate(newValue, updatedValues);
                schema.parse(newValue);
                setErrors(name, []);
            }
            catch (error) {
                const errorMessages = error.errors
                    ? error.errors.map((e) => e.message)
                    : [error.message];
                setErrors(name, errorMessages);
            }
        }
        revalidateDependentFields(name, updatedValues);
    }, [name, onChangeValidate, setValue, setErrors, revalidateDependentFields]);
    const handleBlur = useCallback(() => {
        const currentValue = getValue(name);
        const updatedValues = valuesRef.current;
        if (onBlurValidate) {
            try {
                const schema = onBlurValidate(currentValue, updatedValues);
                schema.parse(currentValue);
                setErrors(name, []);
            }
            catch (error) {
                const errorMessages = error.errors
                    ? error.errors.map((e) => e.message)
                    : [error.message];
                setErrors(name, errorMessages);
            }
        }
    }, [name, onBlurValidate, getValue, setErrors]);
    return (React__default.createElement(React__default.Fragment, null, children({
        value: value || "",
        onChange: handleChange,
        errors: fieldErrors,
        setErrors,
        onBlur: handleBlur,
        isValid: isFormValid(errors, values, fields),
        validate: validateField,
        isSubmitting,
        props,
    })));
};

function getFileExtension(filename) {
    return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase();
}

const FILE_TYPE_CONFIGS = {
    image: {
        mimeTypes: ["image/jpeg", "image/png", "image/gif", "image/svg+xml", "image/webp"],
        allowedExtensions: ["jpg", "jpeg", "png", "gif", "svg", "webp"],
    },
    video: {
        mimeTypes: ["video/mp4", "video/webm", "video/ogg"],
        allowedExtensions: ["mp4", "webm", "ogg"],
    },
    audio: {
        mimeTypes: ["audio/mpeg", "audio/wav", "audio/ogg"],
        allowedExtensions: ["mp3", "wav", "ogg"],
    },
    pdf: {
        mimeTypes: ["application/pdf"],
        allowedExtensions: ["pdf"],
    },
};
function validateFile(file, config) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            if (!file) {
                return {
                    isValid: false,
                    error: "No file provided",
                };
            }
            if (config.maxSize && file.size > config.maxSize) {
                return {
                    isValid: false,
                    error: `File size exceeds maximum of ${formatFileSize(config.maxSize)}`,
                };
            }
            const fileTypeValidation = yield validateFileType(file, config);
            if (!fileTypeValidation.isValid) {
                return fileTypeValidation;
            }
            let preview;
            if (fileTypeValidation.type === "image") {
                preview = yield createImagePreview(file);
            }
            return Object.assign(Object.assign({}, fileTypeValidation), { preview });
        }
        catch (error) {
            return {
                isValid: false,
                error: "Error validating file",
            };
        }
    });
}
function formatFileSize(bytes) {
    if (bytes === 0)
        return "Empty file";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
function createImagePreview(file) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                resolve(reader.result);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    });
}
function validateFileType(file, config) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const [type, subType] = file.type.split("/");
        const extension = getFileExtension(file.name);
        if (type === "application") {
            subType.trim !== config.type.trim && ({
                isValid: false,
                error: `Invalid file type. Expected ${config.type}`,
            });
        }
        else {
            type !== config.type && ({
                isValid: false,
                error: `Invalid file type. Expected ${config.type}`,
            });
        }
        const typeConfig = FILE_TYPE_CONFIGS[config.type];
        if (!typeConfig) {
            return {
                isValid: false,
                error: `Unsupported file type: ${config.type}`,
            };
        }
        if (!typeConfig.mimeTypes.includes(file.type)) {
            return {
                isValid: false,
                error: `Invalid file format. Supported formats: ${(_a = typeConfig.allowedExtensions) === null || _a === void 0 ? void 0 : _a.join(", ")}`,
            };
        }
        if (config.subTypes && config.subTypes !== "*") {
            if (!config.subTypes.includes(subType) && !config.subTypes.includes(extension)) {
                return {
                    isValid: false,
                    error: `Invalid file format. Supported formats: ${config.subTypes.join(", ")}`,
                };
            }
        }
        return {
            isValid: true,
            type: type,
            subType: subType,
        };
    });
}

const FileUploaderContext = createContext(null);
function fileUploaderReducer(state, action) {
    switch (action.type) {
        case "ADD_FILES":
            return Object.assign(Object.assign({}, state), { files: [...state.files, ...action.payload] });
        case "REMOVE_FILE":
            return Object.assign(Object.assign({}, state), { files: state.files.filter((file) => file.id !== action.payload) });
        case "REMOVE_ALL_FILES":
            return Object.assign(Object.assign({}, state), { files: action.payload });
        case "REORDER_FILES": {
            const { startIndex, endIndex } = action.payload;
            const newFiles = [...state.files];
            const [removed] = newFiles.splice(startIndex, 1);
            newFiles.splice(endIndex, 0, removed);
            return Object.assign(Object.assign({}, state), { files: newFiles });
        }
        case "SET_ERROR":
            return Object.assign(Object.assign({}, state), { error: action.payload });
        case "CLEAR_ERROR":
            return Object.assign(Object.assign({}, state), { error: undefined });
        default:
            return state;
    }
}
const FileUploaderProvider = ({ children, config, initialFiles = [], onChange, errorMessages = {}, }) => {
    const [state, dispatch] = useReducer(fileUploaderReducer, {
        files: initialFiles,
        error: undefined,
    });
    const processFiles = useCallback((files) => __awaiter(void 0, void 0, void 0, function* () {
        const processedFiles = [];
        for (const file of files) {
            const validation = yield validateFile(file, config);
            if (!validation.isValid) {
                dispatch({ type: "SET_ERROR", payload: validation.error || "Invalid file" });
                continue;
            }
            const uploadedFile = {
                id: v4(),
                file,
                type: validation.type,
                subType: validation.subType,
                size: file.size,
                name: file.name,
                preview: validation.preview,
            };
            processedFiles.push(uploadedFile);
        }
        return processedFiles;
    }), [config]);
    const addFiles = useCallback((newFiles) => __awaiter(void 0, void 0, void 0, function* () {
        if (state.files.length + newFiles.length > (config.maxNumFiles || 5)) {
            dispatch({
                type: "SET_ERROR",
                payload: errorMessages.maxFiles || `Maximum ${config.maxNumFiles || 5} files allowed`,
            });
            return;
        }
        const processedFiles = yield processFiles(newFiles);
        if (processedFiles.length > 0) {
            dispatch({ type: "ADD_FILES", payload: processedFiles });
            dispatch({ type: "CLEAR_ERROR" });
            onChange === null || onChange === void 0 ? void 0 : onChange([...state.files, ...processedFiles]);
        }
    }), [state.files, config, processFiles, onChange, errorMessages]);
    const removeFile = useCallback((id) => {
        dispatch({ type: "REMOVE_FILE", payload: id });
        const updatedFiles = state.files.filter((file) => file.id !== id);
        onChange === null || onChange === void 0 ? void 0 : onChange(updatedFiles);
    }, [state.files, onChange]);
    const removeAllFiles = useCallback(() => {
        dispatch({ type: "REMOVE_ALL_FILES", payload: [] });
        onChange === null || onChange === void 0 ? void 0 : onChange([]);
    }, [state.files, onChange]);
    const contextValue = {
        files: state.files,
        config,
        addFiles,
        removeFile,
        removeAllFiles,
        dispatch,
        hasError: !!state.error,
        errorMessage: state.error,
    };
    return (React__default.createElement(FileUploaderContext.Provider, { value: contextValue }, children));
};
const useFileUploader = () => {
    const context = useContext(FileUploaderContext);
    if (!context) {
        throw new Error("useFileUploader must be used within a FileUploaderProvider");
    }
    return context;
};

const ImagePlaceholder = () => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "45", height: "36" },
    React__default.createElement("defs", null,
        React__default.createElement("clipPath", { id: "a" },
            React__default.createElement("path", { d: "M1440 0v1116H0V0h1440z" })),
        React__default.createElement("clipPath", { id: "b" },
            React__default.createElement("path", { d: "M39 0a3 3 0 0 1 3 3v27a3 3 0 0 1-3 3H3a3 3 0 0 1-3-3V3a3 3 0 0 1 3-3h36z" })),
        React__default.createElement("clipPath", { id: "c" },
            React__default.createElement("path", { d: "m12.284 0 11.968 13.616L33.427 7.5 42 16.622V21a3 3 0 0 1-3 3H3a3 3 0 0 1-3-3v-4.378L12.284 0z" })),
        React__default.createElement("clipPath", { id: "d" },
            React__default.createElement("path", { d: "M4.5 0a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9z" }))),
    React__default.createElement("g", { clipPath: "url(#a)", transform: "translate(-164 -870)" },
        React__default.createElement("g", { clipPath: "url(#b)", transform: "translate(167 870)" },
            React__default.createElement("path", { fill: "#F2F2F2", d: "M0 0h42v33H0V0z" }),
            React__default.createElement("path", { fill: "none", stroke: "#BFBFBF", strokeMiterlimit: "5", strokeWidth: "3", d: "M3 0h36a3 3 0 0 1 3 3v27a3 3 0 0 1-3 3H3a3 3 0 0 1-3-3V3a3 3 0 0 1 3-3z" })),
        React__default.createElement("g", { clipPath: "url(#b)", transform: "translate(164 873)" },
            React__default.createElement("path", { fill: "#FFF", d: "M0 0h42v33H0V0z" }),
            React__default.createElement("path", { fill: "none", stroke: "#595959", strokeMiterlimit: "5", strokeWidth: "3", d: "M3 0h36a3 3 0 0 1 3 3v27a3 3 0 0 1-3 3H3a3 3 0 0 1-3-3V3a3 3 0 0 1 3-3z" })),
        React__default.createElement("g", { clipPath: "url(#b)", transform: "translate(164 873)" },
            React__default.createElement("path", { fill: "#FFF", d: "M0 0h42v33H0V0z" }),
            React__default.createElement("path", { fill: "none", stroke: "#BFBFBF", strokeMiterlimit: "5", strokeWidth: "3", d: "M3 0h36a3 3 0 0 1 3 3v27a3 3 0 0 1-3 3H3a3 3 0 0 1-3-3V3a3 3 0 0 1 3-3z" })),
        React__default.createElement("g", { clipPath: "url(#c)", transform: "translate(164 882)" },
            React__default.createElement("path", { fill: "#F2F2F2", d: "M0 0h42v24H0V0z" }),
            React__default.createElement("path", { fill: "none", stroke: "#BFBFBF", strokeLinejoin: "round", strokeWidth: "3", d: "m12.284 0 11.968 13.616L33.427 7.5 42 16.622V21a3 3 0 0 1-3 3H3a3 3 0 0 1-3-3v-4.378L12.284 0z" })),
        React__default.createElement("g", { clipPath: "url(#d)", transform: "translate(191 876)" },
            React__default.createElement("path", { fill: "#F2F2F2", d: "M0 0h9v9H0V0z" }),
            React__default.createElement("path", { fill: "none", stroke: "#BFBFBF", strokeMiterlimit: "5", strokeWidth: "3", d: "M4.5 9a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9z" })))));
const PlusIcon = () => (React__default.createElement("svg", { width: "12", height: "12", viewBox: "0 0 12 12", fill: "none" },
    React__default.createElement("path", { d: "M6 1v10M1 6h10", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" })));

const BUTTON_SIZES = {
    default: {
        fontSize: getFont("base-font-size"),
        height: getSpacing("size-09"), // TODO: Check height from each theme
        width: getSpacing("size-09"),
        padding: `${getSpacing("size-04")} ${getSpacing("size-05")}`,
        gap: getSpacing("size-04"),
    },
    small: {
        fontSize: getFont("base-font-size-sm"),
        height: getSpacing("size-07"), // TODO: Check height from each theme
        width: getSpacing("size-07"),
        padding: `${getSpacing("size-03")} ${getSpacing("size-04")}`,
        gap: getSpacing("size-03"),
    },
};
const BUTTON_SHAPES = {
    primary: createButtonVariant("primary"),
    "primary-green": createButtonVariant("primary-green"), // TODO: Hablar con UX/UI para decidir qué hacer con este token
    secondary: createButtonVariant("secondary"),
    neutral: createButtonVariant("neutral"),
    error: createButtonVariant("error"),
};
function getSizeStyles(size = "default") {
    return BUTTON_SIZES[size] || BUTTON_SIZES.default;
}
function getShapeStyles(variant = "primary", shape = "solid") {
    return BUTTON_SHAPES[variant][shape];
}
function createButtonVariant(colorKey) {
    const isNeutral = colorKey === "neutral";
    const borderWidth = getBorderWidth("default");
    const borderType = "solid"; // TODO: Use utility theme function
    const buildBorder = (color) => `${borderWidth} ${borderType} ${color}`;
    const resolveColors = (key, shade, neutralShade) => {
        return isNeutral ? getColor(key, neutralShade !== null && neutralShade !== void 0 ? neutralShade : shade) : getColor(key, shade);
    };
    return {
        solid: {
            backgroundColor: resolveColors(colorKey, 500, 900),
            border: buildBorder(resolveColors(colorKey, 500, 900)),
            color: getColor("neutral", "white"),
            hover: {
                backgroundColor: resolveColors(colorKey, 700, 100),
                border: buildBorder(resolveColors(colorKey, 700)),
                color: getColor("neutral", "white"),
            },
            active: {
                backgroundColor: resolveColors(colorKey, 900, 400),
                border: buildBorder(resolveColors(colorKey, 900)),
            },
            focus: {
                backgroundColor: resolveColors(colorKey, 500),
                border: buildBorder(resolveColors(colorKey, 900)),
            },
            disabled: {
                backgroundColor: getColor("neutral", 300),
                border: buildBorder(getColor("neutral", 300)),
                color: getColor("neutral", "white"),
            },
        },
        outlined: {
            backgroundColor: "transparent",
            color: resolveColors(colorKey, 500, 900),
            border: buildBorder(resolveColors(colorKey, 500, 900)),
            hover: {
                backgroundColor: resolveColors(colorKey, 100),
                border: buildBorder(resolveColors(colorKey, 700, 900)),
            },
            active: {
                backgroundColor: resolveColors(colorKey, 200, 400),
                border: buildBorder(resolveColors(colorKey, 700, 900)),
            },
            focus: {
                backgroundColor: getColor("neutral", "white"),
                border: buildBorder(resolveColors(colorKey, 900)),
            },
            disabled: {
                backgroundColor: getColor("neutral", "white"),
                border: buildBorder(getColor("neutral", 300)),
                color: getColor("neutral", 300),
            },
        },
        ghost: {
            backgroundColor: "transparent",
            border: buildBorder("transparent"),
            color: resolveColors(colorKey, 500, 900),
            hover: {
                backgroundColor: resolveColors(colorKey, 100),
                border: buildBorder(resolveColors(colorKey, 100)),
            },
            active: {
                backgroundColor: resolveColors(colorKey, 200),
                border: buildBorder(resolveColors(colorKey, 200)),
            },
            focus: {
                backgroundColor: getColor("neutral", "white"),
                border: buildBorder(resolveColors(colorKey, 900)),
            },
            disabled: {
                backgroundColor: getColor("neutral", "white"),
                border: buildBorder(getColor("neutral", "white")),
                color: getColor("neutral", 300),
            },
        },
    };
}
const IconWrapper = styled$1.i `
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: inherit;
    height: inherit;
  }
`;
const Spinner = styled$1.div `
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 0.6s linear infinite;
  border: ${({ size = "default" }) => `${getBorderWidth(size)} solid transparent`};
  border-top: ${({ variant = "primary", size = "default" }) => `${getBorderWidth(size)} solid ${getColor(variant, 700)}`};

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  &.spinner {
    position: absolute;
  }
`;
const StyledButton$1 = styled$1.button `
  align-items: center;
  border-radius: ${({ size = "default" }) => getBorderRadius(size)};
  justify-content: center;
  display: inline-flex;
  transition: all 0.3s ease;
  position: relative;
  font-family: ${getFont("family-primary")};
  font-weight: 700; // TODO: Use utility theme function
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  gap: ${({ size }) => { var _a; return (_a = getSizeStyles(size)) === null || _a === void 0 ? void 0 : _a.gap; }};
  padding: ${({ size }) => { var _a; return (_a = getSizeStyles(size)) === null || _a === void 0 ? void 0 : _a.padding; }};
  width: ${({ size, $hasIcon, $hasLabel }) => $hasIcon && !$hasLabel && size ? getSizeStyles(size).width : "auto"};
  height: ${({ size }) => getSizeStyles(size).height};

  text-decoration: none;
  color: inherit;

  ${({ variant, shape }) => getShapeStyles(variant, shape)}

  p {
    font-weight: 700;
    font-size: ${({ size }) => { var _a; return (_a = getSizeStyles(size)) === null || _a === void 0 ? void 0 : _a.fontSize; }};
  }

  &:hover {
    ${({ variant, shape, $isLoading }) => { var _a; return !$isLoading && ((_a = getShapeStyles(variant, shape)) === null || _a === void 0 ? void 0 : _a.hover); }}
  }

  &:active {
    ${({ variant, shape, $isLoading }) => { var _a; return !$isLoading && ((_a = getShapeStyles(variant, shape)) === null || _a === void 0 ? void 0 : _a.active); }}
  }

  &:focus,
  &:focus-active {
    ${({ variant, shape, $isLoading }) => { var _a; return !$isLoading && ((_a = getShapeStyles(variant, shape)) === null || _a === void 0 ? void 0 : _a.focus); }}
    outline-offset: 3px;
  }

  &:disabled {
    ${({ variant, shape }) => { var _a; return (_a = getShapeStyles(variant, shape)) === null || _a === void 0 ? void 0 : _a.disabled; }}
  }

  ${({ $isLoading }) => $isLoading &&
    css `
      & > *:not(.spinner) {
        visibility: hidden;
      }
    `}

  i {
    width: ${({ size }) => (size === "small" ? "16px" : "24px")};
    height: ${({ size }) => (size === "small" ? "16px" : "24px")};
  }
`;

const Button = ({ variant = "primary", size = "default", shape = "solid", state = "default", $isLoading = false, label, iconLeft, iconRight, dataQA, style, type, className, onClick, children, nofollow, href, target, }) => {
    const isDisabled = state === "disabled" || $isLoading;
    const hasIcon = Boolean(iconLeft) || Boolean(iconRight);
    const hasLabel = Boolean(label);
    const handleClick = (event) => {
        if (isDisabled) {
            event.preventDefault();
            return;
        }
        if (onClick)
            onClick(event);
    };
    return (React__default.createElement(StyledButton$1, { as: nofollow ? "a" : undefined, href: nofollow && href ? href : undefined, rel: nofollow ? "nofollow" : undefined, target: nofollow && target ? target : undefined, variant: variant, size: size, shape: shape, state: state, type: nofollow ? undefined : type, "data-qa": dataQA, style: style, onClick: handleClick, disabled: isDisabled, label: label, "$isLoading": $isLoading, "$hasIcon": hasIcon, "$hasLabel": hasLabel, className: className },
        children,
        $isLoading && React__default.createElement(Spinner, { className: "spinner" }),
        iconLeft && React__default.createElement(IconWrapper, null, iconLeft),
        label && React__default.createElement("p", null, label),
        iconRight && React__default.createElement(IconWrapper, null, iconRight)));
};

const ProfileUploaderWrapper = styled.div `
  display: flex;
  gap: ${getSpacing("size-06")};
  align-items: center;
  width: 100%;
`;
const ProfileContainer = styled.div `
  position: relative;
  width: 72px;
  height: 72px;
  border-radius: 8px;
  border: 2px solid
    ${({ $hasError }) => ($hasError ? getColor("error", 300) : getColor("neutral", 100))};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ $hasError }) => $hasError ? getColor("error", 300) : getColor("neutral", 100)};
  }
`;
const ImageContainer = styled.div `
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
`;
const UploadButton = styled.div `
  position: absolute;
  top: -12px;
  right: -12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${getColor("neutral", 900)};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  svg {
    width: 12px;
    height: 12px;
    color: ${getColor("neutral", "white")};
  }

  &:hover {
    transform: scale(1.1);
  }
`;
const DeleteButton = styled(Button) `
  display: flex;
  justify-content: center;
  min-width: 120px;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-width: 180px;
  }

  p {
    width: auto;
  }

  &:hover {
    background-color: ${getColor("neutral", "white")};
  }
`;

const ProfileUploader = ({ className, style, dataQA, singleUpload, onChange, }) => {
    const { files, addFiles, removeFile, removeAllFiles, hasError } = useFileUploader();
    useEffect(() => {
        onChange && onChange(files);
    }, [files]);
    const currentFile = files[0];
    const handleRemove = () => {
        if (currentFile) {
            removeFile(currentFile.id);
            return;
        }
    };
    const handleClick = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*,.svg";
        input.onchange = (event) => __awaiter(void 0, void 0, void 0, function* () {
            const fileList = event.target.files;
            if (fileList && fileList.length > 0) {
                if (singleUpload)
                    yield removeAllFiles();
                yield addFiles([fileList[0]]);
            }
        });
        input.click();
    };
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);
    const handleDrop = useCallback((e) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault();
        e.stopPropagation();
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            yield addFiles([files[0]]);
        }
    }), [addFiles]);
    return (React__default.createElement(ProfileUploaderWrapper, null,
        React__default.createElement(ProfileContainer, { "data-testid": "profile-uploader", onClick: handleClick, onDragOver: handleDragOver, onDrop: handleDrop, "$hasError": hasError, "data-qa": `${dataQA}-button`, className: className, style: style },
            React__default.createElement(ImageContainer, null, currentFile ? (React__default.createElement("img", { src: currentFile.preview, alt: "Profile", "data-qa": `${dataQA}-image`, width: "100%", height: "100%", style: { objectFit: "cover", borderRadius: "8px" } })) : (React__default.createElement(ImagePlaceholder, { "data-qa": `${dataQA}-image-placeholder`, "data-testid": "profile-placeholder" }))),
            React__default.createElement(UploadButton, { "data-qa": `${dataQA}-icon-button`, "aria-label": currentFile ? "Remove image" : "Add image", "$hasImage": !!currentFile }, currentFile ? React__default.createElement(FiEdit2, null) : React__default.createElement(PlusIcon, null))),
        files.length > 0 && (React__default.createElement(DeleteButton, { label: "Delete", shape: "outlined", variant: "primary", dataQA: "delete-logo-button", state: "default", onClick: handleRemove }))));
};

const PDFFileIconSvg = () => (React__default.createElement("svg", { width: "32", height: "32", viewBox: "0 0 32 32", xmlns: "http://www.w3.org/2000/svg" },
    React__default.createElement("g", { fill: "none", fillRule: "evenodd" },
        React__default.createElement("rect", { stroke: "#595959", fill: "#F2F2F2", x: "7.5", y: "3.5", width: "19", height: "24", rx: "2" }),
        React__default.createElement("rect", { stroke: "#595959", fill: "#FFF", x: "5.5", y: "5.5", width: "19", height: "24", rx: "2" }),
        React__default.createElement("g", { fill: "#595959", fillRule: "nonzero" },
            React__default.createElement("path", { d: "M17.02 15.244c-.495-.568-.984-1.202-1.424-1.772-.208-.27-.407-.526-.593-.76l-.013-.017c.272-.797.427-1.449.462-1.939.088-1.247-.047-2.05-.414-2.456a.894.894 0 0 0-.969-.248c-.25.086-.59.317-.783.928-.29.91-.15 2.524.672 3.858a28.872 28.872 0 0 1-1.442 3.201c-1.074.387-1.931.897-2.546 1.515-.803.806-1.13 1.607-.896 2.195.144.365.477.583.89.583.288 0 .599-.107.9-.31.761-.51 1.754-2.214 2.287-3.202a13.83 13.83 0 0 1 2.743-.553c.25-.025.499-.045.738-.059.968 1.052 1.76 1.606 2.49 1.744.146.028.293.043.436.043.594 0 1.086-.244 1.315-.653a.99.99 0 0 0-.01-.987c-.405-.72-1.626-1.117-3.439-1.117-.13 0-.265.002-.405.006zm-6.66 3.986c-.144.097-.292.154-.396.154a.161.161 0 0 1-.041-.004c-.018-.094.047-.503.69-1.15.302-.303.684-.58 1.137-.825-.571.944-1.084 1.62-1.39 1.825zm3.8-9.956c.068-.211.151-.307.203-.324l.003-.002c.05.058.263.39.168 1.74a4.95 4.95 0 0 1-.167.888c-.307-.814-.391-1.718-.206-2.302zm1.646 6.05c-.49.049-1.237.149-2.069.351.323-.663.622-1.334.871-1.957l.266.343c.3.39.64.83.99 1.258l-.058.005zm4.259 1.504c.014.025.013.034.01.04-.039.07-.214.18-.518.18-.085 0-.176-.01-.27-.027-.38-.073-.822-.343-1.35-.824 1.433.07 2.012.425 2.128.631zM12.813 21.595a.957.957 0 0 0-.385-.212 2.315 2.315 0 0 0-.62-.064h-.88c-.168 0-.294.04-.376.118-.083.08-.125.208-.125.382v2.688c0 .156.037.278.112.362.075.087.176.13.298.13a.378.378 0 0 0 .294-.13c.076-.086.114-.21.114-.367v-.92h.564c.434 0 .768-.097.992-.29.227-.195.343-.484.343-.86 0-.174-.028-.333-.083-.474a.962.962 0 0 0-.248-.363zm-.57 1.123a.406.406 0 0 1-.214.147c-.102.033-.232.05-.387.05h-.396v-.93h.396c.358 0 .503.073.56.132a.466.466 0 0 1 .115.33.458.458 0 0 1-.073.271zM16.068 21.651a1.161 1.161 0 0 0-.48-.264 2.43 2.43 0 0 0-.615-.07h-.895c-.166 0-.29.042-.368.123-.079.081-.119.208-.119.378v2.557c0 .12.01.216.032.293a.334.334 0 0 0 .153.203.597.597 0 0 0 .316.068h.895c.158 0 .303-.01.43-.031.13-.022.252-.06.365-.113.113-.053.22-.125.315-.214.12-.113.22-.244.298-.389.077-.144.134-.308.17-.486.037-.176.055-.374.055-.587 0-.651-.186-1.145-.552-1.468zm-.598 2.445a.49.49 0 0 1-.159.095.757.757 0 0 1-.185.043c-.062.006-.15.01-.262.01h-.455v-2.232h.388c.203 0 .377.023.517.068.131.04.245.14.336.297.094.16.142.408.142.738 0 .465-.108.795-.322.981zM19.184 21.319h-1.62a.616.616 0 0 0-.266.05.342.342 0 0 0-.17.173.664.664 0 0 0-.05.277v2.683c0 .161.039.286.114.369.076.085.176.129.297.129a.384.384 0 0 0 .294-.127c.076-.084.114-.209.114-.371V23.42h1.065a.4.4 0 0 0 .277-.088.314.314 0 0 0 .101-.243c0-.1-.033-.183-.099-.244-.064-.06-.158-.09-.279-.09h-1.065v-.757h1.287c.126 0 .223-.03.289-.093a.326.326 0 0 0 .101-.248c0-.1-.034-.183-.101-.246s-.164-.093-.29-.093z" })))));
const FileIconSvg = () => (React__default.createElement("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none" },
    React__default.createElement("path", { d: "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }),
    React__default.createElement("circle", { cx: "8", cy: "8", r: "2", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })));
const AudioIconSvg = () => (React__default.createElement("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none" },
    React__default.createElement("path", { d: "M12 3v18M5 9v6M19 9v6", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })));
const VideoIconSvg = () => (React__default.createElement("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none" },
    React__default.createElement("path", { d: "M9 6l7 6-7 6V6z", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })));
const FileIcon = ({ type }) => {
    switch (type) {
        case "pdf":
            return PDFFileIconSvg();
        case "audio":
            return AudioIconSvg();
        case "video":
            return VideoIconSvg();
        default:
            return FileIconSvg();
    }
};

const FileItemContainer = styled.div `
  display: flex;
  align-items: center;
  gap: 12px;
  ${getPaddingX("size-06")};
  ${getPaddingY("size-05")};
  height: 80px;
  border: 1px solid ${getColor("neutral", 300)};
  border-radius: 8px;
  background-color: ${getColor("neutral", "white")};
  transition: all 0.2s ease;
`;
const FilePreview = styled.div `
  width: 80px;
  height: 80px;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${getColor("neutral", 50)};

  svg {
    width: 80px;
    height: 80px;
  }

  img{
    object-fit: contain;
  }
`;
const FileInfo = styled.div `
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const FileTitle = styled.div `
  font-weight: 600;
  color: ${getColor("neutral", 900)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const FileSize = styled.div `
  font-size: 14px;
  color: ${getColor("neutral", 500)};
`;
const FileActions = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
`;
const ActionButton = styled.button `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${getColor("primary", 500)};
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${getColor("primary", 500)};
  font-size: 24px;

  &:hover {
    background-color: ${getColor("primary", 100)};
    border: none;
  }
`;
styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: ${getColor("neutral", 50)};
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const FileItem = ({ file, className, dataQA }) => {
    const { removeFile } = useFileUploader();
    const handleDelete = (e) => {
        e.stopPropagation();
        removeFile(file.id);
    };
    return (React__default.createElement(FileItemContainer, { className: className, "data-qa": `${dataQA}-file-list-item` },
        React__default.createElement(FilePreview, { "data-qa": `${dataQA}-file-list-item-preview` }, file.type === "image" ? (React__default.createElement("img", { src: file.preview, alt: file.name, width: "100%", height: "100%", style: { objectFit: "cover" } })) : (React__default.createElement(FileIcon, { type: file.type }))),
        React__default.createElement(FileInfo, null,
            React__default.createElement(FileTitle, { "data-qa": `${dataQA}-file-list-item-name` }, file.name),
            React__default.createElement(FileSize, { "data-qa": `${dataQA}-file-list-item-size` },
                "Size: ",
                formatFileSize(file.size))),
        React__default.createElement(FileActions, null,
            React__default.createElement(ActionButton, { "data-qa": `${dataQA}-file-list-item-remove-file`, type: "button", onClick: handleDelete, title: "Remove file" },
                React__default.createElement(MdDelete, null)))));
};

const FileListContainer = styled.div `
  display: flex;
  flex-direction: column;
  gap: 8px;
  ${getMarginTop("size-05")};
`;

const FileList = ({ className, style, dataQA }) => {
    const { files } = useFileUploader();
    if (!files.length)
        return null;
    return (React__default.createElement(FileListContainer, { className: className, "data-qa": `${dataQA}-file-list`, style: style }, files.map((file) => (React__default.createElement("div", { key: file.id },
        React__default.createElement(FileItem, { file: file, dataQA: dataQA }))))));
};

const ErrorMessageContainer = styled.div `
  color: ${getColor("error", 500)};
  display: flex;
  align-items: self-end;
`;
const ErrorMessageSpan = styled.span `
  font-family: ${getFont("family-primary")};
  font-size: 12px;
  line-height: 14px;
  font-weight: 400;
  ${getPaddingLeft("size-02")};
`;

const ErrorMessage = ({ errors = [], icon = true }) => {
    return (React__default.createElement(React__default.Fragment, null, Array.isArray(errors) ? (errors.map((error, index) => (React__default.createElement(ErrorMessageContainer, { key: index },
        icon && React__default.createElement(MdErrorOutline, null),
        React__default.createElement(ErrorMessageSpan, null, error))))) : (React__default.createElement(ErrorMessageContainer, null,
        icon && React__default.createElement(MdErrorOutline, null),
        React__default.createElement(ErrorMessageSpan, null, errors)))));
};

const DragDropContainer = styled.div `
  width: 100%;
  ${getMarginBottom("size-05")};

  .error-message {
    color: ${getColor("error", 500)};
    font-size: 14px;
    ${getMarginTop("size-03")};
  }
`;
const DropZone = styled.div `
  border: 2px dashed ${getColor("neutral", 300)};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${getColor("neutral", 50)};
  ${getPadding("size-05")};

  ${({ $isDragging }) => $isDragging &&
    css `
      border-color: ${getColor("primary", 300)};
      background-color: ${getColor("neutral", 100)};
    `}

  ${({ $isDisabled }) => $isDisabled &&
    css `
      opacity: 0.5;
      cursor: not-allowed;
    `}

  &:hover {
    ${({ $isDisabled }) => !$isDisabled &&
    css `
        border-color: ${getColor("primary", 700)};
    
      `}
  }
`;
const ContentContainer = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;
const SizeText = styled.span `
  color: ${getColor("neutral", 900)};
  font-size: 16px;
  font-height: 24px;
`;

const DragDropUploader = ({ buttonContent = "Add Files", comment, className, style, dataQA, }) => {
    const { files, addFiles, config: { type = "image", maxSize = 2 * 1024 * 1024, subTypes, maxNumFiles = 5 }, hasError, errorMessage, } = useFileUploader();
    const [isDragging, setIsDragging] = useState(false);
    const maxFiles = maxNumFiles;
    const isDisabled = files.length >= maxFiles;
    const handleFileSelect = useCallback((e) => {
        e.stopPropagation();
        if (isDisabled)
            return;
        const input = document.createElement("input");
        input.type = "file";
        input.multiple = true;
        input.accept = `${type}/*`;
        if (subTypes && subTypes !== "*") {
            input.accept = subTypes.map((subType) => `.${subType}`).join(",");
        }
        input.onchange = (event) => __awaiter(void 0, void 0, void 0, function* () {
            const fileList = event.target.files;
            if (fileList) {
                addFiles(Array.from(fileList));
            }
        });
        input.click();
    }, [type, subTypes, isDisabled, addFiles]);
    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDisabled) {
            setIsDragging(true);
        }
    }, [isDisabled]);
    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);
    const handleDrop = useCallback((e) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (isDisabled)
            return;
        const droppedFiles = Array.from(e.dataTransfer.files);
        addFiles(droppedFiles);
    }), [isDisabled, addFiles]);
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);
    return (React__default.createElement("div", null,
        React__default.createElement(DragDropContainer, { className: className, "data-qa": dataQA, style: style },
            React__default.createElement(DropZone, { onDragEnter: handleDragEnter, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, "$isDragging": isDragging, "$hasError": hasError, "$isDisabled": isDisabled },
                React__default.createElement(ContentContainer, null,
                    React__default.createElement(Button, { type: "button", shape: "solid", label: (buttonContent === null || buttonContent === void 0 ? void 0 : buttonContent.toString()) || "Add Files", state: isDisabled ? "disabled" : "default", onClick: handleFileSelect }),
                    React__default.createElement(SizeText, null, comment || `Maximum size: ${formatFileSize(maxSize)}`))),
            hasError && (React__default.createElement(ErrorMessage, { "data-qa": `${dataQA}-error-message`, errors: errorMessage }))),
        files.length > 0 && React__default.createElement(FileList, null)));
};

const FileUploaderContainer = styled.div `
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
  font-family: ${getFont("family-primary")};
`;

const FileUploader = ({ fileConfig, isProfile = false, initialFiles = [], onChange, errorMessages, buttonContent, comment, className, title, dataQA, style, singleUpload, }) => {
    const handleChange = (files) => {
        onChange && onChange(files);
    };
    return (React__default.createElement(FileUploaderProvider, { config: fileConfig, initialFiles: initialFiles, errorMessages: errorMessages },
        React__default.createElement(FileUploaderContainer, { className: className, "data-qa": dataQA, style: style },
            title,
            isProfile ? (React__default.createElement(ProfileUploader, { dataQA: dataQA, singleUpload: singleUpload, onChange: handleChange })) : (React__default.createElement(DragDropUploader, { buttonContent: buttonContent, comment: comment, dataQA: dataQA })))));
};

const useUIStore = create()((set) => ({
    expandedAccordions: {},
    lastToggledAccordion: null,
    toggleAccordion: (accordionId) => set((state) => {
        const current = !!state.expandedAccordions[accordionId];
        return {
            expandedAccordions: Object.assign(Object.assign({}, state.expandedAccordions), { [accordionId]: !current }),
            lastToggledAccordion: accordionId,
        };
    }),
    setAccordionExpanded: (accordionId, isExpanded) => set((state) => ({
        expandedAccordions: Object.assign(Object.assign({}, state.expandedAccordions), { [accordionId]: isExpanded }),
        lastToggledAccordion: accordionId,
    })),
}));

const COLOR_PICKER_SIZE = 200;
const ColorPickerPortal = ({ isOpen, onClose, color, onChange, triggerRef, dataQA, }) => {
    const window = getWindow();
    if (!window)
        return null;
    const popupRef = useRef(null);
    const [position, setPosition] = useState(null);
    const calculatePosition = () => {
        if (!triggerRef.current)
            return null;
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const scrollY = window.scrollY;
        const scrollX = window.scrollX;
        let top = triggerRect.bottom + scrollY + 8;
        let left = triggerRect.right + scrollX - COLOR_PICKER_SIZE;
        if (left + COLOR_PICKER_SIZE > window.innerWidth) {
            left = window.innerWidth - COLOR_PICKER_SIZE - 16;
        }
        const pickerHeight = COLOR_PICKER_SIZE;
        if (top + pickerHeight > window.innerHeight + scrollY) {
            top = triggerRect.top + scrollY - pickerHeight - 8;
        }
        return { top, left };
    };
    useEffect(() => {
        if (isOpen) {
            const initialPosition = calculatePosition();
            if (initialPosition) {
                setPosition(initialPosition);
            }
        }
        else {
            setPosition(null);
        }
    }, [isOpen]);
    useEffect(() => {
        if (!isOpen)
            return;
        const updatePosition = () => {
            const newPosition = calculatePosition();
            if (newPosition) {
                setPosition(newPosition);
            }
        };
        if (getWindow()) {
            window.addEventListener("scroll", updatePosition);
            window.addEventListener("resize", updatePosition);
        }
        return () => {
            if (!getWindow())
                return;
            window.removeEventListener("scroll", updatePosition);
            window.removeEventListener("resize", updatePosition);
        };
    }, [isOpen]);
    useEffect(() => {
        if (!isOpen)
            return;
        const handleClickOutside = (e) => {
            var _a, _b;
            if (e.target instanceof Node &&
                !((_a = popupRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target)) &&
                !((_b = triggerRef.current) === null || _b === void 0 ? void 0 : _b.contains(e.target))) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose]);
    if (!isOpen || !position)
        return null;
    // TODO: Move div to styled component. It is not working right now for some reason
    return createPortal(React__default.createElement("div", { ref: popupRef, style: {
            position: "absolute",
            top: position.top,
            left: position.left,
            zIndex: 1000,
            background: "white",
            padding: "8px",
            borderRadius: "4px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            border: "1px solid #ccc",
        } },
        React__default.createElement(HexColorPicker, { color: color, onChange: onChange, "data-qa": dataQA })), document.body);
};

var _path$C;
function _extends$E() { return _extends$E = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$E.apply(null, arguments); }
var SvgSwap = function SvgSwap(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$E({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "#328bff"
  }, props), _path$C || (_path$C = /*#__PURE__*/React.createElement("path", {
    d: "M4.8 12c0-2.097.918-3.978 2.358-5.292L9.3 8.85v-5.4H3.9l1.98 1.98A8.94 8.94 0 0 0 3 12a9 9 0 0 0 8.1 8.955v-1.818c-3.546-.44-6.3-3.474-6.3-7.137M21 12a9 9 0 0 0-8.1-8.955v1.818c3.546.441 6.3 3.474 6.3 7.137 0 2.097-.918 3.978-2.358 5.292L14.7 15.15v5.4h5.4l-1.98-1.98A8.94 8.94 0 0 0 21 12"
  })));
};

const StyledInputWrapper = styled.div `
  position: relative;
  margin-bottom: 1rem;
  font-family: ${getFont("family-primary")};
  display: flex;
  flex-direction: column;
  gap: 3px;
`;
const StyledLabel = styled.label `
  display: block;
`;
const StyledInput$2 = styled.input `
  width: 100%;
  padding: 0.5rem;
  height: 48px;
  box-sizing: border-box;
  border: 2px solid ${({ $hasError }) => ($hasError ? getColor("error", 300) : "#ccc")};
  border-radius: 4px;
  &:disabled {
    background-color: #f5f5f5;
  }

  &:focus {
    border-color: ${getColor("primary", 300)};
    outline: none;
  }
`;
styled.span `
  color: red;
  margin-top: 0.5rem;
`;
const StyledColorCircleButton = styled.button `
  position: absolute;
  right: 0.5rem;
  top: 0.5rem;
  width: 30px;
  height: 30px;
  background-color: ${({ color }) => color || "#000"};
  border: 1px solid #ccc;
  border-radius: 50%;
  cursor: pointer;
`;
const StyledColorInputGroup = styled.div `
  width: 100%;
  justify-content: space-between;
  display: flex;
  align-items: center;
  flex-direction: column;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: row;
    align-items: flex-start;
  }

  .swap-colors-button {
    top: unset;
    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
      top: 24px;
    }
  }

  > div {
    width: 100%;
  }
`;
const StyledSwapIcon = styled(SvgSwap) `
  fill: ${getColor("primary", 500)};
`;

const ColorInput = ({ id, name, label, required, placeholder = "#000000", onChange, onBlur, errors = [], isLoading = false, disabled = false, value, dataQA, className, initialValue, }) => {
    const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
    const inputRef = useRef(null);
    const colorButtonRef = useRef(null);
    const isValidHex = (hex) => {
        return /^#([0-9A-F]{6}|[0-9A-F]{3})$/i.test(hex);
    };
    const handleInputChange = (e) => {
        let newValue = e.target.value;
        if (newValue.length > 7) {
            newValue = newValue.slice(0, 7);
        }
        onChange(newValue);
    };
    const handleColorCircleClick = () => {
        setIsColorPickerOpen(!isColorPickerOpen);
    };
    const handleColorChange = (newColor) => {
        onChange(newColor);
    };
    const hasError = errors.length > 0 || ((value === null || value === void 0 ? void 0 : value.length) === 7 && !isValidHex(value));
    const isDisabled = isLoading || disabled;
    useEffect(() => {
        onChange(initialValue || "");
    }, [initialValue]);
    return (React__default.createElement(StyledInputWrapper, { className: className, "data-qa": dataQA, ref: inputRef },
        label && (React__default.createElement(StyledLabel, { htmlFor: id || name, "data-qa": `${dataQA}-label` },
            label,
            required && " *")),
        React__default.createElement("div", { style: { position: "relative" } },
            React__default.createElement(StyledInput$2, { id: id || name, "data-qa": `${dataQA}-input`, name: name, type: "text", placeholder: placeholder, onChange: handleInputChange, defaultValue: initialValue, onBlur: onBlur, value: value === "" ? undefined : value, disabled: isDisabled, "$hasError": hasError, maxLength: 7 }),
            React__default.createElement(StyledColorCircleButton, { type: "button", ref: colorButtonRef, onClick: handleColorCircleClick, "data-qa": `${dataQA}-circle-button`, disabled: isDisabled, "aria-label": "Select color", color: value || placeholder }),
            isColorPickerOpen && (React__default.createElement(ColorPickerPortal, { isOpen: isColorPickerOpen, onClose: () => setIsColorPickerOpen(false), color: value || placeholder, onChange: handleColorChange, triggerRef: colorButtonRef, dataQA: `${dataQA}-color-picker` }))),
        errors.map((error, index) => (React__default.createElement(ErrorMessage, { errors: [error], "data-qa": `${dataQA}-error-message`, key: index })))));
};

const ColorInputGroup = ({ color1, color2, onChangeColor1, onChangeColor2, renderColorInputOne, renderColorInputTwo, }) => {
    const handleSwap = () => {
        onChangeColor1(color2);
        onChangeColor2(color1);
    };
    return (React__default.createElement(StyledColorInputGroup, null,
        renderColorInputOne(),
        React__default.createElement(Button, { type: "button", variant: "primary", shape: "ghost", dataQA: "swap-colors-button", iconLeft: React__default.createElement(StyledSwapIcon, null), onClick: handleSwap, className: "swap-colors-button", style: { margin: "0 10px" }, label: "" }),
        renderColorInputTwo()));
};

const InputWrapper = styled.div `
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${getSpacing("size-01")};
`;
const Label = styled.label `
  display: block;
  color: ${getColor("neutral", 900)};
  font-family: ${getFont("family-primary")};
  font-size: 16px;
  font-weight: 400;
`;
const StyledInput$1 = styled.input `
  width: 100%;
  height: 48px;
  ${getPaddingX("size-03")};
  ${getPaddingY("size-04")};
  box-sizing: border-box;
  border: 2px solid
    ${({ $hasError, $hasSuccess }) => $hasError
    ? getColor("error", 300)
    : $hasSuccess
        ? getColor("success", 300)
        : getColor("neutral", 300)};
  border-radius: ${getSpacing("size-02")};
  color: ${getColor("neutral", 900)};
  font-size: 16px;
  background-color: ${getColor("neutral", "white")};

  &::placeholder {
    color: ${getColor("neutral", 600)};
    font-size: 16px;
    line-height: 24px;
    font-weight: 400;
  }
  &:hover {
    background-color: ${getColor("neutral", 50)};
  }
  &:focus {
    border-color: ${getColor("primary", 300)};
    outline: none;
  }
  &:disabled {
    background-color: ${getColor("neutral", 50)};
    border: 2px solid ${getColor("neutral", 100)};
    color: ${getColor("neutral", 400)};
    font-weight: 400;
    cursor: not-allowed;
  }
`;
const StyledTextarea = styled.textarea `
  width: 100%;
  ${getPaddingX("size-03")};
  ${getPaddingY("size-04")};
  box-sizing: border-box;
  border: 2px solid
    ${({ $hasError, $hasSuccess }) => $hasError
    ? getColor("error", 300)
    : $hasSuccess
        ? getColor("success", 300)
        : getColor("neutral", 300)};
  border-radius: ${getSpacing("size-02")};
  resize: vertical;
  min-height: 100px;
  font-size: 16px;
  background-color: ${getColor("neutral", "white")};

  &::placeholder {
    color: ${getColor("neutral", 600)};
    font-size: 16px;
    line-height: 24px;
    font-weight: 400;
  }
  &:hover {
    background-color: ${getColor("neutral", 50)};
  }
  &:focus {
    border-color: ${getColor("primary", 300)};
    outline: none;
  }
  &:disabled {
    background-color: ${getColor("neutral", 50)};
    border: 2px solid ${getColor("neutral", 100)};
    color: ${getColor("neutral", 400)};
    cursor: not-allowed;
  }
`;
const TogglePasswordButton = styled.button `
  background: none;
  border: none;
  display: contents;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  padding: 0;
  color: ${({ $disabled }) => ($disabled ? getColor("neutral", 400) : "black")};
`;
const SuccessIcon = styled.div `
  color: ${getColor("success", 300)};
  display: contents;
`;
const ErrorAndCounterWrapper = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${getSpacing("size-01")};
`;
const CharCounter = styled.span `
  font-size: 12px;
  font-weight: 400;
  line-height: 14px;
  color: ${getColor("neutral", 700)};
`;
const IconContainer$1 = styled.div `
  position: absolute;
  top: 50%;
  right: ${getSpacing("size-03")};
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TooltipLabelContainer = styled.div `
  display: flex;
  flex-direction: row;
  gap: ${getSpacing("size-04")};
  align-items: center;
  width: 100%;
`;
const TooltipWrapper = styled.div `
   display: inline-block;
  position: relative;
  text-align: center;

  svg {
    position: relative;
    bottom: -3px;
  }
`;
const TooltipText = styled.div `
  min-width: 250px;
  max-width: 350px;
  padding: 10px;
  color:  ${getColor("neutral", 800)};
  background-color: ${getColor("primary", 50)} ;
  border: 1px solid  ${getColor("primary", 200)} ;
  border-radius: 8px;
  position: absolute;
  z-index: 3;
  box-sizing: border-box;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;

  i {
    position: absolute;
    overflow: hidden;
  }

  i::after {
    content: "";
    position: absolute;
    width: 12px;
    height: 12px;
    background-color: ${getColor("primary", 50)};
    border: 1px solid ${getColor("primary", 200)};
  }

  &.top {
    top: -10px;
    left: 50%;
    transform: translate(-50%, -100%);
  
    i {
      top: 100%;
      left: 50%;
      margin-left: -12px;
      width: 24px;
      height: 12px;
    }
    i::after {
      left: 50%;
      transform: translate(-50%, -50%) rotate(45deg);
    }
  }

  &.left {
    top: 50%;
    right: 100%;
    transform: translate(0, -50%);
  
    i {
      top: 50%;
      left: 100%;
      margin-top: -12px;
      width: 12px;
      height: 24px;
    }
    i::after {
      left: 0;
      top: 50%;
      transform: translate(-50%, -50%) rotate(-45deg);
    }
  }

  &.bottom {
    top: 100%;
    left: 50%;
    transform: translate(-50%, 0);
  
    i {
      bottom: 100%;
      left: 50%;
      margin-left: -12px;
      width: 24px;
      height: 12px;
    }
    i::after {
      left: 50%;
      transform: translate(-50%, 50%) rotate(45deg);
    }
  }

  &.right {
    top: 50%;
    left: 100%;
    transform: translate(0, -50%);

    i {
      top: 50%;
      right: 100%;
      margin-top: -12px;
      width: 12px;
      height: 24px;
    }

    i::after {
      left: 0;
      top: 50%;
      transform: translate(50%, -50%) rotate(-45deg);
    }
  }
`;

function Tooltip({ text, children, position = "top", id }) {
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipOn = () => setShowTooltip(true);
    const tooltipOff = () => setShowTooltip(false);
    const closeTooltip = (ev) => {
        if (ev.key === "Escape")
            setShowTooltip(false);
    };
    useEffect(() => {
        document.addEventListener("keydown", closeTooltip);
        return () => {
            document.removeEventListener("keydown", closeTooltip);
        };
    }, []);
    useEffect(() => {
        if (showTooltip) {
            setTimeout(() => {
                setShowTooltip(false);
            }, 8000);
        }
    }, [showTooltip]);
    return (React__default.createElement(TooltipWrapper, { "data-testid": "tooltip", onMouseEnter: tooltipOn, onFocus: tooltipOn, onMouseLeave: tooltipOff, onBlur: tooltipOff },
        showTooltip && (React__default.createElement(TooltipText, { className: `tooltip-text-container ${position}`, "data-placement": position, role: "tooltip", "aria-hidden": "true", id: id },
            React__default.createElement("i", null),
            text)),
        children));
}

const InputTooltipHelper = ({ tooltipHelper, id }) => {
    const { item, tooltip } = tooltipHelper;
    const { text, position } = tooltip;
    const icon = (item) => {
        if (item === "help")
            return React__default.createElement(GrCircleQuestion, { className: "tooltip-helper-icon" });
    };
    return (React__default.createElement(Tooltip, { text: text, position: position, id: `${item}-${id}` }, icon(item)));
};

const Input = (_a) => {
    var { id, name, label, required, placeholder, type = "text", onChange, onBlur, errors = [], isLoading = false, maxLength, disabled = false, value, className, dataQA, style, showCharCounter = false, tooltipHelper } = _a, rest = __rest(_a, ["id", "name", "label", "required", "placeholder", "type", "onChange", "onBlur", "errors", "isLoading", "maxLength", "disabled", "value", "className", "dataQA", "style", "showCharCounter", "tooltipHelper"]);
    const [inputType, setInputType] = useState(type);
    const [charCount, setCharCount] = useState((value === null || value === void 0 ? void 0 : value.length) || 0);
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setCharCount(newValue.length);
        if (!maxLength || newValue.length <= maxLength) {
            onChange(newValue);
        }
    };
    const togglePasswordVisibility = () => {
        !isDisabled && setInputType((prevType) => (prevType === "password" ? "text" : "password"));
    };
    const isPasswordType = type === "password";
    const hasError = errors.length > 0;
    const hasSuccess = !hasError && charCount > 0;
    const isDisabled = isLoading || disabled;
    return (React__default.createElement(InputWrapper, { className: className, "data-qa": dataQA, style: style },
        React__default.createElement(TooltipLabelContainer, null,
            label && (React__default.createElement(Label, { htmlFor: id || name, "data-qa": `${dataQA}-label` },
                label,
                required && " *")),
            tooltipHelper && InputTooltipHelper({ tooltipHelper, id })),
        type === "textarea" ? (React__default.createElement(StyledTextarea, Object.assign({ id: id || name, name: name, "data-qa": `${dataQA}-textarea`, placeholder: placeholder, onChange: handleInputChange, onBlur: onBlur, value: value, disabled: isDisabled, "$hasError": hasError, "$hasSuccess": hasSuccess, maxLength: maxLength }, rest))) : (React__default.createElement("div", { style: { position: "relative" } },
            React__default.createElement(StyledInput$1, Object.assign({ id: id || name, name: name, type: inputType, "data-qa": `${dataQA}-field`, placeholder: placeholder, onChange: handleInputChange, onBlur: onBlur, value: value, disabled: isDisabled, "$hasError": hasError, "$hasSuccess": hasSuccess, maxLength: maxLength }, rest)),
            React__default.createElement(IconContainer$1, null,
                isPasswordType && (React__default.createElement(TogglePasswordButton, { type: "button", "data-qa": `${dataQA}-toggle-password`, onClick: togglePasswordVisibility, "$disabled": isDisabled, "aria-label": "Toggle password visibility" }, inputType === "password" ? (React__default.createElement(MdVisibility, { size: 16 })) : (React__default.createElement(MdVisibilityOff, { size: 16 })))),
                hasSuccess && (React__default.createElement(SuccessIcon, null,
                    React__default.createElement(MdOutlineCheckCircle, { size: 16 })))))),
        (hasError || maxLength) && (React__default.createElement(ErrorAndCounterWrapper, null,
            hasError ? (React__default.createElement(ErrorMessage, { "data-qa": `${dataQA}-error-message`, errors: errors.join(", ") })) : (React__default.createElement("span", null)),
            maxLength && showCharCounter && (React__default.createElement(CharCounter, { "data-qa": `${dataQA}-char-counter` },
                charCount,
                "/",
                maxLength))))));
};

var _path$B, _path2$b;
function _extends$D() { return _extends$D = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$D.apply(null, arguments); }
var SvgFrameNone = function SvgFrameNone(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$D({
    xmlns: "http://www.w3.org/2000/svg",
    width: 50,
    height: 66,
    fill: "none"
  }, props), _path$B || (_path$B = /*#__PURE__*/React.createElement("path", {
    fill: "#404040",
    fillRule: "evenodd",
    d: "M45.273 1.295H4.363a3.07 3.07 0 0 0-3.068 3.069v57.272a3.07 3.07 0 0 0 3.068 3.068h40.91a3.07 3.07 0 0 0 3.068-3.068V4.364a3.07 3.07 0 0 0-3.068-3.069M4.363.273a4.09 4.09 0 0 0-4.09 4.09v57.273a4.09 4.09 0 0 0 4.09 4.091h40.91a4.09 4.09 0 0 0 4.09-4.09V4.363a4.09 4.09 0 0 0-4.09-4.091z",
    clipRule: "evenodd"
  })), _path2$b || (_path2$b = /*#__PURE__*/React.createElement("path", {
    fill: "#404040",
    d: "M24.668 39.771a7.17 7.17 0 0 0 4.45-1.534 6.73 6.73 0 0 0 2.432-3.912 6.57 6.57 0 0 0-.69-4.514 6.93 6.93 0 0 0-3.5-3.065 7.25 7.25 0 0 0-4.72-.227 7 7 0 0 0-3.797 2.716 6.6 6.6 0 0 0-1.15 4.425 6.68 6.68 0 0 0 2.018 4.126 7 7 0 0 0 2.272 1.47c.851.341 1.764.516 2.685.515m3.972-2.936a5.7 5.7 0 0 1-3.614 1.57 5.75 5.75 0 0 1-3.786-1.129l7.857-7.602a5.3 5.3 0 0 1 1.17 3.664 5.35 5.35 0 0 1-1.627 3.497m-7.945-7.67a5.7 5.7 0 0 1 3.614-1.57 5.75 5.75 0 0 1 3.786 1.128l-7.857 7.603a5.3 5.3 0 0 1-1.169-3.656 5.33 5.33 0 0 1 1.627-3.489z"
  })));
};

var _path$A, _path2$a, _path3$9, _path4$8, _path5$8, _path6$8, _path7$8, _path8$8, _path9$8, _path10$8, _path11$8, _path12$8, _path13$8, _path14$8, _path15$8, _path16$8, _path17$8, _path18$8, _path19$8, _path20$8, _path21$8, _path22$8, _path23$8, _path24$8, _path25$8, _path26$8, _path27$8, _path28$8, _path29$8, _path30$8, _path31$8, _path32$8, _path33$8, _path34$7, _path35$7, _path36$6, _path37$6, _path38$6, _path39$6, _path40$6;
function _extends$C() { return _extends$C = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$C.apply(null, arguments); }
var SvgFrameMobile = function SvgFrameMobile(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$C({
    xmlns: "http://www.w3.org/2000/svg",
    width: 50,
    height: 66,
    fill: "none"
  }, props), _path$A || (_path$A = /*#__PURE__*/React.createElement("path", {
    fill: "#404040",
    fillRule: "evenodd",
    d: "M4.293.273a4.09 4.09 0 0 0-4.09 4.09v57.273a4.09 4.09 0 0 0 4.09 4.091h40.91a4.09 4.09 0 0 0 4.09-4.09V4.363c0-2.26-1.831-4.091-4.09-4.091zM28.43 60.409a3.682 3.682 0 1 1-7.363 0 3.682 3.682 0 0 1 7.364 0m-1.022 0a2.66 2.66 0 1 1-5.319 0 2.66 2.66 0 0 1 5.319 0",
    clipRule: "evenodd"
  })), _path2$a || (_path2$a = /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    d: "M3.475 12.545H46.02V55.09H3.475zM13.121 8.289q-.496 0-.933-.135a2.2 2.2 0 0 1-.736-.38l.264-.577q.314.24.657.35.35.11.754.11.448 0 .688-.154a.5.5 0 0 0 .245-.441.41.41 0 0 0-.227-.375q-.221-.128-.742-.245a4.4 4.4 0 0 1-.878-.27 1.24 1.24 0 0 1-.51-.393q-.171-.24-.171-.607 0-.38.209-.682.208-.3.589-.466.38-.171.877-.172.454 0 .865.141.412.135.675.375l-.264.577a2 2 0 0 0-1.276-.46q-.417 0-.663.171a.54.54 0 0 0-.239.467.43.43 0 0 0 .215.386q.214.135.73.252.546.123.884.276.344.147.521.38a.92.92 0 0 1 .184.59q0 .386-.208.68a1.3 1.3 0 0 1-.595.448 2.5 2.5 0 0 1-.915.154M17.19 8.289q-.638 0-1.116-.27a1.87 1.87 0 0 1-.73-.78q-.252-.503-.252-1.171 0-.67.252-1.166.257-.504.73-.774.479-.276 1.116-.276.436 0 .823.135t.65.38l-.264.578a2.1 2.1 0 0 0-.583-.338 1.7 1.7 0 0 0-.613-.11q-.626 0-.97.405-.337.405-.337 1.166 0 .766.337 1.172.344.405.97.405.325 0 .613-.105.29-.11.583-.343l.264.576q-.264.246-.65.38-.387.136-.823.136M22.274 8.234 21.84 7.22h-2.105l-.436 1.013h-.797l1.957-4.326h.644l1.958 4.326zm-2.282-1.626h1.583l-.792-1.841zM26.089 3.908h.742v4.326h-.595l-2.32-3.02v3.02h-.736V3.908h.59l2.319 3.012zM32.35 3.908h.62v4.326h-.699v-2.86l-1.141 2.16h-.528l-1.148-2.13.006 2.83h-.699V3.908h.626l1.485 2.828zM33.567 8.234V3.908h2.897v.625H34.34v1.19h2v.62h-2v1.265h2.123v.626z"
  })), _path3$9 || (_path3$9 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M9.376 26.585h-.992v.991h.992zM9.376 27.576h-.992v.992h.992zM9.376 28.568h-.992v.992h.992zM9.376 31.543h-.992v.992h.992zM9.376 32.535h-.992v.992h.992zM9.376 35.51h-.992v.992h.992zM9.376 37.494h-.992v.992h.992zM9.376 39.477h-.992v.992h.992zM9.376 40.469h-.992v.992h.992zM10.367 28.568h-.991v.992h.991z"
  })), _path4$8 || (_path4$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M10.367 29.56h-.991v.992h.991zM10.367 30.552h-.991v.991h.991zM10.367 32.535h-.991v.992h.991zM10.367 35.51h-.991v.992h.991zM10.367 36.502h-.991v.992h.991zM10.367 37.494h-.991v.992h.991zM11.359 25.593h-.992v.992h.992zM11.359 27.576h-.992v.992h.992zM11.359 28.568h-.992v.992h.992zM11.359 34.519h-.992v.991h.992zM11.359 35.51h-.992v.992h.992zM11.359 36.502h-.992v.992h.992zM11.359 38.486h-.992v.991h.992zM11.359 39.477h-.992v.992h.992zM11.359 40.469h-.992v.992h.992zM12.35 26.585h-.991v.991h.992z"
  })), _path5$8 || (_path5$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M12.35 27.576h-.991v.992h.992zM12.35 28.568h-.991v.992h.992z"
  })), _path6$8 || (_path6$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M12.35 29.56h-.991v.992h.992zM12.35 30.552h-.991v.991h.992zM12.35 32.535h-.991v.992h.992zM12.35 34.519h-.991v.991h.992zM12.35 38.486h-.991v.991h.992zM12.35 39.477h-.991v.992h.992zM12.35 40.469h-.991v.992h.992z"
  })), _path7$8 || (_path7$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M12.35 41.46h-.991v.992h.992zM13.343 25.593h-.992v.992h.991zM13.343 30.552h-.992v.991h.991zM13.343 31.543h-.992v.992h.991zM13.343 32.535h-.992v.992h.991zM13.343 33.527h-.992v.992h.991zM13.343 34.519h-.992v.991h.991zM13.343 36.502h-.992v.992h.991zM13.343 40.469h-.992v.992h.991z"
  })), _path8$8 || (_path8$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M13.343 41.46h-.992v.992h.991zM14.334 25.593h-.992v.992h.992zM14.334 26.585h-.992v.991h.992zM14.334 27.576h-.992v.992h.992zM14.334 31.543h-.992v.992h.992zM14.334 33.527h-.992v.992h.992zM14.334 34.519h-.992v.991h.992zM14.334 37.494h-.992v.992h.992zM14.334 38.486h-.992v.991h.992zM15.326 25.593h-.992v.992h.992zM15.326 27.576h-.992v.992h.992zM15.326 29.56h-.992v.992h.992zM15.326 31.543h-.992v.992h.992zM15.326 33.527h-.992v.992h.992zM15.326 35.51h-.992v.992h.992zM15.326 37.494h-.992v.992h.992zM15.326 39.477h-.992v.992h.992zM15.326 41.46h-.992v.992h.992zM16.318 27.576h-.992v.992h.992zM16.318 28.568h-.992v.992h.992z"
  })), _path9$8 || (_path9$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M16.318 29.56h-.992v.992h.992zM16.318 31.543h-.992v.992h.992zM16.318 33.527h-.992v.992h.992zM16.318 34.519h-.992v.991h.992zM16.318 36.502h-.992v.992h.992zM16.318 37.494h-.992v.992h.992zM16.318 38.486h-.992v.991h.992zM16.318 39.477h-.992v.992h.992zM16.318 40.469h-.992v.992h.992zM17.31 18.65h-.992v.993h.991zM17.31 21.626h-.992v.992h.991zM17.31 23.61h-.992v.991h.991zM17.31 25.593h-.992v.992h.991zM17.31 26.585h-.992v.991h.991zM17.31 27.576h-.992v.992h.991z"
  })), _path10$8 || (_path10$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M17.31 28.568h-.992v.992h.991zM17.31 32.535h-.992v.992h.991zM17.31 33.527h-.992v.992h.991zM17.31 34.519h-.992v.991h.991zM17.31 38.486h-.992v.991h.991zM17.31 39.477h-.992v.992h.991zM17.31 42.453h-.992v.991h.991zM17.31 44.436h-.992v.992h.991zM17.31 45.428h-.992v.991h.991zM17.31 47.411h-.992v.992h.991zM18.301 17.66h-.992v.99h.992zM18.301 20.634h-.992v.992h.992zM18.301 21.626h-.992v.992h.992z"
  })), _path11$8 || (_path11$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18.301 22.618h-.992v.992h.992zM18.301 24.601h-.992v.992h.992zM18.301 26.585h-.992v.991h.992zM18.301 27.576h-.992v.992h.992zM18.301 28.568h-.992v.992h.992z"
  })), _path12$8 || (_path12$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18.301 29.56h-.992v.992h.992zM18.301 30.552h-.992v.991h.992zM18.301 31.543h-.992v.992h.992zM18.301 32.535h-.992v.992h.992zM18.301 34.519h-.992v.991h.992zM18.301 35.51h-.992v.992h.992zM18.301 38.486h-.992v.991h.992zM18.301 40.469h-.992v.992h.992zM18.301 42.453h-.992v.991h.992zM18.301 46.42h-.992v.991h.992zM18.301 47.411h-.992v.992h.992zM18.301 48.403h-.992v.992h.992zM19.293 17.66H18.3v.99h.992zM19.293 22.618H18.3v.992h.992zM19.293 23.61H18.3v.991h.992z"
  })), _path13$8 || (_path13$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M19.293 28.568H18.3v.992h.992z"
  })), _path14$8 || (_path14$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M19.293 29.56H18.3v.992h.992zM19.293 30.552H18.3v.991h.992zM19.293 31.543H18.3v.992h.992zM19.293 35.51H18.3v.992h.992zM19.293 37.494H18.3v.992h.992zM19.293 40.469H18.3v.992h.992zM19.293 42.453H18.3v.991h.992zM19.293 46.42H18.3v.991h.992zM20.285 18.65h-.992v.993h.992zM20.285 21.626h-.992v.992h.992z"
  })), _path15$8 || (_path15$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M20.285 22.618h-.992v.992h.992zM20.285 24.601h-.992v.992h.992zM20.285 28.568h-.992v.992h.992zM20.285 31.543h-.992v.992h.992zM20.285 32.535h-.992v.992h.992zM20.285 36.502h-.992v.992h.992zM20.285 37.494h-.992v.992h.992zM20.285 39.477h-.992v.992h.992zM20.285 41.46h-.992v.992h.992zM20.285 42.453h-.992v.991h.992zM20.285 43.444h-.992v.992h.992zM20.285 48.403h-.992v.992h.992zM21.276 18.65h-.991v.993h.991zM21.276 23.61h-.991v.991h.991zM21.276 25.593h-.991v.992h.991zM21.276 26.585h-.991v.991h.991zM21.276 29.56h-.991v.992h.991zM21.276 30.552h-.991v.991h.991zM21.276 31.543h-.991v.992h.991zM21.276 39.477h-.991v.992h.991zM21.276 40.469h-.991v.992h.991z"
  })), _path16$8 || (_path16$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M21.276 41.46h-.991v.992h.991zM21.276 42.453h-.991v.991h.991zM21.276 43.444h-.991v.992h.991zM21.276 44.436h-.991v.992h.991zM21.276 45.428h-.991v.991h.991zM21.276 46.42h-.991v.991h.991zM22.268 17.66h-.992v.99h.992zM22.268 21.626h-.992v.992h.992zM22.268 24.601h-.992v.992h.992zM22.268 26.585h-.992v.991h.992zM22.268 27.576h-.992v.992h.992zM22.268 28.568h-.992v.992h.992z"
  })), _path17$8 || (_path17$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M22.268 29.56h-.992v.992h.992zM22.268 30.552h-.992v.991h.992zM22.268 32.535h-.992v.992h.992zM22.268 34.519h-.992v.991h.992zM22.268 35.51h-.992v.992h.992zM22.268 36.502h-.992v.992h.992zM22.268 37.494h-.992v.992h.992zM22.268 46.42h-.992v.991h.992zM23.26 21.626h-.992v.992h.992zM23.26 23.61h-.992v.991h.992zM23.26 24.601h-.992v.992h.992zM23.26 25.593h-.992v.992h.992zM23.26 29.56h-.992v.992h.992zM23.26 30.552h-.992v.991h.992zM23.26 32.535h-.992v.992h.992zM23.26 33.527h-.992v.992h.992zM23.26 34.519h-.992v.991h.992zM23.26 37.494h-.992v.992h.992zM23.26 39.477h-.992v.992h.992zM23.26 40.469h-.992v.992h.992z"
  })), _path18$8 || (_path18$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M23.26 41.46h-.992v.992h.992zM23.26 42.453h-.992v.991h.992zM23.26 43.444h-.992v.992h.992zM23.26 44.436h-.992v.992h.992zM23.26 45.428h-.992v.992h.992zM23.26 47.411h-.992v.992h.992zM23.26 48.403h-.992v.992h.992zM24.252 17.66h-.992v.99h.992zM24.252 18.65h-.992v.993h.992zM24.252 20.634h-.992v.992h.992zM24.252 26.585h-.992v.991h.992zM24.252 27.576h-.992v.992h.992zM24.252 29.56h-.992v.992h.992zM24.252 30.552h-.992v.991h.992zM24.252 33.527h-.992v.992h.992zM24.252 34.519h-.992v.991h.992zM24.252 38.486h-.992v.991h.992z"
  })), _path19$8 || (_path19$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M24.252 40.469h-.992v.992h.992zM24.252 42.453h-.992v.991h.992zM24.252 43.444h-.992v.992h.992zM24.252 45.428h-.992v.991h.992zM25.243 17.66h-.991v.99h.991zM25.243 18.65h-.991v.993h.991zM25.243 22.618h-.991v.992h.991zM25.243 23.61h-.991v.991h.991zM25.243 26.585h-.991v.991h.991zM25.243 31.543h-.991v.992h.991zM25.243 32.535h-.991v.992h.991zM25.243 34.519h-.991v.991h.991zM25.243 36.502h-.991v.992h.991zM25.243 37.494h-.991v.992h.991zM25.243 38.486h-.991v.991h.991zM25.243 39.477h-.991v.992h.991zM25.243 41.46h-.991v.992h.991zM25.243 43.444h-.991v.992h.991zM25.243 45.428h-.991v.991h.991zM25.243 46.42h-.991v.991h.991zM25.243 48.403h-.991v.992h.991zM26.235 17.66h-.992v.99h.992zM26.235 20.634h-.992v.992h.992zM26.235 21.626h-.992v.992h.992zM26.235 25.593h-.992v.992h.992zM26.235 26.585h-.992v.991h.992zM26.235 27.576h-.992v.992h.992zM26.235 28.568h-.992v.992h.992zM26.235 30.552h-.992v.991h.992zM26.235 34.519h-.992v.991h.992zM26.235 39.477h-.992v.992h.992zM26.235 40.469h-.992v.992h.992z"
  })), _path20$8 || (_path20$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M26.235 41.46h-.992v.992h.992zM26.235 46.42h-.992v.991h.992zM26.235 47.411h-.992v.992h.992zM26.235 48.403h-.992v.992h.992zM27.227 17.66h-.992v.99h.992zM27.227 21.626h-.992v.992h.992z"
  })), _path21$8 || (_path21$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.227 22.618h-.992v.992h.992zM27.227 23.61h-.992v.991h.992zM27.227 24.601h-.992v.992h.992zM27.227 25.593h-.992v.992h.992zM27.227 29.56h-.992v.992h.992zM27.227 31.543h-.992v.992h.992zM27.227 32.535h-.992v.992h.992zM27.227 33.527h-.992v.992h.992zM27.227 35.51h-.992v.992h.992zM27.227 38.486h-.992v.991h.992zM27.227 39.477h-.992v.992h.992zM27.227 40.469h-.992v.992h.992z"
  })), _path22$8 || (_path22$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.227 41.46h-.992v.992h.992zM27.227 42.453h-.992v.991h.992zM27.227 44.436h-.992v.992h.992zM27.227 46.42h-.992v.991h.992zM27.227 48.403h-.992v.992h.992zM28.219 18.65h-.992v.993h.991zM28.219 19.643h-.992v.991h.991zM28.219 20.634h-.992v.992h.991zM28.219 21.626h-.992v.992h.991z"
  })), _path23$8 || (_path23$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M28.219 22.618h-.992v.992h.991zM28.219 25.593h-.992v.992h.991zM28.219 29.56h-.992v.992h.991zM28.219 35.51h-.992v.992h.991zM28.219 37.494h-.992v.992h.991zM28.219 38.486h-.992v.991h.991zM28.219 41.46h-.992v.992h.991zM28.219 42.453h-.992v.991h.991zM28.219 43.444h-.992v.992h.991zM28.219 44.436h-.992v.992h.991zM28.219 48.403h-.992v.992h.991zM28.219 49.395h-.992v.991h.991zM29.21 17.66h-.992v.99h.992zM29.21 18.65h-.992v.993h.992zM29.21 20.634h-.992v.992h.992z"
  })), _path24$8 || (_path24$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.21 21.626h-.992v.992h.992zM29.21 23.61h-.992v.991h.992zM29.21 25.593h-.992v.992h.992zM29.21 26.585h-.992v.991h.992zM29.21 28.568h-.992v.992h.992zM29.21 31.543h-.992v.992h.992zM29.21 32.535h-.992v.992h.992zM29.21 36.502h-.992v.992h.992zM29.21 37.494h-.992v.992h.992zM29.21 42.453h-.992v.991h.992zM29.21 43.444h-.992v.992h.992zM29.21 44.436h-.992v.992h.992zM29.21 45.428h-.992v.991h.992zM29.21 46.42h-.992v.991h.992zM30.202 17.66h-.992v.99h.992zM30.202 19.643h-.992v.991h.992zM30.202 24.601h-.992v.992h.992zM30.202 25.593h-.992v.992h.992zM30.202 28.568h-.992v.992h.992z"
  })), _path25$8 || (_path25$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M30.202 29.56h-.992v.992h.992zM30.202 33.527h-.992v.992h.992zM30.202 36.502h-.992v.992h.992zM30.202 39.477h-.992v.992h.992zM30.202 40.469h-.992v.992h.992z"
  })), _path26$8 || (_path26$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M30.202 41.46h-.992v.992h.992zM30.202 47.411h-.992v.992h.992zM30.202 48.403h-.992v.992h.992zM31.194 19.643h-.992v.991h.992zM31.194 20.634h-.992v.992h.992zM31.194 21.626h-.992v.992h.992z"
  })), _path27$8 || (_path27$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.194 22.618h-.992v.992h.992zM31.194 23.61h-.992v.991h.992zM31.194 24.601h-.992v.992h.992zM31.194 25.593h-.992v.992h.992zM31.194 27.576h-.992v.992h.992zM31.194 28.568h-.992v.992h.992z"
  })), _path28$8 || (_path28$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.194 29.56h-.992v.992h.992zM31.194 30.552h-.992v.991h.992zM31.194 31.543h-.992v.992h.992zM31.194 35.51h-.992v.992h.992zM31.194 36.502h-.992v.992h.992zM31.194 39.477h-.992v.992h.992zM31.194 40.469h-.992v.992h.992zM31.194 42.453h-.992v.991h.992zM31.194 48.403h-.992v.992h.992zM32.185 19.643h-.991v.991h.991zM32.185 22.618h-.991v.992h.991zM32.185 24.601h-.991v.992h.991zM32.185 26.585h-.991v.991h.991zM32.185 27.576h-.991v.992h.991zM32.185 28.568h-.991v.992h.991zM32.185 30.552h-.991v.991h.991zM32.185 33.527h-.991v.992h.991zM32.185 36.502h-.991v.992h.991zM32.185 37.494h-.991v.992h.991zM32.185 38.486h-.991v.991h.991zM32.185 39.477h-.991v.992h.991zM32.185 42.453h-.991v.991h.991zM32.185 43.444h-.991v.992h.991zM32.185 48.403h-.991v.992h.991zM33.177 17.66h-.992v.99h.992zM33.177 18.65h-.992v.992h.992zM33.177 19.643h-.992v.991h.992zM33.177 22.618h-.992v.992h.992zM33.177 23.61h-.992v.991h.992zM33.177 24.601h-.992v.992h.992zM33.177 27.576h-.992v.992h.992zM33.177 29.56h-.992v.992h.992zM33.177 31.543h-.992v.992h.992zM33.177 32.535h-.992v.992h.992zM33.177 34.519h-.992v.991h.992zM33.177 35.51h-.992v.992h.992zM33.177 38.486h-.992v.991h.992zM33.177 39.477h-.992v.992h.992zM33.177 41.46h-.992v.992h.992zM33.177 42.453h-.992v.991h.992zM33.177 43.444h-.992v.992h.992zM33.177 44.436h-.992v.992h.992zM33.177 45.428h-.992v.991h.992zM33.177 46.42h-.992v.991h.992zM33.177 49.395h-.992v.991h.992zM34.169 25.593h-.992v.992h.992zM34.169 27.576h-.992v.992h.992zM34.169 28.568h-.992v.992h.992z"
  })), _path29$8 || (_path29$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M34.169 29.56h-.992v.992h.992zM34.169 30.552h-.992v.991h.992zM34.169 35.51h-.992v.992h.992zM34.169 38.486h-.992v.991h.992zM34.169 40.469h-.992v.992h.992z"
  })), _path30$8 || (_path30$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M34.169 41.46h-.992v.992h.992zM34.169 45.428h-.992v.991h.992zM34.169 47.411h-.992v.992h.992zM34.169 48.403h-.992v.992h.992zM35.16 26.585h-.991v.991h.992zM35.16 30.552h-.991v.991h.992zM35.16 31.543h-.991v.992h.992zM35.16 32.535h-.991v.992h.992zM35.16 33.527h-.991v.992h.992zM35.16 37.494h-.991v.992h.992zM35.16 41.46h-.991v.992h.992zM35.16 43.444h-.991v.992h.992zM35.16 45.428h-.991v.991h.992zM35.16 46.42h-.991v.991h.992zM35.16 48.403h-.991v.992h.992zM36.152 26.585h-.991v.991h.991zM36.152 30.552h-.991v.991h.991zM36.152 32.535h-.991v.992h.991zM36.152 39.477h-.991v.992h.991zM36.152 41.46h-.991v.992h.991zM36.152 45.428h-.991v.991h.991zM36.152 48.403h-.991v.992h.991zM36.152 49.395h-.991v.991h.991zM37.144 25.593h-.992v.992h.992zM37.144 28.568h-.992v.992h.992z"
  })), _path31$8 || (_path31$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M37.144 29.56h-.992v.992h.992zM37.144 31.543h-.992v.992h.992zM37.144 32.535h-.992v.992h.992zM37.144 36.502h-.992v.992h.992zM37.144 39.477h-.992v.992h.992zM37.144 41.46h-.992v.992h.992zM37.144 42.453h-.992v.991h.992zM37.144 43.444h-.992v.992h.992zM37.144 44.436h-.992v.992h.992zM37.144 45.428h-.992v.991h.992zM37.144 46.42h-.992v.991h.992zM37.144 48.403h-.992v.992h.992zM37.144 49.395h-.992v.991h.992zM38.136 27.576h-.992v.992h.992zM38.136 28.568h-.992v.992h.992z"
  })), _path32$8 || (_path32$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M38.136 29.56h-.992v.992h.992zM38.136 30.552h-.992v.991h.992zM38.136 34.519h-.992v.991h.992zM38.136 36.502h-.992v.992h.992zM38.136 37.494h-.992v.992h.992zM38.136 40.469h-.992v.992h.992zM38.136 43.444h-.992v.992h.992zM38.136 44.436h-.992v.992h.992zM38.136 45.428h-.992v.991h.992zM38.136 47.411h-.992v.992h.992zM38.136 48.403h-.992v.992h.992zM39.128 29.56h-.992v.992h.992zM39.128 31.543h-.992v.992h.992zM39.128 34.519h-.992v.991h.992zM39.128 37.494h-.992v.992h.992zM39.128 38.486h-.992v.991h.992zM39.128 42.453h-.992v.991h.992zM39.128 43.444h-.992v.992h.992zM39.128 44.436h-.992v.992h.992zM39.128 45.428h-.992v.991h.992zM39.128 46.42h-.992v.991h.992zM39.128 47.411h-.992v.992h.992zM40.12 25.593h-.992v.992h.991zM40.12 26.585h-.992v.991h.991zM40.12 31.543h-.992v.992h.991zM40.12 32.535h-.992v.992h.991zM40.12 35.51h-.992v.992h.991zM40.12 38.486h-.992v.991h.991zM40.12 41.46h-.992v.992h.991zM40.12 46.42h-.992v.991h.991z"
  })), _path33$8 || (_path33$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M41.111 26.585h-.992v.991h.992zM41.111 29.56h-.992v.992h.992zM41.111 31.543h-.992v.992h.992zM41.111 36.502h-.992v.992h.992zM41.111 37.494h-.992v.992h.992zM41.111 38.486h-.992v.991h.992zM41.111 39.477h-.992v.992h.992zM41.111 41.46h-.992v.992h.992zM41.111 49.395h-.992v.991h.992z"
  })), _path34$7 || (_path34$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M8.384 17.66V24.6h6.942V17.66zm.992.99h4.958v4.96H9.376z",
    clipRule: "evenodd"
  })), _path35$7 || (_path35$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M13.343 19.643h-2.976v2.975h2.975z"
  })), _path36$6 || (_path36$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M41.111 17.66H34.17V24.6h6.942zm-.992.99v4.96h-4.958v-4.96z",
    clipRule: "evenodd"
  })), _path37$6 || (_path37$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M39.128 22.618v-2.976h-2.976v2.976z"
  })), _path38$6 || (_path38$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M39.128 19.643h-2.976v2.975h2.976z"
  })), _path39$6 || (_path39$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M8.384 50.386h6.942v-6.942H8.384zm.992-.992v-4.958h4.958v4.959z",
    clipRule: "evenodd"
  })), _path40$6 || (_path40$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M13.343 45.428h-2.976v2.975h2.975z"
  })));
};

var _rect$8, _path$z, _path2$9, _path3$8, _path4$7, _path5$7, _path6$7, _path7$7, _path8$7, _path9$7, _path10$7, _path11$7, _path12$7, _path13$7, _path14$7, _path15$7, _path16$7, _path17$7, _path18$7, _path19$7, _path20$7, _path21$7, _path22$7, _path23$7, _path24$7, _path25$7, _path26$7, _path27$7, _path28$7, _path29$7, _path30$7, _path31$7, _path32$7, _path33$7, _path34$6, _path35$6, _path36$5, _path37$5, _path38$5, _path39$5, _path40$5, _path41$5, _path42$4, _path43$4, _path44$4, _path45$4, _path46$3, _path47$3, _path48$3, _path49$3;
function _extends$B() { return _extends$B = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$B.apply(null, arguments); }
var SvgFrameBlack = function SvgFrameBlack(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$B({
    xmlns: "http://www.w3.org/2000/svg",
    width: 50,
    height: 66,
    fill: "none"
  }, props), _rect$8 || (_rect$8 = /*#__PURE__*/React.createElement("rect", {
    width: 49.091,
    height: 65.454,
    x: 0.131,
    y: 0.273,
    fill: "#404040",
    rx: 2.727
  })), _path$z || (_path$z = /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    d: "M3.199 3.341h42.545v42.545H3.199zM13.05 57.38q-.496 0-.933-.135a2.2 2.2 0 0 1-.736-.38l.264-.578q.312.24.656.35.35.11.755.11.449 0 .687-.153a.5.5 0 0 0 .246-.442.41.41 0 0 0-.227-.374q-.221-.13-.743-.245a4.4 4.4 0 0 1-.877-.27 1.24 1.24 0 0 1-.51-.393q-.171-.24-.171-.608 0-.38.208-.68.21-.302.59-.467.38-.172.877-.172.454 0 .865.141.412.135.675.375l-.264.576a2 2 0 0 0-1.276-.46q-.418 0-.663.172a.54.54 0 0 0-.24.466.43.43 0 0 0 .216.387q.214.135.73.252.547.122.883.276.344.147.522.38a.92.92 0 0 1 .184.59q0 .386-.209.68a1.3 1.3 0 0 1-.595.448 2.5 2.5 0 0 1-.914.154M17.12 57.38q-.64 0-1.118-.27a1.87 1.87 0 0 1-.73-.78q-.251-.503-.251-1.172t.251-1.166q.258-.503.73-.773.48-.276 1.117-.276.435 0 .823.135.386.135.65.38l-.264.577a2.1 2.1 0 0 0-.583-.337 1.7 1.7 0 0 0-.614-.11q-.625 0-.97.404-.337.406-.337 1.166 0 .768.338 1.172.343.405.97.405.324 0 .613-.104a2.2 2.2 0 0 0 .583-.344l.264.577q-.264.245-.65.38-.387.136-.823.136M22.203 57.325l-.436-1.013h-2.104l-.436 1.013h-.798l1.958-4.327h.644l1.957 4.327zm-2.283-1.627h1.583l-.791-1.84zM26.018 52.998h.742v4.327h-.595l-2.32-3.02v3.02h-.736v-4.327h.59l2.319 3.013zM32.28 52.998h.62v4.327h-.7v-2.86l-1.142 2.16h-.527l-1.148-2.13.006 2.83h-.7v-4.327h.627l1.485 2.83zM33.496 57.325v-4.327h2.896v.626H34.27v1.19h2v.62h-2V56.7h2.124v.626z"
  })), _path2$9 || (_path2$9 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M9.304 17.38h-.992v.992h.992zM9.304 18.372h-.992v.991h.992zM9.304 19.364h-.992v.991h.992zM9.304 22.339h-.992v.992h.992z"
  })), _path3$8 || (_path3$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M9.304 23.33h-.992v.992h.992zM9.304 26.306h-.992v.991h.992zM9.304 28.29h-.992v.991h.992zM9.304 30.273h-.992v.991h.992zM9.304 31.264h-.992v.992h.992zM10.296 19.364h-.992v.991h.992zM10.296 20.355h-.992v.992h.992zM10.296 21.347h-.992v.992h.992z"
  })), _path4$7 || (_path4$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M10.296 23.33h-.992v.992h.992zM10.296 26.306h-.992v.991h.992zM10.296 27.298h-.992v.991h.992zM10.296 28.29h-.992v.991h.992zM11.288 16.388h-.992v.992h.992zM11.288 18.372h-.992v.992h.992zM11.288 19.364h-.992v.991h.992zM11.288 25.314h-.992v.992h.992zM11.288 26.306h-.992v.991h.992zM11.288 27.298h-.992v.991h.992zM11.288 29.28l-.992.001v.992h.992z"
  })), _path5$7 || (_path5$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M11.288 30.273h-.992v.991h.992zM11.288 31.264h-.992v.992h.992zM12.28 17.38h-.992v.992h.991zM12.28 18.372h-.992v.991h.991zM12.28 19.364h-.992v.991h.991zM12.28 20.355h-.992v.992h.991zM12.28 21.347h-.992v.992h.991zM12.28 23.33h-.992v.992h.991zM12.28 25.314h-.992v.992h.991zM12.28 29.28h-.992v.993h.991zM12.28 30.273h-.992v.991h.991zM12.28 31.264h-.992v.992h.991zM12.28 32.256h-.992v.992h.991z"
  })), _path6$7 || (_path6$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M13.271 16.388h-.992v.992h.992zM13.271 21.347h-.992v.992h.992z"
  })), _path7$7 || (_path7$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M13.271 22.339h-.992v.992h.992z"
  })), _path8$7 || (_path8$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M13.271 23.33h-.992v.992h.992zM13.271 24.322h-.992v.992h.992zM13.271 25.314h-.992v.992h.992zM13.271 27.298h-.992v.991h.992zM13.271 31.264h-.992v.992h.992zM13.271 32.256h-.992v.992h.992zM14.263 16.388h-.992v.992h.992z"
  })), _path9$7 || (_path9$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M14.263 17.38h-.992v.992h.992zM14.263 18.372h-.992v.991h.992zM14.263 22.339h-.992v.992h.992zM14.263 24.322h-.992v.992h.992zM14.263 25.314h-.992v.992h.992zM14.263 28.29h-.992v.991h.992zM14.263 29.281h-.992v.992h.992zM15.255 16.388h-.992v.992h.992zM15.255 18.372h-.992v.991h.992zM15.255 20.355h-.992v.992h.992zM15.255 22.339h-.992v.992h.992zM15.255 24.322h-.992v.992h.992zM15.255 26.306h-.992v.991h.992zM15.255 28.29h-.992v.991h.992zM15.255 30.273h-.992v.991h.992zM15.255 32.256h-.992v.992h.992z"
  })), _path10$7 || (_path10$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M16.246 18.372h-.991v.991h.991zM16.246 19.364h-.991v.991h.991zM16.246 20.355h-.991v.992h.991zM16.246 22.339h-.991v.992h.991zM16.246 24.322h-.991v.992h.991zM16.246 25.314h-.991v.992h.991zM16.246 27.298h-.991v.991h.991zM16.246 28.29h-.991v.991h.991zM16.246 29.281h-.991v.992h.991zM16.246 30.273h-.991v.991h.991zM16.246 31.264h-.991v.992h.991zM17.238 9.446h-.992v.992h.992zM17.238 12.421h-.992v.992h.992zM17.238 14.405h-.992v.992h.992zM17.238 16.388h-.992v.992h.992z"
  })), _path11$7 || (_path11$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M17.238 17.38h-.992v.992h.992zM17.238 18.372h-.992v.991h.992zM17.238 19.364h-.992v.991h.992zM17.238 23.33h-.992v.992h.992zM17.238 24.322h-.992v.992h.992zM17.238 25.314h-.992v.992h.992zM17.238 29.28l-.992.001v.992h.992z"
  })), _path12$7 || (_path12$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M17.238 30.273h-.992v.991h.992zM17.238 33.248h-.992v.992h.992zM17.238 35.231h-.992v.992h.992zM17.238 36.223h-.992v.992h.992zM17.238 38.207h-.992v.991h.992zM18.23 8.454h-.992v.992h.992zM18.23 11.43h-.992v.991h.992zM18.23 12.421h-.992v.992h.992zM18.23 13.413h-.992v.992h.992zM18.23 15.397h-.992v.991h.992zM18.23 17.38h-.992v.992h.992zM18.23 18.372h-.992v.991h.992zM18.23 19.364h-.992v.991h.992zM18.23 20.355h-.992v.992h.992zM18.23 21.347h-.992v.992h.992z"
  })), _path13$7 || (_path13$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18.23 22.339h-.992v.992h.992z"
  })), _path14$7 || (_path14$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18.23 23.33h-.992v.992h.992zM18.23 25.314h-.992v.992h.992zM18.23 26.306h-.992v.991h.992zM18.23 29.28h-.992v.993h.992zM18.23 31.264h-.992v.992h.992zM18.23 33.248h-.992v.992h.992zM18.23 37.215h-.992v.992h.992zM18.23 38.207h-.992v.991h.992zM18.23 39.198h-.992v.992h.992zM19.222 8.454h-.992v.992h.992zM19.222 13.413h-.992v.992h.992z"
  })), _path15$7 || (_path15$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M19.222 14.405h-.992v.992h.992zM19.222 19.364h-.992v.991h.992zM19.222 20.355h-.992v.992h.992zM19.222 21.347h-.992v.992h.992z"
  })), _path16$7 || (_path16$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M19.222 22.339h-.992v.992h.992zM19.222 26.306h-.992v.991h.992zM19.222 28.29h-.992v.99l.992.001zM19.222 31.264h-.992v.992h.992zM19.222 33.248h-.992v.992h.992zM19.222 37.215h-.992v.992h.992zM20.213 9.446h-.991v.992h.991zM20.213 12.421h-.991v.992h.991zM20.213 13.413h-.991v.992h.991zM20.213 15.397h-.991v.991h.991zM20.213 19.364h-.991v.991h.991zM20.213 22.339h-.991v.992h.991z"
  })), _path17$7 || (_path17$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M20.213 23.33h-.991v.992h.991zM20.213 27.298h-.991v.991h.991zM20.213 28.29h-.991v.991h.991zM20.213 30.273h-.991v.991h.991zM20.213 32.256h-.991v.992h.991zM20.213 33.248h-.991v.992h.991zM20.213 34.24h-.991v.991h.991zM20.213 39.198h-.991v.992h.991zM21.205 9.446h-.992v.992h.992zM21.205 14.405h-.992v.992h.992zM21.205 16.388h-.992v.992h.992z"
  })), _path18$7 || (_path18$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M21.205 17.38h-.992v.992h.992zM21.205 20.355h-.992v.992h.992zM21.205 21.347h-.992v.992h.992z"
  })), _path19$7 || (_path19$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M21.205 22.339h-.992v.992h.992zM21.205 30.273h-.992v.991h.992zM21.205 31.264h-.992v.992h.992zM21.205 32.256h-.992v.992h.992zM21.205 33.248h-.992v.992h.992zM21.205 34.24h-.992v.991h.992zM21.205 35.231h-.992v.992h.992zM21.205 36.223h-.992v.992h.992zM21.205 37.215h-.992v.992h.992zM22.197 8.454h-.992v.992h.992zM22.197 12.421h-.992v.992h.992zM22.197 15.397h-.992v.991h.992zM22.197 17.38h-.992v.992h.992zM22.197 18.372h-.992v.991h.992zM22.197 19.364h-.992v.991h.992zM22.197 20.355h-.992v.992h.992zM22.197 21.347h-.992v.992h.992zM22.197 23.33h-.992v.992h.992zM22.197 25.314h-.992v.992h.992zM22.197 26.306h-.992v.991h.992zM22.197 27.298h-.992v.991h.992zM22.197 28.29h-.992v.991h.992zM22.197 37.215h-.992v.992h.992zM23.189 12.421h-.992v.992h.992zM23.189 14.405h-.992v.992h.992zM23.189 15.397h-.992v.991h.992zM23.189 16.388h-.992v.992h.992zM23.189 20.355h-.992v.992h.992zM23.189 21.347h-.992v.992h.992zM23.189 23.33h-.992v.992h.992zM23.189 24.322h-.992v.992h.992zM23.189 25.314h-.992v.992h.992zM23.189 28.29h-.992v.991h.992zM23.189 30.273h-.992v.991h.992zM23.189 31.264h-.992v.992h.992zM23.189 32.256h-.992v.992h.992zM23.189 33.248h-.992v.992h.992zM23.189 34.24h-.992v.991h.992zM23.189 35.231h-.992v.992h.992zM23.189 36.223h-.992v.992h.992zM23.189 38.207h-.992v.991h.992zM23.189 39.198h-.992v.992h.992zM24.18 8.454h-.991v.992h.991z"
  })), _path20$7 || (_path20$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M24.18 9.446h-.991v.992h.991zM24.18 11.43h-.991v.991h.991zM24.18 17.38h-.991v.992h.991zM24.18 18.372h-.991v.991h.991zM24.18 20.355h-.991v.992h.991zM24.18 21.347h-.991v.992h.991zM24.18 24.322h-.991v.992h.991zM24.18 25.314h-.991v.992h.991zM24.18 29.28l-.991.001v.992h.991zM24.18 31.264h-.991v.992h.991zM24.18 33.248h-.991v.992h.991zM24.18 34.24h-.991v.991h.991zM24.18 36.223h-.991v.992h.991zM25.172 8.454h-.992v.992h.992z"
  })), _path21$7 || (_path21$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M25.172 9.446h-.992v.992h.992zM25.172 13.413h-.992v.992h.992z"
  })), _path22$7 || (_path22$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M25.172 14.405h-.992v.992h.992zM25.172 17.38h-.992v.992h.992zM25.172 22.339h-.992v.992h.992z"
  })), _path23$7 || (_path23$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M25.172 23.33h-.992v.992h.992zM25.172 25.314h-.992v.992h.992zM25.172 27.298h-.992v.991h.992zM25.172 28.29h-.992v.99l.992.001zM25.172 29.281h-.992v.992h.992zM25.172 30.273h-.992v.991h.992zM25.172 32.256h-.992v.992h.992zM25.172 34.24h-.992v.991h.992zM25.172 36.223h-.992v.992h.992zM25.172 37.215h-.992v.992h.992zM25.172 39.198h-.992v.992h.992zM26.164 8.454h-.992v.992h.992zM26.164 11.43h-.992v.991h.992zM26.164 12.421h-.992v.992h.992zM26.164 16.388h-.992v.992h.992z"
  })), _path24$7 || (_path24$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M26.164 17.38h-.992v.992h.992zM26.164 18.372h-.992v.991h.992zM26.164 19.364h-.992v.991h.992zM26.164 21.347h-.992v.992h.992zM26.164 25.314h-.992v.992h.992zM26.164 30.273h-.992v.991h.992zM26.164 31.264h-.992v.992h.992zM26.164 32.256h-.992v.992h.992zM26.164 37.215h-.992v.992h.992zM26.164 38.207h-.992v.991h.992zM26.164 39.198h-.992v.992h.992zM27.156 8.454h-.992v.992h.991zM27.156 12.421h-.992v.992h.991zM27.156 13.413h-.992v.992h.991z"
  })), _path25$7 || (_path25$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.156 14.405h-.992v.992h.991zM27.156 15.397h-.992v.991h.991zM27.156 16.388h-.992v.992h.991zM27.156 20.355h-.992v.992h.991zM27.156 22.339h-.992v.992h.991z"
  })), _path26$7 || (_path26$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.156 23.33h-.992v.992h.991zM27.156 24.322h-.992v.992h.991zM27.156 26.306h-.992v.991h.991zM27.156 29.28h-.992v.993h.991zM27.156 30.273h-.992v.991h.991zM27.156 31.264h-.992v.992h.991zM27.156 32.256h-.992v.992h.991zM27.156 33.248h-.992v.992h.991zM27.156 35.231h-.992v.992h.991zM27.156 37.215h-.992v.992h.991zM27.156 39.198h-.992v.992h.991zM28.147 9.446h-.991v.992h.991zM28.147 10.438h-.991v.992h.991zM28.147 11.43h-.991v.991h.991zM28.147 12.421h-.991v.992h.991zM28.147 13.413h-.991v.992h.991zM28.147 16.388h-.991v.992h.991zM28.147 20.355h-.991v.992h.991zM28.147 26.306h-.991v.991h.991zM28.147 28.29h-.991v.99l.991.001zM28.147 29.281h-.991v.992h.991zM28.147 32.256h-.991v.992h.991zM28.147 33.248h-.991v.992h.991zM28.147 34.24h-.991v.991h.991zM28.147 35.231h-.991v.992h.991zM28.147 39.198h-.991v.992h.991zM28.147 40.19h-.991v.992h.991zM29.139 8.454h-.992v.992h.992z"
  })), _path27$7 || (_path27$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.139 9.446h-.992v.992h.992zM29.139 11.43h-.992v.991h.992zM29.139 12.421h-.992v.992h.992zM29.139 14.405h-.992v.992h.992zM29.139 16.388h-.992v.992h.992z"
  })), _path28$7 || (_path28$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.139 17.38h-.992v.992h.992zM29.139 19.364h-.992v.991h.992zM29.139 22.339h-.992v.992h.992z"
  })), _path29$7 || (_path29$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.139 23.33h-.992v.992h.992zM29.139 27.298h-.992v.991h.992zM29.139 28.29h-.992v.991h.992zM29.139 33.248h-.992v.992h.992zM29.139 34.24h-.992v.991h.992zM29.139 35.231h-.992v.992h.992zM29.139 36.223h-.992v.992h.992zM29.139 37.215h-.992v.992h.992zM30.13 8.454h-.991v.992h.992zM30.13 10.438h-.991v.992h.992zM30.13 15.397h-.991v.991h.992zM30.13 16.388h-.991v.992h.992zM30.13 19.364h-.991v.991h.992zM30.13 20.355h-.991v.992h.992zM30.13 24.322h-.991v.992h.992zM30.13 27.298h-.991v.991h.992zM30.13 30.273h-.991v.991h.992zM30.13 31.264h-.991v.992h.992zM30.13 32.256h-.991v.992h.992zM30.13 38.207h-.991v.991h.992zM30.13 39.198h-.991v.992h.992zM31.122 10.438h-.991v.992h.991zM31.122 11.43h-.991v.991h.991zM31.122 12.421h-.991v.992h.991zM31.122 13.413h-.991v.992h.991z"
  })), _path30$7 || (_path30$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.122 14.405h-.991v.992h.991zM31.122 15.397h-.991v.991h.991zM31.122 16.388h-.991v.992h.991zM31.122 18.372h-.991v.992h.991zM31.122 19.364h-.991v.991h.991zM31.122 20.355h-.991v.992h.991zM31.122 21.347h-.991v.992h.991z"
  })), _path31$7 || (_path31$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.122 22.339h-.991v.992h.991zM31.122 26.306h-.991v.991h.991zM31.122 27.298h-.991v.991h.991zM31.122 30.273h-.991v.991h.991zM31.122 31.264h-.991v.992h.991zM31.122 33.248h-.991v.992h.991zM31.122 39.198h-.991v.992h.991zM32.114 10.438h-.992v.992h.992zM32.114 13.413h-.992v.992h.992zM32.114 15.397h-.992v.991h.992zM32.114 17.38h-.992v.992h.992zM32.114 18.372h-.992v.991h.992zM32.114 19.364h-.992v.991h.992z"
  })), _path32$7 || (_path32$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M32.114 21.347h-.992v.992h.992zM32.114 24.322h-.992v.992h.992zM32.114 27.298h-.992v.991h.992zM32.114 28.29h-.992v.991h.992zM32.114 29.281h-.992v.992h.992zM32.114 30.273h-.992v.991h.992zM32.114 33.248h-.992v.992h.992zM32.114 34.24h-.992v.991h.992zM32.114 39.198h-.992v.992h.992zM33.106 8.454h-.992v.992h.992z"
  })), _path33$7 || (_path33$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.106 9.446h-.992v.992h.992zM33.106 10.438h-.992v.992h.992zM33.106 13.413h-.992v.992h.992z"
  })), _path34$6 || (_path34$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.106 14.405h-.992v.992h.992zM33.106 15.397h-.992v.991h.992zM33.106 18.372h-.992v.991h.992zM33.106 20.355h-.992v.992h.992zM33.106 22.339h-.992v.992h.992z"
  })), _path35$6 || (_path35$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.106 23.33h-.992v.992h.992zM33.106 25.314h-.992v.992h.992zM33.106 26.306h-.992v.991h.992zM33.106 29.28l-.992.001v.992h.992z"
  })), _path36$5 || (_path36$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.106 30.273h-.992v.991h.992zM33.106 32.256h-.992v.992h.992zM33.106 33.248h-.992v.992h.992zM33.106 34.24h-.992v.991h.992zM33.106 35.231h-.992v.992h.992zM33.106 36.223h-.992v.992h.992zM33.106 37.215h-.992v.992h.992zM33.106 40.19h-.992v.992h.992zM34.098 16.388h-.992v.992h.992zM34.098 18.372h-.992v.991h.992zM34.098 19.364h-.992v.991h.992zM34.098 20.355h-.992v.992h.992zM34.098 21.347h-.992v.992h.992zM34.098 26.306h-.992v.991h.992zM34.098 29.28h-.992v.993h.992zM34.098 31.264h-.992v.992h.992zM34.098 32.256h-.992v.992h.992zM34.098 36.223h-.992v.992h.992zM34.098 38.207h-.992v.991h.992zM34.098 39.198h-.992v.992h.992zM35.09 17.38h-.992v.992h.991zM35.09 21.347h-.992v.992h.991z"
  })), _path37$5 || (_path37$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M35.09 22.339h-.992v.992h.991z"
  })), _path38$5 || (_path38$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M35.09 23.33h-.992v.992h.991zM35.09 24.322h-.992v.992h.991zM35.09 28.29h-.992v.99l.991.001zM35.09 32.256h-.992v.992h.991zM35.09 34.24h-.992v.991h.991zM35.09 36.223h-.992v.992h.991zM35.09 37.215h-.992v.992h.991zM35.09 39.198h-.992v.992h.991zM36.081 17.38h-.992v.992h.992zM36.081 21.347h-.992v.992h.992z"
  })), _path39$5 || (_path39$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M36.081 23.33h-.992v.992h.992zM36.081 30.273h-.992v.991h.992zM36.081 32.256h-.992v.992h.992zM36.081 36.223h-.992v.992h.992zM36.081 39.198h-.992v.992h.992zM36.081 40.19h-.992v.992h.992zM37.073 16.388h-.992v.992h.992zM37.073 19.364h-.992v.991h.992zM37.073 20.355h-.992v.992h.992zM37.073 22.339h-.992v.992h.992z"
  })), _path40$5 || (_path40$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M37.073 23.33h-.992v.992h.992zM37.073 27.298h-.992v.991h.992zM37.073 30.273h-.992v.991h.992zM37.073 32.256h-.992v.992h.992zM37.073 33.248h-.992v.992h.992zM37.073 34.24h-.992v.991h.992zM37.073 35.231h-.992v.992h.992zM37.073 36.223h-.992v.992h.992zM37.073 37.215h-.992v.992h.992zM37.073 39.198h-.992v.992h.992zM37.073 40.19h-.992v.992h.992zM38.065 18.372h-.992v.992h.992zM38.065 19.364h-.992v.991h.992zM38.065 20.355h-.992v.992h.992zM38.065 21.347h-.992v.992h.992zM38.065 25.314h-.992v.992h.992zM38.065 27.298h-.992v.991h.992zM38.065 28.29h-.992v.991h.992zM38.065 31.264h-.992v.992h.992zM38.065 34.24h-.992v.991h.992zM38.065 35.231h-.992v.992h.992zM38.065 36.223h-.992v.992h.992zM38.065 38.207h-.992v.991h.992zM38.065 39.198h-.992v.992h.992zM39.056 20.355h-.991v.992h.991zM39.056 22.339h-.991v.992h.991zM39.056 25.314h-.991v.992h.991zM39.056 28.29h-.991v.991h.991zM39.056 29.281h-.991v.992h.991zM39.056 33.248h-.991v.992h.991zM39.056 34.24h-.991v.991h.991zM39.056 35.231h-.991v.992h.991zM39.056 36.223h-.991v.992h.991zM39.056 37.215h-.991v.992h.991zM39.056 38.207h-.991v.991h.991zM40.048 16.388h-.992v.992h.992z"
  })), _path41$5 || (_path41$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M40.048 17.38h-.992v.992h.992zM40.048 22.339h-.992v.992h.992z"
  })), _path42$4 || (_path42$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M40.048 23.33h-.992v.992h.992zM40.048 26.306h-.992v.991h.992zM40.048 29.28l-.992.001v.992h.992zM40.048 32.256h-.992v.992h.992zM40.048 37.215h-.992v.992h.992zM41.04 17.38h-.992v.992h.992zM41.04 20.355h-.992v.992h.992zM41.04 22.339h-.992v.992h.992zM41.04 27.298h-.992v.991h.992zM41.04 28.29h-.992v.99l.992.001zM41.04 29.281h-.992v.992h.992zM41.04 30.273h-.992v.991h.992zM41.04 32.256h-.992v.992h.992zM41.04 40.19h-.992v.992h.992z"
  })), _path43$4 || (_path43$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M8.313 8.454v6.943h6.942V8.454zm.991.992h4.959v4.959H9.304z"
  })), _path44$4 || (_path44$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M13.271 10.438h-2.975v2.975h2.975z"
  })), _path45$4 || (_path45$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M41.04 8.454h-6.942v6.943h6.942zm-.992.992v4.959H35.09V9.446z"
  })), _path46$3 || (_path46$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M39.056 13.413v-2.975h-2.975v2.975z"
  })), _path47$3 || (_path47$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M39.056 10.438h-2.975v2.975h2.975z"
  })), _path48$3 || (_path48$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M8.313 41.182h6.942V34.24H8.312zm.991-.992v-4.959h4.959v4.959z"
  })), _path49$3 || (_path49$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M13.271 36.223h-2.975v2.975h2.975z"
  })));
};

var _rect$7, _path$y, _path2$8, _path3$7, _path4$6, _path5$6, _path6$6, _path7$6, _path8$6, _path9$6, _path10$6, _path11$6, _path12$6, _path13$6, _path14$6, _path15$6, _path16$6, _path17$6, _path18$6, _path19$6, _path20$6, _path21$6, _path22$6, _path23$6, _path24$6, _path25$6, _path26$6, _path27$6, _path28$6, _path29$6, _path30$6, _path31$6, _path32$6, _path33$6, _path34$5, _path35$5, _path36$4, _path37$4, _path38$4, _path39$4, _path40$4, _path41$4, _path42$3, _path43$3, _path44$3, _path45$3;
function _extends$A() { return _extends$A = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$A.apply(null, arguments); }
var SvgFrameWhite = function SvgFrameWhite(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$A({
    xmlns: "http://www.w3.org/2000/svg",
    width: 50,
    height: 66,
    fill: "none"
  }, props), _rect$7 || (_rect$7 = /*#__PURE__*/React.createElement("rect", {
    width: 49.091,
    height: 65.454,
    x: 0.061,
    y: 0.273,
    fill: "#fff",
    rx: 2.045
  })), _path$y || (_path$y = /*#__PURE__*/React.createElement("path", {
    fill: "#404040",
    fillRule: "evenodd",
    d: "M47.106 1.295h-45c-.565 0-1.023.458-1.023 1.023v45c0 .565.458 1.023 1.023 1.023h45c.565 0 1.023-.458 1.023-1.023v-45c0-.565-.458-1.023-1.023-1.023m-45-1.022C.976.273.061 1.188.061 2.318v45c0 1.13.915 2.046 2.045 2.046h45c1.13 0 2.045-.916 2.045-2.046v-45c0-1.13-.915-2.045-2.045-2.045z",
    clipRule: "evenodd"
  })), _path2$8 || (_path2$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    d: "M3.129 3.341h42.545v42.545H3.129z"
  })), _path3$7 || (_path3$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#404040",
    d: "M12.98 59.937q-.498 0-.933-.135a2.2 2.2 0 0 1-.737-.38l.264-.578q.314.24.657.35.35.11.755.11.447 0 .687-.153a.5.5 0 0 0 .245-.442.41.41 0 0 0-.226-.374q-.222-.13-.743-.245a4.4 4.4 0 0 1-.877-.27 1.24 1.24 0 0 1-.51-.393q-.171-.24-.172-.608 0-.38.209-.68.208-.302.589-.467.38-.172.877-.172.455 0 .866.141.411.135.675.375l-.264.576a2 2 0 0 0-1.277-.46q-.417 0-.662.172a.54.54 0 0 0-.24.466.43.43 0 0 0 .215.387q.215.135.73.251.548.123.884.276.344.148.522.381a.92.92 0 0 1 .184.59q0 .386-.209.68a1.3 1.3 0 0 1-.595.448 2.5 2.5 0 0 1-.914.154M17.049 59.937q-.638 0-1.117-.27a1.87 1.87 0 0 1-.73-.78q-.252-.503-.252-1.172t.252-1.166q.258-.503.73-.773.479-.276 1.117-.276.435 0 .822.135t.65.38l-.263.577a2.1 2.1 0 0 0-.583-.337 1.7 1.7 0 0 0-.614-.11q-.625 0-.97.404-.336.405-.337 1.166 0 .767.338 1.172.343.405.97.405.324 0 .613-.104a2.2 2.2 0 0 0 .583-.344l.264.577q-.264.245-.65.38-.388.136-.823.136M22.133 59.881l-.436-1.012h-2.105l-.435 1.012h-.798l1.957-4.326h.645l1.957 4.326zm-2.283-1.626h1.583l-.791-1.84zM25.947 55.555h.743v4.326h-.595l-2.32-3.019v3.02h-.736v-4.327h.589l2.32 3.013zM32.21 55.555h.619v4.326h-.7v-2.86l-1.141 2.16h-.528l-1.147-2.128.006 2.828h-.7v-4.326h.626l1.485 2.83zM33.426 59.881v-4.326h2.896v.626H34.2v1.19h2v.62h-2v1.264h2.123v.626zM36.76 58.58l-.24-3.03.945.005-.239 3.026zm-.197.455h.86v.846h-.86z"
  })), _path4$6 || (_path4$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M9.234 17.38h-.992v.992h.992zM9.234 18.372h-.992v.992h.992zM9.234 19.364h-.992v.991h.992zM9.234 22.339h-.992v.992h.992z"
  })), _path5$6 || (_path5$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M9.234 23.33h-.992v.992h.992zM9.234 26.306h-.992v.992h.992zM9.234 28.29h-.992v.991h.992zM9.234 30.273h-.992v.992h.992zM9.234 31.265h-.992v.991h.992zM10.226 19.364h-.992v.991h.992zM10.226 20.355h-.992v.992h.992zM10.226 21.347h-.992v.992h.992zM10.226 23.33h-.992v.992h.992zM10.226 26.306h-.992v.992h.992z"
  })), _path6$6 || (_path6$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M10.226 27.298h-.992v.991h.992zM10.226 28.29h-.992v.991h.992zM11.217 16.389h-.991v.991h.991zM11.217 18.372h-.991v.992h.991zM11.217 19.364h-.991v.991h.991zM11.217 25.314h-.991v.992h.991z"
  })), _path7$6 || (_path7$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M11.217 26.306h-.991v.992h.991z"
  })), _path8$6 || (_path8$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M11.217 27.298h-.991v.991h.991zM11.217 29.281h-.991v.992h.991zM11.217 30.273h-.991v.992h.991zM11.217 31.265h-.991v.991h.991zM12.21 17.38h-.993v.992h.992zM12.21 18.372h-.993v.992h.992zM12.21 19.364h-.993v.991h.992zM12.21 20.355h-.993v.992h.992zM12.21 21.347h-.993v.992h.992zM12.21 23.33h-.993v.992h.992zM12.21 25.314h-.993v.992h.992zM12.21 29.281h-.993v.992h.992zM12.21 30.273h-.993v.992h.992zM12.21 31.265h-.993v.991h.992zM12.21 32.256h-.993v.992h.992zM13.2 16.389h-.99v.991h.99z"
  })), _path9$6 || (_path9$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M13.2 21.347h-.99v.992h.99z"
  })), _path10$6 || (_path10$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M13.2 22.339h-.99v.992h.99z"
  })), _path11$6 || (_path11$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M13.2 23.33h-.99v.992h.99zM13.2 24.322h-.99v.992h.99zM13.2 25.314h-.99v.992h.99zM13.2 27.298h-.99v.991h.99zM13.2 31.265h-.99v.991h.99zM13.2 32.256h-.99v.992h.99zM14.193 16.389H13.2v.991h.992zM14.193 17.38H13.2v.992h.992zM14.193 18.372H13.2v.992h.992zM14.193 22.339H13.2v.992h.992zM14.193 24.322H13.2v.992h.992zM14.193 25.314H13.2v.992h.992zM14.193 28.29H13.2v.991h.992zM14.193 29.281H13.2v.992h.992zM15.184 16.389h-.991v.991h.991zM15.184 18.372h-.991v.992h.991zM15.184 20.356h-.991v.991h.991zM15.184 22.339h-.991v.992h.991zM15.184 24.322h-.991v.992h.991zM15.184 26.306h-.991v.992h.991zM15.184 28.29h-.991v.991h.991zM15.184 30.273h-.991v.992h.991zM15.184 32.256h-.991v.992h.991z"
  })), _path12$6 || (_path12$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M16.176 18.372h-.992v.992h.992zM16.176 19.364h-.992v.991h.992zM16.176 20.355h-.992v.992h.992zM16.176 22.339h-.992v.992h.992zM16.176 24.322h-.992v.992h.992zM16.176 25.314h-.992v.992h.992zM16.176 27.298h-.992v.991h.992zM16.176 28.29h-.992v.991h.992zM16.176 29.281h-.992v.992h.992zM16.176 30.273h-.992v.992h.992zM16.176 31.265h-.992v.991h.992zM17.168 9.446h-.992v.992h.992zM17.168 12.422h-.992v.991h.992zM17.168 14.405h-.992v.992h.992zM17.168 16.389h-.992v.991h.992zM17.168 17.38h-.992v.992h.992zM17.168 18.372h-.992v.992h.992zM17.168 19.364h-.992v.991h.992zM17.168 23.33h-.992v.992h.992zM17.168 24.322h-.992v.992h.992zM17.168 25.314h-.992v.992h.992zM17.168 29.281h-.992v.992h.992zM17.168 30.273h-.992v.992h.992zM17.168 33.248h-.992v.992h.992zM17.168 35.232h-.992v.991h.992zM17.168 36.223h-.992v.992h.992zM17.168 38.207h-.992v.991h.992zM18.16 8.455h-.992v.991h.992zM18.16 11.43h-.992v.992h.992zM18.16 12.422h-.992v.991h.992zM18.16 13.413h-.992v.992h.992zM18.16 15.397h-.992v.992h.992zM18.16 17.38h-.992v.992h.992zM18.16 18.372h-.992v.992h.992zM18.16 19.364h-.992v.991h.992zM18.16 20.355h-.992v.992h.992zM18.16 21.347h-.992v.992h.992z"
  })), _path13$6 || (_path13$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18.16 22.339h-.992v.992h.992z"
  })), _path14$6 || (_path14$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18.16 23.33h-.992v.992h.992zM18.16 25.314h-.992v.992h.992zM18.16 26.306h-.992v.992h.992zM18.16 29.281h-.992v.992h.992zM18.16 31.265h-.992v.991h.992zM18.16 33.248h-.992v.992h.992zM18.16 37.215h-.992v.992h.992zM18.16 38.207h-.992v.991h.992zM18.16 39.198h-.992v.992h.992zM19.151 8.455h-.991v.991h.991zM19.151 13.413h-.991v.992h.991z"
  })), _path15$6 || (_path15$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M19.151 14.405h-.991v.992h.991zM19.151 19.364h-.991v.991h.991zM19.151 20.355h-.991v.992h.991zM19.151 21.347h-.991v.992h.991z"
  })), _path16$6 || (_path16$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M19.151 22.339h-.991v.992h.991zM19.151 26.306h-.991v.992h.991zM19.151 28.29h-.991v.991h.991zM19.151 31.265h-.991v.991h.991zM19.151 33.248h-.991v.992h.991zM19.151 37.215h-.991v.992h.991zM20.143 9.446h-.992v.992h.992zM20.143 12.422h-.992v.991h.992zM20.143 13.413h-.992v.992h.992zM20.143 15.397h-.992v.992h.992zM20.143 19.364h-.992v.991h.992zM20.143 22.339h-.992v.992h.992z"
  })), _path17$6 || (_path17$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M20.143 23.33h-.992v.992h.992zM20.143 27.298h-.992v.991h.992zM20.143 28.29h-.992v.991h.992zM20.143 30.273h-.992v.992h.992zM20.143 32.256h-.992v.992h.992zM20.143 33.248h-.992v.992h.992zM20.143 34.24h-.992v.991h.992zM20.143 39.198h-.992v.992h.992zM21.135 9.446h-.992v.992h.992zM21.135 14.405h-.992v.992h.992zM21.135 16.389h-.992v.991h.992zM21.135 17.38h-.992v.992h.992zM21.135 20.356h-.992v.991h.992zM21.135 21.347h-.992v.992h.992z"
  })), _path18$6 || (_path18$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M21.135 22.339h-.992v.992h.992zM21.135 30.273h-.992v.992h.992zM21.135 31.265h-.992v.991h.992zM21.135 32.256h-.992v.992h.992zM21.135 33.248h-.992v.992h.992zM21.135 34.24h-.992v.991h.992zM21.135 35.232h-.992v.991h.992zM21.135 36.223h-.992v.992h.992zM21.135 37.215h-.992v.992h.992zM22.127 8.455h-.992v.991h.991zM22.127 12.422h-.992v.991h.991zM22.127 15.397h-.992v.992h.991zM22.127 17.38h-.992v.992h.991zM22.127 18.372h-.992v.992h.991zM22.127 19.364h-.992v.991h.991zM22.127 20.355h-.992v.992h.991z"
  })), _path19$6 || (_path19$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M22.127 21.347h-.992v.992h.991zM22.127 23.33h-.992v.992h.991zM22.127 25.314h-.992v.992h.991zM22.127 26.306h-.992v.992h.991z"
  })), _path20$6 || (_path20$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M22.127 27.298h-.992v.991h.991zM22.127 28.29h-.992v.991h.991zM22.127 37.215h-.992v.992h.991zM23.118 12.422h-.991v.991h.991zM23.118 14.405h-.991v.992h.991zM23.118 15.397h-.991v.992h.991zM23.118 16.389h-.991v.991h.991zM23.118 20.356h-.991v.991h.991zM23.118 21.347h-.991v.992h.991zM23.118 23.33h-.991v.992h.991zM23.118 24.322h-.991v.992h.991zM23.118 25.314h-.991v.992h.991zM23.118 28.29h-.991v.991h.991zM23.118 30.273h-.991v.992h.991zM23.118 31.265h-.991v.991h.991zM23.118 32.256h-.991v.992h.991zM23.118 33.248h-.991v.992h.991zM23.118 34.24h-.991v.991h.991zM23.118 35.232h-.991v.991h.991zM23.118 36.223h-.991v.992h.991zM23.118 38.207h-.991v.991h.991zM23.118 39.198h-.991v.992h.991zM24.11 8.455h-.992v.991h.992zM24.11 9.446h-.992v.992h.992zM24.11 11.43h-.992v.992h.992zM24.11 17.38h-.992v.992h.992zM24.11 18.372h-.992v.992h.992zM24.11 20.356h-.992v.991h.992zM24.11 21.347h-.992v.992h.992zM24.11 24.322h-.992v.992h.992zM24.11 25.314h-.992v.992h.992zM24.11 29.281h-.992v.992h.992zM24.11 31.265h-.992v.991h.992zM24.11 33.248h-.992v.992h.992zM24.11 34.24h-.992v.991h.992zM24.11 36.223h-.992v.992h.992zM25.102 8.455h-.992v.991h.992zM25.102 9.446h-.992v.992h.992zM25.102 13.413h-.992v.992h.992z"
  })), _path21$6 || (_path21$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M25.102 14.405h-.992v.992h.992zM25.102 17.38h-.992v.992h.992zM25.102 22.339h-.992v.992h.992z"
  })), _path22$6 || (_path22$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M25.102 23.33h-.992v.992h.992zM25.102 25.314h-.992v.992h.992zM25.102 27.298h-.992v.991h.992zM25.102 28.29h-.992v.991h.992zM25.102 29.281h-.992v.992h.992zM25.102 30.273h-.992v.992h.992zM25.102 32.256h-.992v.992h.992zM25.102 34.24h-.992v.991h.992zM25.102 36.223h-.992v.992h.992zM25.102 37.215h-.992v.992h.992zM25.102 39.198h-.992v.992h.992zM26.093 8.455h-.991v.991h.991zM26.093 11.43h-.991v.992h.991zM26.093 12.422h-.991v.991h.991zM26.093 16.389h-.991v.991h.991zM26.093 17.38h-.991v.992h.991zM26.093 18.372h-.991v.992h.991zM26.093 19.364h-.991v.991h.991zM26.093 21.347h-.991v.992h.991zM26.093 25.314h-.991v.992h.991zM26.093 30.273h-.991v.992h.991zM26.093 31.265h-.991v.991h.991zM26.093 32.256h-.991v.992h.991zM26.093 37.215h-.991v.992h.991zM26.093 38.207h-.991v.991h.991zM26.093 39.198h-.991v.992h.991zM27.085 8.455h-.992v.991h.992zM27.085 12.422h-.992v.991h.992zM27.085 13.413h-.992v.992h.992z"
  })), _path23$6 || (_path23$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.085 14.405h-.992v.992h.992zM27.085 15.397h-.992v.992h.992zM27.085 16.389h-.992v.991h.992zM27.085 20.356h-.992v.991h.992zM27.085 22.339h-.992v.992h.992z"
  })), _path24$6 || (_path24$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.085 23.33h-.992v.992h.992zM27.085 24.322h-.992v.992h.992zM27.085 26.306h-.992v.992h.992zM27.085 29.281h-.992v.992h.992zM27.085 30.273h-.992v.992h.992zM27.085 31.265h-.992v.991h.992zM27.085 32.256h-.992v.992h.992zM27.085 33.248h-.992v.992h.992zM27.085 35.232h-.992v.991h.992zM27.085 37.215h-.992v.992h.992zM27.085 39.198h-.992v.992h.992zM28.077 9.446h-.992v.992h.992zM28.077 10.438h-.992v.992h.992zM28.077 11.43h-.992v.992h.992zM28.077 12.422h-.992v.991h.992zM28.077 13.413h-.992v.992h.992zM28.077 16.389h-.992v.991h.992zM28.077 20.356h-.992v.991h.992zM28.077 26.306h-.992v.992h.992zM28.077 28.29h-.992v.991h.992zM28.077 29.281h-.992v.992h.992zM28.077 32.256h-.992v.992h.992zM28.077 33.248h-.992v.992h.992zM28.077 34.24h-.992v.991h.992zM28.077 35.232h-.992v.991h.992zM28.077 39.198h-.992v.992h.992zM28.077 40.19h-.992v.992h.992zM29.069 8.455h-.992v.991h.992z"
  })), _path25$6 || (_path25$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.069 9.446h-.992v.992h.992zM29.069 11.43h-.992v.992h.992zM29.069 12.422h-.992v.991h.992zM29.069 14.405h-.992v.992h.992zM29.069 16.389h-.992v.991h.992zM29.069 17.38h-.992v.992h.992zM29.069 19.364h-.992v.991h.992zM29.069 22.339h-.992v.992h.992z"
  })), _path26$6 || (_path26$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.069 23.33h-.992v.992h.992zM29.069 27.298h-.992v.991h.992zM29.069 28.29h-.992v.991h.992zM29.069 33.248h-.992v.992h.992zM29.069 34.24h-.992v.991h.992zM29.069 35.232h-.992v.991h.992zM29.069 36.223h-.992v.992h.992zM29.069 37.215h-.992v.992h.992zM30.06 8.455h-.991v.991h.991zM30.06 10.438h-.991v.992h.991zM30.06 15.397h-.991v.992h.991zM30.06 16.389h-.991v.991h.991zM30.06 19.364h-.991v.991h.991zM30.06 20.355h-.991v.992h.991zM30.06 24.322h-.991v.992h.991zM30.06 27.298h-.991v.991h.991zM30.06 30.273h-.991v.992h.991zM30.06 31.265h-.991v.991h.991zM30.06 32.256h-.991v.992h.991zM30.06 38.207h-.991v.991h.991zM30.06 39.198h-.991v.992h.991zM31.052 10.438h-.992v.992h.992zM31.052 11.43h-.992v.992h.992zM31.052 12.422h-.992v.991h.992zM31.052 13.413h-.992v.992h.992z"
  })), _path27$6 || (_path27$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.052 14.405h-.992v.992h.992zM31.052 15.397h-.992v.992h.992zM31.052 16.389h-.992v.991h.992zM31.052 18.372h-.992v.992h.992zM31.052 19.364h-.992v.991h.992zM31.052 20.355h-.992v.992h.992zM31.052 21.347h-.992v.992h.992z"
  })), _path28$6 || (_path28$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.052 22.339h-.992v.992h.992zM31.052 26.306h-.992v.992h.992z"
  })), _path29$6 || (_path29$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.052 27.298h-.992v.991h.992zM31.052 30.273h-.992v.992h.992zM31.052 31.265h-.992v.991h.992zM31.052 33.248h-.992v.992h.992zM31.052 39.198h-.992v.992h.992zM32.044 10.438h-.992v.992h.992zM32.044 13.413h-.992v.992h.992zM32.044 15.397h-.992v.992h.992zM32.044 17.38h-.992v.992h.992zM32.044 18.372h-.992v.992h.992zM32.044 19.364h-.992v.991h.992zM32.044 21.347h-.992v.992h.992zM32.044 24.322h-.992v.992h.992z"
  })), _path30$6 || (_path30$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M32.044 27.298h-.992v.991h.992zM32.044 28.29h-.992v.991h.992zM32.044 29.281h-.992v.992h.992zM32.044 30.273h-.992v.992h.992zM32.044 33.248h-.992v.992h.992zM32.044 34.24h-.992v.991h.992zM32.044 39.198h-.992v.992h.992zM33.036 8.455h-.992v.991h.992zM33.036 9.446h-.992v.992h.992zM33.036 10.438h-.992v.992h.992zM33.036 13.413h-.992v.992h.992z"
  })), _path31$6 || (_path31$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.036 14.405h-.992v.992h.992zM33.036 15.397h-.992v.992h.992zM33.036 18.372h-.992v.992h.992zM33.036 20.356h-.992v.991h.992zM33.036 22.339h-.992v.992h.992z"
  })), _path32$6 || (_path32$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.036 23.33h-.992v.992h.992zM33.036 25.314h-.992v.992h.992zM33.036 26.306h-.992v.992h.992zM33.036 29.281h-.992v.992h.992zM33.036 30.273h-.992v.992h.992zM33.036 32.256h-.992v.992h.992zM33.036 33.248h-.992v.992h.992zM33.036 34.24h-.992v.991h.992zM33.036 35.232h-.992v.991h.992zM33.036 36.223h-.992v.992h.992zM33.036 37.215h-.992v.992h.992zM33.036 40.19h-.992v.992h.992zM34.027 16.389h-.991v.991h.991zM34.027 18.372h-.991v.992h.991zM34.027 19.364h-.991v.991h.991zM34.027 20.355h-.991v.992h.991zM34.027 21.347h-.991v.992h.991zM34.027 26.306h-.991v.992h.991zM34.027 29.281h-.991v.992h.991zM34.027 31.265h-.991v.991h.991zM34.027 32.256h-.991v.992h.991zM34.027 36.223h-.991v.992h.991zM34.027 38.207h-.991v.991h.991zM34.027 39.198h-.991v.992h.991zM35.02 17.38h-.993v.992h.992zM35.02 21.347h-.993v.992h.992z"
  })), _path33$6 || (_path33$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M35.02 22.339h-.993v.992h.992z"
  })), _path34$5 || (_path34$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M35.02 23.33h-.993v.992h.992zM35.02 24.322h-.993v.992h.992zM35.02 28.29h-.993v.991h.992zM35.02 32.256h-.993v.992h.992zM35.02 34.24h-.993v.991h.992zM35.02 36.223h-.993v.992h.992zM35.02 37.215h-.993v.992h.992zM35.02 39.198h-.993v.992h.992zM36.01 17.38h-.99v.992h.99zM36.01 21.347h-.99v.992h.99z"
  })), _path35$5 || (_path35$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M36.01 23.33h-.99v.992h.99zM36.01 30.273h-.99v.992h.99zM36.01 32.256h-.99v.992h.99zM36.01 36.223h-.99v.992h.99zM36.01 39.198h-.99v.992h.99zM36.01 40.19h-.99v.992h.99zM37.003 16.389h-.992v.991h.991zM37.003 19.364h-.992v.991h.991zM37.003 20.355h-.992v.992h.991zM37.003 22.339h-.992v.992h.991z"
  })), _path36$4 || (_path36$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M37.003 23.33h-.992v.992h.991zM37.003 27.298h-.992v.991h.991zM37.003 30.273h-.992v.992h.991zM37.003 32.256h-.992v.992h.991zM37.003 33.248h-.992v.992h.991zM37.003 34.24h-.992v.991h.991zM37.003 35.232h-.992v.991h.991zM37.003 36.223h-.992v.992h.991zM37.003 37.215h-.992v.992h.991zM37.003 39.198h-.992v.992h.991zM37.003 40.19h-.992v.992h.991zM37.994 18.372h-.992v.992h.992zM37.994 19.364h-.992v.991h.992zM37.994 20.355h-.992v.992h.992zM37.994 21.347h-.992v.992h.992zM37.994 25.314h-.992v.992h.992z"
  })), _path37$4 || (_path37$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M37.994 27.298h-.992v.991h.992zM37.994 28.29h-.992v.991h.992zM37.994 31.265h-.992v.991h.992zM37.994 34.24h-.992v.991h.992zM37.994 35.232h-.992v.991h.992zM37.994 36.223h-.992v.992h.992zM37.994 38.207h-.992v.991h.992zM37.994 39.198h-.992v.992h.992zM38.986 20.356h-.992v.991h.992zM38.986 22.339h-.992v.992h.992zM38.986 25.314h-.992v.992h.992zM38.986 28.29h-.992v.991h.992zM38.986 29.281h-.992v.992h.992zM38.986 33.248h-.992v.992h.992zM38.986 34.24h-.992v.991h.992zM38.986 35.232h-.992v.991h.992zM38.986 36.223h-.992v.992h.992zM38.986 37.215h-.992v.992h.992zM38.986 38.207h-.992v.991h.992zM39.978 16.389h-.992v.991h.992zM39.978 17.38h-.992v.992h.992zM39.978 22.339h-.992v.992h.992z"
  })), _path38$4 || (_path38$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M39.978 23.33h-.992v.992h.992zM39.978 26.306h-.992v.992h.992zM39.978 29.281h-.992v.992h.992zM39.978 32.256h-.992v.992h.992zM39.978 37.215h-.992v.992h.992zM40.97 17.38h-.992v.992h.991zM40.97 20.356h-.992v.991h.991zM40.97 22.339h-.992v.992h.991zM40.97 27.298h-.992v.991h.991zM40.97 28.29h-.992v.991h.991zM40.97 29.281h-.992v.992h.991zM40.97 30.273h-.992v.992h.991zM40.97 32.256h-.992v.992h.991zM40.97 40.19h-.992v.992h.991z"
  })), _path39$4 || (_path39$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M8.242 8.455v6.942h6.942V8.455zm.992.991h4.959v4.959h-4.96z",
    clipRule: "evenodd"
  })), _path40$4 || (_path40$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M13.2 10.438h-2.974v2.975H13.2z"
  })), _path41$4 || (_path41$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M40.97 8.455h-6.943v6.942h6.942zm-.992.991v4.959h-4.959V9.446z",
    clipRule: "evenodd"
  })), _path42$3 || (_path42$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M38.986 13.413v-2.975h-2.975v2.975z"
  })), _path43$3 || (_path43$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M38.986 10.438h-2.975v2.975h2.975z"
  })), _path44$3 || (_path44$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M8.242 41.182h6.942V34.24H8.242zm.992-.992v-4.959h4.959v4.959z",
    clipRule: "evenodd"
  })), _path45$3 || (_path45$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M13.2 36.223h-2.974v2.975H13.2z"
  })));
};

var _rect$6, _rect2$2, _path$x, _path2$7, _path3$6, _path4$5, _path5$5, _path6$5, _path7$5, _path8$5, _path9$5, _path10$5, _path11$5, _path12$5, _path13$5, _path14$5, _path15$5, _path16$5, _path17$5, _path18$5, _path19$5, _path20$5, _path21$5, _path22$5, _path23$5, _path24$5, _path25$5, _path26$5, _path27$5, _path28$5, _path29$5, _path30$5, _path31$5, _path32$5, _path33$5, _path34$4, _path35$4, _path36$3, _path37$3, _path38$3, _path39$3, _path40$3, _path41$3;
function _extends$z() { return _extends$z = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$z.apply(null, arguments); }
var SvgFrameButton = function SvgFrameButton(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$z({
    xmlns: "http://www.w3.org/2000/svg",
    width: 51,
    height: 66,
    fill: "none"
  }, props), _rect$6 || (_rect$6 = /*#__PURE__*/React.createElement("rect", {
    width: 49.091,
    height: 49.091,
    x: 0.989,
    y: 0.273,
    fill: "#404040",
    rx: 2.045
  })), _rect2$2 || (_rect2$2 = /*#__PURE__*/React.createElement("rect", {
    width: 49.091,
    height: 14.318,
    x: 0.989,
    y: 51.409,
    fill: "#404040",
    rx: 2.045
  })), _path$x || (_path$x = /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    d: "M4.058 3.341h42.545v42.545H4.058z"
  })), _path2$7 || (_path2$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M10.163 17.38H9.17v.992h.992zM10.163 18.372H9.17v.992h.992zM10.163 19.364H9.17v.991h.992zM10.163 22.339H9.17v.992h.992z"
  })), _path3$6 || (_path3$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M10.163 23.33H9.17v.992h.992zM10.163 26.306H9.17v.992h.992zM10.163 28.29H9.17v.991h.992zM10.163 30.273H9.17v.992h.992zM10.163 31.265H9.17v.991h.992zM11.154 19.364h-.991v.991h.991zM11.154 20.355h-.991v.992h.991zM11.154 21.347h-.991v.992h.991zM11.154 23.33h-.991v.992h.991zM11.154 26.306h-.991v.992h.991z"
  })), _path4$5 || (_path4$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M11.154 27.298h-.991v.991h.991zM11.154 28.29h-.991v.991h.991zM12.146 16.389h-.992v.991h.992zM12.146 18.372h-.992v.992h.992zM12.146 19.364h-.992v.991h.992zM12.146 25.314h-.992v.992h.992zM12.146 26.306h-.992v.992h.992z"
  })), _path5$5 || (_path5$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M12.146 27.298h-.992v.991h.992zM12.146 29.281h-.992v.992h.992zM12.146 30.273h-.992v.992h.992zM12.146 31.265h-.992v.991h.992zM13.138 17.38h-.992v.992h.992zM13.138 18.372h-.992v.992h.992zM13.138 19.364h-.992v.991h.992zM13.138 20.355h-.992v.992h.992zM13.138 21.347h-.992v.992h.992zM13.138 23.33h-.992v.992h.992zM13.138 25.314h-.992v.992h.992zM13.138 29.281h-.992v.992h.992zM13.138 30.273h-.992v.992h.992zM13.138 31.265h-.992v.991h.992zM13.138 32.256h-.992v.992h.992zM14.13 16.389h-.992v.991h.992zM14.13 21.347h-.992v.992h.992z"
  })), _path6$5 || (_path6$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M14.13 22.339h-.992v.992h.992z"
  })), _path7$5 || (_path7$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M14.13 23.33h-.992v.992h.992zM14.13 24.322h-.992v.992h.992zM14.13 25.314h-.992v.992h.992zM14.13 27.298h-.992v.991h.992zM14.13 31.265h-.992v.991h.992zM14.13 32.256h-.992v.992h.992zM15.121 16.389h-.991v.991h.991zM15.121 17.38h-.991v.992h.991zM15.121 18.372h-.991v.992h.991z"
  })), _path8$5 || (_path8$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M15.121 22.339h-.991v.992h.991zM15.121 24.322h-.991v.992h.991zM15.121 25.314h-.991v.992h.991zM15.121 28.29h-.991v.991h.991zM15.121 29.281h-.991v.992h.991zM16.113 16.389h-.992v.991h.992zM16.113 18.372h-.992v.992h.992zM16.113 20.356h-.992v.991h.992z"
  })), _path9$5 || (_path9$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M16.113 22.339h-.992v.992h.992zM16.113 24.322h-.992v.992h.992zM16.113 26.306h-.992v.992h.992zM16.113 28.29h-.992v.991h.992zM16.113 30.273h-.992v.992h.992zM16.113 32.256h-.992v.992h.992zM17.105 18.372h-.992v.992h.992zM17.105 19.364h-.992v.991h.992zM17.105 20.355h-.992v.992h.992zM17.105 22.339h-.992v.992h.992zM17.105 24.322h-.992v.992h.992zM17.105 25.314h-.992v.992h.992zM17.105 27.298h-.992v.991h.992zM17.105 28.29h-.992v.991h.992zM17.105 29.281h-.992v.992h.992zM17.105 30.273h-.992v.992h.992zM17.105 31.265h-.992v.991h.992zM18.097 9.446h-.992v.992h.991zM18.097 12.422h-.992v.991h.991zM18.097 14.405h-.992v.992h.991zM18.097 16.389h-.992v.991h.991zM18.097 17.38h-.992v.992h.991zM18.097 18.372h-.992v.992h.991zM18.097 19.364h-.992v.991h.991zM18.097 23.33h-.992v.992h.991zM18.097 24.322h-.992v.992h.991zM18.097 25.314h-.992v.992h.991zM18.097 29.281h-.992v.992h.991zM18.097 30.273h-.992v.992h.991zM18.097 33.248h-.992v.992h.991zM18.097 35.232h-.992v.991h.991zM18.097 36.223h-.992v.992h.991zM18.097 38.207h-.992v.991h.991zM19.088 8.455h-.992v.991h.992zM19.088 11.43h-.992v.992h.992z"
  })), _path10$5 || (_path10$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M19.088 12.422h-.992v.991h.992zM19.088 13.413h-.992v.992h.992zM19.088 15.397h-.992v.992h.992zM19.088 17.38h-.992v.992h.992zM19.088 18.372h-.992v.992h.992zM19.088 19.364h-.992v.991h.992zM19.088 20.355h-.992v.992h.992zM19.088 21.347h-.992v.992h.992z"
  })), _path11$5 || (_path11$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M19.088 22.339h-.992v.992h.992z"
  })), _path12$5 || (_path12$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M19.088 23.33h-.992v.992h.992zM19.088 25.314h-.992v.992h.992zM19.088 26.306h-.992v.992h.992zM19.088 29.281h-.992v.992h.992zM19.088 31.265h-.992v.991h.992zM19.088 33.248h-.992v.992h.992zM19.088 37.215h-.992v.992h.992zM19.088 38.207h-.992v.991h.992zM19.088 39.198h-.992v.992h.992zM20.08 8.455h-.992v.991h.992zM20.08 13.413h-.992v.992h.992z"
  })), _path13$5 || (_path13$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M20.08 14.405h-.992v.992h.992zM20.08 19.364h-.992v.991h.992zM20.08 20.355h-.992v.992h.992zM20.08 21.347h-.992v.992h.992z"
  })), _path14$5 || (_path14$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M20.08 22.339h-.992v.992h.992zM20.08 26.306h-.992v.992h.992zM20.08 28.29h-.992v.991h.992zM20.08 31.265h-.992v.991h.992zM20.08 33.248h-.992v.992h.992zM20.08 37.215h-.992v.992h.992zM21.072 9.446h-.992v.992h.992zM21.072 12.422h-.992v.991h.992zM21.072 13.413h-.992v.992h.992zM21.072 15.397h-.992v.992h.992zM21.072 19.364h-.992v.991h.992zM21.072 22.339h-.992v.992h.992z"
  })), _path15$5 || (_path15$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M21.072 23.33h-.992v.992h.992zM21.072 27.298h-.992v.991h.992zM21.072 28.29h-.992v.991h.992zM21.072 30.273h-.992v.992h.992zM21.072 32.256h-.992v.992h.992zM21.072 33.248h-.992v.992h.992zM21.072 34.24h-.992v.991h.992zM21.072 39.198h-.992v.992h.992zM22.064 9.446h-.992v.992h.992zM22.064 14.405h-.992v.992h.992zM22.064 16.389h-.992v.991h.992zM22.064 17.38h-.992v.992h.992zM22.064 20.356h-.992v.991h.992zM22.064 21.347h-.992v.992h.992z"
  })), _path16$5 || (_path16$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M22.064 22.339h-.992v.992h.992zM22.064 30.273h-.992v.992h.992zM22.064 31.265h-.992v.991h.992zM22.064 32.256h-.992v.992h.992zM22.064 33.248h-.992v.992h.992zM22.064 34.24h-.992v.991h.992zM22.064 35.232h-.992v.991h.992zM22.064 36.223h-.992v.992h.992zM22.064 37.215h-.992v.992h.992zM23.055 8.455h-.991v.991h.991zM23.055 12.422h-.991v.991h.991zM23.055 15.397h-.991v.992h.991zM23.055 17.38h-.991v.992h.991zM23.055 18.372h-.991v.992h.991zM23.055 19.364h-.991v.991h.991zM23.055 20.355h-.991v.992h.991zM23.055 21.347h-.991v.992h.991zM23.055 23.33h-.991v.992h.991zM23.055 25.314h-.991v.992h.991zM23.055 26.306h-.991v.992h.991z"
  })), _path17$5 || (_path17$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M23.055 27.298h-.991v.991h.991zM23.055 28.29h-.991v.991h.991zM23.055 37.215h-.991v.992h.991zM24.047 12.422h-.992v.991h.992zM24.047 14.405h-.992v.992h.992zM24.047 15.397h-.992v.992h.992zM24.047 16.389h-.992v.991h.992zM24.047 20.356h-.992v.991h.992zM24.047 21.347h-.992v.992h.992zM24.047 23.33h-.992v.992h.992zM24.047 24.322h-.992v.992h.992zM24.047 25.314h-.992v.992h.992zM24.047 28.29h-.992v.991h.992zM24.047 30.273h-.992v.992h.992zM24.047 31.265h-.992v.991h.992zM24.047 32.256h-.992v.992h.992zM24.047 33.248h-.992v.992h.992zM24.047 34.24h-.992v.991h.992zM24.047 35.232h-.992v.991h.992zM24.047 36.223h-.992v.992h.992zM24.047 38.207h-.992v.991h.992zM24.047 39.198h-.992v.992h.992zM25.039 8.455h-.992v.991h.992zM25.039 9.446h-.992v.992h.992zM25.039 11.43h-.992v.992h.992zM25.039 17.38h-.992v.992h.992zM25.039 18.372h-.992v.992h.992zM25.039 20.356h-.992v.991h.992zM25.039 21.347h-.992v.992h.992zM25.039 24.322h-.992v.992h.992zM25.039 25.314h-.992v.992h.992zM25.039 29.281h-.992v.992h.992zM25.039 31.265h-.992v.991h.992zM25.039 33.248h-.992v.992h.992zM25.039 34.24h-.992v.991h.992zM25.039 36.223h-.992v.992h.992zM26.03 8.455h-.991v.991h.991zM26.03 9.446h-.991v.992h.991zM26.03 13.413h-.991v.992h.991z"
  })), _path18$5 || (_path18$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M26.03 14.405h-.991v.992h.991zM26.03 17.38h-.991v.992h.991zM26.03 22.339h-.991v.992h.991z"
  })), _path19$5 || (_path19$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M26.03 23.33h-.991v.992h.991zM26.03 25.314h-.991v.992h.991zM26.03 27.298h-.991v.991h.991zM26.03 28.29h-.991v.991h.991zM26.03 29.281h-.991v.992h.991zM26.03 30.273h-.991v.992h.991zM26.03 32.256h-.991v.992h.991zM26.03 34.24h-.991v.991h.991zM26.03 36.223h-.991v.992h.991zM26.03 37.215h-.991v.992h.991zM26.03 39.198h-.991v.992h.991zM27.022 8.455h-.992v.991h.992zM27.022 11.43h-.992v.992h.992zM27.022 12.422h-.992v.991h.992zM27.022 16.389h-.992v.991h.992zM27.022 17.38h-.992v.992h.992zM27.022 18.372h-.992v.992h.992zM27.022 19.364h-.992v.991h.992zM27.022 21.347h-.992v.992h.992z"
  })), _path20$5 || (_path20$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.022 25.314h-.992v.992h.992zM27.022 30.273h-.992v.992h.992zM27.022 31.265h-.992v.991h.992zM27.022 32.256h-.992v.992h.992zM27.022 37.215h-.992v.992h.992zM27.022 38.207h-.992v.991h.992zM27.022 39.198h-.992v.992h.992zM28.014 8.455h-.992v.991h.992zM28.014 12.422h-.992v.991h.992zM28.014 13.413h-.992v.992h.992z"
  })), _path21$5 || (_path21$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M28.014 14.405h-.992v.992h.992zM28.014 15.397h-.992v.992h.992zM28.014 16.389h-.992v.991h.992zM28.014 20.356h-.992v.991h.992zM28.014 22.339h-.992v.992h.992z"
  })), _path22$5 || (_path22$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M28.014 23.33h-.992v.992h.992zM28.014 24.322h-.992v.992h.992zM28.014 26.306h-.992v.992h.992zM28.014 29.281h-.992v.992h.992zM28.014 30.273h-.992v.992h.992zM28.014 31.265h-.992v.991h.992zM28.014 32.256h-.992v.992h.992zM28.014 33.248h-.992v.992h.992zM28.014 35.232h-.992v.991h.992zM28.014 37.215h-.992v.992h.992zM28.014 39.198h-.992v.992h.992zM29.006 9.446h-.992v.992h.992zM29.006 10.438h-.992v.992h.992zM29.006 11.43h-.992v.992h.992zM29.006 12.422h-.992v.991h.992zM29.006 13.413h-.992v.992h.992zM29.006 16.389h-.992v.991h.992zM29.006 20.356h-.992v.991h.992zM29.006 26.306h-.992v.992h.992zM29.006 28.29h-.992v.991h.992zM29.006 29.281h-.992v.992h.992zM29.006 32.256h-.992v.992h.992zM29.006 33.248h-.992v.992h.992zM29.006 34.24h-.992v.991h.992zM29.006 35.232h-.992v.991h.992zM29.006 39.198h-.992v.992h.992zM29.006 40.19h-.992v.992h.992zM29.997 8.455h-.991v.991h.991zM29.997 9.446h-.991v.992h.991zM29.997 11.43h-.991v.992h.991zM29.997 12.422h-.991v.991h.991zM29.997 14.405h-.991v.992h.991zM29.997 16.389h-.991v.991h.991zM29.997 17.38h-.991v.992h.991zM29.997 19.364h-.991v.991h.991zM29.997 22.339h-.991v.992h.991z"
  })), _path23$5 || (_path23$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.997 23.33h-.991v.992h.991zM29.997 27.298h-.991v.991h.991zM29.997 28.29h-.991v.991h.991zM29.997 33.248h-.991v.992h.991zM29.997 34.24h-.991v.991h.991zM29.997 35.232h-.991v.991h.991zM29.997 36.223h-.991v.992h.991zM29.997 37.215h-.991v.992h.991zM30.99 8.455h-.993v.991h.992zM30.99 10.438h-.993v.992h.992zM30.99 15.397h-.993v.992h.992zM30.99 16.389h-.993v.991h.992zM30.99 19.364h-.993v.991h.992zM30.99 20.355h-.993v.992h.992zM30.99 24.322h-.993v.992h.992zM30.99 27.298h-.993v.991h.992zM30.99 30.273h-.993v.992h.992zM30.99 31.265h-.993v.991h.992zM30.99 32.256h-.993v.992h.992zM30.99 38.207h-.993v.991h.992zM30.99 39.198h-.993v.992h.992zM31.98 10.438h-.99v.992h.99zM31.98 11.43h-.99v.992h.99zM31.98 12.422h-.99v.991h.99zM31.98 13.413h-.99v.992h.99z"
  })), _path24$5 || (_path24$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.98 14.405h-.99v.992h.99zM31.98 15.397h-.99v.992h.99zM31.98 16.389h-.99v.991h.99zM31.98 18.372h-.99v.992h.99zM31.98 19.364h-.99v.991h.99zM31.98 20.355h-.99v.992h.99zM31.98 21.347h-.99v.992h.99z"
  })), _path25$5 || (_path25$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.98 22.339h-.99v.992h.99zM31.98 26.306h-.99v.992h.99z"
  })), _path26$5 || (_path26$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.98 27.298h-.99v.991h.99zM31.98 30.273h-.99v.992h.99zM31.98 31.265h-.99v.991h.99zM31.98 33.248h-.99v.992h.99zM31.98 39.198h-.99v.992h.99zM32.973 10.438h-.992v.992h.992zM32.973 13.413h-.992v.992h.992zM32.973 15.397h-.992v.992h.992zM32.973 17.38h-.992v.992h.992zM32.973 18.372h-.992v.992h.992zM32.973 19.364h-.992v.991h.992zM32.973 21.347h-.992v.992h.992zM32.973 24.322h-.992v.992h.992zM32.973 27.298h-.992v.991h.992zM32.973 28.29h-.992v.991h.992zM32.973 29.281h-.992v.992h.992zM32.973 30.273h-.992v.992h.992zM32.973 33.248h-.992v.992h.992zM32.973 34.24h-.992v.991h.992zM32.973 39.198h-.992v.992h.992zM33.964 8.455h-.991v.991h.991zM33.964 9.446h-.991v.992h.991zM33.964 10.438h-.991v.992h.991zM33.964 13.413h-.991v.992h.991z"
  })), _path27$5 || (_path27$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.964 14.405h-.991v.992h.991zM33.964 15.397h-.991v.992h.991zM33.964 18.372h-.991v.992h.991zM33.964 20.356h-.991v.991h.991zM33.964 22.339h-.991v.992h.991z"
  })), _path28$5 || (_path28$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.964 23.33h-.991v.992h.991zM33.964 25.314h-.991v.992h.991zM33.964 26.306h-.991v.992h.991zM33.964 29.281h-.991v.992h.991zM33.964 30.273h-.991v.992h.991zM33.964 32.256h-.991v.992h.991zM33.964 33.248h-.991v.992h.991zM33.964 34.24h-.991v.991h.991zM33.964 35.232h-.991v.991h.991zM33.964 36.223h-.991v.992h.991zM33.964 37.215h-.991v.992h.991zM33.964 40.19h-.991v.992h.991zM34.956 16.389h-.992v.991h.992zM34.956 18.372h-.992v.992h.992zM34.956 19.364h-.992v.991h.992zM34.956 20.355h-.992v.992h.992zM34.956 21.347h-.992v.992h.992zM34.956 26.306h-.992v.992h.992zM34.956 29.281h-.992v.992h.992zM34.956 31.265h-.992v.991h.992zM34.956 32.256h-.992v.992h.992zM34.956 36.223h-.992v.992h.992zM34.956 38.207h-.992v.991h.992zM34.956 39.198h-.992v.992h.992zM35.948 17.38h-.992v.992h.992zM35.948 21.347h-.992v.992h.992z"
  })), _path29$5 || (_path29$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M35.948 22.339h-.992v.992h.992z"
  })), _path30$5 || (_path30$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M35.948 23.33h-.992v.992h.992zM35.948 24.322h-.992v.992h.992zM35.948 28.29h-.992v.991h.992zM35.948 32.256h-.992v.992h.992zM35.948 34.24h-.992v.991h.992zM35.948 36.223h-.992v.992h.992zM35.948 37.215h-.992v.992h.992zM35.948 39.198h-.992v.992h.992zM36.94 17.38h-.992v.992h.992zM36.94 21.347h-.992v.992h.992zM36.94 23.33h-.992v.992h.992zM36.94 30.273h-.992v.992h.992zM36.94 32.256h-.992v.992h.992zM36.94 36.223h-.992v.992h.992zM36.94 39.198h-.992v.992h.992zM36.94 40.19h-.992v.992h.992zM37.931 16.389h-.991v.991h.991zM37.931 19.364h-.991v.991h.991zM37.931 20.355h-.991v.992h.991zM37.931 22.339h-.991v.992h.991z"
  })), _path31$5 || (_path31$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M37.931 23.33h-.991v.992h.991zM37.931 27.298h-.991v.991h.991zM37.931 30.273h-.991v.992h.991zM37.931 32.256h-.991v.992h.991zM37.931 33.248h-.991v.992h.991zM37.931 34.24h-.991v.991h.991zM37.931 35.232h-.991v.991h.991zM37.931 36.223h-.991v.992h.991zM37.931 37.215h-.991v.992h.991zM37.931 39.198h-.991v.992h.991zM37.931 40.19h-.991v.992h.991zM38.923 18.372h-.992v.992h.992zM38.923 19.364h-.992v.991h.992zM38.923 20.355h-.992v.992h.992zM38.923 21.347h-.992v.992h.992zM38.923 25.314h-.992v.992h.992zM38.923 27.298h-.992v.991h.992zM38.923 28.29h-.992v.991h.992zM38.923 31.265h-.992v.991h.992zM38.923 34.24h-.992v.991h.992zM38.923 35.232h-.992v.991h.992zM38.923 36.223h-.992v.992h.992zM38.923 38.207h-.992v.991h.992zM38.923 39.198h-.992v.992h.992zM39.915 20.356h-.992v.991h.992zM39.915 22.339h-.992v.992h.992zM39.915 25.314h-.992v.992h.992zM39.915 28.29h-.992v.991h.992zM39.915 29.281h-.992v.992h.992zM39.915 33.248h-.992v.992h.992zM39.915 34.24h-.992v.991h.992zM39.915 35.232h-.992v.991h.992zM39.915 36.223h-.992v.992h.992zM39.915 37.215h-.992v.992h.992zM39.915 38.207h-.992v.991h.992zM40.907 16.389h-.992v.991h.992zM40.907 17.38h-.992v.992h.992zM40.907 22.339h-.992v.992h.992z"
  })), _path32$5 || (_path32$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M40.907 23.33h-.992v.992h.992zM40.907 26.306h-.992v.992h.992zM40.907 29.281h-.992v.992h.992zM40.907 32.256h-.992v.992h.992zM40.907 37.215h-.992v.992h.992zM41.898 17.38h-.992v.992h.992zM41.898 20.356h-.992v.991h.992z"
  })), _path33$5 || (_path33$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M41.898 22.339h-.992v.992h.992zM41.898 27.298h-.992v.991h.992zM41.898 28.29h-.992v.991h.992zM41.898 29.281h-.992v.992h.992zM41.898 30.273h-.992v.992h.992zM41.898 32.256h-.992v.992h.992zM41.898 40.19h-.992v.992h.992z"
  })), _path34$4 || (_path34$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M9.17 8.455v6.942h6.943V8.455zm.993.991h4.958v4.959h-4.958z",
    clipRule: "evenodd"
  })), _path35$4 || (_path35$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M14.13 10.438h-2.976v2.975h2.976z"
  })), _path36$3 || (_path36$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M41.898 8.455h-6.942v6.942h6.942zm-.992.991v4.959h-4.958V9.446z",
    clipRule: "evenodd"
  })), _path37$3 || (_path37$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M39.915 13.413v-2.975H36.94v2.975z"
  })), _path38$3 || (_path38$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M39.915 10.438H36.94v2.975h2.975z"
  })), _path39$3 || (_path39$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M9.17 41.182h6.943V34.24H9.171zm.993-.992v-4.959h4.958v4.959z",
    clipRule: "evenodd"
  })), _path40$3 || (_path40$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M14.13 36.223h-2.976v2.975h2.976z"
  })), _path41$3 || (_path41$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    d: "M13.908 60.96q-.497 0-.932-.136a2.2 2.2 0 0 1-.737-.38l.264-.577q.314.24.657.35.35.11.755.11.447 0 .687-.153a.5.5 0 0 0 .245-.442.41.41 0 0 0-.227-.374q-.221-.129-.742-.246a4.4 4.4 0 0 1-.878-.27 1.24 1.24 0 0 1-.51-.392q-.171-.24-.171-.608 0-.38.209-.681.208-.3.589-.467.38-.171.877-.171.454 0 .866.14.411.136.675.375l-.264.577a2 2 0 0 0-1.277-.46q-.417 0-.662.172a.54.54 0 0 0-.24.466.43.43 0 0 0 .215.386q.215.135.73.252.546.123.884.276.344.147.521.38a.92.92 0 0 1 .184.59q0 .387-.208.68a1.3 1.3 0 0 1-.595.449 2.5 2.5 0 0 1-.915.153M17.978 60.96q-.639 0-1.117-.27a1.87 1.87 0 0 1-.73-.78q-.252-.503-.252-1.172 0-.67.252-1.166.257-.503.73-.773.478-.276 1.117-.276.435 0 .822.135t.65.38l-.264.577a2.1 2.1 0 0 0-.582-.337 1.7 1.7 0 0 0-.614-.111q-.625 0-.97.405-.337.405-.337 1.166 0 .767.337 1.172.345.405.97.405.325 0 .614-.104a2.2 2.2 0 0 0 .582-.344l.264.577q-.264.245-.65.38-.387.135-.822.135M23.062 60.904l-.436-1.012H20.52l-.436 1.012h-.797l1.957-4.326h.644l1.958 4.326zm-2.283-1.626h1.583l-.792-1.841zM26.876 56.578h.743v4.326h-.596l-2.32-3.019v3.02h-.736v-4.327h.59l2.32 3.013zM33.138 56.578h.62v4.326h-.7v-2.86l-1.141 2.16h-.528l-1.148-2.129.007 2.83h-.7v-4.327h.626l1.485 2.829zM34.355 60.904v-4.326h2.896v.626h-2.123v1.19h2v.62h-2v1.264h2.123v.626zM37.688 59.603l-.239-3.031.945.006-.24 3.025zm-.196.454h.86v.847h-.86z"
  })));
};

var _path$w, _path2$6, _path3$5, _path4$4, _path5$4, _path6$4, _path7$4, _path8$4, _path9$4, _path10$4, _path11$4, _path12$4, _path13$4, _path14$4, _path15$4, _path16$4, _path17$4, _path18$4, _path19$4, _path20$4, _path21$4, _path22$4, _path23$4, _path24$4, _path25$4, _path26$4, _path27$4, _path28$4, _path29$4, _path30$4, _path31$4, _path32$4, _path33$4;
function _extends$y() { return _extends$y = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$y.apply(null, arguments); }
var SvgFrameTicket = function SvgFrameTicket(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$y({
    xmlns: "http://www.w3.org/2000/svg",
    width: 50,
    height: 66,
    fill: "none"
  }, props), _path$w || (_path$w = /*#__PURE__*/React.createElement("path", {
    fill: "#404040",
    fillRule: "evenodd",
    d: "M44.555.273H6.373A5.455 5.455 0 0 1 .92 5.727v54.546a5.455 5.455 0 0 1 5.454 5.454h38.182a5.455 5.455 0 0 1 5.455-5.454V5.727A5.455 5.455 0 0 1 44.555.273",
    clipRule: "evenodd"
  })), _path2$6 || (_path2$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    d: "M4.191 15h42.545v42.545H4.191z"
  })), _path3$5 || (_path3$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M10.092 28.63h-.991v.992h.991zM10.092 29.622h-.991v.992h.991zM10.092 30.614h-.991v.991h.991zM10.092 33.589h-.991v.992h.991z"
  })), _path4$4 || (_path4$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M10.092 34.58h-.991v.992h.991zM10.092 37.556h-.991v.992h.991zM10.092 39.54h-.991v.991h.991zM10.092 41.523h-.991v.991h.991zM10.092 42.515h-.991v.991h.991zM11.084 30.614h-.992v.991h.992zM11.084 31.605h-.992v.992h.992zM11.084 32.597h-.992v.992h.992z"
  })), _path5$4 || (_path5$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M11.084 34.58h-.992v.992h.992zM11.084 37.556h-.992v.992h.992zM11.084 38.548h-.992v.991h.992zM11.084 39.54h-.992v.991h.992zM12.076 27.639h-.992v.991h.992zM12.076 29.622h-.992v.992h.992zM12.076 30.614h-.992v.991h.992zM12.076 36.564h-.992v.992h.992zM12.076 37.556h-.992v.992h.992zM12.076 38.548h-.992v.991h.992zM12.076 40.531h-.992v.992h.992zM12.076 41.523h-.992v.991h.992zM12.076 42.515h-.992v.991h.992zM13.068 28.63h-.992v.992h.992zM13.068 29.622h-.992v.992h.992zM13.068 30.614h-.992v.991h.992zM13.068 31.605h-.992v.992h.992zM13.068 32.597h-.992v.992h.992zM13.068 34.58h-.992v.992h.992zM13.068 36.564h-.992v.992h.992zM13.068 40.531h-.992v.992h.992zM13.068 41.523h-.992v.991h.992zM13.068 42.515h-.992v.991h.992zM13.068 43.506h-.992v.992h.992zM14.06 27.639h-.992v.991h.991zM14.06 32.597h-.992v.992h.991zM14.06 33.589h-.992v.992h.991z"
  })), _path6$4 || (_path6$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M14.06 34.58h-.992v.992h.991zM14.06 35.572h-.992v.992h.991zM14.06 36.564h-.992v.992h.991zM14.06 38.548h-.992v.991h.991zM14.06 42.515h-.992v.991h.991zM14.06 43.506h-.992v.992h.991zM15.051 27.639h-.992v.991h.992zM15.051 28.63h-.992v.992h.992zM15.051 29.622h-.992v.992h.992z"
  })), _path7$4 || (_path7$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M15.051 33.589h-.992v.992h.992zM15.051 35.572h-.992v.992h.992zM15.051 36.564h-.992v.992h.992zM15.051 39.54h-.992v.991h.992zM15.051 40.531h-.992v.992h.992zM16.043 27.639h-.992v.991h.992zM16.043 29.622h-.992v.992h.992zM16.043 31.606h-.992v.991h.992z"
  })), _path8$4 || (_path8$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M16.043 33.589h-.992v.992h.992zM16.043 35.572h-.992v.992h.992zM16.043 37.556h-.992v.992h.992zM16.043 39.54h-.992v.991h.992zM16.043 41.523h-.992v.991h.992zM16.043 43.506h-.992v.992h.992zM17.035 29.622h-.992v.992h.992zM17.035 30.614h-.992v.991h.992zM17.035 31.605h-.992v.992h.992zM17.035 33.589h-.992v.992h.992zM17.035 35.572h-.992v.992h.992zM17.035 36.564h-.992v.992h.992zM17.035 38.548h-.992v.991h.992zM17.035 39.54h-.992v.991h.992zM17.035 40.531h-.992v.992h.992zM17.035 41.523h-.992v.991h.992zM17.035 42.515h-.992v.991h.992zM18.026 20.696h-.991v.992h.991zM18.026 23.672h-.991v.991h.991zM18.026 25.655h-.991v.992h.991zM18.026 27.639h-.991v.991h.991zM18.026 28.63h-.991v.992h.991zM18.026 29.622h-.991v.992h.991zM18.026 30.614h-.991v.991h.991zM18.026 34.58h-.991v.992h.991zM18.026 35.572h-.991v.992h.991zM18.026 36.564h-.991v.992h.991zM18.026 40.531h-.991v.992h.991zM18.026 41.523h-.991v.991h.991zM18.026 44.498h-.991v.992h.991zM18.026 46.482h-.991v.991h.991zM18.026 47.473h-.991v.992h.991zM18.026 49.457h-.991v.991h.991zM19.018 19.705h-.992v.991h.992zM19.018 22.68h-.992v.992h.992zM19.018 23.672h-.992v.991h.992zM19.018 24.663h-.992v.992h.992zM19.018 26.647h-.992v.992h.992zM19.018 28.63h-.992v.992h.992zM19.018 29.622h-.992v.992h.992zM19.018 30.614h-.992v.991h.992zM19.018 31.605h-.992v.992h.992zM19.018 32.597h-.992v.992h.992zM19.018 33.589h-.992v.992h.992z"
  })), _path9$4 || (_path9$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M19.018 34.58h-.992v.992h.992zM19.018 36.564h-.992v.992h.992zM19.018 37.556h-.992v.992h.992zM19.018 40.531h-.992v.992h.992zM19.018 42.515h-.992v.991h.992zM19.018 44.498h-.992v.992h.992zM19.018 48.465h-.992v.992h.992zM19.018 49.457h-.992v.991h.992zM19.018 50.448h-.992v.992h.992zM20.01 19.705h-.992v.991h.992zM20.01 24.663h-.992v.992h.992zM20.01 25.655h-.992v.992h.992zM20.01 30.614h-.992v.991h.992zM20.01 31.605h-.992v.992h.992zM20.01 32.597h-.992v.992h.992zM20.01 33.589h-.992v.992h.992zM20.01 37.556h-.992v.992h.992zM20.01 39.54h-.992v.991h.992zM20.01 42.515h-.992v.991h.992zM20.01 44.498h-.992v.992h.992zM20.01 48.465h-.992v.992h.992zM21.001 20.696h-.991v.992H21zM21.001 23.672h-.991v.991H21zM21.001 24.663h-.991v.992H21zM21.001 26.647h-.991v.992H21zM21.001 30.614h-.991v.991H21zM21.001 33.589h-.991v.992H21z"
  })), _path10$4 || (_path10$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M21.001 34.58h-.991v.992H21zM21.001 38.548h-.991v.991H21zM21.001 39.54h-.991v.991H21zM21.001 41.523h-.991v.992H21zM21.001 43.506h-.991v.992H21zM21.001 44.498h-.991v.992H21zM21.001 45.49h-.991v.991H21zM21.001 50.448h-.991v.992H21zM21.993 20.696h-.992v.992h.992zM21.993 25.655h-.992v.992h.992zM21.993 27.639h-.992v.991h.992zM21.993 28.63h-.992v.992h.992zM21.993 31.606h-.992v.991h.992zM21.993 32.597h-.992v.992h.992z"
  })), _path11$4 || (_path11$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M21.993 33.589h-.992v.992h.992zM21.993 41.523h-.992v.991h.992zM21.993 42.515h-.992v.991h.992zM21.993 43.506h-.992v.992h.992zM21.993 44.498h-.992v.992h.992zM21.993 45.49h-.992v.991h.992zM21.993 46.482h-.992v.991h.992zM21.993 47.473h-.992v.992h.992zM21.993 48.465h-.992v.992h.992zM22.985 19.705h-.992v.991h.992zM22.985 23.672h-.992v.991h.992zM22.985 26.647h-.992v.992h.992zM22.985 28.63h-.992v.992h.992zM22.985 29.622h-.992v.992h.992zM22.985 30.614h-.992v.991h.992zM22.985 31.605h-.992v.992h.992zM22.985 32.597h-.992v.992h.992zM22.985 34.58h-.992v.992h.992zM22.985 36.564h-.992v.992h.992zM22.985 37.556h-.992v.992h.992zM22.985 38.548h-.992v.991h.992zM22.985 39.54h-.992v.991h.992zM22.985 48.465h-.992v.992h.992zM23.977 23.672h-.992v.991h.992zM23.977 25.655h-.992v.992h.992z"
  })), _path12$4 || (_path12$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M23.977 26.647h-.992v.992h.992zM23.977 27.639h-.992v.991h.992zM23.977 31.606h-.992v.991h.992zM23.977 32.597h-.992v.992h.992zM23.977 34.58h-.992v.992h.992zM23.977 35.572h-.992v.992h.992zM23.977 36.564h-.992v.992h.992zM23.977 39.54h-.992v.991h.992zM23.977 41.523h-.992v.991h.992zM23.977 42.515h-.992v.991h.992zM23.977 43.506h-.992v.992h.992zM23.977 44.498h-.992v.992h.992zM23.977 45.49h-.992v.991h.992zM23.977 46.482h-.992v.991h.992zM23.977 47.473h-.992v.992h.992zM23.977 49.457h-.992v.991h.992zM23.977 50.448h-.992v.992h.992zM24.968 19.705h-.991v.991h.991zM24.968 20.696h-.991v.992h.991zM24.968 22.68h-.991v.992h.991zM24.968 28.63h-.991v.992h.991zM24.968 29.622h-.991v.992h.991zM24.968 31.606h-.991v.991h.991zM24.968 32.597h-.991v.992h.991zM24.968 35.572h-.991v.992h.991zM24.968 36.564h-.991v.992h.991zM24.968 40.531h-.991v.992h.991zM24.968 42.515h-.991v.991h.991zM24.968 44.498h-.991v.992h.991zM24.968 45.49h-.991v.991h.991zM24.968 47.473h-.991v.992h.991zM25.96 19.705h-.992v.991h.992zM25.96 20.696h-.992v.992h.992zM25.96 24.663h-.992v.992h.992zM25.96 25.655h-.992v.992h.992zM25.96 28.63h-.992v.992h.992zM25.96 33.589h-.992v.992h.992z"
  })), _path13$4 || (_path13$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M25.96 34.58h-.992v.992h.992zM25.96 36.564h-.992v.992h.992zM25.96 38.548h-.992v.991h.992zM25.96 39.54h-.992v.991h.992zM25.96 40.531h-.992v.992h.992zM25.96 41.523h-.992v.992h.992zM25.96 43.506h-.992v.992h.992zM25.96 45.49h-.992v.991h.992zM25.96 47.473h-.992v.992h.992zM25.96 48.465h-.992v.992h.992zM25.96 50.448h-.992v.992h.992zM26.952 19.705h-.992v.991h.992zM26.952 22.68h-.992v.992h.992zM26.952 23.672h-.992v.991h.992zM26.952 27.639h-.992v.991h.992zM26.952 28.63h-.992v.992h.992zM26.952 29.622h-.992v.992h.992zM26.952 30.614h-.992v.991h.992zM26.952 32.597h-.992v.992h.992zM26.952 36.564h-.992v.992h.992zM26.952 41.523h-.992v.991h.992zM26.952 42.515h-.992v.991h.992zM26.952 43.506h-.992v.992h.992zM26.952 48.465h-.992v.992h.992zM26.952 49.457h-.992v.991h.992zM26.952 50.448h-.992v.992h.992z"
  })), _path14$4 || (_path14$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.944 19.705h-.992v.991h.992zM27.944 23.672h-.992v.991h.992zM27.944 24.663h-.992v.992h.992zM27.944 25.655h-.992v.992h.992z"
  })), _path15$4 || (_path15$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.944 26.647h-.992v.992h.992zM27.944 27.639h-.992v.991h.992zM27.944 31.606h-.992v.991h.992zM27.944 33.589h-.992v.992h.992z"
  })), _path16$4 || (_path16$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.944 34.58h-.992v.992h.992zM27.944 35.572h-.992v.992h.992zM27.944 37.556h-.992v.992h.992zM27.944 40.531h-.992v.992h.992zM27.944 41.523h-.992v.991h.992zM27.944 42.515h-.992v.991h.992zM27.944 43.506h-.992v.992h.992zM27.944 44.498h-.992v.992h.992zM27.944 46.482h-.992v.991h.992zM27.944 48.465h-.992v.992h.992zM27.944 50.448h-.992v.992h.992zM28.935 20.696h-.991v.992h.991z"
  })), _path17$4 || (_path17$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M28.935 21.688h-.991v.992h.991zM28.935 22.68h-.991v.992h.991zM28.935 23.672h-.991v.991h.991zM28.935 24.663h-.991v.992h.991zM28.935 27.639h-.991v.991h.991zM28.935 31.606h-.991v.991h.991zM28.935 37.556h-.991v.992h.991zM28.935 39.54h-.991v.991h.991zM28.935 40.531h-.991v.992h.991zM28.935 43.506h-.991v.992h.991zM28.935 44.498h-.991v.992h.991zM28.935 45.49h-.991v.991h.991zM28.935 46.482h-.991v.991h.991zM28.935 50.448h-.991v.992h.991zM28.935 51.44h-.991v.992h.991zM29.927 19.705h-.992v.991h.992zM29.927 20.696h-.992v.992h.992zM29.927 22.68h-.992v.992h.992zM29.927 23.672h-.992v.991h.992zM29.927 25.655h-.992v.992h.992zM29.927 27.639h-.992v.991h.992zM29.927 28.63h-.992v.992h.992zM29.927 30.614h-.992v.991h.992zM29.927 33.589h-.992v.992h.992z"
  })), _path18$4 || (_path18$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.927 34.58h-.992v.992h.992zM29.927 38.548h-.992v.991h.992zM29.927 39.54h-.992v.991h.992zM29.927 44.498h-.992v.992h.992zM29.927 45.49h-.992v.991h.992zM29.927 46.482h-.992v.991h.992zM29.927 47.473h-.992v.992h.992zM29.927 48.465h-.992v.992h.992zM30.919 19.705h-.992v.991h.992zM30.919 21.688h-.992v.992h.992zM30.919 26.647h-.992v.992h.992zM30.919 27.639h-.992v.991h.992zM30.919 30.614h-.992v.991h.992zM30.919 31.605h-.992v.992h.992zM30.919 35.572h-.992v.992h.992zM30.919 38.548h-.992v.991h.992zM30.919 41.523h-.992v.991h.992zM30.919 42.515h-.992v.991h.992zM30.919 43.506h-.992v.992h.992zM30.919 49.457h-.992v.991h.992zM30.919 50.448h-.992v.992h.992zM31.91 21.688h-.991v.992h.991zM31.91 22.68h-.991v.992h.991zM31.91 23.672h-.991v.991h.991zM31.91 24.663h-.991v.992h.991zM31.91 25.655h-.991v.992h.991z"
  })), _path19$4 || (_path19$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.91 26.647h-.991v.992h.991zM31.91 27.639h-.991v.991h.991zM31.91 29.622h-.991v.992h.991zM31.91 30.614h-.991v.991h.991zM31.91 31.605h-.991v.992h.991zM31.91 32.597h-.991v.992h.991zM31.91 33.589h-.991v.992h.991zM31.91 37.556h-.991v.992h.991zM31.91 38.548h-.991v.991h.991zM31.91 41.523h-.991v.991h.991zM31.91 42.515h-.991v.991h.991zM31.91 44.498h-.991v.992h.991zM31.91 50.448h-.991v.992h.991zM32.902 21.688h-.992v.992h.992zM32.902 24.663h-.992v.992h.992zM32.902 26.647h-.992v.992h.992zM32.902 28.63h-.992v.992h.992zM32.902 29.622h-.992v.992h.992zM32.902 30.614h-.992v.991h.992zM32.902 32.597h-.992v.992h.992zM32.902 35.572h-.992v.992h.992zM32.902 38.548h-.992v.991h.992zM32.902 39.54h-.992v.991h.992zM32.902 40.531h-.992v.992h.992zM32.902 41.523h-.992v.991h.992zM32.902 44.498h-.992v.992h.992zM32.902 45.49h-.992v.991h.992zM32.902 50.448h-.992v.992h.992zM33.894 19.705h-.992v.991h.992zM33.894 20.696h-.992v.992h.992z"
  })), _path20$4 || (_path20$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.894 21.688h-.992v.992h.992zM33.894 24.663h-.992v.992h.992zM33.894 25.655h-.992v.992h.992z"
  })), _path21$4 || (_path21$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.894 26.647h-.992v.992h.992zM33.894 29.622h-.992v.992h.992zM33.894 31.606h-.992v.991h.992zM33.894 33.589h-.992v.992h.992z"
  })), _path22$4 || (_path22$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.894 34.58h-.992v.992h.992zM33.894 36.564h-.992v.992h.992zM33.894 37.556h-.992v.992h.992zM33.894 40.531h-.992v.992h.992zM33.894 41.523h-.992v.991h.992zM33.894 43.506h-.992v.992h.992zM33.894 44.498h-.992v.992h.992zM33.894 45.49h-.992v.991h.992zM33.894 46.482h-.992v.991h.992zM33.894 47.473h-.992v.992h.992zM33.894 48.465h-.992v.992h.992zM33.894 51.44h-.992v.992h.992zM34.886 27.639h-.992v.991h.992zM34.886 29.622h-.992v.992h.992zM34.886 30.614h-.992v.991h.992zM34.886 31.605h-.992v.992h.992zM34.886 32.597h-.992v.992h.992zM34.886 37.556h-.992v.992h.992zM34.886 40.531h-.992v.992h.992zM34.886 42.515h-.992v.991h.992zM34.886 43.506h-.992v.992h.992zM34.886 47.473h-.992v.992h.992zM34.886 49.457h-.992v.991h.992zM34.886 50.448h-.992v.992h.992zM35.878 28.63h-.992v.992h.991zM35.878 32.597h-.992v.992h.991zM35.878 33.589h-.992v.992h.991z"
  })), _path23$4 || (_path23$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M35.878 34.58h-.992v.992h.991zM35.878 35.572h-.992v.992h.991zM35.878 39.54h-.992v.991h.991zM35.878 43.506h-.992v.992h.991zM35.878 45.49h-.992v.991h.991zM35.878 47.473h-.992v.992h.991zM35.878 48.465h-.992v.992h.991zM35.878 50.448h-.992v.992h.991zM36.87 28.63h-.992v.992h.991zM36.87 32.597h-.992v.992h.991zM36.87 34.58h-.992v.992h.991zM36.87 41.523h-.992v.991h.991zM36.87 43.506h-.992v.992h.991zM36.87 47.473h-.992v.992h.991zM36.87 50.448h-.992v.992h.991zM36.87 51.44h-.992v.992h.991zM37.86 27.639h-.99v.991h.99zM37.86 30.614h-.99v.991h.99zM37.86 31.605h-.99v.992h.99zM37.86 33.589h-.99v.992h.99z"
  })), _path24$4 || (_path24$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M37.86 34.58h-.99v.992h.99zM37.86 38.548h-.99v.991h.99zM37.86 41.523h-.99v.991h.99zM37.86 43.506h-.99v.992h.99zM37.86 44.498h-.99v.992h.99zM37.86 45.49h-.99v.991h.99zM37.86 46.482h-.99v.991h.99zM37.86 47.473h-.99v.992h.99zM37.86 48.465h-.99v.992h.99zM37.86 50.448h-.99v.992h.99zM37.86 51.44h-.99v.992h.99zM38.853 29.622h-.992v.992h.992zM38.853 30.614h-.992v.991h.992zM38.853 31.605h-.992v.992h.992zM38.853 32.597h-.992v.992h.992zM38.853 36.564h-.992v.992h.992zM38.853 38.548h-.992v.991h.992zM38.853 39.54h-.992v.991h.992zM38.853 42.515h-.992v.991h.992zM38.853 45.49h-.992v.991h.992zM38.853 46.482h-.992v.991h.992zM38.853 47.473h-.992v.992h.992zM38.853 49.457h-.992v.991h.992zM38.853 50.448h-.992v.992h.992zM39.844 31.606h-.991v.991h.991zM39.844 33.589h-.991v.992h.991zM39.844 36.564h-.991v.992h.991zM39.844 39.54h-.991v.991h.991zM39.844 40.531h-.991v.992h.991zM39.844 44.498h-.991v.992h.991zM39.844 45.49h-.991v.991h.991zM39.844 46.482h-.991v.991h.991zM39.844 47.473h-.991v.992h.991zM39.844 48.465h-.991v.992h.991zM39.844 49.457h-.991v.991h.991zM40.836 27.639h-.992v.991h.992zM40.836 28.63h-.992v.992h.992zM40.836 33.589h-.992v.992h.992z"
  })), _path25$4 || (_path25$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M40.836 34.58h-.992v.992h.992zM40.836 37.556h-.992v.992h.992zM40.836 40.531h-.992v.992h.992zM40.836 43.506h-.992v.992h.992zM40.836 48.465h-.992v.992h.992zM41.828 28.63h-.992v.992h.992zM41.828 31.606h-.992v.991h.992zM41.828 33.589h-.992v.992h.992zM41.828 38.548h-.992v.991h.992zM41.828 39.54h-.992v.991h.992zM41.828 40.531h-.992v.992h.992zM41.828 41.523h-.992v.991h.992zM41.828 43.506h-.992v.992h.992zM41.828 51.44h-.992v.992h.992z"
  })), _path26$4 || (_path26$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M9.1 19.705v6.942h6.943v-6.942zm.992.991h4.959v4.959h-4.959z",
    clipRule: "evenodd"
  })), _path27$4 || (_path27$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M14.06 21.688h-2.976v2.975h2.975z"
  })), _path28$4 || (_path28$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M41.828 19.705h-6.942v6.942h6.942zm-.992.991v4.959h-4.959v-4.959z",
    clipRule: "evenodd"
  })), _path29$4 || (_path29$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M39.844 24.663v-2.975H36.87v2.975z"
  })), _path30$4 || (_path30$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M39.844 21.688H36.87v2.975h2.975z"
  })), _path31$4 || (_path31$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M9.1 52.432h6.943V45.49H9.1zm.992-.992v-4.959h4.959v4.959z",
    clipRule: "evenodd"
  })), _path32$4 || (_path32$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M14.06 47.473h-2.976v2.975h2.975z"
  })), _path33$4 || (_path33$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    d: "M13.838 10.334q-.496 0-.933-.135a2.2 2.2 0 0 1-.736-.38l.264-.577q.312.24.656.35.35.11.755.11.449 0 .687-.153a.5.5 0 0 0 .246-.442.41.41 0 0 0-.227-.374q-.22-.129-.743-.246a4.4 4.4 0 0 1-.877-.27 1.24 1.24 0 0 1-.51-.392q-.171-.24-.171-.608 0-.38.208-.681.21-.3.59-.466.38-.172.877-.172.454 0 .865.14.412.136.675.375l-.264.577a2 2 0 0 0-1.276-.46q-.418 0-.663.172a.54.54 0 0 0-.24.466.43.43 0 0 0 .216.387q.214.135.73.251.547.123.883.276.345.147.522.38a.92.92 0 0 1 .184.59q0 .387-.208.68a1.3 1.3 0 0 1-.596.449 2.5 2.5 0 0 1-.914.153M17.907 10.334q-.637 0-1.116-.27a1.87 1.87 0 0 1-.73-.779q-.252-.503-.252-1.172 0-.67.251-1.166.259-.503.73-.773.48-.276 1.117-.276.435 0 .823.135.386.135.65.38l-.264.577a2.1 2.1 0 0 0-.583-.338 1.7 1.7 0 0 0-.613-.11q-.626 0-.97.405-.337.405-.337 1.166 0 .767.337 1.172.344.405.97.405.324 0 .613-.104.288-.111.583-.344l.264.577q-.264.245-.65.38-.387.135-.823.135M22.991 10.28l-.436-1.013h-2.104l-.436 1.012h-.798l1.958-4.326h.644l1.958 4.326zM20.71 8.652h1.583L21.5 6.812zM26.806 5.953h.742v4.326h-.595l-2.32-3.019v3.02h-.736V5.952h.59l2.319 3.013zM33.068 5.953h.62v4.326h-.7V7.42l-1.142 2.16h-.527L30.17 7.45l.006 2.83h-.7V5.952h.627l1.485 2.829zM34.284 10.28V5.952h2.897v.626h-2.124v1.19h2v.62h-2v1.264h2.124v.626zM37.618 8.978l-.24-3.031.946.006-.24 3.025zm-.196.454h.859v.847h-.86z"
  })));
};

var _path$v, _path2$5, _path3$4, _path4$3, _path5$3, _path6$3, _path7$3, _path8$3, _path9$3, _path10$3, _path11$3, _path12$3, _path13$3, _path14$3, _path15$3, _path16$3, _path17$3, _path18$3, _path19$3, _path20$3, _path21$3, _path22$3, _path23$3, _path24$3, _path25$3, _path26$3, _path27$3, _path28$3, _path29$3, _path30$3, _path31$3, _path32$3, _path33$3, _path34$3, _path35$3, _path36$2, _path37$2, _path38$2, _path39$2, _path40$2, _path41$2, _path42$2, _path43$2, _path44$2, _path45$2, _path46$2, _path47$2, _path48$2, _path49$2, _path50$2, _path51$2, _path52$2, _path53$2, _path54$1, _path55$1, _path56$1, _path57$1, _path58$1, _path59$1, _path60$1, _path61$1, _path62$1, _path63$1, _path64$1, _path65$1;
function _extends$x() { return _extends$x = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$x.apply(null, arguments); }
var SvgFrameMuseum = function SvgFrameMuseum(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$x({
    xmlns: "http://www.w3.org/2000/svg",
    width: 50,
    height: 66,
    fill: "none"
  }, props), _path$v || (_path$v = /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    d: "M3.917 3.39h42.955v43.221H3.917z"
  })), _path2$5 || (_path2$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M10.022 17.652H9.03v1.007h.992zM10.022 18.66H9.03v1.007h.992zM10.022 19.667H9.03v1.007h.992zM10.022 22.69H9.03v1.007h.992z"
  })), _path3$4 || (_path3$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M10.022 23.697H9.03v1.007h.992zM10.022 26.72H9.03v1.007h.992zM10.022 28.734H9.03v1.007h.992zM10.022 30.749H9.03v1.008h.992zM10.022 31.757H9.03v1.007h.992zM11.014 19.667h-.992v1.007h.992z"
  })), _path4$3 || (_path4$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M11.014 20.674h-.992v1.008h.992zM11.014 21.682h-.992v1.007h.992zM11.014 23.697h-.992v1.007h.992zM11.014 26.72h-.992v1.007h.992zM11.014 27.727h-.992v1.007h.992zM11.014 28.734h-.992v1.007h.992zM12.006 16.644h-.992v1.008h.992zM12.006 18.66h-.992v1.007h.992zM12.006 19.667h-.992v1.007h.992zM12.006 25.712h-.992v1.007h.992zM12.006 26.72h-.992v1.007h.992zM12.006 27.727h-.992v1.007h.992zM12.006 29.742h-.992v1.007h.992z"
  })), _path5$3 || (_path5$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M12.006 30.749h-.992v1.008h.992zM12.006 31.757h-.992v1.007h.992zM12.997 17.652h-.992v1.007h.992zM12.997 18.66h-.992v1.007h.992zM12.997 19.667h-.992v1.007h.992z"
  })), _path6$3 || (_path6$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M12.997 20.674h-.992v1.008h.992zM12.997 21.682h-.992v1.007h.992zM12.997 23.697h-.992v1.007h.992zM12.997 25.712h-.992v1.007h.992zM12.997 29.742h-.992v1.007h.992z"
  })), _path7$3 || (_path7$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M12.997 30.749h-.992v1.008h.992zM12.997 31.757h-.992v1.007h.992zM12.997 32.764h-.992v1.007h.992zM13.989 16.644h-.992v1.008h.992zM13.989 21.682h-.992v1.007h.992zM13.989 22.69h-.992v1.007h.992z"
  })), _path8$3 || (_path8$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M13.989 23.697h-.992v1.007h.992zM13.989 24.704h-.992v1.008h.992zM13.989 25.712h-.992v1.007h.992zM13.989 27.727h-.992v1.007h.992zM13.989 31.757h-.992v1.007h.992zM13.989 32.764h-.992v1.007h.992zM14.98 16.644h-.991v1.008h.992zM14.98 17.652h-.991v1.007h.992zM14.98 18.66h-.991v1.007h.992zM14.98 22.69h-.991v1.007h.992zM14.98 24.704h-.991v1.008h.992zM14.98 25.712h-.991v1.007h.992zM14.98 28.734h-.991v1.007h.992zM14.98 29.742h-.991v1.007h.992z"
  })), _path9$3 || (_path9$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M15.972 16.644h-.991v1.008h.991zM15.972 18.66h-.991v1.007h.991zM15.972 20.674h-.991v1.008h.991zM15.972 22.69h-.991v1.007h.991zM15.972 24.704h-.991v1.008h.991zM15.972 26.72h-.991v1.007h.991zM15.972 28.734h-.991v1.007h.991zM15.972 30.749h-.991v1.008h.991zM15.972 32.764h-.991v1.007h.991z"
  })), _path10$3 || (_path10$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M16.964 18.66h-.992v1.007h.992zM16.964 19.667h-.992v1.007h.992z"
  })), _path11$3 || (_path11$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M16.964 20.674h-.992v1.008h.992zM16.964 22.69h-.992v1.007h.992zM16.964 24.704h-.992v1.008h.992zM16.964 25.712h-.992v1.007h.992zM16.964 27.727h-.992v1.007h.992zM16.964 28.734h-.992v1.007h.992zM16.964 29.742h-.992v1.007h.992z"
  })), _path12$3 || (_path12$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M16.964 30.749h-.992v1.008h.992zM16.964 31.757h-.992v1.007h.992zM17.956 9.592h-.992V10.6h.992zM17.956 12.614h-.992v1.008h.992zM17.956 14.63h-.992v1.007h.992zM17.956 16.644h-.992v1.008h.992zM17.956 17.652h-.992v1.007h.992zM17.956 18.66h-.992v1.007h.992zM17.956 19.667h-.992v1.007h.992zM17.956 23.697h-.992v1.007h.992zM17.956 24.704h-.992v1.008h.992zM17.956 25.712h-.992v1.007h.992zM17.956 29.742h-.992v1.007h.992z"
  })), _path13$3 || (_path13$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M17.956 30.749h-.992v1.008h.992zM17.956 33.771h-.992v1.008h.992zM17.956 35.786h-.992v1.008h.992z"
  })), _path14$3 || (_path14$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M17.956 36.794h-.992V37.8h.992zM17.956 38.809h-.992v1.007h.992zM18.948 8.584h-.992v1.008h.992zM18.948 11.607h-.992v1.007h.992zM18.948 12.614h-.992v1.008h.992zM18.948 13.622h-.992v1.007h.992zM18.948 15.637h-.992v1.007h.992zM18.948 17.652h-.992v1.007h.992zM18.948 18.66h-.992v1.007h.992zM18.948 19.667h-.992v1.007h.992z"
  })), _path15$3 || (_path15$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18.948 20.674h-.992v1.008h.992zM18.948 21.682h-.992v1.007h.992zM18.948 22.69h-.992v1.007h.992z"
  })), _path16$3 || (_path16$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18.948 23.697h-.992v1.007h.992zM18.948 25.712h-.992v1.007h.992zM18.948 26.72h-.992v1.007h.992zM18.948 29.742h-.992v1.007h.992zM18.948 31.757h-.992v1.007h.992zM18.948 33.771h-.992v1.008h.992zM18.948 37.801h-.992v1.008h.992z"
  })), _path17$3 || (_path17$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18.948 38.809h-.992v1.007h.992zM18.948 39.816h-.992v1.008h.992zM19.94 8.584h-.992v1.008h.991zM19.94 13.622h-.992v1.007h.991zM19.94 14.63h-.992v1.007h.991zM19.94 19.667h-.992v1.007h.991z"
  })), _path18$3 || (_path18$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M19.94 20.674h-.992v1.008h.991zM19.94 21.682h-.992v1.007h.991zM19.94 22.69h-.992v1.007h.991zM19.94 26.72h-.992v1.007h.991zM19.94 28.734h-.992v1.007h.991zM19.94 31.757h-.992v1.007h.991zM19.94 33.771h-.992v1.008h.991zM19.94 37.801h-.992v1.008h.991zM20.931 9.592h-.992V10.6h.992zM20.931 12.614h-.992v1.008h.992zM20.931 13.622h-.992v1.007h.992zM20.931 15.637h-.992v1.007h.992z"
  })), _path19$3 || (_path19$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M20.931 19.667h-.992v1.007h.992zM20.931 22.69h-.992v1.007h.992z"
  })), _path20$3 || (_path20$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M20.931 23.697h-.992v1.007h.992zM20.931 27.727h-.992v1.007h.992zM20.931 28.734h-.992v1.007h.992zM20.931 30.749h-.992v1.008h.992zM20.931 32.764h-.992v1.007h.992zM20.931 33.771h-.992v1.008h.992zM20.931 34.779h-.992v1.007h.992zM20.931 39.816h-.992v1.008h.992zM21.923 9.592h-.992V10.6h.992zM21.923 14.63h-.992v1.007h.992zM21.923 16.644h-.992v1.008h.992zM21.923 17.652h-.992v1.007h.992zM21.923 20.674h-.992v1.008h.992zM21.923 21.682h-.992v1.007h.992z"
  })), _path21$3 || (_path21$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M21.923 22.69h-.992v1.007h.992zM21.923 30.749h-.992v1.008h.992zM21.923 31.757h-.992v1.007h.992zM21.923 32.764h-.992v1.007h.992zM21.923 33.771h-.992v1.008h.992zM21.923 34.779h-.992v1.007h.992zM21.923 35.786h-.992v1.008h.992z"
  })), _path22$3 || (_path22$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M21.923 36.794h-.992V37.8h.992zM21.923 37.801h-.992v1.008h.992zM22.915 8.584h-.992v1.008h.992zM22.915 12.614h-.992v1.008h.992zM22.915 15.637h-.992v1.007h.992zM22.915 17.652h-.992v1.007h.992zM22.915 18.66h-.992v1.007h.992zM22.915 19.667h-.992v1.007h.992z"
  })), _path23$3 || (_path23$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M22.915 20.674h-.992v1.008h.992zM22.915 21.682h-.992v1.007h.992zM22.915 23.697h-.992v1.007h.992zM22.915 25.712h-.992v1.007h.992zM22.915 26.72h-.992v1.007h.992zM22.915 27.727h-.992v1.007h.992zM22.915 28.734h-.992v1.007h.992zM22.915 37.801h-.992v1.008h.992zM23.906 12.614h-.991v1.008h.991zM23.906 14.63h-.991v1.007h.991zM23.906 15.637h-.991v1.007h.991z"
  })), _path24$3 || (_path24$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M23.906 16.644h-.991v1.008h.991zM23.906 20.674h-.991v1.008h.991zM23.906 21.682h-.991v1.007h.991zM23.906 23.697h-.991v1.007h.991zM23.906 24.704h-.991v1.008h.991zM23.906 25.712h-.991v1.007h.991zM23.906 28.734h-.991v1.007h.991zM23.906 30.749h-.991v1.008h.991zM23.906 31.757h-.991v1.007h.991zM23.906 32.764h-.991v1.007h.991zM23.906 33.771h-.991v1.008h.991zM23.906 34.779h-.991v1.007h.991zM23.906 35.786h-.991v1.008h.991z"
  })), _path25$3 || (_path25$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M23.906 36.794h-.991V37.8h.991zM23.906 38.809h-.991v1.007h.991zM23.906 39.816h-.991v1.008h.991zM24.898 8.584h-.992v1.008h.992zM24.898 9.592h-.992V10.6h.992zM24.898 11.607h-.992v1.007h.992zM24.898 17.652h-.992v1.007h.992zM24.898 18.66h-.992v1.007h.992zM24.898 20.674h-.992v1.008h.992zM24.898 21.682h-.992v1.007h.992zM24.898 24.704h-.992v1.008h.992zM24.898 25.712h-.992v1.007h.992zM24.898 29.742h-.992v1.007h.992zM24.898 31.757h-.992v1.007h.992zM24.898 33.771h-.992v1.008h.992zM24.898 34.779h-.992v1.007h.992zM24.898 36.794h-.992V37.8h.992zM25.89 8.584h-.992v1.008h.992zM25.89 9.592h-.992V10.6h.992zM25.89 13.622h-.992v1.007h.992zM25.89 14.63h-.992v1.007h.992zM25.89 17.652h-.992v1.007h.992zM25.89 22.69h-.992v1.007h.992z"
  })), _path26$3 || (_path26$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M25.89 23.697h-.992v1.007h.992zM25.89 25.712h-.992v1.007h.992zM25.89 27.727h-.992v1.007h.992zM25.89 28.734h-.992v1.007h.992zM25.89 29.742h-.992v1.007h.992z"
  })), _path27$3 || (_path27$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M25.89 30.749h-.992v1.008h.992zM25.89 32.764h-.992v1.007h.992zM25.89 34.779h-.992v1.007h.992zM25.89 36.794h-.992V37.8h.992zM25.89 37.801h-.992v1.008h.992zM25.89 39.816h-.992v1.008h.992zM26.882 8.584h-.992v1.008h.991zM26.882 11.607h-.992v1.007h.991zM26.882 12.614h-.992v1.008h.991zM26.882 16.644h-.992v1.008h.991zM26.882 17.652h-.992v1.007h.991zM26.882 18.66h-.992v1.007h.991zM26.882 19.667h-.992v1.007h.991zM26.882 21.682h-.992v1.007h.991zM26.882 25.712h-.992v1.007h.991zM26.882 30.749h-.992v1.008h.991zM26.882 31.757h-.992v1.007h.991zM26.882 32.764h-.992v1.007h.991zM26.882 37.801h-.992v1.008h.991z"
  })), _path28$3 || (_path28$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M26.882 38.809h-.992v1.007h.991zM26.882 39.816h-.992v1.008h.991zM27.873 8.584h-.992v1.008h.992zM27.873 12.614h-.992v1.008h.992zM27.873 13.622h-.992v1.007h.992zM27.873 14.63h-.992v1.007h.992zM27.873 15.637h-.992v1.007h.992z"
  })), _path29$3 || (_path29$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.873 16.644h-.992v1.008h.992zM27.873 20.674h-.992v1.008h.992zM27.873 22.69h-.992v1.007h.992z"
  })), _path30$3 || (_path30$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.873 23.697h-.992v1.007h.992zM27.873 24.704h-.992v1.008h.992zM27.873 26.72h-.992v1.007h.992zM27.873 29.742h-.992v1.007h.992z"
  })), _path31$3 || (_path31$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.873 30.749h-.992v1.008h.992zM27.873 31.757h-.992v1.007h.992zM27.873 32.764h-.992v1.007h.992zM27.873 33.771h-.992v1.008h.992zM27.873 35.786h-.992v1.008h.992zM27.873 37.801h-.992v1.008h.992zM27.873 39.816h-.992v1.008h.992zM28.865 9.592h-.992V10.6h.992zM28.865 10.6h-.992v1.007h.992zM28.865 11.607h-.992v1.007h.992zM28.865 12.614h-.992v1.008h.992zM28.865 13.622h-.992v1.007h.992zM28.865 16.644h-.992v1.008h.992zM28.865 20.674h-.992v1.008h.992zM28.865 26.72h-.992v1.007h.992zM28.865 28.734h-.992v1.007h.992z"
  })), _path32$3 || (_path32$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M28.865 29.742h-.992v1.007h.992zM28.865 32.764h-.992v1.007h.992zM28.865 33.771h-.992v1.008h.992zM28.865 34.779h-.992v1.007h.992zM28.865 35.786h-.992v1.008h.992zM28.865 39.816h-.992v1.008h.992z"
  })), _path33$3 || (_path33$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M28.865 40.824h-.992v1.007h.992zM29.857 8.584h-.992v1.008h.992zM29.857 9.592h-.992V10.6h.992zM29.857 11.607h-.992v1.007h.992zM29.857 12.614h-.992v1.008h.992zM29.857 14.63h-.992v1.007h.992zM29.857 16.644h-.992v1.008h.992zM29.857 17.652h-.992v1.007h.992zM29.857 19.667h-.992v1.007h.992zM29.857 22.69h-.992v1.007h.992z"
  })), _path34$3 || (_path34$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.857 23.697h-.992v1.007h.992zM29.857 27.727h-.992v1.007h.992zM29.857 28.734h-.992v1.007h.992zM29.857 33.771h-.992v1.008h.992zM29.857 34.779h-.992v1.007h.992zM29.857 35.786h-.992v1.008h.992z"
  })), _path35$3 || (_path35$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.857 36.794h-.992V37.8h.992zM29.857 37.801h-.992v1.008h.992zM30.849 8.584h-.992v1.008h.992zM30.849 10.6h-.992v1.007h.992zM30.849 15.637h-.992v1.007h.992z"
  })), _path36$2 || (_path36$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M30.849 16.644h-.992v1.008h.992zM30.849 19.667h-.992v1.007h.992z"
  })), _path37$2 || (_path37$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M30.849 20.674h-.992v1.008h.992zM30.849 24.704h-.992v1.008h.992zM30.849 27.727h-.992v1.007h.992zM30.849 30.749h-.992v1.008h.992zM30.849 31.757h-.992v1.007h.992zM30.849 32.764h-.992v1.007h.992zM30.849 38.809h-.992v1.007h.992zM30.849 39.816h-.992v1.008h.992zM31.84 10.6h-.991v1.007h.991zM31.84 11.607h-.991v1.007h.991zM31.84 12.614h-.991v1.008h.991zM31.84 13.622h-.991v1.007h.991zM31.84 14.63h-.991v1.007h.991zM31.84 15.637h-.991v1.007h.991z"
  })), _path38$2 || (_path38$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.84 16.644h-.991v1.008h.991zM31.84 18.66h-.991v1.007h.991zM31.84 19.667h-.991v1.007h.991z"
  })), _path39$2 || (_path39$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.84 20.674h-.991v1.008h.991zM31.84 21.682h-.991v1.007h.991zM31.84 22.69h-.991v1.007h.991zM31.84 26.72h-.991v1.007h.991zM31.84 27.727h-.991v1.007h.991zM31.84 30.749h-.991v1.008h.991zM31.84 31.757h-.991v1.007h.991zM31.84 33.771h-.991v1.008h.991zM31.84 39.816h-.991v1.008h.991zM32.832 10.6h-.992v1.007h.992zM32.832 13.622h-.992v1.007h.992zM32.832 15.637h-.992v1.007h.992zM32.832 17.652h-.992v1.007h.992zM32.832 18.66h-.992v1.007h.992zM32.832 19.667h-.992v1.007h.992zM32.832 21.682h-.992v1.007h.992zM32.832 24.704h-.992v1.008h.992zM32.832 27.727h-.992v1.007h.992zM32.832 28.734h-.992v1.007h.992zM32.832 29.742h-.992v1.007h.992z"
  })), _path40$2 || (_path40$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M32.832 30.749h-.992v1.008h.992zM32.832 33.771h-.992v1.008h.992zM32.832 34.779h-.992v1.007h.992zM32.832 39.816h-.992v1.008h.992zM33.824 8.584h-.992v1.008h.992zM33.824 9.592h-.992v1.007h.992zM33.824 10.6h-.992v1.007h.992zM33.824 13.622h-.992v1.007h.992zM33.824 14.63h-.992v1.007h.992zM33.824 15.637h-.992v1.007h.992zM33.824 18.66h-.992v1.007h.992zM33.824 20.674h-.992v1.008h.992zM33.824 22.69h-.992v1.007h.992z"
  })), _path41$2 || (_path41$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.824 23.697h-.992v1.007h.992zM33.824 25.712h-.992v1.007h.992zM33.824 26.72h-.992v1.007h.992zM33.824 29.742h-.992v1.007h.992z"
  })), _path42$2 || (_path42$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.824 30.749h-.992v1.008h.992zM33.824 32.764h-.992v1.007h.992zM33.824 33.771h-.992v1.008h.992zM33.824 34.779h-.992v1.007h.992zM33.824 35.786h-.992v1.008h.992z"
  })), _path43$2 || (_path43$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.824 36.794h-.992V37.8h.992zM33.824 37.801h-.992v1.008h.992zM33.824 40.824h-.992v1.007h.992zM34.815 16.644h-.991v1.008h.991zM34.815 18.66h-.991v1.007h.991zM34.815 19.667h-.991v1.007h.991z"
  })), _path44$2 || (_path44$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M34.815 20.674h-.991v1.008h.991zM34.815 21.682h-.991v1.007h.991zM34.815 26.72h-.991v1.007h.991zM34.815 29.742h-.991v1.007h.991zM34.815 31.757h-.991v1.007h.991zM34.815 32.764h-.991v1.007h.991zM34.815 36.794h-.991V37.8h.991zM34.815 38.809h-.991v1.007h.991zM34.815 39.816h-.991v1.008h.991zM35.807 17.652h-.992v1.007h.992zM35.807 21.682h-.992v1.007h.992zM35.807 22.69h-.992v1.007h.992z"
  })), _path45$2 || (_path45$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M35.807 23.697h-.992v1.007h.992zM35.807 24.704h-.992v1.008h.992zM35.807 28.734h-.992v1.007h.992zM35.807 32.764h-.992v1.007h.992zM35.807 34.779h-.992v1.007h.992zM35.807 36.794h-.992V37.8h.992zM35.807 37.801h-.992v1.008h.992zM35.807 39.816h-.992v1.008h.992zM36.799 17.652h-.992v1.007h.992zM36.799 21.682h-.992v1.007h.992zM36.799 23.697h-.992v1.007h.992zM36.799 30.749h-.992v1.008h.992zM36.799 32.764h-.992v1.007h.992zM36.799 36.794h-.992V37.8h.992zM36.799 39.816h-.992v1.008h.992z"
  })), _path46$2 || (_path46$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M36.799 40.824h-.992v1.007h.992zM37.79 16.644H36.8v1.008h.992zM37.79 19.667H36.8v1.007h.992z"
  })), _path47$2 || (_path47$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M37.79 20.674H36.8v1.008h.992zM37.79 22.69H36.8v1.007h.992z"
  })), _path48$2 || (_path48$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M37.79 23.697H36.8v1.007h.992zM37.79 27.727H36.8v1.007h.992zM37.79 30.749H36.8v1.008h.992zM37.79 32.764H36.8v1.007h.992zM37.79 33.771H36.8v1.008h.992zM37.79 34.779H36.8v1.007h.992zM37.79 35.786H36.8v1.008h.992z"
  })), _path49$2 || (_path49$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M37.79 36.794H36.8V37.8h.992zM37.79 37.801H36.8v1.008h.992zM37.79 39.816H36.8v1.008h.992z"
  })), _path50$2 || (_path50$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M37.79 40.824H36.8v1.007h.992zM38.782 18.66h-.991v1.007h.991zM38.782 19.667h-.991v1.007h.991z"
  })), _path51$2 || (_path51$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M38.782 20.674h-.991v1.008h.991zM38.782 21.682h-.991v1.007h.991zM38.782 25.712h-.991v1.007h.991zM38.782 27.727h-.991v1.007h.991zM38.782 28.734h-.991v1.007h.991zM38.782 31.757h-.991v1.007h.991zM38.782 34.779h-.991v1.007h.991zM38.782 35.786h-.991v1.008h.991z"
  })), _path52$2 || (_path52$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M38.782 36.794h-.991V37.8h.991zM38.782 38.809h-.991v1.007h.991zM38.782 39.816h-.991v1.008h.991zM39.774 20.674h-.992v1.008h.992zM39.774 22.69h-.992v1.007h.992zM39.774 25.712h-.992v1.007h.992zM39.774 28.734h-.992v1.007h.992zM39.774 29.742h-.992v1.007h.992zM39.774 33.771h-.992v1.008h.992zM39.774 34.779h-.992v1.007h.992zM39.774 35.786h-.992v1.008h.992z"
  })), _path53$2 || (_path53$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M39.774 36.794h-.992V37.8h.992zM39.774 37.801h-.992v1.008h.992z"
  })), _path54$1 || (_path54$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M39.774 38.809h-.992v1.007h.992zM40.766 16.644h-.992v1.008h.992zM40.766 17.652h-.992v1.007h.992zM40.766 22.69h-.992v1.007h.992z"
  })), _path55$1 || (_path55$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M40.766 23.697h-.992v1.007h.992zM40.766 26.72h-.992v1.007h.992zM40.766 29.742h-.992v1.007h.992zM40.766 32.764h-.992v1.007h.992zM40.766 37.801h-.992v1.008h.992zM41.758 17.652h-.992v1.007h.992zM41.758 20.674h-.992v1.008h.992zM41.758 22.69h-.992v1.007h.992zM41.758 27.727h-.992v1.007h.992zM41.758 28.734h-.992v1.007h.992zM41.758 29.742h-.992v1.007h.992z"
  })), _path56$1 || (_path56$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M41.758 30.749h-.992v1.008h.992zM41.758 32.764h-.992v1.007h.992zM41.758 40.824h-.992v1.007h.992z"
  })), _path57$1 || (_path57$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M9.03 8.584v7.053h6.942V8.584zm.992 1.008h4.959v5.037h-4.959z",
    clipRule: "evenodd"
  })), _path58$1 || (_path58$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M13.989 10.6h-2.975v3.022h2.975z"
  })), _path59$1 || (_path59$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M41.758 8.584h-6.943v7.053h6.943zm-.992 1.008v5.037h-4.959V9.592z",
    clipRule: "evenodd"
  })), _path60$1 || (_path60$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M39.774 13.622v-3.023H36.8v3.023z"
  })), _path61$1 || (_path61$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M39.774 10.6H36.8v3.022h2.975z"
  })), _path62$1 || (_path62$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M9.03 41.831h6.942V34.78H9.03zm.992-1.007v-5.038h4.959v5.038z",
    clipRule: "evenodd"
  })), _path63$1 || (_path63$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M13.989 36.794h-2.975v3.022h2.975z"
  })), _path64$1 || (_path64$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#404040",
    fillRule: "evenodd",
    d: "M.849 50.143V.273h49.09v49.87zM2.532 1.312h45.724l-1.9 2.078H4.432zm0 47.792 1.9-2.078h41.924l1.9 2.078zM45.85 4.429H4.939v41.558h40.91z",
    clipRule: "evenodd"
  })), _path65$1 || (_path65$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#404040",
    d: "M11.477 56.112q.023.022.023.078a.3.3 0 0 1-.053.155.34.34 0 0 1-.123.133q.015.015.015.093a.7.7 0 0 1-.015.148 1 1 0 0 1-.015.086q.022.024.046.101.022.078.03.125.07 0 .13.047.061.047.093.132.008-.008.038-.008.045 0 .107.016.062.015.1.031.023-.023.1-.023.13 0 .169.062a.2.2 0 0 1 .1-.023q.122 0 .183.062.04-.03.161-.03a.6.6 0 0 1 .17.022q.083.024.107.063a.2.2 0 0 1 .069-.008q.13 0 .245.07.123.063.123.14a.24.24 0 0 1 .184.047.5.5 0 0 1 .138.156q.138.023.26.257a.95.95 0 0 1 .123.437.3.3 0 0 1-.038.155q.115.078.115.289a.6.6 0 0 1-.061.272q.015.063.015.094a.5.5 0 0 1-.076.265q-.07.124-.17.156a.47.47 0 0 1-.137.249.9.9 0 0 1-.238.164q-.015.101-.146.202-.13.094-.253.063a.4.4 0 0 1-.2.116 1.1 1.1 0 0 1-.582 0q-.123-.04-.17-.116a.4.4 0 0 1-.153.03.4.4 0 0 1-.207-.054.3.3 0 0 1-.13-.148q-.092.024-.2-.039a.4.4 0 0 1-.145-.21.46.46 0 0 1-.253-.086q-.123-.085-.138-.171a.3.3 0 0 1-.192-.11.33.33 0 0 1-.061-.194q0-.125.076-.226a.27.27 0 0 1 .215-.102q.07 0 .107.016.062-.015.085-.015.114 0 .207.077a.26.26 0 0 1 .107.195.3.3 0 0 1 .077-.015q.107 0 .192.1a.37.37 0 0 1 .107.22.2.2 0 0 1 .1-.024q.085 0 .16.047.078.046.085.109a.4.4 0 0 1 .138-.024q.123 0 .2.102a.26.26 0 0 1 .145-.047q.04 0 .13.03.1-.147.2-.116a.8.8 0 0 1 .138-.148.27.27 0 0 1 .154-.078.34.34 0 0 1 .069-.125.4.4 0 0 1 .123-.093.2.2 0 0 1-.016-.078q0-.078.03-.164.031-.085.07-.109a.24.24 0 0 1-.062-.163q0-.078.054-.18a.28.28 0 0 1-.138-.249.34.34 0 0 1-.169-.11.4.4 0 0 1-.214-.038.24.24 0 0 1-.108-.156.4.4 0 0 1-.084.016.35.35 0 0 1-.146-.032.4.4 0 0 1-.115-.1.2.2 0 0 1-.123.038.4.4 0 0 1-.214-.078.32.32 0 0 1-.307 0 .3.3 0 0 1-.077.016.4.4 0 0 1-.13-.024q-.054-.03-.062-.07-.276 0-.299-.101a.6.6 0 0 1-.176-.062q-.084-.055-.092-.125a.22.22 0 0 1-.13-.086.32.32 0 0 1-.054-.187.4.4 0 0 1-.115-.195 1 1 0 0 1-.046-.296q0-.249.1-.343a.3.3 0 0 1-.016-.093q0-.11.038-.226.046-.117.092-.14 0-.102.054-.234a.7.7 0 0 1 .161-.156.32.32 0 0 1 .2-.07.24.24 0 0 1 .091-.148.3.3 0 0 1 .192-.062.26.26 0 0 1 .13-.148.505.505 0 0 1 .383-.031.16.16 0 0 1 .086-.063.4.4 0 0 1 .13-.023q.038 0 .084.015.069-.07.246-.07.192 0 .291.07a.4.4 0 0 1 .085-.008q.23 0 .39.273.085-.015.185.047a.45.45 0 0 1 .169.171.3.3 0 0 1 .061.195.6.6 0 0 1 .184.195 1 1 0 0 1 .085.304.27.27 0 0 1-.085.226.3.3 0 0 1-.215.086.27.27 0 0 1-.2-.086.27.27 0 0 1-.16-.094q-.07-.077-.061-.155-.216-.149-.192-.328a.4.4 0 0 1-.23-.047.5.5 0 0 1-.2-.179q-.053.078-.215.078a.4.4 0 0 1-.191-.039q-.054.07-.192.07a.4.4 0 0 1-.13-.023.34.34 0 0 1-.269.218.5.5 0 0 1-.115.125.26.26 0 0 1-.146.047M17.955 58.512a.27.27 0 0 1 .123-.14.34.34 0 0 1 .192-.063q.03 0 .092.016.222.062.222.265 0 .178-.153.366.015.062.015.109a.5.5 0 0 1-.046.203.6.6 0 0 1-.115.187.8.8 0 0 1-.069.218 1.2 1.2 0 0 1-.123.202.7.7 0 0 1-.184.18 1.3 1.3 0 0 1-.237.093.6.6 0 0 1-.123.203.28.28 0 0 1-.207.085.4.4 0 0 1-.13-.023.4.4 0 0 1-.154.101.6.6 0 0 1-.192.031.35.35 0 0 1-.207-.054.6.6 0 0 1-.238.039q-.19 0-.368-.062a.57.57 0 0 1-.276-.195.4.4 0 0 1-.307-.086.3.3 0 0 1-.138-.14q-.284-.14-.276-.515a.7.7 0 0 1-.207-.21 1.1 1.1 0 0 1-.107-.312.22.22 0 0 1-.062-.163q0-.047.008-.078a.26.26 0 0 1-.07-.187q0-.055.024-.11-.108-.116-.108-.334 0-.195.085-.304a.3.3 0 0 1-.054-.172q0-.07.038-.148-.038-.046-.038-.187 0-.156.046-.311.046-.164.115-.219a.3.3 0 0 1-.023-.132.7.7 0 0 1 .039-.234q.045-.11.122-.117a.3.3 0 0 1 .13-.257.4.4 0 0 1 .077-.242.5.5 0 0 1 .207-.179.5.5 0 0 1 .138-.288.42.42 0 0 1 .254-.133.3.3 0 0 1 .138-.116.46.46 0 0 1 .207-.055q.075 0 .13.031a.37.37 0 0 1 .261-.109q.123 0 .161.063a.4.4 0 0 1 .107-.016q.14 0 .208.101.03-.023.092-.023.107 0 .237.062.138.055.162.11.16.03.29.163.132.125.108.257a.32.32 0 0 1 .154.156q.045.093.03.18a.26.26 0 0 1 .093.14 1 1 0 0 0 .03.093.42.42 0 0 1 .138.436q.008.032.008.078a.3.3 0 0 1-.1.226.3.3 0 0 1-.215.086.3.3 0 0 1-.214-.094q-.216-.233-.238-.467a.27.27 0 0 1-.146-.117.35.35 0 0 1-.046-.203.33.33 0 0 1-.107-.117.33.33 0 0 1-.023-.155.27.27 0 0 1-.215-.086.3.3 0 0 1-.085.008.8.8 0 0 1-.184-.024.5.5 0 0 1-.153-.07.4.4 0 0 1-.092.008.3.3 0 0 1-.115-.016.36.36 0 0 1-.192.063 1 1 0 0 1-.107-.016.3.3 0 0 1-.161.078.7.7 0 0 1-.07.226q-.044.102-.122.117.015.093-.03.187a.4.4 0 0 1-.131.14.4.4 0 0 1-.038.125.25.25 0 0 1-.077.086l.015.093a1 1 0 0 1 .008.117q0 .165-.138.257a.3.3 0 0 1-.039.164.35.35 0 0 1 .039.156q0 .078-.03.14.045.078.045.148a.4.4 0 0 1-.038.164.7.7 0 0 0-.046.187.27.27 0 0 1 .107.109.3.3 0 0 1 .039.148q0 .086-.047.156.062.03.092.109.03.078.031.163 0 .086-.03.14.168.095.23.195.069.094.1.304a.6.6 0 0 1 .122.078.4.4 0 0 1 .1.094.5.5 0 0 1 .268.062.37.37 0 0 1 .2.171.35.35 0 0 1 .145-.062.7.7 0 0 1 .207-.031q.108 0 .146.031a.33.33 0 0 1 .33-.125 1.5 1.5 0 0 1 .192-.14.5.5 0 0 1 .16-.07.4.4 0 0 1 .093-.226.5.5 0 0 1 .046-.148.6.6 0 0 1 .077-.14.3.3 0 0 1-.008-.078q0-.102.054-.218a.5.5 0 0 1 .122-.187M23.024 59.704l.13.054q.138.055.107.367.03.062.031.14 0 .124-.1.203a.36.36 0 0 1-.23.077.3.3 0 0 1-.2-.07.3.3 0 0 1-.076-.218q-.084-.015-.176-.132a.44.44 0 0 1-.085-.25q0-.03.016-.093a.35.35 0 0 1-.138-.156 1.1 1.1 0 0 1-.092-.444q-.1-.047-.162-.226a.8.8 0 0 1-.038-.32.3.3 0 0 1-.13.024.48.48 0 0 1-.284-.094q-.1.101-.3.101-.183 0-.26-.093a.32.32 0 0 1-.215.086.24.24 0 0 1-.145-.04.27.27 0 0 1-.162.04.6.6 0 0 1-.184-.031.4.4 0 0 1-.145-.086.1.1 0 0 0-.039.015q.046.149.046.265 0 .18-.176.312.03.062.03.148a.54.54 0 0 1-.115.327q-.114.156-.222.172.03.07.03.163 0 .21-.137.39a.42.42 0 0 1-.422.164q-.1 0-.154-.063a.24.24 0 0 1-.053-.163q0-.07.038-.164a.4.4 0 0 1-.008-.093q0-.156.154-.273a.2.2 0 0 1-.008-.07q0-.117.054-.234a.32.32 0 0 1 .153-.164.2.2 0 0 1-.008-.07q0-.133.062-.304.06-.18.138-.202-.015-.343.222-.694 0-.18.054-.335.054-.164.192-.234a.9.9 0 0 1 .069-.296q.06-.148.138-.25a.6.6 0 0 1-.023-.155q0-.11.038-.171.046-.07.1-.086a.4.4 0 0 1-.008-.094q0-.171.123-.272l-.008-.047q0-.117.092-.21a.4.4 0 0 1-.007-.086.6.6 0 0 1 .069-.28.4.4 0 0 1 .153-.188q.139-.195.292-.195.068 0 .176.047.222.008.322.35.07.087.1.203.03.109.023.203.07.047.1.125a.3.3 0 0 1 .03.163.54.54 0 0 1 .13.218.7.7 0 0 1 .062.281q0 .063-.008.086a.44.44 0 0 1 .093.156q.046.101.046.187a.2.2 0 0 1-.008.07q.1.07.16.218.07.148.093.296.03.14.015.195l.085.117a.7.7 0 0 1 .107.179.4.4 0 0 1 .038.164q0 .07-.015.1l.07.094q.038.047.06.086a.2.2 0 0 1 .023.094.3.3 0 0 1-.007.078.56.56 0 0 1 .153.358q0 .077-.03.117.085.062.13.18.045.116.03.233m-1.412-1.691q.016-.047.07-.055.053-.015.091.008a.3.3 0 0 1-.015-.07q0-.062.03-.093a.44.44 0 0 1-.114-.14.34.34 0 0 1-.031-.172.2.2 0 0 1-.1-.078.3.3 0 0 1-.038-.148q0-.023.015-.086a.34.34 0 0 1-.16-.304q0-.078.022-.179l-.038-.031q-.03-.03-.023-.094-.154-.046-.146-.249a.23.23 0 0 1-.1-.117.29.29 0 0 1-.138.28.5.5 0 0 1-.03.196.44.44 0 0 1-.093.163q.009.031.008.101 0 .18-.145.476-.04.086-.062.156-.03.078-.069.155a.26.26 0 0 1-.084.117q0 .078-.031.14l.061-.007q.078 0 .115.039.024-.04.093-.04.045 0 .069.024a.37.37 0 0 1 .23-.07q.1 0 .184.031a.4.4 0 0 1 .138.086q.076-.086.16-.086.055 0 .131.047M27.91 59.68a.16.16 0 0 1 .046.117.13.13 0 0 1-.039.094.3.3 0 0 1 .038.14.3.3 0 0 1-.03.125q.015.093.015.117 0 .109-.123.202a.33.33 0 0 1-.207.07.38.38 0 0 1-.26-.1.28.28 0 0 1-.185-.118.33.33 0 0 1-.046-.241 1 1 0 0 0-.092-.063 1 1 0 0 1-.092-.078.4.4 0 0 1-.046-.109.28.28 0 0 1-.145-.109.35.35 0 0 1-.046-.25.28.28 0 0 1-.17-.093.36.36 0 0 1-.068-.187 1.4 1.4 0 0 1-.223-.132q-.069-.055-.084-.171a.44.44 0 0 1-.153-.156.6.6 0 0 1-.093-.21.29.29 0 0 1-.16-.234.44.44 0 0 1-.154-.149.5.5 0 0 1-.092-.218.47.47 0 0 1-.207-.179.65.65 0 0 1-.115-.296.5.5 0 0 1-.146-.156.9.9 0 0 1-.107-.218 1 1 0 0 1-.138-.203 1 1 0 0 1-.1-.257.6.6 0 0 1-.13-.171 1 1 0 0 1-.093-.219q.015.048.016.133 0 .102-.023.125.046.186.046.311a.5.5 0 0 1-.016.11.6.6 0 0 1 .031.21.7.7 0 0 1-.023.195q.045.054.046.187a.44.44 0 0 1-.038.194q.045.078.046.156a.4.4 0 0 1-.046.18l-.046.116q.069.102.069.343a.7.7 0 0 1-.039.242.4.4 0 0 1 .062.202.9.9 0 0 1-.085.312.3.3 0 0 1 .077.218.65.65 0 0 1-.084.312l.023.195q.023.124.023.202 0 .336-.307.335a.28.28 0 0 1-.207-.085.38.38 0 0 1-.092-.21.633.633 0 0 1-.008-.46 1 1 0 0 1-.038-.265q0-.102.023-.172a.6.6 0 0 1-.023-.148q0-.117.046-.234a.5.5 0 0 1-.061-.257q0-.17.076-.265a.7.7 0 0 1-.061-.28.6.6 0 0 1 .069-.28.4.4 0 0 1-.03-.125q0-.023.015-.086-.1-.102-.1-.327 0-.118.023-.226a.4.4 0 0 1 .069-.156.8.8 0 0 1-.085-.398q0-.155.047-.249a.46.46 0 0 1-.047-.218q0-.164.062-.265a.5.5 0 0 1-.023-.18q0-.108.023-.147 0-.195.122-.304a.42.42 0 0 1 .292-.11q.168 0 .284.118a.37.37 0 0 1 .107.311q.177.024.33.218.152.195.146.421.099.07.192.21.1.141.107.258a1 1 0 0 1 .215.28.8.8 0 0 1 .122.359q.093.008.192.179.108.171.123.288.076.032.153.156a.43.43 0 0 1 .077.218q.061.015.107.125.054.11.039.171a.36.36 0 0 1 .23.273q.084-.007.184.101a.5.5 0 0 1 .123.219q.076.03.16.14.093.1.116.195a1 1 0 0 1-.023-.172q0-.054.038-.093-.076-.085-.076-.25 0-.085.023-.14a.35.35 0 0 1-.054-.171q0-.07.038-.148-.077-.07-.077-.242 0-.078.023-.125a.6.6 0 0 1-.06-.28q0-.047.014-.14-.084-.102-.084-.328 0-.186.061-.272a.6.6 0 0 1-.069-.148 1 1 0 0 1-.023-.187q0-.15.062-.226a.27.27 0 0 1-.07-.195.8.8 0 0 1 .039-.218.5.5 0 0 1-.077-.28q0-.126.046-.196a.5.5 0 0 1-.038-.179q0-.156.153-.218a.5.5 0 0 1 .154-.031q.13 0 .184.101.06.1.061.242a.5.5 0 0 1-.007.109q.06.046.1.171a.9.9 0 0 1 .038.257q0 .117-.031.172a.8.8 0 0 1 .054.327.54.54 0 0 1-.039.242q.046.093.046.272a.5.5 0 0 1-.03.203.5.5 0 0 1 .061.171.7.7 0 0 1 .03.195q0 .132-.053.187a.3.3 0 0 1 .084.156q.03.11.031.226a.6.6 0 0 1-.023.171q.061.102.061.242 0 .117-.038.163a.6.6 0 0 1 .038.156q.015.11 0 .156.078.055.077.226a.37.37 0 0 1-.053.21.5.5 0 0 1 .038.164q0 .024-.015.102M34.404 57.896q.03.086.03.195a.8.8 0 0 1-.038.25.45.45 0 0 1-.084.178q.069.07.069.312 0 .234-.07.327a.3.3 0 0 1 .062.14q.024.102.023.227a.7.7 0 0 1-.084.366q.045.148.046.265 0 .18-.092.273a.3.3 0 0 1-.215.093.27.27 0 0 1-.192-.078.27.27 0 0 1-.084-.21q0-.07.023-.14a.8.8 0 0 1-.1-.406q0-.226.1-.327a.3.3 0 0 1-.077-.218q0-.133.077-.21a.8.8 0 0 1-.077-.351q0-.195.077-.265a.94.94 0 0 1-.077-.413q0-.257.077-.327a.6.6 0 0 1-.107-.351q0-.18.107-.28a.17.17 0 0 1-.03-.11q0-.054.015-.14.015-.094.015-.171 0 .202-.092.53-.092.319-.253.436 0 .514-.192.6 0 .172-.061.405-.062.225-.123.226a.4.4 0 0 1-.077.21q-.069.11-.138.11a.6.6 0 0 1-.015.381.4.4 0 0 1-.084.148.46.46 0 0 1-.054.234.8.8 0 0 1-.146.18.3.3 0 0 1-.1.233.34.34 0 0 1-.445.008.3.3 0 0 1-.092-.234q0-.07.008-.101-.077 0-.146-.133a.7.7 0 0 1-.061-.296q-.07 0-.146-.117a1.2 1.2 0 0 1-.122-.272.45.45 0 0 1-.023-.234.5.5 0 0 1-.123-.18.7.7 0 0 1-.054-.28.6.6 0 0 1 .061-.273q-.1-.008-.176-.14a.6.6 0 0 1-.069-.273q0-.077.038-.116a.6.6 0 0 1-.145-.203.6.6 0 0 1-.062-.226q-.1-.102-.1-.413-.106 0-.184-.195.015.149-.03.296a.5.5 0 0 1-.108.21q.069.063.108.242.038.172.038.35 0 .165-.03.235.076.124.076.382a1.2 1.2 0 0 1-.077.467q.062.133.062.265 0 .117-.062.265.03.03.054.156.023.116.023.257 0 .265-.077.32a1 1 0 0 1 .008.124q0 .202-.1.312a.29.29 0 0 1-.23.109.34.34 0 0 1-.245-.11.4.4 0 0 1-.108-.288q0-.132.07-.296a2.2 2.2 0 0 1-.093-.6.9.9 0 0 1 .07-.397q-.078-.101-.077-.32 0-.101.023-.187a.4.4 0 0 1 .053-.132.9.9 0 0 1-.076-.358q0-.187.076-.265a.4.4 0 0 1-.06-.14.7.7 0 0 1-.024-.18q0-.14.046-.21a.7.7 0 0 1-.077-.343q0-.273.115-.39-.107-.108-.107-.444 0-.156.03-.296.032-.148.077-.195 0-.148.108-.241a.374.374 0 0 1 .522.023q.153 0 .276.14.13.14.13.289a.4.4 0 0 1 .092.132.37.37 0 0 1 .015.288q.093.008.146.11.061.1.061.194.062.055.1.156.046.093.046.195 0 .093-.03.18.037 0 .069.07a.26.26 0 0 1 .038.14q0 .062-.03.093.083.047.145.195.069.14.069.234a.4.4 0 0 1 .084.179.87.87 0 0 1 .016.444q.076.054.145.226a.8.8 0 0 1 .07.32.4.4 0 0 1-.008.093q.092.07.13.132.045.063.077.187.015-.342.146-.413a.4.4 0 0 1-.016-.093q0-.117.054-.226.06-.11.138-.11a.3.3 0 0 1-.038-.132q0-.171.238-.436a.3.3 0 0 1-.023-.125.8.8 0 0 1 .053-.272.4.4 0 0 1 .169-.203q0-.234.03-.39a.33.33 0 0 1 .177-.233.4.4 0 0 1 .061-.226q.069-.094.162-.094a.3.3 0 0 1-.039-.163q0-.164.07-.336a.5.5 0 0 1 .176-.233q0-.172.092-.296.099-.126.306-.125a.3.3 0 0 1 .108-.016q.153 0 .222.14a.5.5 0 0 1 .077.242q.069.242.069.366 0 .117-.069.367.069.077.069.226a.8.8 0 0 1-.069.303q.069.078.069.258 0 .187-.077.28.1.132.1.335t-.092.343M37.726 55.075a.34.34 0 0 1 .199-.054q.177 0 .353.117.084 0 .138.07a.27.27 0 0 1 .054.171.38.38 0 0 1-.077.234.25.25 0 0 1-.215.101.46.46 0 0 1-.215-.062.22.22 0 0 1-.16.062.4.4 0 0 1-.223-.078.5.5 0 0 1-.23.04.7.7 0 0 1-.346-.07.55.55 0 0 1-.237.054.6.6 0 0 1-.192-.031.5.5 0 0 1-.192.038.5.5 0 0 1-.245-.07.7.7 0 0 1-.246.094.8.8 0 0 1 .054.288q0 .195-.084.312a.61.61 0 0 1 .023.608q.069.078.069.194a.44.44 0 0 1-.054.188.34.34 0 0 1 .169-.04.3.3 0 0 1 .107.016.3.3 0 0 1 .184-.062q.1 0 .2.086a.7.7 0 0 1 .153-.024q.04 0 .13.016a1 1 0 0 1 .13-.016q.132 0 .193.062a.25.25 0 0 1 .138-.038q.092 0 .115.038a.4.4 0 0 1 .107-.023q.053 0 .146.031a.4.4 0 0 1 .192-.054.3.3 0 0 1 .222.101.3.3 0 0 1 .092.21.24.24 0 0 1-.069.172q-.07.07-.215.078-.069.093-.23.093a.46.46 0 0 1-.26-.078q-.078.078-.223.078a.35.35 0 0 1-.215-.062.2.2 0 0 1-.13.047.1.1 0 0 1-.077-.032.33.33 0 0 1-.23.078.43.43 0 0 1-.2-.046.3.3 0 0 1-.13.039.26.26 0 0 1-.153-.055.4.4 0 0 1-.085.039.2.2 0 0 1-.084 0 .55.55 0 0 1 .069.218.4.4 0 0 1-.046.164.23.23 0 0 1 .054.156.34.34 0 0 1-.039.148.24.24 0 0 1 .054.163.23.23 0 0 1-.054.156q.046.094.046.148a.4.4 0 0 1-.061.156q.03.031.03.086 0 .07-.045.093a.23.23 0 0 1 .076.18q0 .039-.015.132l.046-.008q.084 0 .176.078a.5.5 0 0 1 .3-.086q.069 0 .145.016a.3.3 0 0 1 .13-.086.5.5 0 0 1 .185-.03q.16 0 .245.07a.3.3 0 0 1 .161-.125.6.6 0 0 1 .23-.047q.085 0 .139.023a.3.3 0 0 1 .153-.047q.122 0 .215.102a.34.34 0 0 1 .092.233.29.29 0 0 1-.146.258.26.26 0 0 1-.123.109.5.5 0 0 1-.2.039.3.3 0 0 1-.168-.04.6.6 0 0 1-.161.11.5.5 0 0 1-.2.039.32.32 0 0 1-.237-.086.44.44 0 0 1-.338.14 1 1 0 0 1-.13-.015.6.6 0 0 1-.3.093.33.33 0 0 1-.199-.062.27.27 0 0 1-.245.132.34.34 0 0 1-.246-.109.28.28 0 0 1-.069-.257.3.3 0 0 1-.069-.14.8.8 0 0 1-.015-.288q-.1-.102-.1-.343 0-.102.023-.187a.4.4 0 0 1 .054-.133.37.37 0 0 1-.023-.265.26.26 0 0 1-.069-.187q0-.109.054-.171a.3.3 0 0 1-.039-.14q0-.087.054-.164a.7.7 0 0 1-.061-.273.55.55 0 0 1 .1-.311 1.6 1.6 0 0 1-.085-.25 1.4 1.4 0 0 1-.03-.28q0-.249.114-.343a4 4 0 0 1-.038-.18 1.3 1.3 0 0 1-.046-.264q0-.118.054-.28a.5.5 0 0 1-.062-.18.6.6 0 0 1-.015-.18.3.3 0 0 1-.092-.225q0-.102.061-.187a.33.33 0 0 1 .177-.125.4.4 0 0 1 .145-.086.5.5 0 0 1 .192-.039q.13 0 .223.07a1 1 0 0 1 .26-.038q.184 0 .3.085.07-.03.16-.03.085 0 .131.038.046-.039.222-.039.23 0 .361.078a.28.28 0 0 1 .207-.086q.077 0 .176.04M39.069 58.395a.44.44 0 0 1-.039-.195q0-.109.038-.164a.4.4 0 0 1-.015-.132q0-.11.03-.218.031-.11.07-.14-.07-.063-.07-.273 0-.094.016-.164.015-.078.046-.093a.6.6 0 0 1-.038-.25q0-.124.023-.226a.5.5 0 0 1 .061-.163.35.35 0 0 1-.092-.258q0-.101.03-.187a.4.4 0 0 1 .093-.14.43.43 0 0 1-.07-.25q0-.093.04-.225a.33.33 0 0 1-.078-.195.26.26 0 0 1 .108-.21.35.35 0 0 1 .238-.094q.092 0 .16.063a.3.3 0 0 1 .108.17q.085.102.085.22 0 .077-.031.132.023.023.023.381.015.063.015.102a.9.9 0 0 1-.069.335q.045.061.046.21 0 .101-.03.203a.5.5 0 0 1-.054.14q.008.023.023.086.015.061.015.117a.3.3 0 0 1-.038.148q.053.078.053.311a.6.6 0 0 1-.015.148q.03.038.03.164 0 .102-.03.203-.022.093-.076.132a.25.25 0 0 1 .053.172.3.3 0 0 1-.038.163q.038.015.038.11a.5.5 0 0 1-.015.116q-.015.055-.038.062.015.063.015.102 0 .124-.092.226a.28.28 0 0 1-.215.093.298.298 0 0 1-.3-.35.7.7 0 0 1-.038-.25q0-.086.023-.132m.045 1.901q0 .14.085.226a.32.32 0 0 0 .215.078q.107 0 .191-.062a.32.32 0 0 0 .108-.195.36.36 0 0 0 .069-.218l-.008-.148q-.015-.21-.184-.281a.292.292 0 0 0-.33.07.28.28 0 0 0-.092.218q0 .039.016.102a.37.37 0 0 0-.07.21"
  })));
};

var _rect$5, _path$u, _path2$4, _path3$3, _path4$2, _path5$2, _path6$2, _path7$2, _path8$2, _path9$2, _path10$2, _path11$2, _path12$2, _path13$2, _path14$2, _path15$2, _path16$2, _path17$2, _path18$2, _path19$2, _path20$2, _path21$2, _path22$2, _path23$2, _path24$2, _path25$2, _path26$2, _path27$2, _path28$2, _path29$2, _path30$2, _path31$2, _path32$2, _path33$2, _path34$2, _path35$2, _path36$1, _path37$1, _path38$1, _path39$1, _path40$1, _path41$1, _path42$1, _path43$1, _path44$1, _path45$1, _path46$1, _path47$1, _path48$1, _path49$1, _path50$1, _path51$1, _path52$1, _path53$1, _path54, _path55, _path56, _path57, _path58, _path59, _path60, _path61, _path62, _path63, _path64, _path65, _path66, _path67, _path68, _path69, _path70, _path71, _path72, _path73;
function _extends$w() { return _extends$w = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$w.apply(null, arguments); }
var SvgFrameCafe = function SvgFrameCafe(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$w({
    xmlns: "http://www.w3.org/2000/svg",
    width: 50,
    height: 66,
    fill: "none"
  }, props), _rect$5 || (_rect$5 = /*#__PURE__*/React.createElement("rect", {
    width: 49.091,
    height: 65.454,
    x: 0.777,
    y: 0.273,
    fill: "#fff",
    rx: 2.045
  })), _path$u || (_path$u = /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    stroke: "#404040",
    strokeWidth: 1.169,
    d: "M43.056 28.744a5.55 5.55 0 0 1-3.342 1.595V19.297a5.552 5.552 0 0 1 3.342 9.447Z"
  })), _path2$4 || (_path2$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    stroke: "#404040",
    strokeWidth: 1.023,
    d: "M11.005 13.568c0-.847.686-1.534 1.534-1.534h25.568c.847 0 1.534.687 1.534 1.534v20.455a5.625 5.625 0 0 1-5.625 5.625H16.63a5.625 5.625 0 0 1-5.625-5.625z"
  })), _path3$3 || (_path3$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#404040",
    d: "M6.402 38.455c0-.189.153-.341.341-.341h37.67c.189 0 .342.152.342.34a3.75 3.75 0 0 1-3.75 3.75H10.152a3.75 3.75 0 0 1-3.75-3.75M11.918 49.614q.023.023.023.077a.3.3 0 0 1-.054.153.34.34 0 0 1-.123.13q.016.017.016.093t-.016.145a1 1 0 0 1-.015.085q.022.023.046.1.023.076.03.122.07 0 .131.046.061.047.092.13.008-.006.038-.007.046 0 .108.015.06.015.1.031.022-.023.1-.023.129 0 .168.062a.2.2 0 0 1 .1-.024q.122 0 .184.062.039-.03.16-.03.085 0 .17.022.084.023.107.062a.2.2 0 0 1 .069-.008.47.47 0 0 1 .245.069q.123.06.123.138a.24.24 0 0 1 .184.046q.085.06.138.153.139.023.261.254a.9.9 0 0 1 .123.43.3.3 0 0 1-.038.152q.115.078.115.284 0 .146-.062.269.015.06.016.092a.5.5 0 0 1-.077.26q-.069.123-.169.154a.46.46 0 0 1-.138.245.9.9 0 0 1-.238.162q-.015.1-.146.199-.129.092-.253.061a.4.4 0 0 1-.199.115 1.1 1.1 0 0 1-.583 0q-.123-.037-.169-.115a.4.4 0 0 1-.153.031.43.43 0 0 1-.207-.054.3.3 0 0 1-.13-.145.27.27 0 0 1-.2-.039.4.4 0 0 1-.146-.207.47.47 0 0 1-.253-.084q-.123-.085-.138-.169a.3.3 0 0 1-.192-.107.32.32 0 0 1-.061-.192q0-.123.077-.223a.27.27 0 0 1 .214-.1q.069 0 .108.016a.4.4 0 0 1 .084-.015q.115 0 .207.076a.26.26 0 0 1 .108.192.3.3 0 0 1 .076-.015q.108 0 .192.1a.36.36 0 0 1 .107.214.2.2 0 0 1 .1-.023q.084 0 .161.046.077.047.085.108a.4.4 0 0 1 .138-.023.24.24 0 0 1 .2.1.26.26 0 0 1 .145-.047q.038 0 .13.031.1-.145.2-.115a.8.8 0 0 1 .138-.146.27.27 0 0 1 .153-.076.3.3 0 0 1 .07-.123.4.4 0 0 1 .122-.092.2.2 0 0 1-.015-.077.5.5 0 0 1 .03-.161q.03-.084.07-.107a.24.24 0 0 1-.062-.162q0-.076.054-.176a.27.27 0 0 1-.138-.245.35.35 0 0 1-.17-.108.4.4 0 0 1-.214-.038.24.24 0 0 1-.107-.154.4.4 0 0 1-.085.016q-.076 0-.145-.03a.4.4 0 0 1-.116-.1.2.2 0 0 1-.122.038.4.4 0 0 1-.215-.077.33.33 0 0 1-.307 0 .3.3 0 0 1-.077.016.4.4 0 0 1-.13-.024q-.054-.03-.061-.069-.276 0-.3-.1a.6.6 0 0 1-.176-.06q-.084-.054-.092-.123a.22.22 0 0 1-.13-.085.3.3 0 0 1-.054-.184.4.4 0 0 1-.115-.192.9.9 0 0 1-.046-.291q0-.246.1-.338a.3.3 0 0 1-.016-.092q0-.107.039-.222.045-.115.092-.138 0-.1.053-.23a.7.7 0 0 1 .162-.154.33.33 0 0 1 .199-.069.24.24 0 0 1 .092-.146.32.32 0 0 1 .192-.06.26.26 0 0 1 .13-.147.46.46 0 0 1 .223-.053q.091 0 .16.023a.16.16 0 0 1 .085-.062.4.4 0 0 1 .13-.023.3.3 0 0 1 .085.016q.07-.07.245-.07.192 0 .292.07a.4.4 0 0 1 .084-.008q.23 0 .391.268a.26.26 0 0 1 .184.046.45.45 0 0 1 .17.17q.068.1.06.191a.6.6 0 0 1 .185.192 1 1 0 0 1 .084.299.27.27 0 0 1-.084.222.3.3 0 0 1-.215.085.27.27 0 0 1-.2-.085.27.27 0 0 1-.16-.092q-.07-.076-.062-.153-.215-.146-.192-.322a.4.4 0 0 1-.23-.046.5.5 0 0 1-.2-.177q-.053.077-.214.077a.4.4 0 0 1-.192-.038q-.054.069-.192.069a.4.4 0 0 1-.13-.023.33.33 0 0 1-.107.145.35.35 0 0 1-.162.07.5.5 0 0 1-.115.122.26.26 0 0 1-.145.046M18.396 51.977a.27.27 0 0 1 .123-.138.34.34 0 0 1 .191-.062q.031 0 .092.016.223.06.223.26a.57.57 0 0 1-.154.361q.016.061.016.107a.5.5 0 0 1-.046.2.6.6 0 0 1-.115.184.8.8 0 0 1-.07.215 1 1 0 0 1-.122.2.7.7 0 0 1-.184.175q-.084.046-.238.093a.6.6 0 0 1-.123.199.28.28 0 0 1-.207.084.4.4 0 0 1-.13-.023.4.4 0 0 1-.154.1.6.6 0 0 1-.191.03.36.36 0 0 1-.207-.053.6.6 0 0 1-.238.038q-.192 0-.368-.061a.57.57 0 0 1-.277-.192.41.41 0 0 1-.306-.084.3.3 0 0 1-.138-.138q-.285-.138-.277-.506a.7.7 0 0 1-.207-.207 1 1 0 0 1-.107-.307.22.22 0 0 1-.061-.161q0-.047.007-.077a.25.25 0 0 1-.069-.184q0-.054.023-.108-.107-.114-.107-.33 0-.19.084-.299a.3.3 0 0 1-.053-.168q0-.07.038-.146-.039-.045-.039-.184 0-.153.046-.307.047-.16.116-.215a.3.3 0 0 1-.023-.13q0-.123.038-.23.046-.108.123-.115 0-.162.13-.253a.4.4 0 0 1 .077-.238.47.47 0 0 1 .207-.177.48.48 0 0 1 .138-.284.42.42 0 0 1 .253-.13.3.3 0 0 1 .138-.115.5.5 0 0 1 .207-.054q.077 0 .13.031a.37.37 0 0 1 .261-.107q.123 0 .162.061a.5.5 0 0 1 .107-.015q.138 0 .207.1a.15.15 0 0 1 .092-.024q.107 0 .238.062.139.053.16.107.162.03.292.161.13.123.108.253a.32.32 0 0 1 .153.154q.046.092.03.176a.26.26 0 0 1 .093.138 1 1 0 0 0 .03.092.41.41 0 0 1 .139.43q.007.03.007.077a.3.3 0 0 1-.1.222.31.31 0 0 1-.429-.008q-.214-.23-.238-.46a.27.27 0 0 1-.145-.115.34.34 0 0 1-.046-.2.3.3 0 0 1-.108-.114.3.3 0 0 1-.023-.154.28.28 0 0 1-.215-.084.3.3 0 0 1-.084.008 1 1 0 0 1-.184-.024.5.5 0 0 1-.153-.069.4.4 0 0 1-.093.008.3.3 0 0 1-.115-.015.37.37 0 0 1-.191.061 1 1 0 0 1-.108-.015.3.3 0 0 1-.16.077.6.6 0 0 1-.07.222q-.046.1-.123.115.015.092-.03.184a.4.4 0 0 1-.13.138.4.4 0 0 1-.04.123.25.25 0 0 1-.076.084l.016.092a1 1 0 0 1 .007.115.29.29 0 0 1-.138.253.3.3 0 0 1-.038.161.34.34 0 0 1 .038.154q0 .077-.03.138.046.077.046.146a.4.4 0 0 1-.039.16.7.7 0 0 0-.046.185.295.295 0 0 1 .146.253.27.27 0 0 1-.046.154q.061.03.092.107a.4.4 0 0 1 .03.16q0 .085-.03.139a.6.6 0 0 1 .23.192q.07.091.1.299.053.022.123.077a.4.4 0 0 1 .1.092.53.53 0 0 1 .268.061.37.37 0 0 1 .2.169.35.35 0 0 1 .145-.062q.107-.03.207-.03.108 0 .146.03a.33.33 0 0 1 .33-.122q.122-.1.191-.138.07-.046.161-.07a.4.4 0 0 1 .092-.222q.008-.061.046-.146a.6.6 0 0 1 .077-.138.3.3 0 0 1-.008-.076.5.5 0 0 1 .054-.215.5.5 0 0 1 .123-.184M23.464 53.15l.13.054q.138.054.108.36.03.061.03.139a.24.24 0 0 1-.1.199.37.37 0 0 1-.23.077.3.3 0 0 1-.199-.07.3.3 0 0 1-.076-.214q-.085-.015-.177-.13a.43.43 0 0 1-.084-.246.4.4 0 0 1 .015-.092.34.34 0 0 1-.138-.153 1 1 0 0 1-.077-.253q-.022-.13-.015-.185-.1-.045-.161-.222a.8.8 0 0 1-.038-.315.3.3 0 0 1-.13.024.5.5 0 0 1-.285-.093q-.1.1-.299.1-.184 0-.26-.092a.33.33 0 0 1-.215.085.25.25 0 0 1-.146-.039.28.28 0 0 1-.161.038.6.6 0 0 1-.184-.03.4.4 0 0 1-.146-.085.1.1 0 0 0-.038.016q.046.146.046.26 0 .177-.177.307.03.062.03.146a.53.53 0 0 1-.114.322q-.115.154-.223.169.03.07.031.161a.6.6 0 0 1-.138.384.42.42 0 0 1-.422.16q-.099 0-.153-.06a.24.24 0 0 1-.054-.162q0-.07.038-.16a.4.4 0 0 1-.007-.093q0-.153.153-.268a.2.2 0 0 1-.008-.07.54.54 0 0 1 .054-.23.32.32 0 0 1 .154-.16.2.2 0 0 1-.008-.07.9.9 0 0 1 .061-.299q.061-.176.138-.2-.015-.336.223-.682 0-.175.053-.33a.38.38 0 0 1 .192-.23.9.9 0 0 1 .07-.291q.06-.146.137-.246a.6.6 0 0 1-.023-.153q0-.108.039-.169.046-.069.1-.084a.4.4 0 0 1-.008-.092q0-.169.122-.269l-.007-.046q0-.114.092-.207a.4.4 0 0 1-.008-.084q0-.138.07-.276a.4.4 0 0 1 .153-.184q.138-.192.291-.192.069 0 .177.046.222.008.322.345.069.084.1.2a.6.6 0 0 1 .022.199q.07.045.1.123a.3.3 0 0 1 .03.16.5.5 0 0 1 .131.215q.061.147.061.277a.3.3 0 0 1-.007.084.4.4 0 0 1 .092.153q.046.1.046.184 0 .046-.008.07.1.069.161.214.07.145.092.292.03.138.016.192l.084.114a.7.7 0 0 1 .108.177.4.4 0 0 1 .038.161q0 .07-.015.1l.069.092q.038.046.06.084a.2.2 0 0 1 .024.092.3.3 0 0 1-.008.077.55.55 0 0 1 .154.353q0 .076-.031.115.085.061.13.176a.45.45 0 0 1 .031.23m-1.411-1.664q.015-.046.069-.054a.11.11 0 0 1 .092.008.2.2 0 0 1-.016-.07q0-.06.031-.091a.4.4 0 0 1-.115-.138.33.33 0 0 1-.03-.17.2.2 0 0 1-.1-.076.3.3 0 0 1-.039-.146q0-.023.016-.084a.33.33 0 0 1-.161-.299q0-.077.023-.176l-.039-.031q-.03-.03-.023-.092-.153-.046-.145-.246a.23.23 0 0 1-.1-.115.28.28 0 0 1-.138.276.45.45 0 0 1-.031.192.4.4 0 0 1-.092.161q.008.03.008.1 0 .177-.146.468a2 2 0 0 0-.062.153 2 2 0 0 1-.069.154.25.25 0 0 1-.084.115q0 .076-.03.138l.06-.008q.078 0 .116.038.023-.038.092-.038.046 0 .069.023a.37.37 0 0 1 .23-.069q.1 0 .184.03.084.032.138.085.077-.084.161-.084.054 0 .13.046M28.35 53.127q.046.047.046.115a.13.13 0 0 1-.038.092.3.3 0 0 1 .038.138q0 .062-.03.123.015.093.015.115 0 .108-.123.2a.34.34 0 0 1-.207.069.4.4 0 0 1-.261-.1.28.28 0 0 1-.184-.115.32.32 0 0 1-.046-.238 1 1 0 0 0-.092-.061 1 1 0 0 1-.092-.077.3.3 0 0 1-.046-.107.28.28 0 0 1-.146-.108.34.34 0 0 1-.046-.245.28.28 0 0 1-.169-.092.35.35 0 0 1-.069-.184 1.4 1.4 0 0 1-.222-.13q-.069-.054-.085-.17a.43.43 0 0 1-.153-.153.55.55 0 0 1-.092-.207.29.29 0 0 1-.161-.23.44.44 0 0 1-.154-.146.5.5 0 0 1-.092-.215.46.46 0 0 1-.207-.176.63.63 0 0 1-.115-.291.5.5 0 0 1-.145-.154.8.8 0 0 1-.108-.215 1 1 0 0 1-.138-.199 1 1 0 0 1-.1-.253.6.6 0 0 1-.13-.169 1 1 0 0 1-.092-.215q.015.047.015.13 0 .1-.023.123.046.184.046.307 0 .046-.015.108.03.069.03.207a.6.6 0 0 1-.023.191q.047.054.047.185 0 .114-.039.191.046.077.046.154a.4.4 0 0 1-.046.176l-.046.115q.07.1.07.338a.7.7 0 0 1-.04.237q.063.1.062.2a.9.9 0 0 1-.084.307.3.3 0 0 1 .077.215.63.63 0 0 1-.085.306l.023.192q.023.123.023.2 0 .33-.307.33a.28.28 0 0 1-.207-.085.37.37 0 0 1-.092-.207.614.614 0 0 1-.008-.453 1 1 0 0 1-.038-.26.5.5 0 0 1 .023-.17.6.6 0 0 1-.023-.145q0-.115.046-.23a.5.5 0 0 1-.061-.253q0-.17.077-.261a.7.7 0 0 1-.062-.276q0-.153.07-.276a.4.4 0 0 1-.031-.123q0-.023.015-.084-.1-.1-.1-.323 0-.114.023-.222a.4.4 0 0 1 .07-.153.8.8 0 0 1-.085-.392q0-.153.046-.245a.45.45 0 0 1-.046-.215q0-.16.061-.26a.5.5 0 0 1-.023-.177q0-.108.023-.146 0-.191.123-.299a.43.43 0 0 1 .291-.107q.169 0 .284.115a.36.36 0 0 1 .108.307q.176.023.33.214.153.192.145.415a.8.8 0 0 1 .192.207.5.5 0 0 1 .107.253 1 1 0 0 1 .215.276q.116.199.123.353.091.008.192.176a.75.75 0 0 1 .122.284q.078.03.154.153a.4.4 0 0 1 .076.215q.061.015.108.123.053.108.038.169a.35.35 0 0 1 .23.268q.085-.008.184.1a.46.46 0 0 1 .123.215q.077.03.161.138.093.1.115.191a1 1 0 0 1-.023-.168q0-.054.039-.092-.077-.085-.077-.246 0-.084.023-.138a.34.34 0 0 1-.054-.169q0-.069.039-.145-.078-.07-.077-.238 0-.076.023-.123a.6.6 0 0 1-.062-.276q0-.046.016-.138-.084-.1-.084-.322 0-.184.06-.269a.6.6 0 0 1-.068-.145 1 1 0 0 1-.023-.184q0-.147.061-.223a.26.26 0 0 1-.069-.192q0-.084.038-.215a.5.5 0 0 1-.076-.276q0-.122.046-.191a.5.5 0 0 1-.039-.177q0-.153.154-.215a.5.5 0 0 1 .153-.03q.131 0 .184.1.062.099.062.237a.5.5 0 0 1-.008.108q.061.046.1.168a.9.9 0 0 1 .038.254q0 .115-.03.168.053.123.053.322a.5.5 0 0 1-.038.238q.046.093.046.269 0 .138-.03.2.038.06.06.168.03.099.031.192 0 .13-.053.184a.3.3 0 0 1 .084.153q.03.108.03.222a.5.5 0 0 1-.023.17q.062.099.062.237 0 .115-.038.161.023.046.038.154.015.106 0 .153.077.054.077.222a.36.36 0 0 1-.054.208q.038.1.038.16a1 1 0 0 1-.015.1M34.844 51.37q.03.085.031.193a.8.8 0 0 1-.038.245.44.44 0 0 1-.085.176q.07.07.07.307 0 .23-.07.322a.3.3 0 0 1 .062.139q.023.099.023.222a.66.66 0 0 1-.085.36q.046.146.046.261 0 .176-.092.269a.3.3 0 0 1-.215.092.277.277 0 0 1-.276-.284q0-.069.023-.138a.8.8 0 0 1-.1-.399q0-.222.1-.322a.3.3 0 0 1-.076-.215.28.28 0 0 1 .076-.207.76.76 0 0 1-.076-.345q0-.192.076-.261a.9.9 0 0 1-.076-.406q0-.255.076-.323a.57.57 0 0 1-.107-.345q0-.176.107-.276a.17.17 0 0 1-.03-.107q0-.054.015-.139.015-.09.015-.168 0 .2-.092.521-.091.315-.253.43 0 .506-.191.59 0 .17-.062.4-.06.222-.123.222 0 .092-.076.207-.069.108-.138.107a.6.6 0 0 1-.016.376.4.4 0 0 1-.084.146.45.45 0 0 1-.054.23.8.8 0 0 1-.145.176.29.29 0 0 1-.1.23.346.346 0 0 1-.445.008.3.3 0 0 1-.092-.23q0-.07.008-.1-.078 0-.146-.13a.7.7 0 0 1-.062-.291q-.069 0-.145-.115a1.2 1.2 0 0 1-.123-.269q-.045-.153-.023-.23a.5.5 0 0 1-.123-.176.7.7 0 0 1-.053-.277q0-.153.06-.268-.099-.008-.176-.138a.6.6 0 0 1-.069-.269q0-.076.039-.115a.6.6 0 0 1-.146-.2.6.6 0 0 1-.061-.222q-.1-.1-.1-.406-.108 0-.184-.192.015.145-.03.292a.47.47 0 0 1-.108.207q.07.06.107.238.038.168.038.345 0 .16-.03.23.076.123.076.376 0 .283-.076.46a.6.6 0 0 1 .061.26.7.7 0 0 1-.061.261q.03.03.053.154.023.114.023.253 0 .261-.076.314.008.047.008.123 0 .2-.1.307a.29.29 0 0 1-.23.107.34.34 0 0 1-.246-.107.38.38 0 0 1-.107-.284.8.8 0 0 1 .069-.291 2.1 2.1 0 0 1-.092-.59q0-.255.069-.392-.077-.1-.077-.315 0-.099.023-.184a.4.4 0 0 1 .054-.13.9.9 0 0 1-.077-.353q0-.185.077-.26a.4.4 0 0 1-.062-.139.7.7 0 0 1-.023-.176q0-.138.046-.207a.66.66 0 0 1-.076-.338q0-.269.115-.383-.108-.108-.108-.437 0-.154.031-.292.03-.146.077-.192 0-.145.107-.238a.37.37 0 0 1 .246-.092q.161 0 .276.115.153 0 .276.139.13.138.13.283a.4.4 0 0 1 .092.13.36.36 0 0 1 .016.285q.092.007.146.107.06.099.06.192.063.053.1.153.047.092.047.192 0 .092-.031.176.037 0 .069.07.038.06.038.137 0 .062-.03.092.084.046.145.192a.54.54 0 0 1 .07.23.4.4 0 0 1 .084.177.85.85 0 0 1 .015.437q.077.054.146.222a.8.8 0 0 1 .069.315q0 .061-.008.092a.5.5 0 0 1 .13.13q.047.062.077.184.016-.337.146-.406a.4.4 0 0 1-.015-.092q0-.115.053-.223.061-.107.139-.107a.3.3 0 0 1-.039-.13q0-.17.238-.43a.3.3 0 0 1-.023-.123q0-.13.054-.268a.4.4 0 0 1 .168-.2q0-.229.031-.383a.33.33 0 0 1 .176-.23.37.37 0 0 1 .062-.223q.07-.092.16-.092a.3.3 0 0 1-.038-.16q0-.162.07-.33a.5.5 0 0 1 .176-.23q0-.17.092-.292.1-.123.307-.123a.3.3 0 0 1 .107-.015q.154 0 .223.138a.5.5 0 0 1 .076.238q.07.237.07.36 0 .115-.07.36.07.078.07.223a.8.8 0 0 1-.07.3q.07.076.07.252 0 .184-.077.276.1.13.1.33t-.093.338M38.166 48.594a.34.34 0 0 1 .2-.054q.176 0 .352.115.084 0 .138.07a.27.27 0 0 1 .054.168.37.37 0 0 1-.077.23.26.26 0 0 1-.215.1.46.46 0 0 1-.214-.061.22.22 0 0 1-.161.061.4.4 0 0 1-.223-.077.5.5 0 0 1-.23.039.7.7 0 0 1-.345-.07.56.56 0 0 1-.238.054.6.6 0 0 1-.192-.03.5.5 0 0 1-.191.038.5.5 0 0 1-.246-.069.7.7 0 0 1-.245.092.7.7 0 0 1 .053.284q0 .192-.084.307a.59.59 0 0 1 .023.598.28.28 0 0 1 .07.192.4.4 0 0 1-.055.184.34.34 0 0 1 .17-.038q.075 0 .107.015a.3.3 0 0 1 .184-.062.3.3 0 0 1 .2.085.7.7 0 0 1 .153-.023q.037 0 .13.015a1 1 0 0 1 .13-.015q.13 0 .192.061a.26.26 0 0 1 .138-.038q.092 0 .115.038a.4.4 0 0 1 .108-.023q.053 0 .145.03a.4.4 0 0 1 .192-.053q.13 0 .222.1.093.092.092.207a.23.23 0 0 1-.069.169q-.068.069-.214.076-.07.093-.23.093a.46.46 0 0 1-.261-.077q-.077.075-.223.076a.36.36 0 0 1-.214-.06.2.2 0 0 1-.13.045.1.1 0 0 1-.078-.03.33.33 0 0 1-.23.076.44.44 0 0 1-.2-.046.3.3 0 0 1-.13.038.27.27 0 0 1-.153-.053.4.4 0 0 1-.084.038.2.2 0 0 1-.085 0 .5.5 0 0 1 .07.215.4.4 0 0 1-.047.161q.054.06.054.153a.3.3 0 0 1-.038.146q.053.062.053.161a.22.22 0 0 1-.053.154q.045.092.046.145 0 .046-.062.154.03.03.031.084 0 .07-.046.092a.23.23 0 0 1 .077.177 1 1 0 0 1-.016.13l.046-.008q.085 0 .177.077a.5.5 0 0 1 .299-.084q.07 0 .146.015a.3.3 0 0 1 .13-.084.5.5 0 0 1 .184-.031.38.38 0 0 1 .246.069.3.3 0 0 1 .16-.123.6.6 0 0 1 .23-.046q.085 0 .139.023a.3.3 0 0 1 .153-.046q.123 0 .215.1a.33.33 0 0 1 .092.23.28.28 0 0 1-.146.253.26.26 0 0 1-.122.108.5.5 0 0 1-.2.038.3.3 0 0 1-.168-.038.6.6 0 0 1-.161.107.6.6 0 0 1-.2.038.32.32 0 0 1-.238-.084.44.44 0 0 1-.337.138 1 1 0 0 1-.13-.015.6.6 0 0 1-.3.092.33.33 0 0 1-.2-.062.27.27 0 0 1-.245.13.34.34 0 0 1-.245-.107.27.27 0 0 1-.07-.253.3.3 0 0 1-.068-.138.8.8 0 0 1-.016-.284q-.1-.1-.1-.337a.7.7 0 0 1 .024-.184.4.4 0 0 1 .053-.13.36.36 0 0 1-.023-.261.25.25 0 0 1-.069-.184q0-.108.054-.17a.3.3 0 0 1-.038-.137q0-.085.053-.161a.7.7 0 0 1-.06-.269q0-.161.099-.307a1.5 1.5 0 0 1-.085-.245q-.03-.146-.03-.276 0-.246.115-.338a4 4 0 0 1-.039-.176 1.3 1.3 0 0 1-.046-.261q0-.115.054-.276a.5.5 0 0 1-.061-.177.6.6 0 0 1-.016-.176.3.3 0 0 1-.092-.222q0-.1.062-.184a.33.33 0 0 1 .176-.123.4.4 0 0 1 .146-.085.5.5 0 0 1 .192-.038.36.36 0 0 1 .222.069 1 1 0 0 1 .26-.038q.185 0 .3.084.07-.03.161-.03.084 0 .13.038.046-.039.223-.039.23 0 .36.077a.28.28 0 0 1 .208-.084.5.5 0 0 1 .176.038M39.509 51.862a.4.4 0 0 1-.038-.192q0-.107.038-.161a.4.4 0 0 1-.015-.13q0-.108.03-.215.03-.108.07-.138-.07-.061-.07-.269a.8.8 0 0 1 .016-.16q.015-.078.046-.093a.6.6 0 0 1-.039-.245q0-.123.023-.223a.4.4 0 0 1 .062-.161.34.34 0 0 1-.092-.253q0-.1.03-.184a.4.4 0 0 1 .092-.138.42.42 0 0 1-.069-.246q0-.091.039-.222a.32.32 0 0 1-.077-.192q0-.122.107-.207a.36.36 0 0 1 .238-.092q.093 0 .161.061a.3.3 0 0 1 .108.17.33.33 0 0 1 .084.214q0 .076-.03.13.022.023.022.376.015.061.016.1a.9.9 0 0 1-.07.33q.047.06.047.207 0 .1-.031.2a.5.5 0 0 1-.054.137q.008.024.023.085.016.06.016.115a.27.27 0 0 1-.039.145q.054.078.054.307a.6.6 0 0 1-.015.146q.03.04.03.161 0 .1-.03.2-.023.09-.077.13.054.06.054.169 0 .1-.039.16.039.016.039.108a.5.5 0 0 1-.016.115q-.015.053-.038.062.015.06.015.1a.32.32 0 0 1-.092.222.28.28 0 0 1-.215.092.3.3 0 0 1-.23-.1.29.29 0 0 1-.069-.245.7.7 0 0 1-.038-.246q0-.084.023-.13m.046 1.871a.3.3 0 0 0 .084.223.33.33 0 0 0 .215.076q.108 0 .192-.06a.3.3 0 0 0 .107-.193.35.35 0 0 0 .07-.215l-.008-.145q-.015-.207-.184-.276a.294.294 0 0 0-.33.07.28.28 0 0 0-.092.213q0 .04.015.1a.36.36 0 0 0-.069.207"
  })), _path4$2 || (_path4$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M16.226 20.68h-.62v.62h.62zM16.226 21.3h-.62v.62h.62zM16.226 21.92h-.62v.62h.62zM16.226 23.78h-.62v.62h.62z"
  })), _path5$2 || (_path5$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M16.226 24.4h-.62v.62h.62zM16.226 26.26h-.62v.62h.62zM16.226 27.499h-.62v.62h.62zM16.226 28.739h-.62v.62h.62zM16.226 29.359h-.62v.62h.62zM16.846 21.92h-.62v.62h.62z"
  })), _path6$2 || (_path6$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M16.846 22.54h-.62v.62h.62zM16.846 23.16h-.62v.62h.62zM16.846 24.4h-.62v.62h.62zM16.846 26.26h-.62v.62h.62z"
  })), _path7$2 || (_path7$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M16.846 26.88h-.62v.619h.62zM16.846 27.499h-.62v.62h.62zM17.466 20.061h-.62v.62h.62zM17.466 21.3h-.62v.62h.62zM17.466 21.92h-.62v.62h.62zM17.466 25.64h-.62v.62h.62zM17.466 26.26h-.62v.62h.62z"
  })), _path8$2 || (_path8$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M17.466 26.88h-.62v.619h.62zM17.466 28.119h-.62v.62h.62zM17.466 28.739h-.62v.62h.62zM17.466 29.359h-.62v.62h.62zM18.086 20.68h-.62v.62h.62zM18.086 21.3h-.62v.62h.62zM18.086 21.92h-.62v.62h.62z"
  })), _path9$2 || (_path9$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18.086 22.54h-.62v.62h.62zM18.086 23.16h-.62v.62h.62zM18.086 24.4h-.62v.62h.62zM18.086 25.64h-.62v.62h.62zM18.086 28.119h-.62v.62h.62zM18.086 28.739h-.62v.62h.62zM18.086 29.359h-.62v.62h.62z"
  })), _path10$2 || (_path10$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18.086 29.978h-.62v.62h.62zM18.706 20.061h-.62v.62h.62zM18.706 23.16h-.62v.62h.62zM18.706 23.78h-.62v.62h.62z"
  })), _path11$2 || (_path11$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18.706 24.4h-.62v.62h.62zM18.706 25.02h-.62v.62h.62zM18.706 25.64h-.62v.62h.62zM18.706 26.88h-.62v.619h.62zM18.706 29.359h-.62v.62h.62z"
  })), _path12$2 || (_path12$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18.706 29.978h-.62v.62h.62zM19.325 20.061h-.62v.62h.62z"
  })), _path13$2 || (_path13$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M19.325 20.68h-.62v.62h.62zM19.325 21.3h-.62v.62h.62zM19.325 23.78h-.62v.62h.62zM19.325 25.02h-.62v.62h.62zM19.325 25.64h-.62v.62h.62zM19.325 27.499h-.62v.62h.62zM19.325 28.119h-.62v.62h.62zM19.945 20.061h-.62v.62h.62zM19.945 21.3h-.62v.62h.62zM19.945 22.54h-.62v.62h.62zM19.945 23.78h-.62v.62h.62zM19.945 25.02h-.62v.62h.62zM19.945 26.26h-.62v.62h.62zM19.945 27.499h-.62v.62h.62zM19.945 28.739h-.62v.62h.62zM19.945 29.978h-.62v.62h.62zM20.565 21.3h-.62v.62h.62zM20.565 21.92h-.62v.62h.62z"
  })), _path14$2 || (_path14$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M20.565 22.54h-.62v.62h.62zM20.565 23.78h-.62v.62h.62zM20.565 25.02h-.62v.62h.62zM20.565 25.64h-.62v.62h.62zM20.565 26.88h-.62v.619h.62zM20.565 27.499h-.62v.62h.62zM20.565 28.119h-.62v.62h.62zM20.565 28.739h-.62v.62h.62zM20.565 29.359h-.62v.62h.62zM21.185 15.722h-.62v.62h.62zM21.185 17.582h-.62v.62h.62zM21.185 18.821h-.62v.62h.62zM21.185 20.061h-.62v.62h.62z"
  })), _path15$2 || (_path15$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M21.185 20.68h-.62v.62h.62zM21.185 21.3h-.62v.62h.62zM21.185 21.92h-.62v.62h.62zM21.185 24.4h-.62v.62h.62zM21.185 25.02h-.62v.62h.62zM21.185 25.64h-.62v.62h.62zM21.185 28.119h-.62v.62h.62zM21.185 28.739h-.62v.62h.62zM21.185 30.598h-.62v.62h.62zM21.185 31.838h-.62v.62h.62zM21.185 32.458h-.62v.62h.62zM21.185 33.697h-.62v.62h.62zM21.805 15.102h-.62v.62h.62zM21.805 16.962h-.62v.62h.62zM21.805 17.582h-.62v.62h.62zM21.805 18.202h-.62v.62h.62zM21.805 19.441h-.62v.62h.62zM21.805 20.68h-.62v.62h.62zM21.805 21.3h-.62v.62h.62zM21.805 21.92h-.62v.62h.62z"
  })), _path16$2 || (_path16$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M21.805 22.54h-.62v.62h.62zM21.805 23.16h-.62v.62h.62zM21.805 23.78h-.62v.62h.62z"
  })), _path17$2 || (_path17$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M21.805 24.4h-.62v.62h.62zM21.805 25.64h-.62v.62h.62zM21.805 26.26h-.62v.62h.62zM21.805 28.119h-.62v.62h.62zM21.805 29.359h-.62v.62h.62zM21.805 30.598h-.62v.62h.62zM21.805 33.078h-.62v.62h.62z"
  })), _path18$2 || (_path18$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M21.805 33.697h-.62v.62h.62zM21.805 34.317h-.62v.62h.62zM22.425 15.102h-.62v.62h.62zM22.425 18.202h-.62v.62h.62z"
  })), _path19$2 || (_path19$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M22.425 18.821h-.62v.62h.62zM22.425 21.92h-.62v.62h.62z"
  })), _path20$2 || (_path20$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M22.425 22.54h-.62v.62h.62zM22.425 23.16h-.62v.62h.62zM22.425 23.78h-.62v.62h.62zM22.425 26.26h-.62v.62h.62zM22.425 27.499h-.62v.62h.62zM22.425 29.359h-.62v.62h.62zM22.425 30.598h-.62v.62h.62zM22.425 33.078h-.62v.62h.62zM23.045 15.722h-.62v.62h.62zM23.045 17.582h-.62v.62h.62zM23.045 18.202h-.62v.62h.62zM23.045 19.441h-.62v.62h.62zM23.045 21.92h-.62v.62h.62zM23.045 23.78h-.62v.62h.62z"
  })), _path21$2 || (_path21$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M23.045 24.4h-.62v.62h.62zM23.045 26.88h-.62v.619h.62zM23.045 27.499h-.62v.62h.62zM23.045 28.739h-.62v.62h.62zM23.045 29.978h-.62v.62h.62z"
  })), _path22$2 || (_path22$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M23.045 30.598h-.62v.62h.62zM23.045 31.218h-.62v.62h.62zM23.045 34.317h-.62v.62h.62zM23.664 15.722h-.62v.62h.62zM23.664 18.821h-.62v.62h.62zM23.664 20.061h-.62v.62h.62z"
  })), _path23$2 || (_path23$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M23.664 20.68h-.62v.62h.62zM23.664 22.54h-.62v.62h.62zM23.664 23.16h-.62v.62h.62zM23.664 23.78h-.62v.62h.62zM23.664 28.739h-.62v.62h.62zM23.664 29.359h-.62v.62h.62z"
  })), _path24$2 || (_path24$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M23.664 29.978h-.62v.62h.62z"
  })), _path25$2 || (_path25$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M23.664 30.598h-.62v.62h.62zM23.664 31.218h-.62v.62h.62zM23.664 31.838h-.62v.62h.62zM23.664 32.458h-.62v.62h.62zM23.664 33.078h-.62v.62h.62zM24.284 15.102h-.62v.62h.62zM24.284 17.582h-.62v.62h.62zM24.284 19.441h-.62v.62h.62zM24.284 20.68h-.62v.62h.62zM24.284 21.3h-.62v.62h.62zM24.284 21.92h-.62v.62h.62z"
  })), _path26$2 || (_path26$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M24.284 22.54h-.62v.62h.62zM24.284 23.16h-.62v.62h.62zM24.284 24.4h-.62v.62h.62zM24.284 25.64h-.62v.62h.62zM24.284 26.26h-.62v.62h.62z"
  })), _path27$2 || (_path27$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M24.284 26.88h-.62v.619h.62zM24.284 27.499h-.62v.62h.62zM24.284 33.078h-.62v.62h.62zM24.904 17.582h-.62v.62h.62zM24.904 18.821h-.62v.62h.62z"
  })), _path28$2 || (_path28$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M24.904 19.441h-.62v.62h.62zM24.904 20.061h-.62v.62h.62zM24.904 22.54h-.62v.62h.62zM24.904 23.16h-.62v.62h.62zM24.904 24.4h-.62v.62h.62zM24.904 25.02h-.62v.62h.62zM24.904 25.64h-.62v.62h.62zM24.904 27.499h-.62v.62h.62zM24.904 28.739h-.62v.62h.62zM24.904 29.359h-.62v.62h.62z"
  })), _path29$2 || (_path29$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M24.904 29.978h-.62v.62h.62z"
  })), _path30$2 || (_path30$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M24.904 30.598h-.62v.62h.62zM24.904 31.218h-.62v.62h.62zM24.904 31.838h-.62v.62h.62zM24.904 32.458h-.62v.62h.62zM24.904 33.697h-.62v.62h.62zM24.904 34.317h-.62v.62h.62zM25.524 15.102h-.62v.62h.62zM25.524 15.722h-.62v.62h.62zM25.524 16.962h-.62v.62h.62zM25.524 20.68h-.62v.62h.62zM25.524 21.3h-.62v.62h.62zM25.524 22.54h-.62v.62h.62zM25.524 23.16h-.62v.62h.62zM25.524 25.02h-.62v.62h.62zM25.524 25.64h-.62v.62h.62zM25.524 28.119h-.62v.62h.62zM25.524 29.359h-.62v.62h.62zM25.524 30.598h-.62v.62h.62zM25.524 31.218h-.62v.62h.62zM25.524 32.458h-.62v.62h.62z"
  })), _path31$2 || (_path31$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M26.144 15.102h-.62v.62h.62zM26.144 15.722h-.62v.62h.62zM26.144 18.202h-.62v.62h.62z"
  })), _path32$2 || (_path32$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M26.144 18.821h-.62v.62h.62zM26.144 20.68h-.62v.62h.62zM26.144 23.78h-.62v.62h.62z"
  })), _path33$2 || (_path33$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M26.144 24.4h-.62v.62h.62zM26.144 25.64h-.62v.62h.62zM26.144 26.88h-.62v.619h.62zM26.144 27.499h-.62v.62h.62zM26.144 28.119h-.62v.62h.62zM26.144 28.739h-.62v.62h.62zM26.144 29.978h-.62v.62h.62zM26.144 31.218h-.62v.62h.62zM26.144 32.458h-.62v.62h.62zM26.144 33.078h-.62v.62h.62zM26.144 34.317h-.62v.62h.62zM26.764 15.102h-.62v.62h.62zM26.764 16.962h-.62v.62h.62zM26.764 17.582h-.62v.62h.62zM26.764 20.061h-.62v.62h.62z"
  })), _path34$2 || (_path34$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M26.764 20.68h-.62v.62h.62zM26.764 21.3h-.62v.62h.62zM26.764 21.92h-.62v.62h.62zM26.764 23.16h-.62v.62h.62zM26.764 25.64h-.62v.62h.62zM26.764 28.739h-.62v.62h.62zM26.764 29.359h-.62v.62h.62z"
  })), _path35$2 || (_path35$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M26.764 29.978h-.62v.62h.62zM26.764 33.078h-.62v.62h.62z"
  })), _path36$1 || (_path36$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M26.764 33.697h-.62v.62h.62zM26.764 34.317h-.62v.62h.62zM27.383 15.102h-.62v.62h.62zM27.383 17.582h-.62v.62h.62zM27.383 18.202h-.62v.62h.62z"
  })), _path37$1 || (_path37$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.383 18.821h-.62v.62h.62z"
  })), _path38$1 || (_path38$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.383 19.441h-.62v.62h.62zM27.383 20.061h-.62v.62h.62zM27.383 22.54h-.62v.62h.62zM27.383 23.78h-.62v.62h.62z"
  })), _path39$1 || (_path39$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.383 24.4h-.62v.62h.62zM27.383 25.02h-.62v.62h.62zM27.383 26.26h-.62v.62h.62zM27.383 28.119h-.62v.62h.62zM27.383 28.739h-.62v.62h.62zM27.383 29.359h-.62v.62h.62z"
  })), _path40$1 || (_path40$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.383 29.978h-.62v.62h.62z"
  })), _path41$1 || (_path41$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.383 30.598h-.62v.62h.62zM27.383 31.838h-.62v.62h.62zM27.383 33.078h-.62v.62h.62zM27.383 34.317h-.62v.62h.62zM28.003 15.722h-.62v.62h.62zM28.003 16.342h-.62v.62h.62zM28.003 16.962h-.62v.62h.62zM28.003 17.582h-.62v.62h.62zM28.003 18.202h-.62v.62h.62zM28.003 20.061h-.62v.62h.62zM28.003 22.54h-.62v.62h.62zM28.003 26.26h-.62v.62h.62zM28.003 27.499h-.62v.62h.62zM28.003 28.119h-.62v.62h.62zM28.003 29.978h-.62v.62h.62z"
  })), _path42$1 || (_path42$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M28.003 30.598h-.62v.62h.62zM28.003 31.218h-.62v.62h.62zM28.003 31.838h-.62v.62h.62zM28.003 34.317h-.62v.62h.62zM28.003 34.937h-.62v.62h.62zM28.623 15.102h-.62v.62h.62zM28.623 15.722h-.62v.62h.62zM28.623 16.962h-.62v.62h.62zM28.623 17.582h-.62v.62h.62zM28.623 18.821h-.62v.62h.62zM28.623 20.061h-.62v.62h.62z"
  })), _path43$1 || (_path43$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M28.623 20.68h-.62v.62h.62zM28.623 21.92h-.62v.62h.62zM28.623 23.78h-.62v.62h.62z"
  })), _path44$1 || (_path44$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M28.623 24.4h-.62v.62h.62zM28.623 26.88h-.62v.619h.62zM28.623 27.499h-.62v.62h.62zM28.623 30.598h-.62v.62h.62zM28.623 31.218h-.62v.62h.62zM28.623 31.838h-.62v.62h.62zM28.623 32.458h-.62v.62h.62zM28.623 33.078h-.62v.62h.62zM29.243 15.102h-.62v.62h.62zM29.243 16.342h-.62v.62h.62zM29.243 19.441h-.62v.62h.62zM29.243 20.061h-.62v.62h.62zM29.243 21.92h-.62v.62h.62z"
  })), _path45$1 || (_path45$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.243 22.54h-.62v.62h.62zM29.243 25.02h-.62v.62h.62zM29.243 26.88h-.62v.619h.62zM29.243 28.739h-.62v.62h.62zM29.243 29.359h-.62v.62h.62z"
  })), _path46$1 || (_path46$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.243 29.978h-.62v.62h.62zM29.243 33.697h-.62v.62h.62zM29.243 34.317h-.62v.62h.62zM29.863 16.342h-.62v.62h.62zM29.863 16.962h-.62v.62h.62zM29.863 17.582h-.62v.62h.62zM29.863 18.202h-.62v.62h.62z"
  })), _path47$1 || (_path47$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.863 18.821h-.62v.62h.62z"
  })), _path48$1 || (_path48$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.863 19.441h-.62v.62h.62zM29.863 20.061h-.62v.62h.62zM29.863 21.3h-.62v.62h.62zM29.863 21.92h-.62v.62h.62z"
  })), _path49$1 || (_path49$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.863 22.54h-.62v.62h.62zM29.863 23.16h-.62v.62h.62zM29.863 23.78h-.62v.62h.62zM29.863 26.26h-.62v.62h.62z"
  })), _path50$1 || (_path50$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.863 26.88h-.62v.619h.62zM29.863 28.739h-.62v.62h.62zM29.863 29.359h-.62v.62h.62zM29.863 30.598h-.62v.62h.62zM29.863 34.317h-.62v.62h.62zM30.483 16.342h-.62v.62h.62zM30.483 18.202h-.62v.62h.62zM30.483 19.441h-.62v.62h.62zM30.483 20.68h-.62v.62h.62zM30.483 21.3h-.62v.62h.62zM30.483 21.92h-.62v.62h.62zM30.483 23.16h-.62v.62h.62zM30.483 25.02h-.62v.62h.62zM30.483 26.88h-.62v.619h.62zM30.483 27.499h-.62v.62h.62zM30.483 28.119h-.62v.62h.62zM30.483 28.739h-.62v.62h.62zM30.483 30.598h-.62v.62h.62zM30.483 31.218h-.62v.62h.62zM30.483 34.317h-.62v.62h.62zM31.102 15.102h-.62v.62h.62zM31.102 15.722h-.62v.62h.62z"
  })), _path51$1 || (_path51$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.102 16.342h-.62v.62h.62zM31.102 18.202h-.62v.62h.62z"
  })), _path52$1 || (_path52$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.102 18.821h-.62v.62h.62z"
  })), _path53$1 || (_path53$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.102 19.441h-.62v.62h.62zM31.102 21.3h-.62v.62h.62zM31.102 22.54h-.62v.62h.62zM31.102 23.78h-.62v.62h.62z"
  })), _path54 || (_path54 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.102 24.4h-.62v.62h.62zM31.102 25.64h-.62v.62h.62zM31.102 26.26h-.62v.62h.62zM31.102 28.119h-.62v.62h.62zM31.102 28.739h-.62v.62h.62zM31.102 29.978h-.62v.62h.62z"
  })), _path55 || (_path55 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.102 30.598h-.62v.62h.62zM31.102 31.218h-.62v.62h.62zM31.102 31.838h-.62v.62h.62zM31.102 32.458h-.62v.62h.62zM31.102 33.078h-.62v.62h.62zM31.102 34.937h-.62v.62h.62zM31.722 20.061h-.62v.62h.62zM31.722 21.3h-.62v.62h.62zM31.722 21.92h-.62v.62h.62z"
  })), _path56 || (_path56 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.722 22.54h-.62v.62h.62zM31.722 23.16h-.62v.62h.62zM31.722 26.26h-.62v.62h.62zM31.722 28.119h-.62v.62h.62zM31.722 29.359h-.62v.62h.62z"
  })), _path57 || (_path57 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.722 29.978h-.62v.62h.62zM31.722 32.458h-.62v.62h.62zM31.722 33.697h-.62v.62h.62zM31.722 34.317h-.62v.62h.62zM32.342 20.68h-.62v.62h.62zM32.342 23.16h-.62v.62h.62zM32.342 23.78h-.62v.62h.62z"
  })), _path58 || (_path58 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M32.342 24.4h-.62v.62h.62zM32.342 25.02h-.62v.62h.62zM32.342 27.499h-.62v.62h.62zM32.342 29.978h-.62v.62h.62zM32.342 31.218h-.62v.62h.62zM32.342 32.458h-.62v.62h.62zM32.342 33.078h-.62v.62h.62zM32.342 34.317h-.62v.62h.62zM32.962 20.68h-.62v.62h.62zM32.962 23.16h-.62v.62h.62zM32.962 24.4h-.62v.62h.62zM32.962 28.739h-.62v.62h.62zM32.962 29.978h-.62v.62h.62zM32.962 32.458h-.62v.62h.62zM32.962 34.317h-.62v.62h.62zM32.962 34.937h-.62v.62h.62zM33.582 20.061h-.62v.62h.62zM33.582 21.92h-.62v.62h.62z"
  })), _path59 || (_path59 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.582 22.54h-.62v.62h.62zM33.582 23.78h-.62v.62h.62z"
  })), _path60 || (_path60 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.582 24.4h-.62v.62h.62zM33.582 26.88h-.62v.619h.62zM33.582 28.739h-.62v.62h.62zM33.582 29.978h-.62v.62h.62z"
  })), _path61 || (_path61 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.582 30.598h-.62v.62h.62zM33.582 31.218h-.62v.62h.62zM33.582 31.838h-.62v.62h.62zM33.582 32.458h-.62v.62h.62zM33.582 33.078h-.62v.62h.62zM33.582 34.317h-.62v.62h.62zM33.582 34.937h-.62v.62h.62zM34.202 21.3h-.62v.62h.62zM34.202 21.92h-.62v.62h.62z"
  })), _path62 || (_path62 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M34.202 22.54h-.62v.62h.62zM34.202 23.16h-.62v.62h.62zM34.202 25.64h-.62v.62h.62zM34.202 26.88h-.62v.619h.62zM34.202 27.499h-.62v.62h.62zM34.202 29.359h-.62v.62h.62zM34.202 31.218h-.62v.62h.62zM34.202 31.838h-.62v.62h.62zM34.202 32.458h-.62v.62h.62zM34.202 33.697h-.62v.62h.62zM34.202 34.317h-.62v.62h.62z"
  })), _path63 || (_path63 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M34.821 22.54h-.62v.62h.62zM34.821 23.78h-.62v.62h.62zM34.821 25.64h-.62v.62h.62zM34.821 27.499h-.62v.62h.62zM34.821 28.119h-.62v.62h.62zM34.821 30.598h-.62v.62h.62zM34.821 31.218h-.62v.62h.62zM34.821 31.838h-.62v.62h.62zM34.821 32.458h-.62v.62h.62zM34.821 33.078h-.62v.62h.62z"
  })), _path64 || (_path64 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M34.821 33.697h-.62v.62h.62zM35.441 20.061h-.62v.62h.62z"
  })), _path65 || (_path65 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M35.441 20.68h-.62v.62h.62zM35.441 23.78h-.62v.62h.62z"
  })), _path66 || (_path66 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M35.441 24.4h-.62v.62h.62zM35.441 26.26h-.62v.62h.62zM35.441 28.119h-.62v.62h.62zM35.441 29.978h-.62v.62h.62zM35.441 33.078h-.62v.62h.62zM36.061 20.68h-.62v.62h.62zM36.061 22.54h-.62v.62h.62zM36.061 23.78h-.62v.62h.62zM36.061 26.88h-.62v.619h.62zM36.061 27.499h-.62v.62h.62zM36.061 28.119h-.62v.62h.62zM36.061 28.739h-.62v.62h.62zM36.061 29.978h-.62v.62h.62zM36.061 34.937h-.62v.62h.62z"
  })), _path67 || (_path67 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M15.606 15.102v4.34h4.34v-4.34zm.62.62h3.1v3.1h-3.1z",
    clipRule: "evenodd"
  })), _path68 || (_path68 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18.706 16.342h-1.86v1.86h1.86z"
  })), _path69 || (_path69 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M36.06 15.102h-4.338v4.34h4.338zm-.62.62v3.1h-3.098v-3.1z",
    clipRule: "evenodd"
  })), _path70 || (_path70 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M34.82 18.202v-1.86h-1.859v1.86z"
  })), _path71 || (_path71 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M34.82 16.342h-1.859v1.86h1.86z"
  })), _path72 || (_path72 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M15.606 35.557h4.34v-4.34h-4.34zm.62-.62v-3.1h3.1v3.1z",
    clipRule: "evenodd"
  })), _path73 || (_path73 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18.706 32.458h-1.86v1.86h1.86z"
  })));
};

var _path$t, _path2$3, _path3$2, _path4$1, _path5$1, _path6$1, _path7$1, _path8$1, _path9$1, _path10$1, _path11$1, _path12$1, _path13$1, _path14$1, _path15$1, _path16$1, _path17$1, _path18$1, _path19$1, _path20$1, _path21$1, _path22$1, _path23$1, _path24$1, _path25$1, _path26$1, _path27$1, _path28$1, _path29$1, _path30$1, _path31$1, _path32$1, _path33$1, _path34$1, _path35$1, _path36, _path37, _path38, _path39, _path40, _path41, _path42, _path43, _path44, _path45, _path46, _path47, _path48, _path49, _path50, _path51, _path52, _path53;
function _extends$v() { return _extends$v = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$v.apply(null, arguments); }
var SvgFrameBoard = function SvgFrameBoard(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$v({
    xmlns: "http://www.w3.org/2000/svg",
    width: 50,
    height: 66,
    fill: "none"
  }, props), _path$t || (_path$t = /*#__PURE__*/React.createElement("path", {
    fill: "#FCFCFC",
    stroke: "#404040",
    strokeWidth: 1.278,
    d: "M5.437 11.011c0-.776.63-1.406 1.406-1.406h36.818c.777 0 1.407.63 1.407 1.406v47.046c0 .776-.63 1.406-1.407 1.406H6.843c-.776 0-1.406-.63-1.406-1.406z"
  })), _path2$3 || (_path2$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#404040",
    d: "M13.626 55.846q-.496 0-.933-.135a2.2 2.2 0 0 1-.736-.38l.264-.578q.313.24.656.35.35.11.755.11.448 0 .688-.153a.5.5 0 0 0 .245-.442.41.41 0 0 0-.227-.374q-.22-.129-.743-.245a4.4 4.4 0 0 1-.877-.27 1.24 1.24 0 0 1-.51-.393q-.171-.24-.171-.608 0-.38.208-.68.21-.302.59-.467.38-.171.877-.172.454 0 .865.141.412.135.675.375l-.264.577a2 2 0 0 0-1.276-.46q-.418 0-.663.171a.54.54 0 0 0-.239.466.43.43 0 0 0 .215.387q.214.135.73.252.546.122.884.276.344.147.521.38a.92.92 0 0 1 .184.59q0 .386-.208.68a1.3 1.3 0 0 1-.596.448 2.5 2.5 0 0 1-.914.154M17.695 55.846q-.637 0-1.116-.27a1.87 1.87 0 0 1-.73-.78q-.252-.503-.252-1.172t.251-1.166q.258-.503.73-.773.48-.276 1.117-.276.435 0 .823.135.386.135.65.38l-.264.578a2.1 2.1 0 0 0-.583-.338 1.7 1.7 0 0 0-.613-.11q-.626 0-.97.404-.337.406-.337 1.166 0 .767.337 1.172.344.405.97.406.325 0 .613-.105.29-.11.583-.343l.264.576q-.264.245-.65.38-.387.136-.823.136M22.78 55.79l-.436-1.012h-2.105l-.436 1.013h-.798l1.958-4.327h.644l1.958 4.327zm-2.283-1.626h1.583l-.792-1.84zM26.594 51.464h.742v4.327h-.595l-2.32-3.02v3.02h-.736v-4.327h.59l2.319 3.013zM32.856 51.464h.62v4.327h-.7v-2.86l-1.142 2.16h-.527l-1.148-2.13.006 2.83h-.7v-4.327h.627l1.485 2.83zM34.072 55.79v-4.326h2.897v.626h-2.124v1.19h2v.62h-2v1.265h2.124v.626zM37.406 54.49l-.24-3.032.946.006-.24 3.026zm-.196.454h.859v.846h-.86z"
  })), _path3$2 || (_path3$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M11.818 25.097h-.883v.883h.883zM11.818 25.98h-.883v.884h.883zM11.818 26.864h-.883v.883h.883zM11.818 29.514h-.883v.883h.883zM11.818 30.397h-.883v.883h.883zM11.818 33.047h-.883v.883h.883zM11.818 34.813h-.883v.883h.883zM11.818 36.58h-.883v.883h.883zM11.818 37.463h-.883v.883h.883zM12.701 26.864h-.883v.883h.883zM12.701 27.747h-.883v.883h.883zM12.701 28.63h-.883v.884h.883zM12.701 30.397h-.883v.883h.883zM12.701 33.047h-.883v.883h.883zM12.701 33.93h-.883v.883h.883zM12.701 34.813h-.883v.883h.883zM13.584 24.214h-.883v.883h.883zM13.584 25.98h-.883v.884h.883zM13.584 26.864h-.883v.883h.883zM13.584 32.163h-.883v.884h.883zM13.584 33.047h-.883v.883h.883zM13.584 33.93h-.883v.883h.883zM13.584 35.696h-.883v.884h.883zM13.584 36.58h-.883v.883h.883zM13.584 37.463h-.883v.883h.883zM14.468 25.097h-.884v.883h.884zM14.468 25.98h-.884v.884h.884zM14.468 26.864h-.884v.883h.884zM14.468 27.747h-.884v.883h.884zM14.468 28.63h-.884v.884h.884zM14.468 30.397h-.884v.883h.884zM14.468 32.163h-.884v.884h.884zM14.468 35.696h-.884v.884h.884zM14.468 36.58h-.884v.883h.884zM14.468 37.463h-.884v.883h.884z"
  })), _path4$1 || (_path4$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M14.468 38.346h-.884v.883h.884zM15.35 24.214h-.882v.883h.883zM15.35 28.63h-.882v.884h.883zM15.35 29.514h-.882v.883h.883zM15.35 30.397h-.882v.883h.883zM15.35 31.28h-.882v.883h.883z"
  })), _path5$1 || (_path5$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M15.35 32.163h-.882v.884h.883zM15.35 33.93h-.882v.883h.883zM15.35 37.463h-.882v.883h.883z"
  })), _path6$1 || (_path6$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M15.35 38.346h-.882v.883h.883zM16.234 24.214h-.883v.883h.883zM16.234 25.097h-.883v.883h.883zM16.234 25.98h-.883v.884h.883zM16.234 29.514h-.883v.883h.883zM16.234 31.28h-.883v.883h.883z"
  })), _path7$1 || (_path7$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M16.234 32.163h-.883v.884h.883zM16.234 34.813h-.883v.883h.883z"
  })), _path8$1 || (_path8$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M16.234 35.696h-.883v.884h.883zM17.117 24.214h-.883v.883h.883zM17.117 25.98h-.883v.884h.883zM17.117 27.747h-.883v.883h.883zM17.117 29.514h-.883v.883h.883zM17.117 31.28h-.883v.883h.883zM17.117 33.047h-.883v.883h.883zM17.117 34.813h-.883v.883h.883zM17.117 36.58h-.883v.883h.883zM17.117 38.346h-.883v.883h.883zM18 25.98h-.883v.884h.884zM18 26.864h-.883v.883h.884zM18 27.747h-.883v.883h.884zM18 29.514h-.883v.883h.884zM18 31.28h-.883v.883h.884z"
  })), _path9$1 || (_path9$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18 32.163h-.883v.884h.884zM18 33.93h-.883v.883h.884zM18 34.813h-.883v.883h.884z"
  })), _path10$1 || (_path10$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18 35.696h-.883v.884h.884zM18 36.58h-.883v.883h.884zM18 37.463h-.883v.883h.884zM18.884 18.031H18v.883h.883zM18.884 20.68H18v.884h.883zM18.884 22.447H18v.884h.883zM18.884 24.214H18v.883h.883zM18.884 25.097H18v.883h.883zM18.884 25.98H18v.884h.883zM18.884 26.864H18v.883h.883zM18.884 30.397H18v.883h.883zM18.884 31.28H18v.883h.883z"
  })), _path11$1 || (_path11$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M18.884 32.163H18v.884h.883zM18.884 35.696H18v.884h.883zM18.884 36.58H18v.883h.883zM18.884 39.23H18v.883h.883zM18.884 40.996H18v.883h.883zM18.884 41.88H18v.883h.883zM18.884 43.646H18v.883h.883zM19.767 17.148h-.883v.883h.883zM19.767 19.798h-.883v.883h.883z"
  })), _path12$1 || (_path12$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M19.767 20.68h-.883v.884h.883zM19.767 21.564h-.883v.883h.883zM19.767 23.33h-.883v.884h.883zM19.767 25.097h-.883v.883h.883zM19.767 25.98h-.883v.884h.883zM19.767 26.864h-.883v.883h.883zM19.767 27.747h-.883v.883h.883zM19.767 28.63h-.883v.884h.883zM19.767 29.514h-.883v.883h.883zM19.767 30.397h-.883v.883h.883zM19.767 32.163h-.883v.884h.883zM19.767 33.047h-.883v.883h.883zM19.767 35.696h-.883v.884h.883zM19.767 37.463h-.883v.883h.883zM19.767 39.23h-.883v.883h.883zM19.767 42.763h-.883v.883h.883zM19.767 43.646h-.883v.883h.883zM19.767 44.529h-.883v.883h.883zM20.65 17.148h-.883v.883h.884zM20.65 21.564h-.883v.883h.884zM20.65 22.447h-.883v.884h.884zM20.65 26.864h-.883v.883h.884zM20.65 27.747h-.883v.883h.884zM20.65 28.63h-.883v.884h.884zM20.65 29.514h-.883v.883h.884zM20.65 33.047h-.883v.883h.884zM20.65 34.813h-.883v.883h.884zM20.65 37.463h-.883v.883h.884zM20.65 39.23h-.883v.883h.884zM20.65 42.763h-.883v.883h.884zM21.534 18.031h-.883v.883h.883zM21.534 20.68h-.883v.884h.883zM21.534 21.564h-.883v.883h.883zM21.534 23.33h-.883v.884h.883zM21.534 26.864h-.883v.883h.883zM21.534 29.514h-.883v.883h.883zM21.534 30.397h-.883v.883h.883zM21.534 33.93h-.883v.883h.883zM21.534 34.813h-.883v.883h.883zM21.534 36.58h-.883v.883h.883zM21.534 38.346h-.883v.884h.883zM21.534 39.23h-.883v.883h.883zM21.534 40.113h-.883v.883h.883zM21.534 44.53h-.883v.882h.883zM22.417 18.031h-.883v.883h.883zM22.417 22.447h-.883v.884h.883zM22.417 24.214h-.883v.883h.883zM22.417 25.097h-.883v.883h.883zM22.417 27.747h-.883v.883h.883zM22.417 28.63h-.883v.884h.883zM22.417 29.514h-.883v.883h.883zM22.417 36.58h-.883v.883h.883zM22.417 37.463h-.883v.883h.883z"
  })), _path13$1 || (_path13$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M22.417 38.346h-.883v.883h.883zM22.417 39.23h-.883v.883h.883zM22.417 40.113h-.883v.883h.883zM22.417 40.996h-.883v.883h.883zM22.417 41.88h-.883v.883h.883z"
  })), _path14$1 || (_path14$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M22.417 42.763h-.883v.883h.883zM23.3 17.148h-.883v.883h.883zM23.3 20.68h-.883v.884h.883zM23.3 23.33h-.883v.884h.883zM23.3 25.097h-.883v.883h.883zM23.3 25.98h-.883v.884h.883zM23.3 26.864h-.883v.883h.883zM23.3 27.747h-.883v.883h.883zM23.3 28.63h-.883v.884h.883zM23.3 30.397h-.883v.883h.883zM23.3 32.163h-.883v.884h.883zM23.3 33.047h-.883v.883h.883zM23.3 33.93h-.883v.883h.883zM23.3 34.813h-.883v.883h.883zM23.3 42.763h-.883v.883h.883zM24.184 20.68H23.3v.884h.883zM24.184 22.447H23.3v.884h.883z"
  })), _path15$1 || (_path15$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M24.184 23.33H23.3v.884h.883zM24.184 24.214H23.3v.883h.883zM24.184 27.747H23.3v.883h.883zM24.184 28.63H23.3v.884h.883zM24.184 30.397H23.3v.883h.883zM24.184 31.28H23.3v.883h.883z"
  })), _path16$1 || (_path16$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M24.184 32.163H23.3v.884h.883zM24.184 34.813H23.3v.883h.883zM24.184 36.58H23.3v.883h.883zM24.184 37.463H23.3v.883h.883z"
  })), _path17$1 || (_path17$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M24.184 38.346H23.3v.883h.883zM24.184 39.23H23.3v.883h.883zM24.184 40.113H23.3v.883h.883zM24.184 40.996H23.3v.883h.883zM24.184 41.88H23.3v.883h.883zM24.184 43.646H23.3v.883h.883zM24.184 44.529H23.3v.883h.883zM25.067 17.148h-.884v.883h.884zM25.067 18.031h-.884v.883h.884zM25.067 19.798h-.884v.883h.884zM25.067 25.097h-.884v.883h.884zM25.067 25.98h-.884v.884h.884zM25.067 27.747h-.884v.883h.884zM25.067 28.63h-.884v.884h.884zM25.067 31.28h-.884v.883h.884z"
  })), _path18$1 || (_path18$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M25.067 32.163h-.884v.884h.884zM25.067 35.696h-.884v.884h.884zM25.067 37.463h-.884v.883h.884zM25.067 39.23h-.884v.883h.884zM25.067 40.113h-.884v.883h.884zM25.067 41.88h-.884v.883h.884zM25.95 17.148h-.883v.883h.883zM25.95 18.031h-.883v.883h.883zM25.95 21.564h-.883v.883h.883zM25.95 22.447h-.883v.884h.883zM25.95 25.097h-.883v.883h.883zM25.95 29.514h-.883v.883h.883zM25.95 30.397h-.883v.883h.883zM25.95 32.163h-.883v.884h.883zM25.95 33.93h-.883v.883h.883zM25.95 34.813h-.883v.883h.883z"
  })), _path19$1 || (_path19$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M25.95 35.696h-.883v.884h.883zM25.95 36.58h-.883v.883h.883zM25.95 38.346h-.883v.884h.883zM25.95 40.113h-.883v.883h.883zM25.95 41.88h-.883v.883h.883z"
  })), _path20$1 || (_path20$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M25.95 42.763h-.883v.883h.883zM25.95 44.53h-.883v.882h.883zM26.833 17.148h-.883v.883h.883zM26.833 19.798h-.883v.883h.883z"
  })), _path21$1 || (_path21$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M26.833 20.68h-.883v.884h.883zM26.833 24.214h-.883v.883h.883zM26.833 25.097h-.883v.883h.883zM26.833 25.98h-.883v.884h.883zM26.833 26.864h-.883v.883h.883zM26.833 28.63h-.883v.884h.883zM26.833 32.163h-.883v.884h.883zM26.833 36.58h-.883v.883h.883zM26.833 37.463h-.883v.883h.883z"
  })), _path22$1 || (_path22$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M26.833 38.346h-.883v.883h.883zM26.833 42.763h-.883v.883h.883zM26.833 43.646h-.883v.883h.883zM26.833 44.529h-.883v.883h.883zM27.717 17.148h-.884v.883h.884zM27.717 20.68h-.884v.884h.884zM27.717 21.564h-.884v.883h.884zM27.717 22.447h-.884v.884h.884z"
  })), _path23$1 || (_path23$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.717 23.33h-.884v.884h.884zM27.717 24.214h-.884v.883h.884zM27.717 27.747h-.884v.883h.884zM27.717 29.514h-.884v.883h.884zM27.717 30.397h-.884v.883h.884zM27.717 31.28h-.884v.883h.884zM27.717 33.047h-.884v.883h.884zM27.717 35.696h-.884v.884h.884zM27.717 36.58h-.884v.883h.884zM27.717 37.463h-.884v.883h.884z"
  })), _path24$1 || (_path24$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.717 38.346h-.884v.883h.884zM27.717 39.23h-.884v.883h.884zM27.717 40.996h-.884v.883h.884zM27.717 42.763h-.884v.883h.884zM27.717 44.53l-.884-.001v.883h.884zM28.6 18.031h-.883v.883h.883zM28.6 18.914h-.883v.884h.883z"
  })), _path25$1 || (_path25$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M28.6 19.798h-.883v.883h.883z"
  })), _path26$1 || (_path26$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M28.6 20.68h-.883v.884h.883zM28.6 21.564h-.883v.883h.883zM28.6 24.214h-.883v.883h.883zM28.6 27.747h-.883v.883h.883zM28.6 33.047h-.883v.883h.883zM28.6 34.813h-.883v.883h.883z"
  })), _path27$1 || (_path27$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M28.6 35.696h-.883v.884h.883zM28.6 38.346h-.883v.883h.883zM28.6 39.23h-.883v.883h.883zM28.6 40.113h-.883v.883h.883zM28.6 40.996h-.883v.883h.883zM28.6 44.53h-.883v.882h.883zM28.6 45.412h-.883v.884h.883zM29.483 17.148H28.6v.883h.883zM29.483 18.031H28.6v.883h.883zM29.483 19.798H28.6v.883h.883z"
  })), _path28$1 || (_path28$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.483 20.68H28.6v.884h.883zM29.483 22.447H28.6v.884h.883zM29.483 24.214H28.6v.883h.883zM29.483 25.097H28.6v.883h.883zM29.483 26.864H28.6v.883h.883zM29.483 29.514H28.6v.883h.883zM29.483 30.397H28.6v.883h.883zM29.483 33.93H28.6v.883h.883zM29.483 34.813H28.6v.883h.883zM29.483 39.23H28.6v.883h.883zM29.483 40.113H28.6v.883h.883zM29.483 40.996H28.6v.883h.883zM29.483 41.88H28.6v.883h.883z"
  })), _path29$1 || (_path29$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M29.483 42.763H28.6v.883h.883zM30.366 17.148h-.883v.883h.883zM30.366 18.914h-.883v.884h.883zM30.366 23.33h-.883v.884h.883zM30.366 24.214h-.883v.883h.883zM30.366 26.864h-.883v.883h.883zM30.366 27.747h-.883v.883h.883zM30.366 31.28h-.883v.883h.883zM30.366 33.93h-.883v.883h.883zM30.366 36.58h-.883v.883h.883zM30.366 37.463h-.883v.883h.883z"
  })), _path30$1 || (_path30$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M30.366 38.346h-.883v.884h.883zM30.366 43.646h-.883v.883h.883zM30.366 44.529h-.883v.883h.883zM31.25 18.914h-.884v.884h.884z"
  })), _path31$1 || (_path31$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.25 19.798h-.884v.883h.884z"
  })), _path32$1 || (_path32$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.25 20.68h-.884v.884h.884zM31.25 21.564h-.884v.883h.884zM31.25 22.447h-.884v.884h.884z"
  })), _path33$1 || (_path33$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.25 23.33h-.884v.884h.884zM31.25 24.214h-.884v.883h.884zM31.25 25.98h-.884v.884h.884zM31.25 26.864h-.884v.883h.884zM31.25 27.747h-.884v.883h.884zM31.25 28.63h-.884v.884h.884zM31.25 29.514h-.884v.883h.884zM31.25 33.047h-.884v.883h.884zM31.25 33.93h-.884v.883h.884zM31.25 36.58h-.884v.883h.884zM31.25 37.463h-.884v.883h.884zM31.25 39.23h-.884v.883h.884zM31.25 44.53l-.884-.001v.883h.884zM32.133 18.914h-.883v.884h.883zM32.133 21.564h-.883v.883h.883zM32.133 23.33h-.883v.884h.883zM32.133 25.097h-.883v.883h.883zM32.133 25.98h-.883v.884h.883zM32.133 26.864h-.883v.883h.883zM32.133 28.63h-.883v.884h.883zM32.133 31.28h-.883v.883h.883zM32.133 33.93h-.883v.883h.883zM32.133 34.813h-.883v.883h.883z"
  })), _path34$1 || (_path34$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M32.133 35.696h-.883v.884h.883zM32.133 36.58h-.883v.883h.883zM32.133 39.23h-.883v.883h.883zM32.133 40.113h-.883v.883h.883zM32.133 44.53h-.883v.882h.883zM33.016 17.148h-.883v.883h.883zM33.016 18.031h-.883v.883h.883zM33.016 18.914h-.883v.884h.883zM33.016 21.564h-.883v.883h.883zM33.016 22.447h-.883v.884h.883z"
  })), _path35$1 || (_path35$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.016 23.33h-.883v.884h.883zM33.016 25.98h-.883v.884h.883zM33.016 27.747h-.883v.883h.883zM33.016 29.514h-.883v.883h.883zM33.016 30.397h-.883v.883h.883zM33.016 32.163h-.883v.884h.883zM33.016 33.047h-.883v.883h.883zM33.016 35.696h-.883v.884h.883zM33.016 36.58h-.883v.883h.883zM33.016 38.346h-.883v.884h.883zM33.016 39.23h-.883v.883h.883zM33.016 40.113h-.883v.883h.883zM33.016 40.996h-.883v.883h.883zM33.016 41.88h-.883v.883h.883z"
  })), _path36 || (_path36 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.016 42.763h-.883v.883h.883zM33.016 45.412h-.883v.884h.883zM33.9 24.214h-.884v.883h.884zM33.9 25.98h-.884v.884h.884zM33.9 26.864h-.884v.883h.884zM33.9 27.747h-.884v.883h.884zM33.9 28.63h-.884v.884h.884zM33.9 33.047h-.884v.883h.884zM33.9 35.696h-.884v.884h.884zM33.9 37.463h-.884v.883h.884z"
  })), _path37 || (_path37 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M33.9 38.346h-.884v.883h.884zM33.9 41.88h-.884v.883h.884zM33.9 43.646h-.884v.883h.884zM33.9 44.529h-.884v.883h.884zM34.783 25.097H33.9v.883h.883zM34.783 28.63H33.9v.884h.883zM34.783 29.514H33.9v.883h.883zM34.783 30.397H33.9v.883h.883zM34.783 31.28H33.9v.883h.883zM34.783 34.813H33.9v.883h.883zM34.783 38.346H33.9v.883h.883zM34.783 40.113H33.9v.883h.883zM34.783 41.88H33.9v.883h.883z"
  })), _path38 || (_path38 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M34.783 42.763H33.9v.883h.883zM34.783 44.53l-.883-.001v.883h.883zM35.666 25.097h-.883v.883h.883zM35.666 28.63h-.883v.884h.883zM35.666 30.397h-.883v.883h.883zM35.666 36.58h-.883v.883h.883zM35.666 38.346h-.883v.883h.883z"
  })), _path39 || (_path39 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M35.666 41.88h-.883v.883h.883zM35.666 44.53h-.883v.882h.883zM35.666 45.412h-.883v.884h.883zM36.55 24.214h-.884v.883h.883zM36.55 26.864h-.884v.883h.883zM36.55 27.747h-.884v.883h.883zM36.55 29.514h-.884v.883h.883zM36.55 30.397h-.884v.883h.883zM36.55 33.93h-.884v.883h.883zM36.55 36.58h-.884v.883h.883zM36.55 38.346h-.884v.883h.883zM36.55 39.23h-.884v.883h.883zM36.55 40.113h-.884v.883h.883zM36.55 40.996h-.884v.883h.883zM36.55 41.88h-.884v.883h.883z"
  })), _path40 || (_path40 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M36.55 42.763h-.884v.883h.883zM36.55 44.53h-.884v.882h.883zM36.55 45.412h-.884v.884h.883zM37.433 25.98h-.884v.884h.883zM37.433 26.864h-.884v.883h.883zM37.433 27.747h-.884v.883h.883zM37.433 28.63h-.884v.884h.883zM37.433 32.163h-.884v.884h.883zM37.433 33.93h-.884v.883h.883zM37.433 34.813h-.884v.883h.883zM37.433 37.463h-.884v.883h.883zM37.433 40.113h-.884v.883h.883zM37.433 40.996h-.884v.883h.883z"
  })), _path41 || (_path41 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M37.433 41.88h-.884v.883h.883zM37.433 43.646h-.884v.883h.883zM37.433 44.529h-.884v.883h.883zM38.316 27.747h-.884v.883h.884zM38.316 29.514h-.884v.883h.884zM38.316 32.163h-.884v.884h.884zM38.316 34.813h-.884v.883h.884z"
  })), _path42 || (_path42 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M38.316 35.696h-.884v.884h.884zM38.316 39.23h-.884v.883h.884zM38.316 40.113h-.884v.883h.884zM38.316 40.996h-.884v.883h.884zM38.316 41.88h-.884v.883h.884z"
  })), _path43 || (_path43 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M38.316 42.763h-.884v.883h.884zM38.316 43.646h-.884v.883h.884zM39.2 24.214h-.884v.883h.883zM39.2 25.097h-.884v.883h.883zM39.2 29.514h-.884v.883h.883zM39.2 30.397h-.884v.883h.883zM39.2 33.047h-.884v.883h.883zM39.2 35.696h-.884v.884h.883zM39.2 38.346h-.884v.884h.883zM39.2 42.763h-.884v.883h.883zM40.082 25.097H39.2v.883h.883zM40.082 27.747H39.2v.883h.883zM40.082 29.514H39.2v.883h.883zM40.082 33.93H39.2v.883h.883zM40.082 34.813H39.2v.883h.883z"
  })), _path44 || (_path44 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M40.082 35.696H39.2v.884h.883zM40.082 36.58H39.2v.883h.883zM40.082 38.346H39.2v.883h.883zM40.082 45.412H39.2v.884h.883z"
  })), _path45 || (_path45 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M10.935 17.148v6.183h6.182v-6.183zm.883.883h4.416v4.416h-4.416z",
    clipRule: "evenodd"
  })), _path46 || (_path46 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M15.35 18.914h-2.649v2.65h2.65z"
  })), _path47 || (_path47 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M40.082 17.148H33.9v6.183h6.183zm-.883.883v4.416h-4.416v-4.416z",
    clipRule: "evenodd"
  })), _path48 || (_path48 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M38.316 21.564v-2.65h-2.65v2.65z"
  })), _path49 || (_path49 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M38.316 18.914h-2.65v2.65h2.65z"
  })), _path50 || (_path50 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M10.935 46.296h6.182v-6.183h-6.182zm.883-.884v-4.416h4.416v4.416z",
    clipRule: "evenodd"
  })), _path51 || (_path51 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M15.35 41.88h-2.649v2.65h2.65z"
  })), _path52 || (_path52 = /*#__PURE__*/React.createElement("path", {
    fill: "#404040",
    d: "M15.536 9.605a2.43 2.43 0 0 1 2.43-2.429h14.573a2.429 2.429 0 1 1 0 4.858H17.965a2.43 2.43 0 0 1-2.429-2.429"
  })), _path53 || (_path53 = /*#__PURE__*/React.createElement("path", {
    fill: "#404040",
    fillRule: "evenodd",
    d: "M25.252 9.605a2.43 2.43 0 1 0 0-4.858 2.43 2.43 0 0 0 0 4.858m0-1.214a1.215 1.215 0 1 0 0-2.43 1.215 1.215 0 0 0 0 2.43",
    clipRule: "evenodd"
  })));
};

var _path$s, _path2$2, _path3$1, _path4, _path5, _path6, _path7, _path8, _path9, _path10, _path11, _path12, _path13, _path14, _path15, _path16, _path17, _path18, _path19, _path20, _path21, _path22, _path23, _path24, _path25, _path26, _path27, _path28, _path29, _path30, _path31, _path32, _path33, _path34, _path35;
function _extends$u() { return _extends$u = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$u.apply(null, arguments); }
var SvgFrameEnvelope = function SvgFrameEnvelope(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$u({
    xmlns: "http://www.w3.org/2000/svg",
    width: 50,
    height: 66,
    fill: "none"
  }, props), _path$s || (_path$s = /*#__PURE__*/React.createElement("path", {
    fill: "#404040",
    stroke: "#404040",
    strokeWidth: 1.244,
    d: "M6.771 56.813V34.224c0-.37.166-.723.452-.959l16.871-13.94a1.244 1.244 0 0 1 1.57-.012l17.458 13.952c.296.236.468.593.468.972v22.575c0 .688-.557 1.245-1.245 1.245H8.015a1.244 1.244 0 0 1-1.244-1.245Z"
  })), _path2$2 || (_path2$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    stroke: "#404040",
    strokeWidth: 1.243,
    d: "M10.863 8.565h28.636c.787 0 1.424.637 1.424 1.424v38.863c0 .787-.637 1.425-1.424 1.425H10.863a1.424 1.424 0 0 1-1.424-1.425V9.99c0-.787.637-1.424 1.424-1.424Z"
  })), _path3$1 || (_path3$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    stroke: "#404040",
    strokeWidth: 1.244,
    d: "M42.483 57.082 28.5 46.553a.622.622 0 0 1 .032-1.016l13.983-9.213a.622.622 0 0 1 .965.52v19.741a.622.622 0 0 1-.997.497ZM7.88 36.532 21.863 47.06a.622.622 0 0 1-.032 1.017L7.847 57.289a.622.622 0 0 1-.964-.52V37.03c0-.514.586-.806.996-.498Z"
  })), _path4 || (_path4 = /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    stroke: "#404040",
    strokeWidth: 1.244,
    d: "M6.771 56.788v-1.936c0-.375.163-.73.445-.972l16.87-14.446a1.226 1.226 0 0 1 1.585-.014L43.13 53.88c.29.24.46.603.46.985v1.923c0 .7-.557 1.269-1.245 1.269H8.015a1.257 1.257 0 0 1-1.244-1.27Z"
  })), _path5 || (_path5 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M13.652 18.728h-.744v.744h.744zM13.652 19.472h-.744v.744h.744zM13.652 20.216h-.744v.744h.744zM13.652 22.447h-.744v.744h.744zM13.652 23.191h-.744v.744h.744zM13.652 25.423h-.744v.743h.744zM13.652 26.91h-.744v.744h.744zM13.652 28.398h-.744v.744h.744zM13.652 29.142h-.744v.743h.744zM14.396 20.216h-.744v.744h.744zM14.396 20.96h-.744v.743h.744zM14.396 21.704h-.744v.743h.744zM14.396 23.191h-.744v.744h.744zM14.396 25.423h-.744v.743h.744zM14.396 26.166h-.744v.744h.744zM14.396 26.91h-.744v.744h.744zM15.14 17.985h-.744v.743h.744zM15.14 19.472h-.744v.744h.744zM15.14 20.216h-.744v.744h.744zM15.14 24.679h-.744v.744h.744zM15.14 25.423h-.744v.743h.744zM15.14 26.166h-.744v.744h.744zM15.14 27.654h-.744v.744h.744zM15.14 28.398h-.744v.744h.744zM15.14 29.142h-.744v.743h.744zM15.883 18.728h-.743v.744h.743z"
  })), _path6 || (_path6 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M15.883 19.472h-.743v.744h.743zM15.883 20.216h-.743v.744h.743zM15.883 20.96h-.743v.743h.743zM15.883 21.704h-.743v.743h.743zM15.883 23.191h-.743v.744h.743zM15.883 24.679h-.743v.744h.743zM15.883 27.654h-.743v.744h.743zM15.883 28.398h-.743v.744h.743zM15.883 29.142h-.743v.743h.743zM15.883 29.885h-.743v.744h.743zM16.627 17.985h-.744v.743h.744zM16.627 21.704h-.744v.743h.744zM16.627 22.447h-.744v.744h.744zM16.627 23.191h-.744v.744h.744zM16.627 23.935h-.744v.744h.744zM16.627 24.679h-.744v.744h.744zM16.627 26.166h-.744v.744h.744zM16.627 29.142h-.744v.743h.744zM16.627 29.885h-.744v.744h.744zM17.371 17.985h-.744v.743h.744zM17.371 18.728h-.744v.744h.744zM17.371 19.472h-.744v.744h.744zM17.371 22.447h-.744v.744h.744zM17.371 23.935h-.744v.744h.744zM17.371 24.679h-.744v.744h.744zM17.371 26.91h-.744v.744h.744zM17.371 27.654h-.744v.744h.744zM18.115 17.985h-.744v.743h.744zM18.115 19.472h-.744v.744h.744zM18.115 20.96h-.744v.743h.744zM18.115 22.447h-.744v.744h.744zM18.115 23.935h-.744v.744h.744zM18.115 25.423h-.744v.743h.744zM18.115 26.91h-.744v.744h.744zM18.115 28.398h-.744v.744h.744zM18.115 29.885h-.744v.744h.744zM18.859 19.472h-.744v.744h.744zM18.859 20.216h-.744v.744h.744zM18.859 20.96h-.744v.743h.744zM18.859 22.447h-.744v.744h.744zM18.859 23.935h-.744v.744h.744zM18.859 24.679h-.744v.744h.744zM18.859 26.166h-.744v.744h.744zM18.859 26.91h-.744v.744h.744zM18.859 27.654h-.744v.744h.744zM18.859 28.398h-.744v.744h.744zM18.859 29.142h-.744v.743h.744zM19.602 12.778h-.743v.744h.743zM19.602 15.01h-.743v.743h.743zM19.602 16.497h-.743v.744h.743zM19.602 17.985h-.743v.743h.743zM19.602 18.728h-.743v.744h.743zM19.602 19.472h-.743v.744h.743zM19.602 20.216h-.743v.744h.743zM19.602 23.191h-.743v.744h.743zM19.602 23.935h-.743v.744h.743zM19.602 24.679h-.743v.744h.743zM19.602 27.654h-.743v.744h.743zM19.602 28.398h-.743v.744h.743zM19.602 30.63h-.743v.743h.743zM19.602 32.117h-.743v.744h.743z"
  })), _path7 || (_path7 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M19.602 32.86h-.743v.744h.743zM19.602 34.348h-.743v.744h.743zM20.346 12.034h-.744v.744h.744zM20.346 14.266h-.744v.743h.744zM20.346 15.01h-.744v.743h.744zM20.346 15.753h-.744v.744h.744zM20.346 17.24h-.744v.745h.744zM20.346 18.728h-.744v.744h.744zM20.346 19.472h-.744v.744h.744zM20.346 20.216h-.744v.744h.744zM20.346 20.96h-.744v.743h.744zM20.346 21.704h-.744v.743h.744zM20.346 22.447h-.744v.744h.744zM20.346 23.191h-.744v.744h.744zM20.346 24.679h-.744v.744h.744zM20.346 25.423h-.744v.743h.744zM20.346 27.654h-.744v.744h.744zM20.346 29.142h-.744v.743h.744zM20.346 30.63h-.744v.743h.744zM20.346 33.604h-.744v.744h.744zM20.346 34.348h-.744v.744h.744zM20.346 35.092h-.744v.744h.744zM21.09 12.034h-.744v.744h.744zM21.09 15.753h-.744v.744h.744zM21.09 16.497h-.744v.744h.744zM21.09 20.216h-.744v.744h.744zM21.09 20.96h-.744v.743h.744zM21.09 21.704h-.744v.743h.744zM21.09 22.447h-.744v.744h.744zM21.09 25.423h-.744v.743h.744zM21.09 26.91h-.744v.744h.744zM21.09 29.142h-.744v.743h.744zM21.09 30.63h-.744v.743h.744zM21.09 33.604h-.744v.744h.744zM21.834 12.778h-.744v.744h.744zM21.834 15.01h-.744v.743h.744zM21.834 15.753h-.744v.744h.744zM21.834 17.24h-.744v.745h.744zM21.834 20.216h-.744v.744h.744zM21.834 22.447h-.744v.744h.744zM21.834 23.191h-.744v.744h.744zM21.834 26.166h-.744v.744h.744zM21.834 26.91h-.744v.744h.744zM21.834 28.398h-.744v.744h.744zM21.834 29.885h-.744v.744h.744zM21.834 30.63h-.744v.743h.744zM21.834 31.373h-.744v.744h.744zM21.834 35.092h-.744v.744h.744zM22.578 12.778h-.744v.744h.744zM22.578 16.497h-.744v.744h.744zM22.578 17.985h-.744v.743h.744zM22.578 18.728h-.744v.744h.744zM22.578 20.96h-.744v.743h.744zM22.578 21.704h-.744v.743h.744zM22.578 22.447h-.744v.744h.744zM22.578 28.398h-.744v.744h.744zM22.578 29.142h-.744v.743h.744zM22.578 29.885h-.744v.744h.744zM22.578 30.63h-.744v.743h.744zM22.578 31.373h-.744v.744h.744z"
  })), _path8 || (_path8 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M22.578 32.117h-.744v.744h.744z"
  })), _path9 || (_path9 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M22.578 32.86h-.744v.744h.744zM22.578 33.604h-.744v.744h.744zM23.321 12.034h-.743v.744h.743zM23.321 15.01h-.743v.743h.743zM23.321 17.24h-.743v.745h.743zM23.321 18.728h-.743v.744h.743zM23.321 19.472h-.743v.744h.743zM23.321 20.216h-.743v.744h.743zM23.321 20.96h-.743v.743h.743zM23.321 21.704h-.743v.743h.743zM23.321 23.191h-.743v.744h.743zM23.321 24.679h-.743v.744h.743zM23.321 25.423h-.743v.743h.743zM23.321 26.166h-.743v.744h.743zM23.321 26.91h-.743v.744h.743zM23.321 33.604h-.743v.744h.743zM24.065 15.01h-.744v.743h.744zM24.065 16.497h-.744v.744h.744z"
  })), _path10 || (_path10 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M24.065 17.24h-.744v.745h.744zM24.065 17.985h-.744v.743h.744zM24.065 20.96h-.744v.743h.744zM24.065 21.704h-.744v.743h.744zM24.065 23.191h-.744v.744h.744zM24.065 23.935h-.744v.744h.744zM24.065 24.679h-.744v.744h.744zM24.065 26.91h-.744v.744h.744zM24.065 28.398h-.744v.744h.744zM24.065 29.142h-.744v.743h.744zM24.065 29.885h-.744v.744h.744zM24.065 30.63h-.744v.743h.744zM24.065 31.373h-.744v.744h.744z"
  })), _path11 || (_path11 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M24.065 32.117h-.744v.744h.744z"
  })), _path12 || (_path12 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M24.065 32.86h-.744v.744h.744zM24.065 34.348h-.744v.744h.744zM24.065 35.092h-.744v.744h.744zM24.809 12.034h-.744v.744h.744zM24.809 12.778h-.744v.744h.744zM24.809 14.266h-.744v.743h.744zM24.809 18.728h-.744v.744h.744zM24.809 19.472h-.744v.744h.744zM24.809 20.96h-.744v.743h.744zM24.809 21.704h-.744v.743h.744zM24.809 23.935h-.744v.744h.744zM24.809 24.679h-.744v.744h.744zM24.809 27.654h-.744v.744h.744zM24.809 29.142h-.744v.743h.744zM24.809 30.63h-.744v.743h.744zM24.809 31.373h-.744v.744h.744zM24.809 32.86h-.744v.744h.744zM25.553 12.034h-.744v.744h.744zM25.553 12.778h-.744v.744h.744zM25.553 15.753h-.744v.744h.744zM25.553 16.497h-.744v.744h.744zM25.553 18.728h-.744v.744h.744zM25.553 22.447h-.744v.744h.744zM25.553 23.191h-.744v.744h.744zM25.553 24.679h-.744v.744h.744zM25.553 26.166h-.744v.744h.744zM25.553 26.91h-.744v.744h.744zM25.553 27.654h-.744v.744h.744zM25.553 28.398h-.744v.744h.744zM25.553 29.885h-.744v.744h.744zM25.553 31.373h-.744v.744h.744zM25.553 32.86h-.744v.744h.744zM25.553 33.604h-.744v.744h.744zM25.553 35.092h-.744v.744h.744zM26.297 12.034h-.744v.744h.744zM26.297 14.266h-.744v.743h.744zM26.297 15.01h-.744v.743h.744zM26.297 17.985h-.744v.743h.744zM26.297 18.728h-.744v.744h.744zM26.297 19.472h-.744v.744h.744zM26.297 20.216h-.744v.744h.744zM26.297 21.704h-.744v.743h.744zM26.297 24.679h-.744v.744h.744zM26.297 28.398h-.744v.744h.744zM26.297 29.142h-.744v.743h.744zM26.297 29.885h-.744v.744h.744zM26.297 33.604h-.744v.744h.744zM26.297 34.348h-.744v.744h.744zM26.297 35.092h-.744v.744h.744zM27.04 12.034h-.743v.744h.743zM27.04 15.01h-.743v.743h.743zM27.04 15.753h-.743v.744h.743zM27.04 16.497h-.743v.744h.743z"
  })), _path13 || (_path13 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.04 17.24h-.743v.745h.743zM27.04 17.985h-.743v.743h.743zM27.04 20.96h-.743v.743h.743zM27.04 22.447h-.743v.744h.743zM27.04 23.191h-.743v.744h.743zM27.04 23.935h-.743v.744h.743zM27.04 25.423h-.743v.743h.743zM27.04 27.654h-.743v.744h.743zM27.04 28.398h-.743v.744h.743zM27.04 29.142h-.743v.743h.743zM27.04 29.885h-.743v.744h.743zM27.04 30.63h-.743v.743h.743zM27.04 32.117h-.743v.744h.743zM27.04 33.604h-.743v.744h.743zM27.04 35.092h-.743v.744h.743zM27.784 12.778h-.744v.744h.744zM27.784 13.522h-.744v.743h.744zM27.784 14.266h-.744v.743h.744zM27.784 15.01h-.744v.743h.744zM27.784 15.753h-.744v.744h.744zM27.784 17.985h-.744v.743h.744zM27.784 20.96h-.744v.743h.744zM27.784 25.423h-.744v.743h.744zM27.784 26.91h-.744v.744h.744zM27.784 27.654h-.744v.744h.744zM27.784 29.885h-.744v.744h.744zM27.784 30.63h-.744v.743h.744zM27.784 31.373h-.744v.744h.744z"
  })), _path14 || (_path14 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M27.784 32.117h-.744v.744h.744zM27.784 35.092h-.744v.744h.744zM27.784 35.836h-.744v.744h.744zM28.528 12.034h-.744v.744h.744zM28.528 12.778h-.744v.744h.744zM28.528 14.266h-.744v.743h.744zM28.528 15.01h-.744v.743h.744zM28.528 16.497h-.744v.744h.744zM28.528 17.985h-.744v.743h.744zM28.528 18.728h-.744v.744h.744zM28.528 20.216h-.744v.744h.744zM28.528 22.447h-.744v.744h.744zM28.528 23.191h-.744v.744h.744zM28.528 26.166h-.744v.744h.744zM28.528 26.91h-.744v.744h.744zM28.528 30.63h-.744v.743h.744zM28.528 31.373h-.744v.744h.744z"
  })), _path15 || (_path15 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M28.528 32.117h-.744v.744h.744z"
  })), _path16 || (_path16 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M28.528 32.86h-.744v.744h.744zM28.528 33.604h-.744v.744h.744zM29.272 12.034h-.744v.744h.744zM29.272 13.522h-.744v.743h.744zM29.272 17.24h-.744v.745h.744zM29.272 17.985h-.744v.743h.744zM29.272 20.216h-.744v.744h.744zM29.272 20.96h-.744v.743h.744zM29.272 23.935h-.744v.744h.744zM29.272 26.166h-.744v.744h.744zM29.272 28.398h-.744v.744h.744zM29.272 29.142h-.744v.743h.744zM29.272 29.885h-.744v.744h.744zM29.272 34.348h-.744v.744h.744zM29.272 35.092h-.744v.744h.744zM30.016 13.522h-.744v.743h.744zM30.016 14.266h-.744v.743h.744zM30.016 15.01h-.744v.743h.744zM30.016 15.753h-.744v.744h.744zM30.016 16.497h-.744v.744h.744z"
  })), _path17 || (_path17 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M30.016 17.24h-.744v.745h.744zM30.016 17.985h-.744v.743h.744zM30.016 19.472h-.744v.744h.744zM30.016 20.216h-.744v.744h.744zM30.016 20.96h-.744v.743h.744zM30.016 21.704h-.744v.743h.744zM30.016 22.447h-.744v.744h.744zM30.016 25.423h-.744v.743h.744zM30.016 26.166h-.744v.744h.744zM30.016 28.398h-.744v.744h.744zM30.016 29.142h-.744v.743h.744zM30.016 30.63h-.744v.743h.744zM30.016 35.092h-.744v.744h.744zM30.76 13.522h-.744v.743h.744zM30.76 15.753h-.744v.744h.744zM30.76 17.24h-.744v.745h.744zM30.76 18.728h-.744v.744h.744zM30.76 19.472h-.744v.744h.744zM30.76 20.216h-.744v.744h.744zM30.76 21.704h-.744v.743h.744zM30.76 23.935h-.744v.744h.744zM30.76 26.166h-.744v.744h.744zM30.76 26.91h-.744v.744h.744zM30.76 27.654h-.744v.744h.744zM30.76 28.398h-.744v.744h.744zM30.76 30.63h-.744v.743h.744zM30.76 31.373h-.744v.744h.744zM30.76 35.092h-.744v.744h.744zM31.503 12.034h-.744v.744h.744zM31.503 12.778h-.744v.744h.744z"
  })), _path18 || (_path18 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.503 13.522h-.744v.743h.744zM31.503 15.753h-.744v.744h.744zM31.503 16.497h-.744v.744h.744z"
  })), _path19 || (_path19 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.503 17.24h-.744v.745h.744zM31.503 19.472h-.744v.744h.744zM31.503 20.96h-.744v.743h.744zM31.503 22.447h-.744v.744h.744zM31.503 23.191h-.744v.744h.744zM31.503 24.679h-.744v.744h.744zM31.503 25.423h-.744v.743h.744zM31.503 27.654h-.744v.744h.744zM31.503 28.398h-.744v.744h.744zM31.503 29.885h-.744v.744h.744zM31.503 30.63h-.744v.743h.744zM31.503 31.373h-.744v.744h.744z"
  })), _path20 || (_path20 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.503 32.117h-.744v.744h.744z"
  })), _path21 || (_path21 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M31.503 32.86h-.744v.744h.744zM31.503 33.604h-.744v.744h.744zM31.503 35.836h-.744v.744h.744zM32.247 17.985h-.744v.743h.744zM32.247 19.472h-.744v.744h.744zM32.247 20.216h-.744v.744h.744zM32.247 20.96h-.744v.743h.744zM32.247 21.704h-.744v.743h.744zM32.247 25.423h-.744v.743h.744zM32.247 27.654h-.744v.744h.744zM32.247 29.142h-.744v.743h.744zM32.247 29.885h-.744v.744h.744zM32.247 32.86h-.744v.744h.744zM32.247 34.348h-.744v.744h.744zM32.247 35.092h-.744v.744h.744zM32.99 18.728h-.743v.744h.744zM32.99 21.704h-.743v.743h.744zM32.99 22.447h-.743v.744h.744zM32.99 23.191h-.743v.744h.744zM32.99 23.935h-.743v.744h.744zM32.99 26.91h-.743v.744h.744zM32.99 29.885h-.743v.744h.744zM32.99 31.373h-.743v.744h.744zM32.99 32.86h-.743v.744h.744zM32.99 33.604h-.743v.744h.744zM32.99 35.092h-.743v.744h.744zM33.735 18.728h-.744v.744h.744zM33.735 21.704h-.744v.743h.744zM33.735 23.191h-.744v.744h.744zM33.735 28.398h-.744v.744h.744zM33.735 29.885h-.744v.744h.744zM33.735 32.86h-.744v.744h.744zM33.735 35.092h-.744v.744h.744zM33.735 35.836h-.744v.744h.744zM34.479 17.985h-.744v.743h.743zM34.479 20.216h-.744v.744h.743zM34.479 20.96h-.744v.743h.743zM34.479 22.447h-.744v.744h.743zM34.479 23.191h-.744v.744h.743zM34.479 26.166h-.744v.744h.743zM34.479 28.398h-.744v.744h.743zM34.479 29.885h-.744v.744h.743zM34.479 30.63h-.744v.743h.743zM34.479 31.373h-.744v.744h.743z"
  })), _path22 || (_path22 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M34.479 32.117h-.744v.744h.743z"
  })), _path23 || (_path23 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M34.479 32.86h-.744v.744h.743zM34.479 33.604h-.744v.744h.743zM34.479 35.092h-.744v.744h.743zM34.479 35.836h-.744v.744h.743zM35.222 19.472h-.744v.744h.744zM35.222 20.216h-.744v.744h.744zM35.222 20.96h-.744v.743h.744zM35.222 21.704h-.744v.743h.744zM35.222 24.679h-.744v.744h.744zM35.222 26.166h-.744v.744h.744zM35.222 26.91h-.744v.744h.744zM35.222 29.142h-.744v.743h.744zM35.222 31.373h-.744v.744h.744z"
  })), _path24 || (_path24 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M35.222 32.117h-.744v.744h.744z"
  })), _path25 || (_path25 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M35.222 32.86h-.744v.744h.744zM35.222 34.348h-.744v.744h.744zM35.222 35.092h-.744v.744h.744zM35.966 20.96h-.744v.743h.744zM35.966 22.447h-.744v.744h.744zM35.966 24.679h-.744v.744h.744zM35.966 26.91h-.744v.744h.744zM35.966 27.654h-.744v.744h.744zM35.966 30.63h-.744v.743h.744zM35.966 31.373h-.744v.744h.744z"
  })), _path26 || (_path26 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M35.966 32.117h-.744v.744h.744z"
  })), _path27 || (_path27 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M35.966 32.86h-.744v.744h.744zM35.966 33.604h-.744v.744h.744zM35.966 34.348h-.744v.744h.744zM36.71 17.985h-.744v.743h.744zM36.71 18.728h-.744v.744h.744zM36.71 22.447h-.744v.744h.744zM36.71 23.191h-.744v.744h.744zM36.71 25.423h-.744v.743h.744zM36.71 27.654h-.744v.744h.744zM36.71 29.885h-.744v.744h.744zM36.71 33.604h-.744v.744h.744zM37.454 18.728h-.744v.744h.744zM37.454 20.96h-.744v.743h.744zM37.454 22.447h-.744v.744h.744zM37.454 26.166h-.744v.744h.744zM37.454 26.91h-.744v.744h.744zM37.454 27.654h-.744v.744h.744zM37.454 28.398h-.744v.744h.744zM37.454 29.885h-.744v.744h.744zM37.454 35.836h-.744v.744h.744z"
  })), _path28 || (_path28 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M12.908 12.034v5.207h5.207v-5.207zm.744.744h3.719v3.719h-3.719z",
    clipRule: "evenodd"
  })), _path29 || (_path29 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M16.627 13.522h-2.231v2.231h2.231z"
  })), _path30 || (_path30 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M37.454 12.034h-5.207v5.207h5.207zm-.744.744v3.719h-3.72v-3.72z",
    clipRule: "evenodd"
  })), _path31 || (_path31 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M35.966 15.753v-2.231h-2.231v2.231z"
  })), _path32 || (_path32 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M35.966 13.522h-2.231v2.231h2.231z"
  })), _path33 || (_path33 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    fillRule: "evenodd",
    d: "M12.908 36.58h5.207v-5.207h-5.207zm.744-.744v-3.72h3.719v3.72z",
    clipRule: "evenodd"
  })), _path34 || (_path34 = /*#__PURE__*/React.createElement("path", {
    fill: "#262626",
    d: "M16.627 32.86h-2.231v2.232h2.231z"
  })), _path35 || (_path35 = /*#__PURE__*/React.createElement("path", {
    fill: "#404040",
    d: "M16.73 54.262q-.375 0-.707-.102a1.6 1.6 0 0 1-.558-.289l.2-.437q.237.18.498.265.265.084.572.084.34 0 .521-.117a.38.38 0 0 0 .186-.335.31.31 0 0 0-.172-.284q-.168-.098-.563-.186a3.4 3.4 0 0 1-.665-.204.94.94 0 0 1-.386-.298.77.77 0 0 1-.13-.46q0-.29.158-.517t.446-.354q.289-.13.666-.13.345 0 .656.107.311.102.511.284l-.2.437a1.52 1.52 0 0 0-.967-.349q-.317 0-.503.13a.4.4 0 0 0-.181.354q0 .19.163.293t.553.19q.415.095.67.21.261.112.396.289.14.176.14.446a.87.87 0 0 1-.159.517 1 1 0 0 1-.451.34 1.9 1.9 0 0 1-.694.116M19.816 54.262q-.483 0-.847-.205a1.4 1.4 0 0 1-.553-.59 1.96 1.96 0 0 1-.191-.89q0-.507.19-.884.195-.382.554-.586.363-.21.847-.21.33 0 .624.103.293.102.493.289l-.2.437a1.6 1.6 0 0 0-.442-.256 1.3 1.3 0 0 0-.466-.084q-.474 0-.735.307-.255.308-.256.885 0 .58.256.888.261.307.735.307.247 0 .466-.079.218-.084.442-.26l.2.437q-.2.186-.493.288a1.9 1.9 0 0 1-.624.103M23.671 54.22l-.33-.768h-1.596l-.33.768h-.606l1.485-3.28h.488l1.485 3.28zm-1.73-1.233h1.2l-.6-1.396zM26.564 50.94h.563v3.28h-.452l-1.759-2.29v2.29h-.558v-3.28h.447l1.759 2.284zM31.312 50.94h.47v3.28h-.53v-2.168l-.866 1.638h-.4l-.87-1.615.004 2.145h-.53v-3.28h.474l1.126 2.145zM32.234 54.22v-3.28h2.197v.474h-1.61v.903h1.517v.47H32.82v.958h1.61v.475z"
  })));
};

const getFrameItems = (selectedItem) => {
    const frameItems = [
        {
            id: "none",
            content: React__default.createElement(SvgFrameNone, null),
            selected: selectedItem === undefined || selectedItem === "none",
        },
        {
            id: "mobile",
            content: React__default.createElement(SvgFrameMobile, null),
            selected: selectedItem === "mobile",
        },
        { id: "black", content: React__default.createElement(SvgFrameBlack, null), selected: selectedItem === "black" },
        { id: "white", content: React__default.createElement(SvgFrameWhite, null), selected: selectedItem === "white" },
        { id: "button", content: React__default.createElement(SvgFrameButton, null), selected: selectedItem === "button" },
        { id: "ticket", content: React__default.createElement(SvgFrameTicket, null), selected: selectedItem === "ticket" },
        { id: "museum", content: React__default.createElement(SvgFrameMuseum, null), selected: selectedItem === "museum" },
        { id: "cafe", content: React__default.createElement(SvgFrameCafe, null), selected: selectedItem === "cafe" },
        { id: "board", content: React__default.createElement(SvgFrameBoard, null), selected: selectedItem === "board" },
        { id: "envelope", content: React__default.createElement(SvgFrameEnvelope, null), selected: selectedItem === "envelope" },
    ];
    return frameItems;
};

const PickerContainer = styled.div `
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${getSpacing("size-03")};
`;
const PickerItemButton = styled.button `
  border: 2px solid ${getColor("neutral", 300)};
  border-radius: ${getSpacing("size-03")};
  background-color: ${getColor("neutral", "white")};
  font-family: ${getFont("family-primary")};
  ${getPadding("size-03")};
  cursor: ${({ $isDisabled }) => ($isDisabled ? "not-allowed" : "pointer")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 0.2s,
    background-color 0.2s;
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.6 : 1)};

  &:hover {
    border-color: ${({ $isDisabled }) => $isDisabled ? getColor("neutral", 300) : getColor("primary", 500)};
  }

  ${({ $isSelected }) => $isSelected &&
    css `
      border-color: ${getColor("primary", 500)};
    `}

  ${({ $isDisabled }) => $isDisabled &&
    css `
      border-color: ${getColor("neutral", 300)};
      cursor: not-allowed;
      opacity: 0.5;
    `}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Picker = ({ items, multiple = false, maxSelectedItems, onChange, dataQA, className, style, disabled, }) => {
    const initialSelectedIds = items.filter((item) => item.selected).map((item) => item.id);
    if (!multiple && initialSelectedIds.length === 0 && items.length > 0) {
        initialSelectedIds.push(items[0].id);
    }
    const [selectedIds, setSelectedIds] = useState(initialSelectedIds);
    useEffect(() => {
        onChange && onChange(selectedIds);
    }, [selectedIds]);
    const handleItemClick = (id) => {
        if (multiple) {
            const isSelected = selectedIds.includes(id);
            let newSelectedIds;
            if (isSelected) {
                newSelectedIds = selectedIds.filter((selectedId) => selectedId !== id);
            }
            else {
                if (maxSelectedItems !== undefined && selectedIds.length >= maxSelectedItems)
                    return;
                newSelectedIds = [...selectedIds, id];
            }
            setSelectedIds(newSelectedIds);
        }
        else {
            setSelectedIds([id]);
        }
    };
    return (React__default.createElement(PickerContainer, { className: className, "data-qa": dataQA, style: style }, items.map((item) => {
        const isSelected = selectedIds.includes(item.id);
        const isDisabled = !isSelected &&
            multiple &&
            maxSelectedItems !== undefined &&
            selectedIds.length >= maxSelectedItems;
        return (React__default.createElement(PickerItemButton, { key: item.id, "data-qa": `${dataQA}-${item.id}`, type: "button", onClick: () => handleItemClick(item.id), "$isSelected": isSelected, "$isDisabled": isDisabled || disabled, disabled: isDisabled || disabled }, item.content));
    })));
};

const StyledQRFrames = styled.div ``;
const StyledQRFramePicker = styled(Picker) `
  ${getMarginY("size-06")};
  display: flex;
  justify-content: center;
  gap: ${getSpacing("size-05")};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    gap: ${getSpacing("size-04")};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: flex-start;
  }

  > button {
    ${getPadding("size-03")};
  }
`;
const StyledInputGroup = styled.div `
  ${getPadding("size-06")};
  ${getMarginTop("size-05")};
  background-color: ${getColor("primary", 50)};
`;
const StyledFrameTextWrapper = styled(StyledInputGroup) `
  display: flex;
  flex-direction: column;
  gap: ${getSpacing("size-00")};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: row;
    gap: ${getSpacing("size-12")};
  }

  > div {
    width: 100%;
  }
`;
const StyledFrameColorWrapper = styled(StyledInputGroup) `
  display: flex;
  flex-direction: column;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: row;
  }
`;
const StyledQRFramesContainer = styled.div `
  display: ${({ $isFrameSelected }) => ($isFrameSelected ? "block" : "none")};
`;

const QRFrames = () => {
    const qrStyle = useQRStore((state) => { var _a; return (_a = state.qrData) === null || _a === void 0 ? void 0 : _a.qrStyle; });
    const isFrameSelected = (qrStyle === null || qrStyle === void 0 ? void 0 : qrStyle.frameStyle) !== "none";
    return (React__default.createElement(StyledQRFrames, null,
        React__default.createElement(Field, { name: "frameStyle" }, ({ onChange }) => (React__default.createElement(StyledQRFramePicker, { dataQA: "qr-style-frame-picker", items: getFrameItems(qrStyle === null || qrStyle === void 0 ? void 0 : qrStyle.frameStyle), onChange: (selectedItems) => {
                onChange(selectedItems[0]);
            } }))),
        isFrameSelected && (React__default.createElement(StyledQRFramesContainer, { "$isFrameSelected": isFrameSelected },
            React__default.createElement(StyledFrameColorWrapper, null,
                React__default.createElement(Field, { required: true, name: "frameColor", initialValue: getFrameColorBasedOnFrameType(qrStyle === null || qrStyle === void 0 ? void 0 : qrStyle.frameStyle) || "#404040", onBlurValidate: () => validateRequiredAndHex(), onChangeValidate: () => validateRequiredAndHex() }, ({ value: color1, onChange: onChangeColor1, onBlur: onBlurColor1, errors: errorsColor1, }) => (React__default.createElement(Field, { required: true, name: "frameBackgroundColor", initialValue: "#FFFFFF", onBlurValidate: () => validateRequiredAndHex(), onChangeValidate: () => validateRequiredAndHex() }, ({ value: color2, onChange: onChangeColor2, onBlur: onBlurColor2, errors: errorsColor2, }) => (React__default.createElement(ColorInputGroup, { color1: color1 || "#404040", color2: color2 || "#FFFFFF", onChangeColor1: onChangeColor1, onChangeColor2: onChangeColor2, renderColorInputOne: () => (React__default.createElement(ColorInput, { label: "Frame color", dataQA: "qr-style-frame-color", value: color1, initialValue: getFrameColorBasedOnFrameType(qrStyle === null || qrStyle === void 0 ? void 0 : qrStyle.frameStyle) || "#404040", placeholder: "#404040", onChange: onChangeColor1, onBlur: onBlurColor1, errors: errorsColor1 })), renderColorInputTwo: () => (React__default.createElement(ColorInput, { label: "Background color", dataQA: "qr-style-frame-background-color", value: color2, initialValue: "#FFFFFF", placeholder: "#FFFFFF", onChange: onChangeColor2, onBlur: onBlurColor2, errors: errorsColor2 })) })))))),
            React__default.createElement(StyledFrameTextWrapper, null,
                React__default.createElement(Field, { name: "frameText", onBlurValidate: () => maxLengthValidation(15) }, ({ onChange }) => (React__default.createElement(Input, { type: "text", label: "Frame text", placeholder: "SCAN ME", value: qrStyle === null || qrStyle === void 0 ? void 0 : qrStyle.frameText, maxLength: 15, dataQA: "qr-style-frame-text", onChange: onChange }))),
                React__default.createElement(Field, { required: true, name: "frameTextColor", onBlurValidate: () => validateRequiredAndHex(), onChangeValidate: () => validateRequiredAndHex() }, ({ onChange, errors, value }) => (React__default.createElement(ColorInput, { label: "Text color", placeholder: "#FFFFFF", value: value, initialValue: getFrameTextColorBasedOnFrameType(qrStyle === null || qrStyle === void 0 ? void 0 : qrStyle.frameStyle) || "#FFFFFF", onChange: onChange, dataQA: "qr-style-frame-text-color", errors: errors }))))))));
};

var _path$r, _defs$5;
function _extends$t() { return _extends$t = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$t.apply(null, arguments); }
var SvgSquare = function SvgSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$t({
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    width: 38,
    height: 38,
    fill: "none"
  }, props), _path$r || (_path$r = /*#__PURE__*/React.createElement("path", {
    fill: "url(#square_svg__a)",
    d: "M1.5 1.5h35v35h-35z"
  })), _defs$5 || (_defs$5 = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("pattern", {
    id: "square_svg__a",
    width: 1,
    height: 1,
    patternContentUnits: "objectBoundingBox"
  }, /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#square_svg__b",
    transform: "matrix(.01667 0 0 .01667 -.083 -.083)"
  })), /*#__PURE__*/React.createElement("image", {
    xlinkHref: "data:image/png;base64,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",
    id: "square_svg__b",
    width: 70,
    height: 70
  }))));
};

var _path$q, _defs$4;
function _extends$s() { return _extends$s = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$s.apply(null, arguments); }
var SvgDots = function SvgDots(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$s({
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    width: 38,
    height: 38,
    fill: "none"
  }, props), _path$q || (_path$q = /*#__PURE__*/React.createElement("path", {
    fill: "url(#dots_svg__a)",
    d: "M1.5 1.5h35v35h-35z"
  })), _defs$4 || (_defs$4 = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("pattern", {
    id: "dots_svg__a",
    width: 1,
    height: 1,
    patternContentUnits: "objectBoundingBox"
  }, /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#dots_svg__b",
    transform: "matrix(.01667 0 0 .01667 -.083 -.1)"
  })), /*#__PURE__*/React.createElement("image", {
    xlinkHref: "data:image/png;base64,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",
    id: "dots_svg__b",
    width: 70,
    height: 70
  }))));
};

var _path$p, _defs$3;
function _extends$r() { return _extends$r = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$r.apply(null, arguments); }
var SvgRounded = function SvgRounded(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$r({
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    width: 38,
    height: 38,
    fill: "none"
  }, props), _path$p || (_path$p = /*#__PURE__*/React.createElement("path", {
    fill: "url(#rounded_svg__a)",
    d: "M1.5 1.5h35v35h-35z"
  })), _defs$3 || (_defs$3 = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("pattern", {
    id: "rounded_svg__a",
    width: 1,
    height: 1,
    patternContentUnits: "objectBoundingBox"
  }, /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#rounded_svg__b",
    transform: "matrix(.01667 0 0 .01667 -.083 -.083)"
  })), /*#__PURE__*/React.createElement("image", {
    xlinkHref: "data:image/png;base64,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",
    id: "rounded_svg__b",
    width: 70,
    height: 70
  }))));
};

var _path$o, _defs$2;
function _extends$q() { return _extends$q = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$q.apply(null, arguments); }
var SvgExtraRounded = function SvgExtraRounded(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$q({
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    width: 38,
    height: 38,
    fill: "none"
  }, props), _path$o || (_path$o = /*#__PURE__*/React.createElement("path", {
    fill: "url(#extra-rounded_svg__a)",
    d: "M1.5 1.5h35v35h-35z"
  })), _defs$2 || (_defs$2 = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("pattern", {
    id: "extra-rounded_svg__a",
    width: 1,
    height: 1,
    patternContentUnits: "objectBoundingBox"
  }, /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#extra-rounded_svg__b",
    transform: "matrix(.01667 0 0 .01667 -.083 -.067)"
  })), /*#__PURE__*/React.createElement("image", {
    xlinkHref: "data:image/png;base64,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",
    id: "extra-rounded_svg__b",
    width: 70,
    height: 70
  }))));
};

var _path$n, _defs$1;
function _extends$p() { return _extends$p = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$p.apply(null, arguments); }
var SvgClassy = function SvgClassy(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$p({
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    width: 38,
    height: 38,
    fill: "none"
  }, props), _path$n || (_path$n = /*#__PURE__*/React.createElement("path", {
    fill: "url(#classy_svg__a)",
    d: "M1.5 1.5h35v35h-35z"
  })), _defs$1 || (_defs$1 = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("pattern", {
    id: "classy_svg__a",
    width: 1,
    height: 1,
    patternContentUnits: "objectBoundingBox"
  }, /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#classy_svg__b",
    transform: "matrix(.01667 0 0 .01667 -.1 -.067)"
  })), /*#__PURE__*/React.createElement("image", {
    xlinkHref: "data:image/png;base64,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",
    id: "classy_svg__b",
    width: 70,
    height: 70
  }))));
};

var _path$m, _defs;
function _extends$o() { return _extends$o = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$o.apply(null, arguments); }
var SvgClassyRounded = function SvgClassyRounded(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$o({
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    width: 38,
    height: 38,
    fill: "none"
  }, props), _path$m || (_path$m = /*#__PURE__*/React.createElement("path", {
    fill: "url(#classy-rounded_svg__a)",
    d: "M1.5 1.5h35v35h-35z"
  })), _defs || (_defs = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("pattern", {
    id: "classy-rounded_svg__a",
    width: 1,
    height: 1,
    patternContentUnits: "objectBoundingBox"
  }, /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#classy-rounded_svg__b",
    transform: "matrix(.01667 0 0 .01667 -.083 -.067)"
  })), /*#__PURE__*/React.createElement("image", {
    xlinkHref: "data:image/png;base64,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",
    id: "classy-rounded_svg__b",
    width: 70,
    height: 70
  }))));
};

const getPatternItems = (selectedItem) => {
    const patternItems = [
        {
            id: "square",
            content: React__default.createElement(SvgSquare, null),
            selected: selectedItem === undefined,
        },
        { id: "dots", content: React__default.createElement(SvgDots, null), selected: selectedItem === "dots" },
        { id: "rounded", content: React__default.createElement(SvgRounded, null), selected: selectedItem === "rounded" },
        {
            id: "extra-rounded",
            content: React__default.createElement(SvgExtraRounded, null),
            selected: selectedItem === "extra-rounded",
        },
        { id: "classy", content: React__default.createElement(SvgClassy, null), selected: selectedItem === "classy" },
        {
            id: "classy-rounded",
            content: React__default.createElement(SvgClassyRounded, null),
            selected: selectedItem === "classy-rounded",
        },
    ];
    return patternItems;
};

const StyledPatternPicker = styled(Picker) `
  display: flex;
  gap: ${getSpacing("size-04")};
  justify-content: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: flex-start;
  }

  ${getMarginTop("size-05")}

  > button {
    ${getPadding("size-03")};
  }
`;
const StyledPatternsColorWrapper = styled(StyledInputGroup) `
  display: flex;
  flex-direction: column;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: row;
  }
`;
const StyledInfoText = styled.div `
  display: flex;
  background-color: ${getColor("neutral", 100)};
  color: ${getColor("neutral", 900)};
  padding: ${getSpacing("size-05")} ${getSpacing("size-03")};
  margin-bottom: ${getSpacing("size-05")};
  border-radius: 8px;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    align-items: center;
  }

  svg {
    min-width: 1.5rem;
    min-height: 1.5rem;
    margin-right: ${getSpacing("size-03")};
  }
`;

const QRPatterns = () => {
    return (React__default.createElement("div", null,
        React__default.createElement(StyledInfoText, null,
            React__default.createElement(TbAlertCircleFilled, null),
            "Choose contrasting colors to make your QR codes easier to read. We recommend using a light and dark color for optimal performance."),
        React__default.createElement(Field, { name: "dotsStyle" }, ({ onChange }) => (React__default.createElement(StyledPatternPicker, { items: getPatternItems(), dataQA: "qr-style-pattern-picker", onChange: (selectedItems) => onChange(selectedItems[0]) }))),
        React__default.createElement(StyledPatternsColorWrapper, null,
            React__default.createElement(Field, { initialValue: "#000000", name: "dotsColor", onChangeValidate: () => validateRequiredAndHex() }, ({ value: color1, onChange: onChangeColor1, onBlur: onBlurColor1, errors: errorsColor1, }) => (React__default.createElement(Field, { name: "backgroundColor", initialValue: "#FFFFFF", onChangeValidate: () => validateRequiredAndHex() }, ({ value: color2, onChange: onChangeColor2, onBlur: onBlurColor2, errors: errorsColor2, }) => (React__default.createElement(ColorInputGroup, { color1: color1 || "#000000", color2: color2 || "#FFFFFF", onChangeColor1: onChangeColor1, onChangeColor2: onChangeColor2, renderColorInputOne: () => (React__default.createElement(ColorInput, { label: "Dot color", dataQA: "qr-style-pattern-dot-color", value: color1, initialValue: "#000000", placeholder: "#000000", onChange: onChangeColor1, onBlur: onBlurColor1, errors: errorsColor1 })), renderColorInputTwo: () => (React__default.createElement(ColorInput, { label: "Background color", dataQA: "qr-style-pattern-background-color", value: color2, initialValue: "#FFFFFF", placeholder: "#FFFFFF", onChange: onChangeColor2, onBlur: onBlurColor2, errors: errorsColor2 })) }))))))));
};

var _rect$4, _rect2$1;
function _extends$n() { return _extends$n = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$n.apply(null, arguments); }
var SvgRoundedDot = function SvgRoundedDot(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$n({
    xmlns: "http://www.w3.org/2000/svg",
    width: 38,
    height: 38,
    fill: "none"
  }, props), _rect$4 || (_rect$4 = /*#__PURE__*/React.createElement("rect", {
    width: 30.722,
    height: 30.722,
    x: 3.639,
    y: 3.639,
    fill: "#fff",
    stroke: "#373A36",
    strokeWidth: 3.5,
    rx: 7.583
  })), _rect2$1 || (_rect2$1 = /*#__PURE__*/React.createElement("rect", {
    width: 15.556,
    height: 15.556,
    x: 11.222,
    y: 11.222,
    fill: "#373A36",
    rx: 5.833
  })));
};

var _rect$3, _path$l;
function _extends$m() { return _extends$m = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$m.apply(null, arguments); }
var SvgRoundedSquare = function SvgRoundedSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$m({
    xmlns: "http://www.w3.org/2000/svg",
    width: 38,
    height: 38,
    fill: "none"
  }, props), _rect$3 || (_rect$3 = /*#__PURE__*/React.createElement("rect", {
    width: 30.722,
    height: 30.722,
    x: 3.639,
    y: 3.639,
    fill: "#fff",
    stroke: "#373A36",
    strokeWidth: 3.5,
    rx: 7.583
  })), _path$l || (_path$l = /*#__PURE__*/React.createElement("path", {
    fill: "#373A36",
    d: "M11.222 11.222h15.556v15.556H11.222z"
  })));
};

var _rect$2, _rect2;
function _extends$l() { return _extends$l = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$l.apply(null, arguments); }
var SvgDotDot = function SvgDotDot(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$l({
    xmlns: "http://www.w3.org/2000/svg",
    width: 38,
    height: 38,
    fill: "none"
  }, props), _rect$2 || (_rect$2 = /*#__PURE__*/React.createElement("rect", {
    width: 30.722,
    height: 30.722,
    x: 3.639,
    y: 3.639,
    fill: "#fff",
    stroke: "#373A36",
    strokeWidth: 3.5,
    rx: 11.083
  })), _rect2 || (_rect2 = /*#__PURE__*/React.createElement("rect", {
    width: 15.556,
    height: 15.556,
    x: 11.222,
    y: 11.222,
    fill: "#373A36",
    rx: 5.833
  })));
};

var _rect$1, _path$k;
function _extends$k() { return _extends$k = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$k.apply(null, arguments); }
var SvgDotSquare = function SvgDotSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$k({
    xmlns: "http://www.w3.org/2000/svg",
    width: 38,
    height: 38,
    fill: "none"
  }, props), _rect$1 || (_rect$1 = /*#__PURE__*/React.createElement("rect", {
    width: 30.722,
    height: 30.722,
    x: 3.639,
    y: 3.639,
    fill: "#fff",
    stroke: "#373A36",
    strokeWidth: 3.5,
    rx: 11.083
  })), _path$k || (_path$k = /*#__PURE__*/React.createElement("path", {
    fill: "#373A36",
    d: "M11.222 11.222h15.556v15.556H11.222z"
  })));
};

var _path$j, _path2$1, _rect;
function _extends$j() { return _extends$j = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$j.apply(null, arguments); }
var SvgSquareDot = function SvgSquareDot(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$j({
    xmlns: "http://www.w3.org/2000/svg",
    width: 38,
    height: 38,
    fill: "none"
  }, props), _path$j || (_path$j = /*#__PURE__*/React.createElement("path", {
    stroke: "#373A36",
    strokeWidth: 3.5,
    d: "M3.639 3.639h30.722v30.722H3.639z"
  })), _path2$1 || (_path2$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    d: "M6.556 6.556h24.889v24.889H6.556z"
  })), _rect || (_rect = /*#__PURE__*/React.createElement("rect", {
    width: 15.556,
    height: 15.556,
    x: 11.222,
    y: 11.222,
    fill: "#373A36",
    rx: 5.833
  })));
};

var _path$i, _path2, _path3;
function _extends$i() { return _extends$i = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$i.apply(null, arguments); }
var SvgSquareSquare = function SvgSquareSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$i({
    xmlns: "http://www.w3.org/2000/svg",
    width: 38,
    height: 38,
    fill: "none"
  }, props), _path$i || (_path$i = /*#__PURE__*/React.createElement("path", {
    stroke: "#373A36",
    strokeWidth: 3.5,
    d: "M3.639 3.639h30.722v30.722H3.639z"
  })), _path2 || (_path2 = /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    d: "M6.556 6.556h24.889v24.889H6.556z"
  })), _path3 || (_path3 = /*#__PURE__*/React.createElement("path", {
    fill: "#373A36",
    d: "M11.222 11.222h15.556v15.556H11.222z"
  })));
};

const getCornerItems = (selectedItem) => {
    const frameItems = [
        {
            id: "rounded-dot",
            content: React__default.createElement(SvgRoundedDot, null),
            selected: selectedItem === undefined,
        },
        {
            id: "rounded-square",
            content: React__default.createElement(SvgRoundedSquare, null),
            selected: selectedItem === "rounded-square",
        },
        { id: "dot-dot", content: React__default.createElement(SvgDotDot, null), selected: selectedItem === "dot-dot" },
        { id: "dot-square", content: React__default.createElement(SvgDotSquare, null), selected: selectedItem === "dot-square" },
        { id: "square-dot", content: React__default.createElement(SvgSquareDot, null), selected: selectedItem === "square-dot" },
        {
            id: "square-square",
            content: React__default.createElement(SvgSquareSquare, null),
            selected: selectedItem === "square-square",
        },
    ];
    return frameItems;
};

const StyledCornerPicker = styled(Picker) `
  display: flex;
  justify-content: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: flex-start;
  }

  ${getMarginTop("size-05")}
  gap: ${getSpacing("size-04")};

  > button {
    ${getPadding("size-03")};
  }
`;
const StyledCornersColorWrapper = styled(StyledInputGroup) `
  display: flex;
  flex-direction: row;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

const QRCorners = () => {
    return (React__default.createElement("div", null,
        React__default.createElement(Field, { name: "cornerStyle" }, ({ onChange }) => (React__default.createElement(StyledCornerPicker, { items: getCornerItems(), dataQA: "qr-style-corner-picker", onChange: (selectedItems) => onChange(selectedItems[0]) }))),
        React__default.createElement(StyledCornersColorWrapper, null,
            React__default.createElement(Field, { name: "cornerBorderColor", onChangeValidate: () => validateRequiredAndHex() }, ({ value: color1, onChange: onChangeColor1, onBlur: onBlurColor1, errors: errorsColor1, }) => (React__default.createElement(Field, { name: "cornerBackgroundColor", initialValue: "#000000", onChangeValidate: () => validateRequiredAndHex() }, ({ value: color2, onChange: onChangeColor2, onBlur: onBlurColor2, errors: errorsColor2, }) => (React__default.createElement(ColorInputGroup, { color1: color1 || "#000000", color2: color2 || "#000000", onChangeColor1: onChangeColor1, onChangeColor2: onChangeColor2, renderColorInputOne: () => (React__default.createElement(ColorInput, { label: "Corner square color", initialValue: "#000000", placeholder: "#000000", value: color1, dataQA: "qr-style-corner-square-color", onChange: onChangeColor1, onBlur: onBlurColor1, errors: errorsColor1 })), renderColorInputTwo: () => (React__default.createElement(ColorInput, { label: "Corner dot color", value: color2, initialValue: "#000000", placeholder: "#000000", dataQA: "qr-style-corner-dot-color", onChange: onChangeColor2, onBlur: onBlurColor2, errors: errorsColor2 })) }))))))));
};

const StyledAccordion$1 = styled(Accordion) `
  ${getMarginBottom("size-06")};
`;
const StyledButtonGroup = styled.div `
  ${getMarginBottom("size-05")};
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  & > button {
    font-weight: 700;
    gap: 4px;
  }

  .selected {
    border: 2px solid transparent;
  }
  .not-selected {
    border: 2px solid transparent;

    &:hover {
      transition: all 0.1s ease;
      background-color: transparent;
      border: 2px solid ${getColor("primary", 500)};
    }
  }
`;

const QRStyles = ({ onToggleAccordion }) => {
    const [selectedSection, setSelectedSection] = React__default.useState("frames");
    const { errors } = useFormContext();
    const isButtonDisabled = (isSectionSelected) => {
        const stylesFieldsWithoutValidation = [
            "qrStyleType",
            "dotsStyle",
            "cornerStyle",
            "logoUrl",
            "frameStyle",
            "frameText",
        ];
        const stylesFields = Object.keys(defaultQRStyle).filter((field) => !stylesFieldsWithoutValidation.includes(field));
        const errorKeys = Object.keys(errors);
        const hasStylesErrors = errorKeys.some((key) => stylesFields.includes(key));
        if (!hasStylesErrors || isSectionSelected) {
            return false;
        }
        return true;
    };
    const renderTabButton = (label, section, numberOfItems) => {
        return (React__default.createElement(Button, { state: isButtonDisabled(selectedSection === section) ? "disabled" : "default", type: "button", variant: "primary", dataQA: `qr-style-${section}-button`, shape: selectedSection === section ? "solid" : "outlined", style: selectedSection === section
                ? { color: getColor("neutral", "white") }
                : { color: getColor("neutral", 900) }, className: selectedSection === section ? "selected" : "not-selected", onClick: () => setSelectedSection(section) },
            React__default.createElement(React__default.Fragment, null,
                label,
                React__default.createElement("span", { style: {
                        color: selectedSection === section
                            ? `${getColor("neutral", "white")}`
                            : `${getColor("neutral", 500)}`,
                        transition: "all 0.3s ease",
                    } },
                    "(",
                    numberOfItems,
                    ")"))));
    };
    return (React__default.createElement(StyledAccordion$1, { id: "styles-accordion", dataQA: "qr-style-accordion", title: "Design your QR Code", onToggle: onToggleAccordion },
        React__default.createElement(StyledButtonGroup, null,
            renderTabButton("Frames", "frames", getFrameItems().length),
            renderTabButton("Patterns", "patterns", getPatternItems().length),
            renderTabButton("Corners", "corners", getCornerItems().length)),
        React__default.createElement("div", null,
            React__default.createElement("div", { style: { display: selectedSection === "frames" ? "block" : "none" } },
                React__default.createElement(QRFrames, null)),
            React__default.createElement("div", { style: { display: selectedSection === "patterns" ? "block" : "none" } },
                React__default.createElement(QRPatterns, null)),
            React__default.createElement("div", { style: { display: selectedSection === "corners" ? "block" : "none" } },
                React__default.createElement(QRCorners, null)))));
};

const QRDesign = () => {
    const setAccordionExpanded = useUIStore((state) => state.setAccordionExpanded);
    const toggleAccordion = useUIStore((state) => state.toggleAccordion);
    const handleAccordionToggle = (isExpanded, id) => {
        toggleAccordion(id);
        setAccordionExpanded(id, isExpanded);
    };
    return (React__default.createElement("div", null,
        React__default.createElement(QRStyles, { onToggleAccordion: handleAccordionToggle }),
        React__default.createElement(Accordion, { id: "logo-accordion", dataQA: "qr-logo-accordion", title: "Add Logo", onToggle: handleAccordionToggle },
            React__default.createElement(Field, { name: "logoUrl" }, ({ onChange }) => (React__default.createElement(FileUploader, { isProfile: true, dataQA: "qr-logo-uploader", title: React__default.createElement("div", null, "Logo"), onChange: (files) => {
                    if (files.length) {
                        const uploadedFile = files[0];
                        const preview = uploadedFile.preview;
                        onChange(preview);
                    }
                    else
                        onChange("");
                }, fileConfig: {
                    type: "image",
                }, singleUpload: true }))))));
};

const SelectWrapper = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${getSpacing("size-01")};
`;
const SelectLabel = styled.label `
  display: block;
  color: ${getColor("neutral", 900)};
  font-family: ${getFont("family-primary")};
  font-size: 16px;
  font-weight: 400;
`;
const SelectContainer = styled.div `
  position: relative;
`;
const SelectElement = styled.select `
  width: 100%;
  height: 48px;
  ${getPadding("size-03")};
  box-sizing: border-box;
  border: 2px solid
    ${({ $hasError, $hasSuccess }) => $hasError
    ? getColor("error", 300)
    : $hasSuccess
        ? getColor("success", 300)
        : getColor("neutral", 300)};
  border-radius: 4px;
  color: ${getColor("neutral", 900)};
  font-size: 16px;
  background-color: ${getColor("neutral", "white")};
  appearance: none;

  &::placeholder {
    color: ${getColor("neutral", 600)};
    font-size: 16px;
    line-height: 24px;
    font-weight: 400;
  }
  &:hover {
    background-color: ${getColor("neutral", 50)};
  }
  &:focus {
    border-color: ${getColor("primary", 300)};
    outline: none;
  }
  &:disabled {
    background-color: ${getColor("neutral", 50)};
    border: 2px solid ${getColor("neutral", 100)};
    color: ${getColor("neutral", 400)};
    cursor: not-allowed;
  }
`;
const IconContainer = styled.div `
  pointer-events: none;
  position: absolute;
  top: 50%;
  right: ${getSpacing("size-03")};
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 10px;

  svg: nth-child(2) {
    color: ${getColor("success", 300)};
  }
`;

const Select = ({ name, label, options, value, onChange, placeholder, disabled = false, required = false, errors = [], onBlur, dataQA, className, tooltipHelper, }) => {
    const hasError = errors.length > 0;
    const hasSuccess = value !== "" && !hasError;
    const handleChange = (event) => {
        onChange(event.target.value);
    };
    return (React__default.createElement(SelectWrapper, { className: className, "data-qa": dataQA },
        React__default.createElement(TooltipLabelContainer, null,
            label && (React__default.createElement(SelectLabel, { htmlFor: name, "data-qa": `${dataQA}-label` },
                label,
                required && " *")),
            tooltipHelper && InputTooltipHelper({ tooltipHelper, id: name })),
        React__default.createElement(SelectContainer, null,
            React__default.createElement(SelectElement, { id: name, name: name, value: value, "data-qa": `${dataQA}-selector`, onChange: handleChange, onBlur: onBlur, disabled: disabled, required: required, "$hasError": hasError, "$hasSuccess": hasSuccess },
                placeholder && (React__default.createElement("option", { key: placeholder, value: "", disabled: true, hidden: true }, placeholder)),
                options.map((option) => (React__default.createElement("option", { key: option.value, value: option.value }, option.label)))),
            React__default.createElement(IconContainer, null,
                React__default.createElement(MdExpandMore, { size: 16 }),
                hasSuccess && React__default.createElement(MdOutlineCheckCircle, { size: 16 }))),
        hasError && (React__default.createElement(ErrorMessage, { "data-qa": `${dataQA}-error-message`, errors: errors }))));
};

const CheckboxWrapper = styled.div `
  width: auto;
  display: block;
  position: relative;
`;
const CheckboxContainer = styled.label `
  display: flex;
  align-items: ;
  align-content: center;
  position: relative;
  cursor:pointer;
  padding-left: 20px; /* Espacio para el checkmark */
  margin-bottom: 0;
  cursor: pointer;
  font-size: 16px;
  line-height: 22px;
  user-select: none;
  font-family: ${getFont("family-primary")};

  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 100%;
    width: 100%;
    z-index: 2; /* Para captar los clics */
  }

  span {
    position: absolute;
    top: 0;
    left: 0;
    height: 18px;
    width: 18px;
    background-color: transparent;
    border-radius: 3px;
    border: 1px solid  ${getColor("neutral", 300)};
    z-index: 1;

    &:after {
      content: "";
      position: absolute;
      display: none;
    }
  }

  input:checked + span {
    background-color: ${getColor("primary", 500)};
    border: 1px solid transparent;

    &:after {
      display: block;
      left: 5px;
      top: 1px;
      width: 5px;
      height: 10px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
`;
const CheckboxInput = styled.input `
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;

  &:checked {
    background-color: ${getColor("primary", 500)};
    border: 1px solid transparent;

    &:after {
      display: block;
      left: 5px;
      top: 1px;
      width: 5px;
      height: 10px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
`;
const CheckboxLabel = styled.label `
  margin-left: 8px;
`;

const Checkbox = ({ name, label, checked, onChange, disabled = false, required = false, errors = [], onBlur, className, style, dataQA, }) => {
    const hasError = errors.length > 0;
    const handleChange = (event) => {
        onChange(event.target.checked);
    };
    return (React__default.createElement(CheckboxWrapper, { className: className, "data-qa": dataQA, style: style },
        React__default.createElement(CheckboxContainer, null,
            React__default.createElement(CheckboxInput, { id: name, "data-qa": `${dataQA}-input`, type: "checkbox", name: name, checked: checked, onChange: handleChange, onBlur: onBlur, disabled: disabled, required: required }),
            React__default.createElement("span", null),
            label && (React__default.createElement(CheckboxLabel, { htmlFor: name, "data-qa": `${dataQA}-label` }, label))),
        hasError && (React__default.createElement(ErrorMessage, { "data-qa": `${dataQA}-error-message`, errors: errors }))));
};

const FieldArrayContext = createContext(null);
const useFieldArray = () => {
    const context = useContext(FieldArrayContext);
    if (!context) {
        throw new Error("useFieldArray must be used within a FieldArray");
    }
    return context;
};

const FieldArray = ({ name, initialValue = [], children, }) => {
    const { getValue, setValue, setErrors: setFormErrors } = useFormContext();
    const [itemErrors, setItemErrors] = useState({});
    const arrayValue = useMemo(() => {
        return (getValue(name) || initialValue);
    }, [getValue, name, initialValue]);
    const validateItem = useCallback((index, errors) => {
        setItemErrors((prev) => {
            const newItemErrors = Object.assign(Object.assign({}, prev), { [index]: errors });
            setFormErrors(name, Object.values(newItemErrors).flat());
            return newItemErrors;
        });
    }, [name, setFormErrors]);
    const contextValue = useMemo(() => ({
        name,
        value: arrayValue,
        add: (item) => {
            const newValue = [...arrayValue, item];
            setValue(name, newValue);
        },
        remove: (index) => {
            const newValue = arrayValue.filter((_, i) => i !== index);
            setValue(name, newValue);
            const newErrors = Object.assign({}, itemErrors);
            delete newErrors[index];
            setItemErrors(newErrors);
        },
        update: (index, item) => {
            const newValue = [...arrayValue];
            newValue[index] = item;
            setValue(name, newValue);
        },
        errors: itemErrors,
        validateItem,
    }), [arrayValue, name, setValue, itemErrors, validateItem]);
    return (React__default.createElement(FieldArrayContext.Provider, { value: contextValue }, children(contextValue)));
};

const FieldArrayItem = ({ index, children, validators, }) => {
    const { value: arrayValue, update, remove, errors: arrayErrors, validateItem } = useFieldArray();
    const itemValue = useMemo(() => {
        return arrayValue[index];
    }, [arrayValue, index]);
    const handleChange = useCallback((newValue) => __awaiter(void 0, void 0, void 0, function* () {
        const valueToValidate = typeof newValue === "object" ? newValue.url : newValue;
        if (validators === null || validators === void 0 ? void 0 : validators.onChangeValidate) {
            try {
                const schema = validators.onChangeValidate(valueToValidate, arrayValue);
                yield schema.parseAsync(valueToValidate);
                validateItem(index, []);
                update(index, newValue);
            }
            catch (error) {
                const errorMessages = error.errors
                    ? error.errors.map((e) => e.message)
                    : [error.message];
                validateItem(index, errorMessages);
                update(index, newValue);
            }
        }
        else {
            update(index, newValue);
        }
    }), [validators, index, update, validateItem, arrayValue]);
    const handleBlur = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const valueToValidate = typeof itemValue === "object" ? itemValue === null || itemValue === void 0 ? void 0 : itemValue.url : itemValue;
        if (validators === null || validators === void 0 ? void 0 : validators.onBlurValidate) {
            try {
                const schema = validators.onBlurValidate(valueToValidate, arrayValue);
                yield schema.parseAsync(valueToValidate);
                validateItem(index, []);
            }
            catch (error) {
                const errorMessages = error.errors
                    ? error.errors.map((e) => e.message)
                    : [error.message];
                validateItem(index, errorMessages);
            }
        }
    }), [validators, index, itemValue, validateItem, arrayValue]);
    return children({
        value: itemValue,
        setValue: update.bind(null, index),
        remove: remove.bind(null, index),
        onChange: handleChange,
        onBlur: handleBlur,
        errors: arrayErrors[index] || [],
    });
};

const StyledSocialMediaList = styled.div ``;
const StyledSocialMediaItem = styled.div `

  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: ${getSpacing("size-05")};
  justify-content: space-between;

  @media (max-width: 744px) {
    flex-direction: column;
    position: relative;
  }

  .social-info-container {
    display: flex;
    gap: ${getSpacing("size-05")};
    width: 95%;

    @media (max-width: 744px) {
      flex-direction: column;
      width: 100%;
    }

    .social-info {
      display: flex;
      align-items: center;
      gap: ${getSpacing("size-05")};
      min-width: 120px;
      width: 20%;
      flex: 0 0 auto;

      display: flex;
      justify-content: flex-start;
      align-items: center;

      img {
        width: 24px;
        height: 24px;
      }

      @media (max-width: 744px) {
        flex: 0 0 auto;
      }
    }

    .input-wrapper{
      width: 80%;
      flex: 1;
      @media (max-width: 744px) {
         width: 100%;
      }
    }

    input {
      flex: 1;
    }
  }

  .social-info-button {
    width: 5%;
    display: flex;
    justify-content: flex-end;
    align-items: center;

   .button__remove{
      border-radius: 100px;
      border: transparent;
      width:20px;
      height: 20px;

      &:hover {
        background-color:transparent;
      }

      svg{
        color:  ${getColor("neutral", 200)};
      }

   }
 

    @media (max-width: 744px) {
      position: absolute;
      right: 0;
    }

    button {
      ${getPadding("size-03")};
    }
  }
 
`;
const StyledSocialMediaPicker$1 = styled(Picker) `
  background-color: ${getColor("neutral", 100)};
  padding: ${getSpacing("size-05")};
  border-radius: ${getSpacing("size-03")};
  ${getMarginTop('size-05')};
`;
const StyledSocialMediaPickerLabel = styled.span `
  font-weight: bolder;
`;

const SocialMediaPicker = ({ name, items, socialMediaMap, className, style, label, dataQA, onValidate, }) => {
    const [fieldArrayValue, setFieldArrayValue] = useState([]);
    const fieldArrayActionsRef = React__default.useRef(null);
    const selectedIds = useMemo(() => new Set(fieldArrayValue.map((item) => item.id)), [fieldArrayValue]);
    const syncedItems = useMemo(() => {
        return items.map((item) => (Object.assign(Object.assign({}, item), { selected: selectedIds.has(item.id) })));
    }, [items, selectedIds]);
    const handlePickerChange = useCallback((selectedIdsArray) => {
        if (!fieldArrayActionsRef.current)
            return;
        const { add, remove } = fieldArrayActionsRef.current;
        const currentIds = new Set(fieldArrayValue.map((item) => item.id));
        const idsToRemove = Array.from(currentIds).filter((id) => !selectedIdsArray.includes(id));
        idsToRemove.forEach((idToRemove) => {
            const index = fieldArrayValue.findIndex((item) => item.id === idToRemove);
            if (index !== -1) {
                remove(index);
            }
        });
        const idsToAdd = selectedIdsArray.filter((id) => !currentIds.has(id));
        idsToAdd.forEach((id) => {
            const socialMediaConfig = socialMediaMap[id];
            if (socialMediaConfig) {
                add(Object.assign(Object.assign({}, socialMediaConfig), { url: "" }));
            }
        });
    }, [socialMediaMap, fieldArrayValue]);
    const refinedZodValidator = useCallback((configValidators, values) => z.string().refine((val) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (configValidators === null || configValidators === void 0 ? void 0 : configValidators.onBlurValidate) {
                yield configValidators.onBlurValidate(val, values).parseAsync(val);
            }
            if (onValidate) {
                yield onValidate(val);
            }
            return true;
        }
        catch (error) {
            return false;
        }
    }), { message: "Please enter a valid URL" }), [onValidate]);
    useEffect(() => {
        return () => {
            fieldArrayActionsRef.current = null;
        };
    }, []);
    return (React__default.createElement(FieldArray, { name: name, initialValue: [] }, ({ value, add, remove }) => {
        useEffect(() => {
            setFieldArrayValue(value);
        }, [value]);
        fieldArrayActionsRef.current = { add, remove, value };
        return (React__default.createElement("div", { className: className, "data-qa": `${dataQA}-list` },
            React__default.createElement(StyledSocialMediaList, null, value.map((social, index) => {
                const socialConfig = socialMediaMap[social.id];
                const configValidators = socialConfig === null || socialConfig === void 0 ? void 0 : socialConfig.validators;
                const combinedValidators = {
                    onBlurValidate: (_, values) => {
                        return refinedZodValidator(configValidators, values);
                    },
                    onChangeValidate: (_, values) => {
                        return refinedZodValidator(configValidators, values);
                    },
                    onSubmitValidate: (_, values) => {
                        return refinedZodValidator(configValidators, values);
                    },
                };
                return (React__default.createElement(FieldArrayItem, { key: social.id, name: `${name}[${index}]`, index: index, validators: combinedValidators }, ({ value: itemValue, onChange, onBlur, errors: itemErrors }) => (React__default.createElement(StyledSocialMediaItem, { "data-qa": itemValue.dataQA },
                    React__default.createElement("div", { className: "social-info-container" },
                        React__default.createElement("div", { className: "social-info" },
                            getSocialMediaIcon(itemValue.icon),
                            React__default.createElement("span", { "data-qa": `${dataQA}-name` }, itemValue.name)),
                        React__default.createElement("div", { className: "input-wrapper" },
                            React__default.createElement(Input, { type: "text", "aria-label": "URL", "data-testid": "social-media-url-input", dataQA: `${dataQA}`, onChange: (newValue) => {
                                    const updatedValue = Object.assign(Object.assign({}, itemValue), { url: newValue });
                                    onChange(updatedValue);
                                }, onBlur: onBlur, errors: itemErrors, placeholder: `Enter ${itemValue.name} URL` }))),
                    React__default.createElement("div", { className: "social-info-button" },
                        React__default.createElement(Button, { onClick: () => {
                                remove(index);
                            }, shape: "outlined", variant: "neutral", iconLeft: React__default.createElement(CgRemove, null), className: "button__remove" }))))));
            })),
            label && (React__default.createElement(StyledSocialMediaPickerLabel, { "data-qa": `${dataQA}-label` }, label)),
            React__default.createElement(StyledSocialMediaPicker$1, { dataQA: dataQA, items: syncedItems, multiple: true, onChange: handlePickerChange, style: style })));
    }));
};

const StyledAccordion = styled(Accordion) `
  ${getMarginBottom("size-06")};
`;
const StyledPicker = styled(Picker) `
  ${getMarginBottom("size-06")};
`;
const StyledInput = styled(Input) `
  ${getMarginBottom("size-06")};
`;
const StyledButton = styled(Button) `
  ${getMarginBottom("size-06")};
`;
const StyledSelect = styled(Select) `
  ${getMarginBottom("size-06")};
`;
const StyledCheckbox = styled(Checkbox) `
  ${getMarginBottom("size-06")};
`;
const StyledFileUploader = styled(FileUploader) `
  ${getMarginBottom("size-06")};
`;
const StyledSocialMediaPicker = styled(SocialMediaPicker) `
  ${getMarginBottom("size-06")};
`;
const StyledGroup = styled.div `
  ${getMarginBottom("size-06")};
`;

const renderField = (props) => {
    const { field, isSubmitting } = props;
    switch (field.type) {
        case "button":
        case "text":
        case "textarea":
        case "password":
        case "select":
        case "checkbox":
        case "fileUploader":
        case "socialMediaPicker":
        case "picker":
            return (React__default.createElement(Field, { key: field.name, name: field.name, required: field.required, initialValue: field.initialValue, onBlurValidate: field.onBlurValidate, onChangeValidate: field.onChangeValidate, onSubmitValidate: field.onSubmitValidate }, ({ value, onChange, onBlur, errors }) => {
                switch (field.type) {
                    case "picker":
                        const PickerComponent = field.styledComponent || StyledPicker;
                        return (React__default.createElement(PickerComponent, { key: field.name, onChange: onChange, dataQA: field.dataQA, items: field.items, style: field.styles, disabled: field.disabled || field.isLoading || isSubmitting }));
                    case "password":
                    case "textarea":
                    case "text":
                        const InputComponent = field.styledComponent || StyledInput;
                        return (React__default.createElement(InputComponent, { key: field.name, label: field.label, name: field.name, type: field.type, value: value, dataQA: field.dataQA, onChange: onChange, maxLength: field.maxLength, onBlur: onBlur, required: field.required, disabled: field.disabled || field.isLoading || isSubmitting, placeholder: field.placeholder, isLoading: field.isLoading, errors: errors, style: field.styles, tooltipHelper: field.tooltipHelper }));
                    case "select":
                        const SelectComponent = field.styledComponent || StyledSelect;
                        return (React__default.createElement(SelectComponent, { key: field.name, name: field.name, label: field.label, options: field.options, value: value, dataQA: field.dataQA, onChange: onChange, onBlur: onBlur, disabled: field.disabled || field.isLoading || isSubmitting, required: field.required, errors: errors, style: field.styles, placeholder: field.placeholder, tooltipHelper: field.tooltipHelper }));
                    case "checkbox":
                        const CheckboxComponent = field.styledComponent || StyledCheckbox;
                        return (React__default.createElement(CheckboxComponent, { key: field.name, name: field.name, label: field.label, dataQA: field.dataQA, checked: value, onChange: onChange, onBlur: onBlur, disabled: field.disabled || field.isLoading || isSubmitting, required: field.required, errors: errors, style: field.styles }));
                    case "button":
                        const ButtonComponent = field.styledComponent || StyledButton;
                        return (React__default.createElement(ButtonComponent, { key: field.name, label: field.label || "", dataQA: field.dataQA, type: field.buttonType || "button", onClick: field.onClick, state: field.state, shape: field.shape, variant: field.variant, className: field.className, iconLeft: field.iconLeft, iconRight: field.iconRight, size: field.size, style: field.styles }));
                    case "fileUploader":
                        const oconvertToBase64 = (file) => {
                            return new Promise((resolve) => {
                                const reader = new FileReader();
                                reader.onload = () => resolve(reader.result);
                                reader.readAsDataURL(file.file);
                            });
                        };
                        const handleChange = (files) => __awaiter(void 0, void 0, void 0, function* () {
                            const base64Files = yield Promise.all(files.map(oconvertToBase64));
                            onChange(base64Files);
                        });
                        const FileUploaderComponent = field.styledComponent || StyledFileUploader;
                        return (React__default.createElement(FileUploaderComponent, { key: field.name, name: field.name, className: field.className, isProfile: field.isProfile, comment: field.comment, dataQA: field.dataQA, style: field.style, fileConfig: field.fileConfig, initialFiles: field.initialFiles, onChange: handleChange, errorMessages: field.errorMessages, buttonContent: field.buttonContent }));
                    case "socialMediaPicker":
                        const SocialMediaPickerComponent = field.styledComponent || StyledSocialMediaPicker;
                        return (React__default.createElement(SocialMediaPickerComponent, { key: field.name, label: field.label, name: "socialNetworks", dataQA: field.dataQA, items: field.items, socialMediaMap: field.socialMediaMap, onValidate: field.onValidate, style: field.styles }));
                    default:
                        return null;
                }
            }));
        case "accordion":
            const AccordionComponent = field.styledComponent || StyledAccordion;
            return (React__default.createElement(AccordionComponent, { id: field.id, key: field.name, dataQA: field.dataQA, title: field.title, style: field.styles, onToggle: field.onToggle, isExpanded: field.isExpanded }, field.fields.map((nestedField) => renderField(Object.assign(Object.assign({}, props), { field: nestedField })))));
        case "group":
            const GroupComponent = field.styledComponent || StyledGroup;
            return (React__default.createElement(GroupComponent, { key: field.id || "group", style: field.styles }, field.fields.map((nestedField) => renderField(Object.assign(Object.assign({}, props), { field: nestedField })))));
        default:
            return null;
    }
};
const FormRenderer = (props) => {
    const { fields } = props, rest = __rest(props, ["fields"]);
    return React__default.createElement(React__default.Fragment, null, fields.map((field) => renderField(Object.assign({ field }, rest))));
};

const Form = ({ onSubmit, schema, children, className }) => {
    const { formValues: values, formErrors: errors, setFormValue: setValue, setFormError: setErrorsFn, resetForm, isLoading: isSubmitting, setLoading: setIsSubmitting, isValidForm, setIsValidForm, } = useQRStore();
    const [fields, setFields] = useState({});
    useEffect(() => {
        const initializeForm = () => {
            setFields({});
            Object.keys(errors).forEach((fieldName) => {
                setErrorsFn(fieldName, []);
            });
            setIsValidForm(false);
            if (schema) {
                const registerFieldsFromSchema = (fields) => {
                    fields.forEach((field) => {
                        if (field.type === "accordion" || field.type === "group") {
                            registerFieldsFromSchema(field.fields);
                        }
                        else if (field.onBlurValidate || field.onChangeValidate || field.onSubmitValidate) {
                            registerField(field.name, {
                                onBlurValidate: field.onBlurValidate,
                                onChangeValidate: field.onChangeValidate,
                                onSubmitValidate: field.onSubmitValidate,
                            }, field.listenTo, field.required);
                        }
                    });
                };
                const formSchema = schema(formContext);
                registerFieldsFromSchema(formSchema.fields);
            }
        };
        initializeForm();
    }, [schema]);
    const getValue = useCallback((name) => {
        return values[name];
    }, [values]);
    const getErrors = useCallback((name) => {
        return errors[name] || [];
    }, [errors]);
    const registerField = useCallback((name, validators, listenTo, required) => {
        setFields((prev) => {
            if (prev[name]) {
                setValue(name, undefined);
                setErrorsFn(name, []);
            }
            return Object.assign(Object.assign({}, prev), { [name]: { validators, listenTo, required } });
        });
    }, [setValue, setErrorsFn]);
    const unregisterField = useCallback((name) => {
        setFields((prev) => {
            const next = Object.assign({}, prev);
            delete next[name];
            return next;
        });
        setValue(name, undefined);
        setErrorsFn(name, []);
    }, []);
    const validateField = useCallback((name, value, validators) => __awaiter(void 0, void 0, void 0, function* () {
        if (validators.onBlurValidate) {
            try {
                yield validators.onBlurValidate(value, values).parseAsync(value);
                setErrorsFn(name, []);
            }
            catch (error) {
                const errorMessages = error.errors
                    ? error.errors.map((e) => e.message)
                    : [error.message];
                setErrorsFn(name, errorMessages);
                setIsValidForm(false);
            }
        }
    }), [setErrorsFn, setIsValidForm]);
    const handleSubmit = (event) => __awaiter(void 0, void 0, void 0, function* () {
        event === null || event === void 0 ? void 0 : event.preventDefault();
        setIsSubmitting(true);
        const newErrors = Object.assign({}, errors);
        for (const name in fields) {
            const value = values[name];
            const fieldValidators = fields[name];
            if (fieldValidators && fieldValidators.validators.onSubmitValidate) {
                try {
                    yield fieldValidators.validators.onSubmitValidate(value, values).parseAsync(value);
                    setErrorsFn(name, []);
                    newErrors[name] = [];
                }
                catch (error) {
                    const errorMessages = error.errors
                        ? error.errors.map((e) => e.message)
                        : [error.message];
                    setErrorsFn(name, errorMessages);
                    newErrors[name] = errorMessages;
                }
            }
        }
        const valid = isFormValid(newErrors, values, fields);
        setIsValidForm(valid);
        if (valid) {
            onSubmit(values);
        }
        setIsSubmitting(false);
    });
    useEffect(() => {
        const valid = isFormValid(errors, values, fields);
        setIsValidForm(valid);
    }, [errors, values, fields, setIsValidForm]);
    const reset = () => {
        resetForm();
    };
    const formContext = {
        values,
        errors,
        isValidForm,
        isSubmitting,
        fields,
        submit: handleSubmit,
        setValue,
        getValue,
        setErrors: setErrorsFn,
        getErrors,
        registerField,
        unregisterField,
        validateField,
        reset,
        setIsValidForm,
    };
    return (React__default.createElement(FormContext.Provider, { value: formContext },
        React__default.createElement("form", { onSubmit: handleSubmit, className: className },
            schema && (React__default.createElement(FormRenderer, Object.assign({}, formContext, { fields: schema(formContext).fields, values: values }))),
            children &&
                children(Object.assign({ isValid: isFormValid(errors, values, fields) }, formContext)))));
};

const StyledQRForm$1 = styled(Form) `
  margin-top: ${getSpacing("size-06")};
`;
const StyledNotShownForm = styled.div `
  display: flex;
  flex-direction: column;
`;
const StyledNotShownFormHeader = styled.div `
  ${getMarginBottom("size-08")};
  ${responsiveMargin({
    mobile: "size-09",
    tablet: "size-11",
}, "top")}
`;
const StyledNotShownFormTitle = styled.h3 `
  font-family: ${getFont("family-primary")};
  font-weight: 700;
  margin-bottom: ${getSpacing("size-03")};
`;
const StyledNotShownFormDescription = styled.p `
  font-family: ${getFont("family-secondary")};
  margin-bottom: ${getSpacing("size-05")};
`;
const StyledNotShownFormButton = styled(Button) `
  align-self: flex-start;
  white-space: nowrap;
  height: ${getSpacing("size-09")};
  padding: 0 16px;

  i > svg {
    width: 16px;
    height: 16px;
  }
`;

const QRForm = ({ qrTypes, mode, className, navigateToSubdomain }) => {
    const { qrCodeService } = useServices();
    const selectedQRType = useQRStore((state) => state.selectedType);
    const qrTypeConfig = qrTypes.find((qrType) => qrType.type === selectedQRType) ||
        qrTypes.find((qrType) => qrType.type === DEFAULT_QR_TYPE);
    const isFormShown = qrTypeConfig.showForm;
    if (!isFormShown) {
        return (React__default.createElement(StyledNotShownForm, null,
            React__default.createElement(StyledNotShownFormHeader, null,
                React__default.createElement(StyledNotShownFormTitle, null, qrTypeConfig.title),
                React__default.createElement(StyledNotShownFormDescription, null, qrTypeConfig.desription)),
            React__default.createElement(StyledNotShownFormButton, { nofollow: true, dataQA: "qr-type-button-register", label: "Register for free now", iconRight: React__default.createElement(FaChevronRight, null), onClick: () => qrCodeService.navigateToAppSignup(navigateToSubdomain) })));
    }
    const formSchema = getFormSchemaByTypeAndMode(qrTypeConfig.type, mode);
    return (React__default.createElement(StyledQRForm$1, { key: selectedQRType, onSubmit: (_) => undefined, schema: formSchema, className: className }, () => React__default.createElement(QRDesign, null)));
};

const generateQRCodeWithFrame = (qrOptions, qrData, framesRegistry) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d;
    try {
        const qrCodeInstance = new QRCodeStyling(qrOptions);
        const rawQRData = yield qrCodeInstance.getRawData("svg");
        const processedQRSvg = yield processQRSvg(rawQRData);
        const chosenFrame = framesRegistry === null || framesRegistry === void 0 ? void 0 : framesRegistry.find((f) => { var _a; return f.id === ((_a = qrData === null || qrData === void 0 ? void 0 : qrData.qrStyle) === null || _a === void 0 ? void 0 : _a.frameStyle); });
        if (!chosenFrame || chosenFrame.id === "none") {
            return processedQRSvg;
        }
        const composedSVG = yield composeFrameWithQR(chosenFrame, processedQRSvg, (_a = qrData === null || qrData === void 0 ? void 0 : qrData.qrStyle) === null || _a === void 0 ? void 0 : _a.frameText, (_b = qrData === null || qrData === void 0 ? void 0 : qrData.qrStyle) === null || _b === void 0 ? void 0 : _b.frameColor, (_c = qrData === null || qrData === void 0 ? void 0 : qrData.qrStyle) === null || _c === void 0 ? void 0 : _c.frameBackgroundColor, (_d = qrData === null || qrData === void 0 ? void 0 : qrData.qrStyle) === null || _d === void 0 ? void 0 : _d.frameTextColor);
        return composedSVG;
    }
    catch (error) {
        console.error("Error generating QR code with frame:", error);
        return null;
    }
});

const StyledQRRenderer = styled.div `
  margin-top: ${getSpacing("size-06")};
  text-align: center;
  width: 100%;
  position: relative;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 296px;
  }
`;
const StyledQRRendererContent = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  max-width: 296px;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: ${({ $isFixed }) => ($isFixed ? "fixed" : "absolute")};
    ${({ $isFixed }) => !$isFixed && `bottom: 0`};
    transition: transform 0.1s ease-out;
  }
`;
const StyledQRRendererButton = styled(Button) `
  font-weight: 700;
  width: 100%;
  ${getMarginTop("size-05")};

  p {
    text-align: center;
  }
`;
const StyledQRRendererDownloadButton = styled(StyledQRRendererButton) `
  display: flex;
  justify-content: center;

  p {
    width: auto;
  }

  &:hover {
    background-color: ${getColor("neutral", "white")};
  }

  .button-icon {
    width: 24px;
    height: 24px;

    svg {
      width: 80%;
      height: 80%;
    }
  }
`;
const StyledQRComponentWrapper = styled.div `
  box-sizing: border-box;
  border-radius: 8px;
  border: 5px solid ${getColor("neutral", 200)};
  width: inherit;
  height: 300px;
  ${getPadding("size-07")};
`;
styled.div `
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  canvas {
    width: 100%;
    height: 100%;
  }
`;
const StyledFramedQRComponent = styled.div `
  width: 100%;
  height: 100%;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const QRCodeComponent = forwardRef(({ options, className, dataQA }, ref) => {
    const qrCodeRef = useRef(null);
    const qrCodeInstance = useRef(null);
    const [finalSVG, setFinalSVG] = useState(null);
    const { backgroundOptions, cornersDotOptions, cornersSquareOptions, dotsOptions, image, data } = options;
    const updatableProperties = useMemo(() => ({
        backgroundOptions,
        cornersDotOptions,
        cornersSquareOptions,
        dotsOptions,
        image,
    }), [backgroundOptions, cornersDotOptions, cornersSquareOptions, dotsOptions, image]);
    useEffect(() => {
        if (!qrCodeInstance.current) {
            qrCodeInstance.current = new QRCodeStyling(options);
            if (qrCodeRef.current && qrCodeRef.current.children.length === 0) {
                qrCodeInstance.current.append(qrCodeRef.current);
            }
            if (ref && typeof ref === "object") {
                ref.current = qrCodeInstance.current;
            }
        }
    }, [options, ref]);
    useEffect(() => {
        const generateQR = () => __awaiter(void 0, void 0, void 0, function* () {
            const svg = yield generateQRCodeWithFrame(options, data);
            setFinalSVG(svg);
        });
        if (qrCodeInstance.current) {
            qrCodeInstance.current.update(updatableProperties);
            generateQR();
        }
    }, [updatableProperties]);
    return (React__default.createElement(StyledFramedQRComponent, { "data-qa": dataQA, className: className, dangerouslySetInnerHTML: { __html: finalSVG || "" } }));
});

const FrameTicket = ({ frameColor = "#404040", frameBackgroundColor = "#FFFFFF", frameTextColor = "#FFFFFF", frameText = "SCAN ME", }) => (React__default.createElement("svg", { width: "50", height: "66", viewBox: "0 0 50 66", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React__default.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M43.6364 5.24521e-06L5.45455 0C5.45455 3.01247 3.01247 5.45455 6.19888e-06 5.45455L0 60C3.01246 60 5.45455 62.4421 5.45455 65.4546H43.6364C43.6364 62.4421 46.0784 60 49.0909 60V5.45455C46.0784 5.45455 43.6364 3.01247 43.6364 5.24521e-06Z", fill: frameColor }),
    React__default.createElement("rect", { x: "3.27295", y: "14.7273", width: "42.5455", height: "42.5455", fill: frameBackgroundColor }),
    React__default.createElement("rect", { id: "qr", x: "8", y: "20", width: "32.73", height: "32.73", fill: "#D9D9D9" }),
    React__default.createElement("text", { id: "text", x: "24", y: "10", fill: frameTextColor, textAnchor: "middle", fontSize: "5", fontFamily: "Arial" }, frameText)));

const FrameMuseum = ({ frameColor = "#404040", frameBackgroundColor = "#FFFFFF", frameTextColor = "#FFFFFF", frameText = "SCAN ME", }) => (React__default.createElement("svg", { width: "50", height: "59", viewBox: "0 0 50 59", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React__default.createElement("path", { id: "frame-color", fillRule: "evenodd", clipRule: "evenodd", d: "M0 49.8701V0H49.0909V49.8701H0ZM1.68384 1.03896H47.4071L45.5077 3.11688H3.58319L1.68384 1.03896ZM1.68384 48.8312L3.58319 46.7533H45.5077L47.4071 48.8312H1.68384ZM45 4.15584H4.09091V45.7143H45V4.15584Z", fill: frameColor }),
    React__default.createElement("path", { id: "frame-cbackground-color", d: "M46.0229 3.11694H3.06836V46.3377H46.0229V3.11694Z", fill: frameBackgroundColor }),
    React__default.createElement("rect", { id: "qr", x: "8.15137", y: "8.72729", width: "32.73", height: "32.73", fill: "#D9D9D9" }),
    React__default.createElement("text", { id: "text", x: "24", y: "58", fill: frameTextColor, textAnchor: "middle", fontSize: "5", fontFamily: "Arial" }, frameText)));

const FrameBlack = ({ frameColor = "#404040", frameBackgroundColor = "#FFFFFF", frameTextColor, frameText = "SCAN ME", }) => {
    return (React__default.createElement("svg", { width: "50", height: "66", viewBox: "0 0 50 66", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React__default.createElement("path", { id: "frame-color", d: "M46.4945 0.272705H2.85813C1.3519 0.272705 0.130859 1.49375 0.130859 2.99997V62.9999C0.130859 64.5062 1.3519 65.7272 2.85813 65.7272H46.4945C48.0007 65.7272 49.2218 64.5062 49.2218 62.9999V2.99997C49.2218 1.49375 48.0007 0.272705 46.4945 0.272705Z", fill: frameColor }),
        React__default.createElement("path", { id: "frame-background-color", d: "M45.7447 3.34094H3.19922V45.8864H45.7447V3.34094Z", fill: frameBackgroundColor }),
        React__default.createElement("text", { id: "text", x: "24", y: "58", fill: frameTextColor, textAnchor: "middle", fontSize: "5", fontFamily: "Arial" }, frameText),
        React__default.createElement("rect", { id: "qr", x: "8", y: "9", width: "32.73", height: "32.73", fill: "#D9D9D9" })));
};

const FrameBoard = ({ frameColor = "#404040", frameBackgroundColor = "#FFFFFF", frameTextColor = "#FFFFFF", frameText = "SCAN ME", }) => (React__default.createElement("svg", { width: "42", height: "57", viewBox: "0 0 42 57", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React__default.createElement("path", { id: "frame-background-color", d: "M1 7.38205C1 6.61877 1.63547 6 2.41936 6H39.5807C40.3645 6 41 6.61877 41 7.38205V53.618C41 54.3812 40.3645 55 39.5807 55H2.41936C1.63547 55 1 54.3812 1 53.618V7.38205Z", fill: frameBackgroundColor }),
    React__default.createElement("path", { id: "frame-color1", d: "M1.43707 7.01141C1.43707 6.23476 2.06667 5.60516 2.84332 5.60516H39.6615C40.4381 5.60516 41.0677 6.23476 41.0677 7.01141V54.0569C41.0677 54.8335 40.4381 55.4631 39.6615 55.4631H2.84332C2.06667 55.4631 1.43707 54.8335 1.43707 54.0569V7.01141Z", stroke: frameColor, strokeWidth: "1.27841" }),
    React__default.createElement("path", { id: "frame-color2", d: "M11.5361 5.60516C11.5361 4.26367 12.6236 3.17618 13.9651 3.17618H28.539C29.8805 3.17618 30.968 4.26367 30.968 5.60516C30.968 6.94664 29.8805 8.03413 28.539 8.03413H13.9651C12.6236 8.03413 11.5361 6.94664 11.5361 5.60516Z", fill: frameColor }),
    React__default.createElement("path", { id: "frame-color3", fillRule: "evenodd", clipRule: "evenodd", d: "M21.252 5.60515C22.5934 5.60515 23.681 4.51766 23.681 3.17617C23.681 1.83468 22.5934 0.747192 21.252 0.747192C19.9106 0.747192 18.8231 1.83468 18.8231 3.17617C18.8231 4.51766 19.9106 5.60515 21.252 5.60515ZM21.252 4.39066C21.9227 4.39066 22.4665 3.84692 22.4665 3.17617C22.4665 2.50542 21.9227 1.96168 21.252 1.96168C20.5813 1.96168 20.0376 2.50542 20.0376 3.17617C20.0376 3.84692 20.5813 4.39066 21.252 4.39066Z", fill: frameColor }),
    React__default.createElement("text", { id: "text", x: "20", y: "51", fill: frameTextColor, textAnchor: "middle", fontSize: "5", fontFamily: "Arial" }, frameText),
    React__default.createElement("rect", { id: "qr", x: "5", y: "12", width: "32.73", height: "32.73", fill: "#D9D9D9" })));

const FrameMobile = ({ frameColor = "#404040", frameBackgroundColor = "#FFFFFF", frameTextColor = "#FFFFFF", frameText = "SCAN ME", }) => (React__default.createElement("svg", { width: "50", height: "66", viewBox: "0 0 50 66", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React__default.createElement("path", { id: "frame-color", fillRule: "evenodd", clipRule: "evenodd", d: "M4.29306 0.272705C2.03371 0.272705 0.202148 2.10427 0.202148 4.36361V61.6363C0.202148 63.8957 2.03371 65.7272 4.29306 65.7272H45.2021C47.4615 65.7272 49.2931 63.8957 49.2931 61.6363V4.36361C49.2931 2.10427 47.4615 0.272705 45.2021 0.272705H4.29306ZM28.4295 60.4091C28.4295 62.4425 26.7811 64.0909 24.7477 64.0909C22.7142 64.0909 21.0658 62.4425 21.0658 60.4091C21.0658 58.3757 22.7142 56.7273 24.7477 56.7273C26.7811 56.7273 28.4295 58.3757 28.4295 60.4091ZM27.4065 60.4091C27.4065 61.8776 26.216 63.0682 24.7474 63.0682C23.2788 63.0682 22.0883 61.8776 22.0883 60.4091C22.0883 58.9405 23.2788 57.75 24.7474 57.75C26.216 57.75 27.4065 58.9405 27.4065 60.4091Z", fill: frameColor }),
    React__default.createElement("path", { id: "frame-background-color", d: "M46.0201 12.5454H3.47461V55.0909H46.0201V12.5454Z", fill: frameBackgroundColor }),
    React__default.createElement("text", { id: "text", x: "24", y: "9", fill: frameTextColor, textAnchor: "middle", fontSize: "5", fontFamily: "Arial" }, frameText),
    React__default.createElement("rect", { id: "qr", x: "8", y: "17", width: "33.73", height: "33.73", fill: "#D9D9D9" })));

const FrameNone = ({ frameColor = "#404040", frameBackgroundColor = "#FFFFFF", frameTextColor = "#FFFFFF", frameText = "SCAN ME", }) => (React__default.createElement("svg", { width: "50", height: "66", viewBox: "0 0 50 66", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React__default.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M45.2725 1.29543H4.36337C2.66886 1.29543 1.29519 2.6691 1.29519 4.36361V61.6363C1.29519 63.3308 2.66886 64.7045 4.36337 64.7045H45.2725C46.967 64.7045 48.3406 63.3308 48.3406 61.6363V4.36361C48.3406 2.6691 46.967 1.29543 45.2725 1.29543ZM4.36337 0.272705C2.10402 0.272705 0.272461 2.10427 0.272461 4.36361V61.6363C0.272461 63.8957 2.10402 65.7272 4.36337 65.7272H45.2725C47.5318 65.7272 49.3634 63.8957 49.3634 61.6363V4.36361C49.3634 2.10427 47.5318 0.272705 45.2725 0.272705H4.36337Z", fill: "#404040" }),
    React__default.createElement("path", { d: "M24.6676 39.7711C26.2901 39.7721 27.8626 39.2299 29.1178 38.2368C30.3726 37.2438 31.232 35.8613 31.5495 34.3251C31.8671 32.7887 31.6231 31.1937 30.8592 29.8115C30.0951 28.4295 28.8584 27.346 27.3596 26.7456C25.861 26.1453 24.1931 26.0653 22.6402 26.5192C21.0872 26.9731 19.7455 27.9329 18.8431 29.2351C17.941 30.5373 17.5344 32.1011 17.6926 33.6602C17.8508 35.2193 18.5641 36.6772 19.7108 37.7855C20.3604 38.4159 21.1328 38.9159 21.9834 39.2567C22.8341 39.5975 23.7466 39.7723 24.6676 39.7711ZM28.6397 36.8351C27.6734 37.7657 26.3885 38.3239 25.0263 38.4051C23.6641 38.4863 22.3179 38.0851 21.2402 37.2764L29.0967 29.6736C29.9354 30.7169 30.3517 32.0195 30.2676 33.3377C30.1835 34.6559 29.6049 35.8992 28.6397 36.8351ZM20.6954 29.1644C21.6617 28.2339 22.9466 27.6756 24.3088 27.5944C25.671 27.5132 27.0172 27.9145 28.0949 28.7231L20.2384 36.3259C19.4009 35.2852 18.9853 33.9856 19.0694 32.6703C19.1535 31.355 19.7317 30.1145 20.6955 29.1813L20.6954 29.1644Z", fill: "#404040" })));

const FrameWhite = ({ frameColor = "#404040", frameBackgroundColor = "#FFFFFF", frameTextColor = "#000000", frameText = "SCAN ME", }) => {
    return (React__default.createElement("svg", { width: "50", height: "66", viewBox: "0 0 50 66", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React__default.createElement("path", { d: "M47.106 0.272705H2.106C0.976326 0.272705 0.0605469 1.18848 0.0605469 2.31816V63.6818C0.0605469 64.8114 0.976326 65.7272 2.106 65.7272H47.106C48.2357 65.7272 49.1514 64.8114 49.1514 63.6818V2.31816C49.1514 1.18848 48.2357 0.272705 47.106 0.272705Z", fill: "white" }),
        React__default.createElement("path", { id: "frame-background-color", d: "M47 1H2C0.895431 1 0 1.89543 0 3V47C0 48.1046 0.89543 49 2 49H47C48.1046 49 49 48.1046 49 47V3C49 1.89543 48.1046 1 47 1Z", fill: frameBackgroundColor }),
        React__default.createElement("path", { id: "frame-color", fillRule: "evenodd", clipRule: "evenodd", d: "M47.106 1.29543H2.106C1.54117 1.29543 1.08327 1.75332 1.08327 2.31816V47.3182C1.08327 47.883 1.54117 48.3409 2.106 48.3409H47.106C47.6708 48.3409 48.1287 47.883 48.1287 47.3182V2.31816C48.1287 1.75332 47.6708 1.29543 47.106 1.29543ZM2.106 0.272705C0.976328 0.272705 0.0605469 1.18849 0.0605469 2.31816V47.3182C0.0605469 48.4478 0.976329 49.3636 2.106 49.3636H47.106C48.2357 49.3636 49.1515 48.4478 49.1515 47.3182V2.31816C49.1515 1.18849 48.2357 0.272705 47.106 0.272705H2.106Z", fill: frameColor }),
        React__default.createElement("text", { id: "text", x: "24", y: "60", fill: frameTextColor, textAnchor: "middle", fontSize: "5", fontFamily: "Arial" }, frameText),
        React__default.createElement("rect", { id: "qr", x: "6.5", y: "6.5", width: "36", height: "36", fill: "#D9D9D9" })));
};

const FrameButton = ({ frameColor = "#404040", frameBackgroundColor = "#FFFFFF", frameTextColor = "#FFFFFF", frameText = "SCAN ME", }) => (React__default.createElement("svg", { width: "51", height: "66", viewBox: "0 0 51 66", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React__default.createElement("path", { id: "frame-color", d: "M48.0347 0.272705H3.03471C1.90504 0.272705 0.989258 1.18848 0.989258 2.31816V47.3182C0.989258 48.4478 1.90504 49.3636 3.03471 49.3636H48.0347C49.1644 49.3636 50.0802 48.4478 50.0802 47.3182V2.31816C50.0802 1.18848 49.1644 0.272705 48.0347 0.272705Z", fill: frameColor }),
    React__default.createElement("path", { id: "frame-color", d: "M48.0347 51.4091H3.03471C1.90504 51.4091 0.989258 52.3249 0.989258 53.4545V63.6818C0.989258 64.8115 1.90504 65.7273 3.03471 65.7273H48.0347C49.1644 65.7273 50.0802 64.8115 50.0802 63.6818V53.4545C50.0802 52.3249 49.1644 51.4091 48.0347 51.4091Z", fill: frameColor }),
    React__default.createElement("path", { id: "frame-background-color", d: "M46.6031 3.34094H4.05762V45.8864H46.6031V3.34094Z", fill: frameBackgroundColor }),
    React__default.createElement("rect", { id: "qr", x: "9", y: "9", width: "32.73", height: "32.73", fill: "#D9D9D9" }),
    React__default.createElement("text", { id: "text", x: "24", y: "60", fill: frameTextColor, textAnchor: "middle", fontSize: "5", fontFamily: "Arial" }, frameText)));

const FrameCafe = ({ frameColor = "#404040", frameBackgroundColor = "#FFFFFF", frameTextColor = "#FFFFFF", frameText = "SCAN ME", }) => (React__default.createElement("svg", { width: "42", height: "43", viewBox: "0 0 42 43", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React__default.createElement("path", { id: "frame-background-color", d: "M7.32269 2.84088C7.32269 2.01246 7.99426 1.34088 8.82269 1.34088H33.8227C34.6512 1.34088 35.3227 2.01245 35.3227 2.84088V22.8409C35.3227 25.8784 32.8603 28.3409 29.8227 28.3409H12.8227C9.78513 28.3409 7.32269 25.8784 7.32269 22.8409V2.84088Z", fill: frameBackgroundColor }),
    React__default.createElement("path", { id: "frame-color", d: "M7.00452 2.56815C7.00452 1.7209 7.69135 1.03406 8.53861 1.03406H34.1068C34.9541 1.03406 35.6409 1.72089 35.6409 2.56815V23.0227C35.6409 26.1293 33.1225 28.6477 30.0159 28.6477H12.6295C9.52292 28.6477 7.00452 26.1293 7.00452 23.0227V2.56815Z", stroke: frameColor, strokeWidth: "1.02273" }),
    React__default.createElement("path", { id: "frame-background-color", d: "M39.3739 17.7439C38.4714 18.6465 37.2901 19.2062 36.0325 19.3392V8.297C37.2901 8.43009 38.4714 8.98979 39.3739 9.89231C40.4151 10.9335 41 12.3457 41 13.8181C41 15.2906 40.4151 16.7028 39.3739 17.7439Z", fill: frameBackgroundColor }),
    React__default.createElement("path", { id: "frame-color", d: "M39.0557 17.7439C38.1532 18.6465 36.9719 19.2062 35.7143 19.3392V8.297C36.9719 8.43009 38.1532 8.98979 39.0557 9.89231C40.0969 10.9335 40.6818 12.3457 40.6818 13.8181C40.6818 15.2906 40.0969 16.7028 39.0557 17.7439Z", stroke: frameColor, strokeWidth: "1.16883" }),
    React__default.createElement("path", { id: "frame-color", d: "M2.40234 27.4546C2.40234 27.2663 2.55497 27.1136 2.74325 27.1136H40.4137C40.602 27.1136 40.7546 27.2663 40.7546 27.4546C40.7546 29.5256 39.0757 31.2046 37.0046 31.2046H6.15234C4.08128 31.2046 2.40234 29.5256 2.40234 27.4546Z", fill: frameColor }),
    React__default.createElement("text", { id: "text", x: "20", y: "40", fill: frameTextColor, textAnchor: "middle", fontSize: "5", fontFamily: "Arial" }, frameText),
    React__default.createElement("rect", { id: "qr", x: "10.7617", y: "4.27985", width: "21.122", height: "21.122", fill: "#D9D9D9" })));

const FrameEnvelope = ({ frameColor = "#FFFFFF", frameBackgroundColor = "#FFFFFF", frameTextColor = "#000000", frameText = "SCAN ME", }) => (React__default.createElement("svg", { width: "39", height: "52", viewBox: "0 0 39 52", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React__default.createElement("path", { d: "M0.771484 49.8125V27.2242C0.771484 26.8531 0.937143 26.5013 1.22325 26.265L18.0943 12.3258C18.5484 11.9506 19.2034 11.9453 19.6636 12.313L37.1221 26.2646C37.4176 26.5007 37.5897 26.8584 37.5897 27.2367V49.8125C37.5897 50.4997 37.0326 51.0568 36.3453 51.0568H2.0158C1.32858 51.0568 0.771484 50.4997 0.771484 49.8125Z", fill: "#404040", stroke: "#404040", strokeWidth: "1.24432" }),
    React__default.createElement("path", { id: "frame-background-color", d: "M4.86244 1.56451H33.4988C34.2853 1.56451 34.9229 2.20213 34.9229 2.98866V41.8523C34.9229 42.6389 34.2853 43.2765 33.4988 43.2765H4.86244C4.0759 43.2765 3.43829 42.6389 3.43829 41.8523V2.98866C3.43829 2.20213 4.0759 1.56451 4.86244 1.56451Z", fill: frameBackgroundColor }),
    React__default.createElement("path", { d: "M4.86281 1.56451H33.4992C34.2857 1.56451 34.9233 2.20213 34.9233 2.98866V41.8523C34.9233 42.6389 34.2857 43.2765 33.4992 43.2765H4.86281C4.07627 43.2765 3.43866 42.6389 3.43866 41.8523V2.98866C3.43866 2.20213 4.07627 1.56451 4.86281 1.56451Z", stroke: "#404040", strokeWidth: "1.24261" }),
    React__default.createElement("path", { id: "frame-color", d: "M36.4831 50.082L22.4998 39.5534C22.1562 39.2947 22.1725 38.7736 22.5318 38.5369L36.515 29.3244C36.9287 29.0518 37.4795 29.3485 37.4795 29.8439V49.585C37.4795 50.0982 36.8931 50.3907 36.4831 50.082ZM1.87926 29.5315L15.8626 40.0601C16.2062 40.3189 16.1899 40.84 15.8306 41.0767L1.84732 50.2892C1.43364 50.5617 0.882876 50.265 0.882876 49.7696L0.882874 30.0285C0.882874 29.5153 1.46926 29.2228 1.87926 29.5315Z", fill: frameColor === "#404040" ? "#FFFFFF" : frameColor, stroke: "#404040", strokeWidth: "1.24432" }),
    React__default.createElement("path", { id: "frame-color", d: "M0.771484 49.7877V47.8522C0.771484 47.4772 0.9341 47.1214 1.2157 46.8803L18.0869 32.4335C18.5437 32.0423 19.2084 32.0368 19.6714 32.4203L37.1297 46.8797C37.4207 47.1207 37.5897 47.4826 37.5897 47.8649V49.7877C37.5897 50.4886 37.0326 51.0568 36.3453 51.0568H2.0158C1.32858 51.0568 0.771484 50.4886 0.771484 49.7877Z", fill: frameColor === "#404040" ? "#FFFFFF" : frameColor, stroke: "#404040", strokeWidth: "1.24432" }),
    React__default.createElement("rect", { id: "qr", x: "6.20917", y: "4.16058", width: "25.944", height: "25.944", fill: "#D9D9D9" }),
    React__default.createElement("text", { id: "text", x: "19", y: "48", fill: frameTextColor, textAnchor: "middle", fontSize: "5", fontFamily: "Arial" }, frameText)));

const framesRegistry = [
    {
        id: "none",
        name: "None Frame",
        Component: FrameNone,
        qrPlaceholderId: "qr",
        textPlaceholderId: "text",
    },
    {
        id: "mobile",
        name: "Mobile Frame",
        Component: FrameMobile,
        qrPlaceholderId: "qr",
        textPlaceholderId: "text",
    },
    {
        id: "black",
        name: "Black Frame",
        Component: FrameBlack,
        qrPlaceholderId: "qr",
        textPlaceholderId: "text",
    },
    {
        id: "white",
        name: "White Frame",
        Component: FrameWhite,
        qrPlaceholderId: "qr",
        textPlaceholderId: "text",
    },
    {
        id: "button",
        name: "Button Frame",
        Component: FrameButton,
        qrPlaceholderId: "qr",
        textPlaceholderId: "text",
    },
    {
        id: "ticket",
        name: "Ticket Frame",
        Component: FrameTicket,
        qrPlaceholderId: "qr",
        textPlaceholderId: "text",
    },
    {
        id: "museum",
        name: "Museum Frame",
        Component: FrameMuseum,
        qrPlaceholderId: "qr",
        textPlaceholderId: "text",
    },
    {
        id: "cafe",
        name: "Cafe Frame",
        Component: FrameCafe,
        qrPlaceholderId: "qr",
        textPlaceholderId: "text",
    },
    {
        id: "board",
        name: "Board Frame",
        Component: FrameBoard,
        qrPlaceholderId: "qr",
        textPlaceholderId: "text",
    },
    {
        id: "envelope",
        name: "Envelope Frame",
        Component: FrameEnvelope,
        qrPlaceholderId: "qr",
        textPlaceholderId: "text",
    },
];

const useFramedQR = ({ qrData, qrOptions, isValidForm }) => {
    const [finalSVG, setFinalSVG] = useState(null);
    const [isQRCodeGenerated, setIsQRCodeGenerated] = useState(false);
    const qrCodeInstanceRef = useRef(null);
    useEffect(() => {
        if (isQRCodeGenerated) {
            setIsQRCodeGenerated(false);
            setFinalSVG(null);
        }
    }, [qrOptions.data]);
    useEffect(() => {
        if (!qrOptions.data)
            return;
        qrCodeInstanceRef.current = new QRCodeStyling(qrOptions);
    }, [qrOptions]);
    useEffect(() => {
        const generateFrameSVG = () => __awaiter(void 0, void 0, void 0, function* () {
            if (!isQRCodeGenerated || !isValidForm || !qrOptions.data || !qrCodeInstanceRef.current) {
                setFinalSVG(null);
                return;
            }
            const svg = yield generateQRCodeWithFrame(qrOptions, qrData, framesRegistry);
            setFinalSVG(svg);
        });
        generateFrameSVG();
    }, [isQRCodeGenerated, isValidForm, qrOptions, qrData]);
    return {
        finalSVG,
        qrCodeInstanceRef,
        isQRCodeGenerated,
        setIsQRCodeGenerated,
    };
};

const useFloatingPosition = (qrData) => {
    const [isFixed, setIsFixed] = useState(true);
    const lastPositionRef = useRef("fixed");
    const rendererRef = useRef(null);
    const contentRef = useRef(null);
    const lastToggled = useUIStore((state) => state.lastToggledAccordion);
    const expandedAccordions = useUIStore((state) => state.expandedAccordions);
    const qrType = qrData === null || qrData === void 0 ? void 0 : qrData.qrType;
    const frameType = qrData === null || qrData === void 0 ? void 0 : qrData.qrStyle.frameStyle;
    const window = getWindow();
    const handleScrollPosition = useCallback(() => {
        if (!rendererRef.current || !contentRef.current)
            return;
        const rendererRect = rendererRef.current.getBoundingClientRect();
        const contentRect = contentRef.current.getBoundingClientRect();
        if (!window)
            return;
        const threshold = 140;
        const scrollY = window.scrollY;
        const rendererTop = rendererRect.top + scrollY;
        const contentTop = isFixed ? scrollY + contentRect.top : contentRect.top + scrollY;
        const contentHeight = contentRect.height;
        const rendererHeight = rendererRect.height;
        const stopPoint = rendererTop + rendererHeight - contentHeight;
        if (rendererHeight <= 480) {
            setIsFixed(false);
            lastPositionRef.current = "absolute";
        }
        else if (scrollY < rendererTop - threshold) {
            setIsFixed(true);
            lastPositionRef.current = "fixed";
        }
        else if (scrollY >= stopPoint - 300) {
            setIsFixed(false);
            lastPositionRef.current = "absolute";
        }
        else {
            if (contentTop >= threshold) {
                setIsFixed(true);
                lastPositionRef.current = "fixed";
            }
        }
    }, [isFixed]);
    useEffect(() => {
        if (!window)
            return;
        window.addEventListener("scroll", handleScrollPosition);
        handleScrollPosition();
        return () => {
            window.removeEventListener("scroll", handleScrollPosition);
        };
    }, [handleScrollPosition]);
    useEffect(() => {
        if (!window)
            return;
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [qrType]);
    useEffect(() => {
        if (!window)
            return;
        const isStylesOpen = expandedAccordions["styles-accordion"] === true;
        const isLogoOpen = expandedAccordions["logo-accordion"] === true;
        const scrollToAccordion = (accordionId, offset = 210) => {
            const el = document.getElementById(accordionId);
            if (!el)
                return;
            const elementPosition = el.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - offset;
            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth",
            });
        };
        if (isStylesOpen && isLogoOpen) {
            if (lastToggled === "logo-accordion") {
                scrollToAccordion("logo-accordion");
            }
            else {
                scrollToAccordion("styles-accordion");
            }
            return;
        }
        if (isStylesOpen && !isLogoOpen) {
            scrollToAccordion("styles-accordion");
            return;
        }
        if (!isStylesOpen && isLogoOpen) {
            scrollToAccordion("logo-accordion");
            return;
        }
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [expandedAccordions, lastToggled, frameType]);
    return {
        isFixed,
        rendererRef,
        contentRef,
        lastPositionRef,
    };
};

const buildVcard = (data) => {
    return `BEGIN:VCARD
VERSION:3.0
FN:${data.fullName}
EMAIL:${data.email}
TEL;type=CELL:${data.mobilePhone}
TEL;type=WORK:${data.landlinePhone}
TEL;type=WORK,FAX:${data.landlinePhone}
URL:${data.website}
ORG:${data.companyName}
TITLE:${data.profession}
NOTE:${data.summary}
ADR;type=WORK:${data.numeration} ${data.address};${data.city};${data.state};${data.zipCode};${data.country}
END:VCARD`;
};
const getDataToEncode = (data) => {
    if (!data || !data.qrData)
        return "";
    try {
        switch (data.qrType) {
            case "vCard": {
                const vCardData = data.qrData;
                if (!vCardData.fullName)
                    return "";
                return buildVcard(vCardData);
            }
            case "url-static": {
                const urlData = data.qrData;
                return urlData.url || "";
            }
            case "wifi": {
                const wifiData = data.qrData;
                return `WIFI:T:${wifiData.qrNetworkSecurityType || ""};S:${wifiData.qrNetworkName || ""};P:${wifiData.qrNetworkPassword || ""};H:${wifiData.qrNetworkHidden || false};`;
            }
            default:
                return "";
        }
    }
    catch (error) {
        console.error("Error encoding QR data:", error);
        return "";
    }
};
const mapQRDataToOptions = (qrData, type, errorCorrectionLevel) => {
    var _a, _b;
    const qrStyle = qrData.qrStyle;
    const cornerDotType = (_a = qrStyle.cornerStyle) === null || _a === void 0 ? void 0 : _a.split("-")[1];
    const cornerSquareType = (_b = qrStyle.cornerStyle) === null || _b === void 0 ? void 0 : _b.split("-")[0];
    return {
        width: 300,
        height: 300,
        type: type || "canvas",
        data: getDataToEncode(qrData) || "",
        image: qrStyle.logoUrl,
        dotsOptions: {
            type: qrStyle.dotsStyle,
            color: qrStyle.dotsColor,
        },
        cornersDotOptions: {
            color: qrStyle === null || qrStyle === void 0 ? void 0 : qrStyle.cornerBackgroundColor,
            type: cornerDotType,
        },
        cornersSquareOptions: {
            type: cornerSquareType && cornerSquareType === "rounded" ? "extra-rounded" : cornerSquareType,
            color: qrStyle.cornerBorderColor,
        },
        backgroundOptions: {
            color: qrData.qrStyle.backgroundColor,
        },
        imageOptions: {
            crossOrigin: "anonymous",
            margin: 10,
            saveAsBlob: true,
            imageSize: 0.4,
        },
        qrOptions: {
            errorCorrectionLevel: errorCorrectionLevel || "Q",
        },
    };
};

const getQRPlaceholderSvgByTheme = (theme) => {
    switch (theme) {
        case "daikiri":
            return (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "300", height: "300", fill: "none", viewBox: "0 0 201 200", style: { width: "100%", height: "100%" } },
                React__default.createElement("path", { fill: "#D9D9D9", d: "M.5 121.875c0-6.904 5.59644-12.5 12.5-12.5h65.625c6.9036 0 12.5 5.596 12.5 12.5V187.5c0 6.904-5.5964 12.5-12.5 12.5H13C6.09644 200 .5 194.404.5 187.5v-65.625Zm72.9688 32.539.0424-14.925c.0197-6.918-5.5825-12.536-12.4999-12.536H30.9688c-6.9036 0-12.5 5.597-12.5 12.5v30.043c0 6.917 5.6181 12.519 12.5355 12.5l14.9254-.043 15.0284-.064c6.8618-.029 12.4171-5.585 12.4464-12.447l.0643-15.028Z" }),
                React__default.createElement("path", { fill: "#D9D9D9", d: "M37.2188 145.703h17.5781v17.578H37.2188v-17.578ZM.5 12.5C.5 5.59644 6.09644 0 13 0h65.625c6.9036 0 12.5 5.59644 12.5 12.5v65.625c0 6.9036-5.5964 12.5-12.5 12.5H13c-6.90356 0-12.5-5.5964-12.5-12.5V12.5Zm73.0469 17.9688c0-6.9036-5.5965-12.5-12.5-12.5H30.9688c-6.9036 0-12.5 5.5964-12.5 12.5v30.0781c0 6.9035 5.5964 12.5 12.5 12.5h30.0781c6.9035 0 12.5-5.5965 12.5-12.5V30.4688Z" }),
                React__default.createElement("path", { fill: "#D9D9D9", d: "M37.2188 36.7188h17.5781v17.5781H37.2188V36.7188ZM109.875 12.5c0-6.90356 5.596-12.5 12.5-12.5H188c6.904 0 12.5 5.59644 12.5 12.5v65.625c0 6.9036-5.596 12.5-12.5 12.5h-65.625c-6.904 0-12.5-5.5964-12.5-12.5V12.5Zm72.656 17.9688c0-6.9036-5.596-12.5-12.5-12.5h-30.078c-6.903 0-12.5 5.5964-12.5 12.5v30.0781c0 6.9035 5.597 12.5 12.5 12.5h30.078c6.904 0 12.5-5.5965 12.5-12.5V30.4688Z" }),
                React__default.createElement("path", { fill: "#D9D9D9", d: "M146.203 36.7188h17.578v17.5781h-17.578V36.7188ZM190.431 109.375h-70.487v10.069h-10.069v70.487h10.069V200h70.487v-10.069H200.5v-70.487h-10.069v-10.069Zm0 10.069v70.487h-70.487v-70.487h70.487Zm-40.278 10.07h10.069v30.208h20.139v10.07h-30.208v-40.278Z" })));
        case "mojito":
            return (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "300", height: "300", viewBox: "0 0 200 200", style: { width: "100%", height: "100%" } },
                React__default.createElement("path", { fill: "#D9D9D9", d: "M79.9988 79.9998h80v80h-80z" }),
                React__default.createElement("path", { fill: "#D9D9D9", d: "M0 40v160h80v-40H40V40h120.001v40h40V0H0v40ZM159.799 159.8h40v40h-40z" })));
        case "kalimotxo":
            return (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "300", height: "300", viewBox: "0 0 200 200", style: { width: "100%", height: "100%" } },
                React__default.createElement("path", { fill: "#D4D5D8", d: "M0 17.1429C0 7.67512 7.67512 0 17.1429 0h62.1428C82.4416 0 85 2.55837 85 5.71428V22.8571c0 3.156-2.5584 5.7143-5.7143 5.7143h-45c-3.1559 0-5.7143 2.5584-5.7143 5.7143V165.714c0 3.156 2.5584 5.715 5.7143 5.715H165.714c3.156 0 5.715-2.559 5.715-5.715v-45c0-3.156 2.558-5.714 5.714-5.714h17.143c3.156 0 5.714 2.558 5.714 5.714v62.143c0 9.468-7.675 17.143-17.143 17.143H17.1429C7.67512 200 0 192.325 0 182.857V17.1429Z" }),
                React__default.createElement("path", { fill: "#D4D5D8", d: "M120.714 0C117.558 0 115 2.55837 115 5.71428V57.5H63.2143c-3.1559 0-5.7143 2.5584-5.7143 5.7143v73.5717c0 3.156 2.5584 5.714 5.7143 5.714h73.5717c3.156 0 5.714-2.558 5.714-5.714V85h51.786c3.156 0 5.714-2.5584 5.714-5.7143V5.71428C200 2.55837 197.442 0 194.286 0h-73.572Z" })));
        case "negroni":
            return (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "300", height: "300", fill: "none", viewBox: "0 0 200 200", style: { width: "100%", height: "100%" } },
                React__default.createElement("g", { fill: "#E0E0E0" },
                    React__default.createElement("path", { d: "M-.000147 136.153c.000001-11.472 8.913657-20.742 19.944247-20.742h39.1087V78.4472c0-11.4716 8.9136-20.7416 19.9443-20.7416h39.6659V20.7415c0-11.47161 8.913-20.74159787 19.944-20.74159691l41.449.00000362C191.086-.00009233 200 9.2699 200 20.7415v43.2213c0 11.3558-8.914 20.6257-19.944 20.6257H140.39v30.8225h39.666c11.03 0 19.944 9.27 19.944 20.742v43.105C200 190.73 191.086 200 180.056 200h-41.449c-11.031 0-19.944-9.27-19.944-20.742v-36.964H81.3369v36.964c0 11.472-8.9137 20.742-19.8329 20.742H19.9441C8.9135 200-.00015178 190.73-.00015077 179.258l.00000377-43.105ZM17.4929 7.06827c-3.454 0-6.3509 2.89687-6.3509 6.48903l-.0001 31.0544c0 3.5921 2.897 6.489 6.351 6.489h29.7493c3.4541 0 6.2396-2.8969 6.2396-6.489V13.5573c0-3.59215-2.7855-6.48902-6.2396-6.48903H17.4929Z" }))));
        default:
            return React__default.createElement("svg", null);
    }
};

const QRRenderer = ({ className, navigateToSubdomain, theme }) => {
    const { qrCodeService } = useServices();
    const qrData = useQRStore((state) => state.qrData);
    const isLoading = useQRStore((state) => state.isLoading);
    const isValidForm = useQRStore((state) => state.isValidForm);
    const frameType = useQRStore((state) => { var _a, _b; return (_b = (_a = state.qrData) === null || _a === void 0 ? void 0 : _a.qrStyle) === null || _b === void 0 ? void 0 : _b.frameStyle; });
    const encodedData = useMemo(() => {
        if (!qrData)
            return "";
        return getDataToEncode(qrData);
    }, [qrData]);
    const qrOptions = useMemo(() => {
        if (!qrData || !encodedData) {
            return { type: "svg", data: "" };
        }
        return Object.assign({ type: "svg", data: encodedData, width: 300, height: 300 }, mapQRDataToOptions(qrData));
    }, [qrData, encodedData]);
    const { finalSVG, qrCodeInstanceRef, isQRCodeGenerated, setIsQRCodeGenerated } = useFramedQR({
        qrData,
        qrOptions,
        isValidForm,
    });
    const { isFixed, rendererRef, contentRef } = useFloatingPosition(qrData);
    const isFramedQR = frameType !== "none" && finalSVG;
    const handleDownload = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!(qrData === null || qrData === void 0 ? void 0 : qrData.qrStyle) || !qrData)
            return;
        try {
            const response = yield qrCodeService.submitQRCode(qrData, qrData.qrStyle);
            if (response) {
                setDownloadQRCookie(response.reference);
            }
            qrCodeService.navigateToAppSignup(navigateToSubdomain, response.reference);
        }
        catch (error) {
            console.error("Error saving QR code:", error);
        }
    });
    return (React__default.createElement(StyledQRRenderer, { id: "qr-renderer-container", className: className, ref: rendererRef },
        React__default.createElement(StyledQRRendererContent, { ref: contentRef, "$isFixed": isFixed },
            React__default.createElement(StyledQRComponentWrapper, { "data-qa": "qr-renderer", onContextMenu: (e) => e.preventDefault() }, isFramedQR ? (React__default.createElement(StyledFramedQRComponent, { "data-qa": "qr-renderer-framed-qr", dangerouslySetInnerHTML: { __html: finalSVG } })) : isQRCodeGenerated && isValidForm && qrOptions ? (React__default.createElement(QRCodeComponent, { dataQA: "qr-renderer-qr", options: Object.assign(Object.assign({}, qrOptions), { type: "canvas", data: encodedData }), ref: qrCodeInstanceRef })) : (React__default.createElement("div", { "data-qa": "qr-renderer-placeholder" }, getQRPlaceholderSvgByTheme(theme)))),
            React__default.createElement(StyledQRRendererButton, { label: "Generate QR Code", variant: "primary-green", state: !isValidForm || isLoading ? "disabled" : "default", dataQA: "generate-qr-code-button", onClick: () => setIsQRCodeGenerated(true) }),
            React__default.createElement(StyledQRRendererDownloadButton, { label: isLoading ? "Saving..." : "Download", shape: "outlined", variant: "primary", dataQA: "download-qr-code-button", iconRight: React__default.createElement(FiDownload, null), state: !isValidForm || !isQRCodeGenerated || isLoading ? "disabled" : "default", onClick: handleDownload }))));
};

var _path$h;
function _extends$h() { return _extends$h = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$h.apply(null, arguments); }
var SvgArrow = function SvgArrow(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$h({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    viewBox: "0 0 13 8"
  }, props), _path$h || (_path$h = /*#__PURE__*/React.createElement("path", {
    d: "M11.09 0 6.5 4.58 1.91 0 .5 1.41l6 6 6-6z"
  })));
};

const BREAKPOINTS = {
    mobile: 768,
    tablet: 1024,
    desktop: 1440,
};

const isMobile = () => {
    if (!getWindow()) {
        return false;
    }
    return window.innerWidth <= BREAKPOINTS.mobile;
};

const StyledGridContainer = styled.div `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${getSpacing("size-03")};

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(5, 1fr);
  }
`;
const StyledButtonWithColoredIcon = styled(Button) `
  padding: ${getSpacing("size-03")};
  justify-content: flex-start;
  color: ${getColor("neutral", 900)};
  box-sizing: border-box;
  height: ${getSpacing("size-08")};
  border: 2px solid transparent;
  border: ${({ state }) => state === "active"
    ? `2px solid ${getColor("primary", 500)}`
    : `2px solid ${getColor("neutral", 400)}`};

  background-color: ${({ state }) => state === "active" ? getColor("primary", 50) : "transparent"};

  i > div {
    background-color: ${({ state }) => state === "active" ? getColor("primary", 100) : getColor("neutral", 200)};
  }

  p {
    font-size: ${getFont("base-font-size-sm")};
    white-space: nowrap;
  }

  &:hover {
    background-color: ${getColor("neutral", "white")};
    border: 2px solid ${getColor("primary", 500)};

    i > div {
      background-color: ${getColor("primary", 100)};
    }
  }
`;
const StyledSeeMoreButton = styled(StyledButtonWithColoredIcon) `
  color: ${getColor("primary", 500)};
  display: flex;
  justify-content: center;

  p {
    font-size: 12px;
    width: auto;
  }

  span {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  i {
    width: 16px;
    height: 16px;
  }

  svg {
    height: auto;
    width: auto;
    fill: ${getColor("primary", 500)};
    margin-top: 1px;
  }
`;

const QRTypeSelectorCompact = ({ onSelect, qrTypes, className, selectedQRType, }) => {
    const { t } = useTranslation("layout");
    const [isExpanded, setIsExpanded] = useState(false);
    const { resetForm } = useQRStore();
    const visibleQRTypes = isExpanded
        ? qrTypes
        : isMobile()
            ? qrTypes.slice(0, 5)
            : qrTypes.slice(0, 9);
    const handleClick = (qrType) => {
        if (qrType === selectedQRType)
            return;
        resetForm();
        onSelect(qrType);
    };
    return (React__default.createElement(StyledGridContainer, { className: className },
        visibleQRTypes.map((qrType) => (React__default.createElement(StyledButtonWithColoredIcon, { key: qrType.type, label: qrType.displayName, dataQA: `qr-type-button-${qrType.type}`, shape: "outlined", variant: "primary", iconLeft: qrType.icon, onClick: () => handleClick(qrType.type), state: selectedQRType === qrType.type ? "active" : "default" }))),
        qrTypes.length > 5 && (React__default.createElement(StyledSeeMoreButton, { key: "lessMore", shape: "outlined", dataQA: "qr-type-button-see-more", iconRight: isExpanded ? React__default.createElement(SvgArrow, { style: { transform: "rotate(180deg)" } }) : React__default.createElement(SvgArrow, null), label: isExpanded ? t("button.seeLess") : t("button.seeMore"), onClick: () => setIsExpanded(!isExpanded) }))));
};

const QRTypeSelectorExtended = ({ onSelect, qrTypes, }) => {
    return React__default.createElement("div", null, "QRTypeSelectorExtended ");
};

const QRTypeSelector = ({ onSelect, qrTypes, mode = "compact" }) => {
    const selectedQRType = useQRStore((state) => state.selectedType);
    if (mode === "compact") {
        return (React__default.createElement(QRTypeSelectorCompact, { onSelect: onSelect, qrTypes: qrTypes, selectedQRType: selectedQRType }));
    }
    else {
        return (React__default.createElement(QRTypeSelectorExtended, { onSelect: onSelect, qrTypes: qrTypes }));
    }
};

var _path$g;
function _extends$g() { return _extends$g = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$g.apply(null, arguments); }
var SvgIconQrApps = function SvgIconQrApps(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$g({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    fill: "none"
  }, props), _path$g || (_path$g = /*#__PURE__*/React.createElement("path", {
    fill: "#1D1D1D",
    fillRule: "evenodd",
    d: "M11.33.667H4.663c-.733 0-1.326.6-1.326 1.333v12c0 .733.593 1.333 1.326 1.333h6.667c.733 0 1.333-.6 1.333-1.333V2c0-.733-.6-1.333-1.333-1.333m0 12H4.663V3.333h6.667zM8.53 9.98V8.813c-1.48 0-2.46.454-3.2 1.454.3-1.427 1.127-2.847 3.2-3.134V6l1.873 1.747c.14.133.14.353 0 .486z",
    clipRule: "evenodd"
  })));
};

var _path$f;
function _extends$f() { return _extends$f = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$f.apply(null, arguments); }
var SvgIconQrBusiness = function SvgIconQrBusiness(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$f({
    xmlns: "http://www.w3.org/2000/svg",
    width: 17,
    height: 16,
    fill: "none"
  }, props), _path$f || (_path$f = /*#__PURE__*/React.createElement("path", {
    fill: "#1D1D1D",
    fillRule: "evenodd",
    d: "M10.2 4.08v3.447h2.667c.733 0 1.333.6 1.333 1.334v4c0 .733-.6 1.333-1.333 1.333H3.533c-.733 0-1.333-.6-1.333-1.333V6.194c0-.733.6-1.333 1.333-1.333H6.2v-.78c0-.354.14-.694.393-.947l1.134-1.133a.664.664 0 0 1 .94 0l1.14 1.14c.253.247.393.587.393.94Zm-6.667 8.78h1.334v-1.332H3.533zm1.334-2.666H3.533V8.861h1.334zM3.533 7.527h1.334V6.194H3.533zm5.334 5.334H7.533v-1.334h1.334zm-1.334-2.667h1.334V8.861H7.533zm1.334-2.667H7.533V6.194h1.334zM7.533 4.861h1.334V3.528H7.533zm5.334 8h-1.334v-1.334h1.334zm-1.334-2.667h1.334V8.861h-1.334z",
    clipRule: "evenodd"
  })));
};

var _path$e;
function _extends$e() { return _extends$e = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$e.apply(null, arguments); }
var SvgIconQrCoupon = function SvgIconQrCoupon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$e({
    xmlns: "http://www.w3.org/2000/svg",
    width: 17,
    height: 16,
    fill: "none"
  }, props), _path$e || (_path$e = /*#__PURE__*/React.createElement("path", {
    fill: "#1D1D1D",
    fillRule: "evenodd",
    d: "M3.933 14h9.334c.733 0 1.333-.6 1.333-1.333V3.333C14.6 2.6 14 2 13.267 2H3.933C3.2 2 2.6 2.6 2.6 3.333v9.334C2.6 13.4 3.2 14 3.933 14m6-7.667h2c.367 0 .667-.3.667-.666S12.3 5 11.933 5h-2c-.366 0-.666.3-.666.667 0 .366.3.666.666.666m0 4.667h2c.367 0 .667-.3.667-.667 0-.366-.3-.666-.667-.666h-2c-.366 0-.666.3-.666.666s.3.667.666.667M5.267 7.333h2c.366 0 .666-.3.666-.666v-2c0-.367-.3-.667-.666-.667h-2c-.367 0-.667.3-.667.667v2c0 .366.3.666.667.666m2-2.666h-2v2h2zm-2 7.333h2c.366 0 .666-.3.666-.667v-2c0-.366-.3-.666-.666-.666h-2c-.367 0-.667.3-.667.666v2c0 .367.3.667.667.667m2-2.667h-2v2h2z",
    clipRule: "evenodd"
  })));
};

var _path$d;
function _extends$d() { return _extends$d = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$d.apply(null, arguments); }
var SvgIconQrFeedback = function SvgIconQrFeedback(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$d({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    fill: "none"
  }, props), _path$d || (_path$d = /*#__PURE__*/React.createElement("path", {
    fill: "#1D1D1D",
    d: "m8 8.317 1.267.766a.31.31 0 0 0 .366-.008.3.3 0 0 0 .134-.342l-.334-1.45 1.134-.983a.32.32 0 0 0 .1-.358q-.067-.21-.3-.225L8.883 5.6 8.3 4.233a.3.3 0 0 0-.3-.2.3.3 0 0 0-.3.2L7.117 5.6l-1.484.117q-.233.016-.3.225a.32.32 0 0 0 .1.358l1.134.983-.334 1.45a.3.3 0 0 0 .134.342.31.31 0 0 0 .366.008zM4 12l-1.533 1.533q-.317.318-.725.142-.41-.175-.409-.625V2.667q0-.55.392-.942t.942-.392h10.666q.55 0 .942.392t.392.942v8q0 .55-.392.941a1.28 1.28 0 0 1-.942.392zm-.567-1.333h9.9v-8H2.667v8.75z"
  })));
};

var _path$c;
function _extends$c() { return _extends$c = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$c.apply(null, arguments); }
var SvgIconQrImage = function SvgIconQrImage(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$c({
    xmlns: "http://www.w3.org/2000/svg",
    width: 17,
    height: 16,
    fill: "none"
  }, props), _path$c || (_path$c = /*#__PURE__*/React.createElement("path", {
    fill: "#1D1D1D",
    fillRule: "evenodd",
    d: "M14.6 3.333v9.334C14.6 13.4 14 14 13.267 14H3.933C3.2 14 2.6 13.4 2.6 12.667V3.333C2.6 2.6 3.2 2 3.933 2h9.334C14 2 14.6 2.6 14.6 3.333m-6.667 7.674-1.4-1.687a.33.33 0 0 0-.52.013l-1.66 2.134c-.173.22-.02.54.26.54h7.994a.333.333 0 0 0 .266-.534l-2.34-3.12A.335.335 0 0 0 10 8.347z",
    clipRule: "evenodd"
  })));
};

var _path$b;
function _extends$b() { return _extends$b = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$b.apply(null, arguments); }
var SvgIconQrLinks = function SvgIconQrLinks(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b({
    xmlns: "http://www.w3.org/2000/svg",
    width: 17,
    height: 16,
    fill: "none"
  }, props), _path$b || (_path$b = /*#__PURE__*/React.createElement("path", {
    fill: "#1D1D1D",
    fillRule: "evenodd",
    d: "M4.867 10h2c.366 0 .666.3.666.667 0 .366-.3.666-.666.666h-2a3.335 3.335 0 0 1 0-6.666h2c.366 0 .666.3.666.666S7.233 6 6.867 6h-2c-1.1 0-2 .9-2 2s.9 2 2 2m6.666-5.333h-2c-.366 0-.666.3-.666.666s.3.667.666.667h2c1.1 0 2 .9 2 2s-.9 2-2 2h-2c-.366 0-.666.3-.666.667 0 .366.3.666.666.666h2A3.335 3.335 0 0 0 14.867 8a3.335 3.335 0 0 0-3.334-3.333M5.533 8c0 .367.3.667.667.667h4c.367 0 .667-.3.667-.667s-.3-.667-.667-.667h-4c-.367 0-.667.3-.667.667",
    clipRule: "evenodd"
  })));
};

var _path$a;
function _extends$a() { return _extends$a = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$a.apply(null, arguments); }
var SvgIconQrMp3 = function SvgIconQrMp3(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a({
    xmlns: "http://www.w3.org/2000/svg",
    width: 17,
    height: 16,
    fill: "none"
  }, props), _path$a || (_path$a = /*#__PURE__*/React.createElement("path", {
    fill: "#1D1D1D",
    fillRule: "evenodd",
    d: "M2.533 3.334H9.2c.367 0 .667.3.667.667s-.3.667-.667.667H2.533A.67.67 0 0 1 1.867 4c0-.367.3-.667.666-.667Zm0 2.667H9.2c.367 0 .667.3.667.667 0 .366-.3.666-.667.666H2.533a.67.67 0 0 1-.666-.666c0-.367.3-.667.666-.667m0 4h4c.367 0 .667-.3.667-.667 0-.366-.3-.666-.667-.666h-4c-.366 0-.666.3-.666.666s.3.667.666.667M11.2 4.668c0-.734.6-1.334 1.333-1.334h1.334c.366 0 .666.3.666.667s-.3.667-.666.667h-1.334v6a1.997 1.997 0 0 1-2.36 1.966 2 2 0 0 1-1.606-1.606 1.997 1.997 0 0 1 1.966-2.36c.234 0 .46.046.667.12z",
    clipRule: "evenodd"
  })));
};

var _path$9;
function _extends$9() { return _extends$9 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$9.apply(null, arguments); }
var SvgIconQrPdf = function SvgIconQrPdf(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9({
    xmlns: "http://www.w3.org/2000/svg",
    width: 17,
    height: 16,
    fill: "none"
  }, props), _path$9 || (_path$9 = /*#__PURE__*/React.createElement("path", {
    fill: "#1D1D1D",
    fillRule: "evenodd",
    d: "M14.133 1.333h-8c-.733 0-1.333.6-1.333 1.334v8C4.8 11.4 5.4 12 6.133 12h8c.734 0 1.334-.6 1.334-1.333v-8c0-.734-.6-1.334-1.334-1.334m-5.666 5c0 .554-.447 1-1 1H6.8v.834c0 .273-.227.5-.5.5a.504.504 0 0 1-.5-.5V5.333c0-.366.3-.666.667-.666h1c.553 0 1 .446 1 1zM10.8 8.667a1 1 0 0 0 1-1v-2c0-.554-.447-1-1-1H9.467A.33.33 0 0 0 9.133 5v3.333a.33.33 0 0 0 .334.334zm3.667-3.5c0 .273-.227.5-.5.5h-.5v.666h.5c.273 0 .5.227.5.5 0 .274-.227.5-.5.5h-.5v.834c0 .273-.227.5-.5.5a.504.504 0 0 1-.5-.5V5.333c0-.366.3-.666.666-.666h.834c.273 0 .5.226.5.5m-7 1.166H6.8v-.666h.667zM2.8 4c-.367 0-.667.3-.667.667v8.666c0 .734.6 1.334 1.334 1.334h8.666c.367 0 .667-.3.667-.667s-.3-.667-.667-.667h-8a.67.67 0 0 1-.666-.666v-8C3.467 4.3 3.167 4 2.8 4m8 3.667h-.667v-2h.667z",
    clipRule: "evenodd"
  })));
};

var _path$8;
function _extends$8() { return _extends$8 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$8.apply(null, arguments); }
var SvgIconQrUrl = function SvgIconQrUrl(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8({
    xmlns: "http://www.w3.org/2000/svg",
    width: 14,
    height: 12,
    fill: "none"
  }, props), _path$8 || (_path$8 = /*#__PURE__*/React.createElement("path", {
    fill: "#1D1D1D",
    fillRule: "evenodd",
    d: "M1.667.667h10.666c.734 0 1.334.6 1.334 1.333v8c0 .733-.6 1.333-1.334 1.333H1.667c-.734 0-1.334-.6-1.334-1.333L.34 2C.34 1.267.933.667 1.667.667m7 3.333h-7v2.333h7zm-7 3.667h7V10H2.333a.67.67 0 0 1-.666-.667zM10 10h1.667c.366 0 .666-.3.666-.667V4H10z",
    clipRule: "evenodd"
  })));
};

var _path$7;
function _extends$7() { return _extends$7 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$7.apply(null, arguments); }
var SvgIconQrVideo = function SvgIconQrVideo(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7({
    xmlns: "http://www.w3.org/2000/svg",
    width: 17,
    height: 16,
    fill: "none"
  }, props), _path$7 || (_path$7 = /*#__PURE__*/React.createElement("path", {
    fill: "#1D1D1D",
    fillRule: "evenodd",
    d: "M13.733 1.333h-8c-.733 0-1.333.6-1.333 1.334v8C4.4 11.4 5 12 5.733 12h8c.734 0 1.334-.6 1.334-1.333v-8c0-.734-.6-1.334-1.334-1.334M2.4 4c-.367 0-.667.3-.667.667v8.666c0 .734.6 1.334 1.334 1.334h8.666c.367 0 .667-.3.667-.667s-.3-.667-.667-.667h-8a.67.67 0 0 1-.666-.666v-8C3.067 4.3 2.767 4 2.4 4m6-.333v6l3.647-2.734a.33.33 0 0 0 0-.533z",
    clipRule: "evenodd"
  })));
};

var _path$6;
function _extends$6() { return _extends$6 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$6.apply(null, arguments); }
var SvgIconQrWifi = function SvgIconQrWifi(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6({
    xmlns: "http://www.w3.org/2000/svg",
    width: 17,
    height: 16,
    fill: "none"
  }, props), _path$6 || (_path$6 = /*#__PURE__*/React.createElement("path", {
    fill: "#1D1D1D",
    fillRule: "evenodd",
    d: "M1.771 6.693c.34.34.88.374 1.247.067 3.113-2.56 7.633-2.56 10.753-.007a.94.94 0 0 0 1.26-.06.946.946 0 0 0-.066-1.4c-3.807-3.113-9.314-3.113-13.127 0a.94.94 0 0 0-.067 1.4m5.174 5.174.98.98c.26.26.68.26.94 0l.98-.98c.313-.314.246-.854-.154-1.06a2.85 2.85 0 0 0-2.606 0c-.38.206-.454.746-.14 1.06m-1.267-2.4a.94.94 0 0 1-1.22-.087c-.407-.407-.38-1.08.08-1.413a6.61 6.61 0 0 1 7.72 0c.46.326.487 1.006.087 1.406l-.007.007a.95.95 0 0 1-1.22.087 4.71 4.71 0 0 0-5.44 0",
    clipRule: "evenodd"
  })));
};

var _path$5;
function _extends$5() { return _extends$5 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$5.apply(null, arguments); }
var SvgIconQrYoutube = function SvgIconQrYoutube(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    fill: "none"
  }, props), _path$5 || (_path$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#1D1D1D",
    fillRule: "evenodd",
    d: "M14 2H2C1.267 2 .667 2.6.667 3.333v8c0 .734.6 1.334 1.333 1.334h3.333v.666c0 .367.3.667.667.667h4c.367 0 .667-.3.667-.667v-.666H14c.733 0 1.327-.6 1.327-1.334l.006-8c0-.74-.6-1.333-1.333-1.333m-.667 9.333H2.667A.67.67 0 0 1 2 10.667V4c0-.367.3-.667.667-.667h10.666c.367 0 .667.3.667.667v6.667c0 .366-.3.666-.667.666M7 9.433l2.653-1.52a.67.67 0 0 0 0-1.16L7 5.233a.668.668 0 0 0-1 .58v3.04a.67.67 0 0 0 1 .58",
    clipRule: "evenodd"
  })));
};

var _path$4;
function _extends$4() { return _extends$4 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$4.apply(null, arguments); }
var SvgIconQrVcard = function SvgIconQrVcard(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4({
    xmlns: "http://www.w3.org/2000/svg",
    width: 17,
    height: 16,
    fill: "none"
  }, props), _path$4 || (_path$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#1D1D1D",
    fillRule: "evenodd",
    d: "M14.133 4.667h-2.666a2.666 2.666 0 1 0-5.334 0H3.467c-.734 0-1.334.6-1.334 1.333v2c0 .74.594 1.333 1.334 1.333h4v-.666c0-.367.3-.667.666-.667h1.334c.366 0 .666.3.666.667v.666h4c.734 0 1.334-.6 1.334-1.333V6c0-.733-.6-1.333-1.334-1.333m-6 6A.67.67 0 0 1 7.467 10h-4.66v2.667c0 .733.6 1.333 1.333 1.333h9.327c.733 0 1.333-.6 1.333-1.333V10h-4.667c0 .367-.3.667-.666.667zM8.8 3.333c-.733 0-1.333.6-1.333 1.334h2.666c0-.734-.6-1.334-1.333-1.334",
    clipRule: "evenodd"
  })));
};

var _path$3;
function _extends$3() { return _extends$3 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$3.apply(null, arguments); }
var SvgIconQrWebsite = function SvgIconQrWebsite(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3({
    xmlns: "http://www.w3.org/2000/svg",
    width: 17,
    height: 16,
    fill: "none"
  }, props), _path$3 || (_path$3 = /*#__PURE__*/React.createElement("path", {
    fill: "#1D1D1D",
    fillRule: "evenodd",
    d: "M2.4 2h12c.733 0 1.333.6 1.333 1.333v8c0 .734-.6 1.334-1.333 1.334h-3.333v.666c0 .367-.3.667-.667.667h-4a.67.67 0 0 1-.667-.667v-.666H2.4c-.733 0-1.333-.6-1.333-1.334v-8C1.067 2.6 1.667 2 2.4 2m.667 9.333h10.666c.367 0 .667-.3.667-.666V4c0-.367-.3-.667-.667-.667H3.067c-.367 0-.667.3-.667.667v6.667c0 .366.3.666.667.666m9.333-6h-6c-.367 0-.667.3-.667.667s.3.667.667.667h6c.367 0 .667-.3.667-.667s-.3-.667-.667-.667M6.4 8h6c.367 0 .667.3.667.667 0 .366-.3.666-.667.666h-6a.67.67 0 0 1-.667-.666c0-.367.3-.667.667-.667M5.067 5.333H3.733v1.334h1.334zM3.733 8h1.334v1.333H3.733z",
    clipRule: "evenodd"
  })));
};

var _path$2;
function _extends$2() { return _extends$2 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$2.apply(null, arguments); }
var SvgIconQrEvent = function SvgIconQrEvent(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2({
    xmlns: "http://www.w3.org/2000/svg",
    width: 17,
    height: 16,
    fill: "none"
  }, props), _path$2 || (_path$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#1D1D1D",
    fillRule: "evenodd",
    d: "M13.467 2h.666c.734 0 1.334.6 1.334 1.333V14c0 .733-.6 1.333-1.334 1.333H3.467c-.734 0-1.334-.6-1.334-1.333V3.333C2.133 2.6 2.733 2 3.467 2h.666v-.667c0-.366.3-.666.667-.666s.667.3.667.666V2h6.666v-.667c0-.366.3-.666.667-.666s.667.3.667.666zM4.133 14h9.334c.366 0 .666-.3.666-.667v-8H3.467v8c0 .367.3.667.666.667",
    clipRule: "evenodd"
  })));
};

var _path$1;
function _extends$1() { return _extends$1 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$1.apply(null, arguments); }
var SvgIconQrSocial = function SvgIconQrSocial(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1({
    xmlns: "http://www.w3.org/2000/svg",
    width: 17,
    height: 16,
    fill: "none"
  }, props), _path$1 || (_path$1 = /*#__PURE__*/React.createElement("path", {
    fill: "#1D1D1D",
    d: "M12.6 10.747c-.507 0-.96.2-1.307.513L6.54 8.493c.033-.153.06-.306.06-.466s-.027-.314-.06-.467l4.7-2.74a1.997 1.997 0 0 0 3.36-1.46c0-1.107-.893-2-2-2s-2 .893-2 2c0 .16.027.313.06.467l-4.7 2.74a1.997 1.997 0 0 0-3.36 1.46 1.997 1.997 0 0 0 3.36 1.46l4.747 2.773c-.034.14-.054.287-.054.433A1.95 1.95 0 0 0 12.6 14.64a1.95 1.95 0 0 0 1.947-1.947 1.95 1.95 0 0 0-1.947-1.946"
  })));
};

var _path;
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
var SvgIconQrText = function SvgIconQrText(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    xmlns: "http://www.w3.org/2000/svg",
    width: 17,
    height: 16,
    fill: "none"
  }, props), _path || (_path = /*#__PURE__*/React.createElement("path", {
    fill: "#1D1D1D",
    d: "M3.733 4a1 1 0 0 0 1 1H7.4v7a1 1 0 1 0 2 0V5h2.667a1 1 0 1 0 0-2H4.733a1 1 0 0 0-1 1"
  })));
};

const COMPACT_QR_LIST = ["url-static", "wifi"];

const StyledQRTypeSelector = styled.div `
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${getColor("neutral", 200)};
  display: flex;
  justify-content: center;
  align-items: center;

  > svg {
    width: auto;
    height: auto;
  }
`;
const urlConfigData = {
    type: "url-static",
    displayName: "URL",
    icon: (React__default.createElement(StyledQRTypeSelector, null,
        React__default.createElement(SvgIconQrUrl, null))),
    showForm: false,
    title: "Create a QR Code for your website",
    desription: "Direct users to your landing page with QR codes. No need to type in the URL, users can reach your site with a simple scan.",
};
const wifiConfigData = {
    type: "wifi",
    displayName: "Wifi",
    icon: (React__default.createElement(StyledQRTypeSelector, null,
        React__default.createElement(SvgIconQrWifi, null))),
    showForm: false,
    title: "Create a QR Code for your WiFi network",
    desription: "No more writing down passwords. Provide fast and secure Wi-Fi access with a QR code—  your customers will thank you!",
};
const vCardConfigData = {
    type: "vCard",
    displayName: "vCard",
    icon: (React__default.createElement(StyledQRTypeSelector, null,
        React__default.createElement(SvgIconQrVcard, null))),
    showForm: false,
    title: "Create a QR Code for your contact details",
    desription: "Printed business cards are a thing of the past. Share your business information the easy way with QR vCards. Limitless and impossible to lose!",
};
const businessConfigData = {
    type: "business",
    displayName: "Business",
    icon: (React__default.createElement(StyledQRTypeSelector, null,
        React__default.createElement(SvgIconQrBusiness, null))),
    showForm: false,
    title: "Create a QR Code for your business",
    desription: "Forget printing. QR codes are the best way to share your business information. Let customers know your opening hours and contact details, your menu, and more in an instant.",
};
const socialsConfigData = {
    type: "socials",
    displayName: "Social Media",
    icon: (React__default.createElement(StyledQRTypeSelector, null,
        React__default.createElement(SvgIconQrSocial, null))),
    showForm: false,
    title: "Create a QR Code for your social media",
    desription: "Link all your social media platforms to a single QR code. Get more followers on Facebook, Instagram, LinkedIn, Twitter, and more.",
};
const appsConfigData = {
    type: "apps",
    displayName: "Apps",
    icon: (React__default.createElement(StyledQRTypeSelector, null,
        React__default.createElement(SvgIconQrApps, null))),
    showForm: false,
    title: "Create a QR Code for your app",
    desription: "Get more downloads. Make your app easier to find and improve user experience.",
};
const videoConfigData = {
    type: "video",
    displayName: "Video",
    icon: (React__default.createElement(StyledQRTypeSelector, null,
        React__default.createElement(SvgIconQrVideo, null))),
    showForm: false,
    title: "Create a QR Code for your video",
    desription: "Get your videos out into the world. Generate QR codes to share your videos and boost your audience.",
};
const mp3ConfigData = {
    type: "mp3",
    displayName: "MP3",
    icon: (React__default.createElement(StyledQRTypeSelector, null,
        React__default.createElement(SvgIconQrMp3, null))),
    showForm: false,
    title: "Create a QR Code for your audio",
    desription: "The best way to share songs, podcasts, and audio files directly to your audience's phones.",
};
const pdfConfigData = {
    type: "pdf",
    displayName: "PDF",
    icon: (React__default.createElement(StyledQRTypeSelector, null,
        React__default.createElement(SvgIconQrPdf, null))),
    showForm: false,
    title: "Create a QR Code for your document",
    desription: "Create QR codes to share PDF files. The fast and secure way to make information and text available to everyone.",
};
const linksConfigData = {
    type: "links",
    displayName: "Links",
    icon: (React__default.createElement(StyledQRTypeSelector, null,
        React__default.createElement(SvgIconQrLinks, null))),
    showForm: false,
    title: "Create a QR Code for your links",
    desription: "Get your message across with QR codes. Gather your links in one place to easily share content with your audience.",
};
const textConfigData = {
    type: "text",
    displayName: "Text",
    icon: (React__default.createElement(StyledQRTypeSelector, null,
        React__default.createElement(SvgIconQrText, null))),
    showForm: false,
    title: "Create a QR Code for your text",
    desription: "Easily share and display short, clear messages, such as communications, reminders, or concise instructions.",
};
const couponConfigData = {
    type: "coupon",
    displayName: "Coupon",
    icon: (React__default.createElement(StyledQRTypeSelector, null,
        React__default.createElement(SvgIconQrCoupon, null))),
    showForm: false,
    title: "Create a QR Code for your coupon",
    desription: "Digitize your discounts. With just a scan, your customers can redeem their coupons and access exclusive offers. A fantastic way to promote your business with ease.",
};
const feedbackConfigData = {
    type: "feedback",
    displayName: "Feedback",
    icon: (React__default.createElement(StyledQRTypeSelector, null,
        React__default.createElement(SvgIconQrFeedback, null))),
    showForm: false,
    title: "Create a QR Code for your feedback",
    desription: "Want to get more reviews from customers? Make it easy with a QR code! All it takes is a quick scan to deliver feedback!",
};
const eventConfigData = {
    type: "event",
    displayName: "Event",
    icon: (React__default.createElement(StyledQRTypeSelector, null,
        React__default.createElement(SvgIconQrEvent, null))),
    showForm: false,
    title: "Create a QR Code for your event",
    desription: "Create a landing page with all your event info. Guests can easily add it to their calendars and RSPV.",
};
const youtubeConfigData = {
    type: "youtube",
    displayName: "YouTube",
    icon: (React__default.createElement(StyledQRTypeSelector, null,
        React__default.createElement(SvgIconQrYoutube, null))),
    showForm: false,
    title: "Create a QR Code for your YouTube video",
    desription: "Want more YouTube views and subscribers? Link the URL to a QR code and let the world see you in action.",
};
const webpageConfigData = {
    type: "webpage",
    displayName: "Landing Page",
    icon: (React__default.createElement(StyledQRTypeSelector, null,
        React__default.createElement(SvgIconQrWebsite, null))),
    showForm: false,
    title: "Create a QR Code for your website",
    desription: "Provide instant access to your website. QR codes help drive traffic to your site and increase engagement.",
};
const imagesConfigData = {
    type: "images",
    displayName: "Image",
    icon: (React__default.createElement(StyledQRTypeSelector, null,
        React__default.createElement(SvgIconQrImage, null))),
    showForm: false,
    title: "Create a QR Code for your image",
    desription: "Sending images is easy thanks to our QR codes. Let family and friends see your snaps and share the memories.",
};
const defaultQRTypeConfigData = [
    urlConfigData,
    wifiConfigData,
    vCardConfigData,
    businessConfigData,
    socialsConfigData,
    appsConfigData,
    videoConfigData,
    pdfConfigData,
    linksConfigData,
    imagesConfigData,
    youtubeConfigData,
    webpageConfigData,
    eventConfigData,
    mp3ConfigData,
    couponConfigData,
    feedbackConfigData,
    textConfigData,
];
const getDefaultQRTypeConfigData = (mode) => {
    if (mode === "compact") {
        return defaultQRTypeConfigData.map((config) => {
            return Object.assign(Object.assign({}, config), { showForm: COMPACT_QR_LIST.includes(config.type) });
        });
    }
    else {
        return defaultQRTypeConfigData;
    }
};

function useFontLoader({ googleFontUrl }) {
    useEffect(() => {
        const link = document.createElement("link");
        link.href = googleFontUrl;
        link.rel = "stylesheet";
        document.head.appendChild(link);
        return () => {
            document.head.removeChild(link);
        };
    }, [googleFontUrl]);
}

const GlobalStyles = createGlobalStyle `
  :root {
    ${({ theme }) => `
      ${createCssProp(theme.colors, `--qe-color`)}
      ${createCssProp(theme.fonts, `--qe-font`)}
      ${createCssProp(theme.breakpoints, `--qe-breakpoints`)}
      ${createCssProp(theme.spacing, `--qe-spacing`)}
      ${createCssProp(theme.border, `--qe-border`)}

      --qe-family-primary: ${theme.fonts["family-primary"]};
      --qe-family-secondary: ${theme.fonts["family-secondary"]};
      --qe-base-font-size: ${theme.fonts["base-font-size"]};
      --qe-base-font-size-sm: ${theme.fonts["base-font-size-sm"]};
      
    `}
  }
`;

const daikiri = {
    colors: {
        primary: {
            900: "#6b3915",
            800: "#8c4a1c",
            700: "#b56024",
            600: "#e87b2e",
            500: "#ff8732",
            400: "#ff9f5b",
            300: "#ffaf76",
            200: "#ffc8a1",
            100: "#ffdabf",
            50: "#FFF3EB",
        },
        ["primary-green"]: {
            900: "#0b3917",
            800: "#0e4a1e",
            700: "#126027",
            600: "#177b32",
            500: "#198737",
            400: "#479f5f",
            300: "#65af79",
            200: "#95c8a3",
            100: "#b8dac1",
            50: "#e8f3eb",
        },
        secondary: {
            900: "#0b3917",
            800: "#0e4a1e",
            700: "#126027",
            600: "#177b32",
            500: "#198737",
            400: "#479f5f",
            300: "#65af79",
            200: "#95c8a3",
            100: "#b8dac1",
            50: "#e8f3eb",
        },
        neutral: {
            900: "#262626",
            800: "#404040",
            700: "#595959",
            600: "#737373",
            500: "#8c8c8c",
            400: "#a6a6a6",
            300: "#bfbfbf",
            200: "#d9d9d9",
            100: "#EEEEEE",
            50: "#F5F5F5",
            white: "#ffffff",
            black: "#000000",
        },
        success: {
            900: "#0d403b",
            800: "#156a62",
            700: "#1e9489",
            600: "#27bfb1",
            500: "#40d8ca",
            400: "#6be1d6",
            300: "#95e9e2",
            200: "#bff2ed",
            100: "#ebfafa",
            50: "#f7fdfd",
        },
        warning: {
            900: "#4a3802",
            800: "#7c5d03",
            700: "#ae8205",
            600: "#f98820",
            500: "#f9c120",
            400: "#facf51",
            300: "#fcdc83",
            200: "#fdeab5",
            100: "#fef8e6",
            50: "#fffcf5",
        },
        error: {
            900: "#4d0000",
            800: "#800000",
            700: "#b30000",
            600: "#e60000",
            500: "#ff1a1a",
            400: "#ff4c4c",
            300: "#ff7f7f",
            200: "#ffb3b3",
            100: "#ffe5e5",
            50: "#fff5f5",
        },
    },
    fonts: {
        "loader-link": "https://fonts.googleapis.com/css2?family=Arimo:ital,wght@0,400..700;1,400..700&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
        "family-primary": "Inter, serif",
        "family-secondary": "Arimo, serif",
        "base-font-size": "16px",
        "base-font-size-sm": "12px",
        "base-line-height": "24px",
        "vertical-rhythm": 1,
    },
    spacing: {
        "size-00": "0px",
        "size-01": "2px",
        "size-02": "4px",
        "size-03": "8px",
        "size-04": "12px",
        "size-05": "16px",
        "size-06": "24px",
        "size-07": "32px",
        "size-08": "40px",
        "size-09": "48px",
        "size-10": "56px",
        "size-11": "64px",
        "size-12": "72px",
        "size-13": "80px",
        "size-14": "88px",
        "size-15": "96px",
        "size-16": "144px",
    },
    breakpoints: {
        mobile: "320px",
        tablet: "744px",
        desktop: "1280px",
    },
    border: {
        width: {
            default: "1px",
            small: "1px",
        },
        radius: {
            default: "4px",
            small: "4px",
        },
    },
};

const kalimotxo = {
    colors: {
        primary: {
            900: "#6b3915",
            800: "#8c4a1c",
            700: "#b56024",
            600: "#e87b2e",
            500: "#ff8732",
            400: "#ff9f5b",
            300: "#ffaf76",
            200: "#ffc8a1",
            100: "#ffdabf",
            50: "#fff7f1",
        },
        ["primary-green"]: {
            900: "#0b3917",
            800: "#0e4a1e",
            700: "#126027",
            600: "#177b32",
            500: "#198737",
            400: "#479f5f",
            300: "#65af79",
            200: "#95c8a3",
            100: "#b8dac1",
            50: "#e8f3eb",
        },
        secondary: {
            900: "#0b3917",
            800: "#0e4a1e",
            700: "#126027",
            600: "#177b32",
            500: "#198737",
            400: "#479f5f",
            300: "#65af79",
            200: "#95c8a3",
            100: "#b8dac1",
            50: "#e8f3eb",
        },
        neutral: {
            900: "#2A2A2A",
            800: "#595a5e",
            700: "#737479",
            600: "#93949b",
            500: "#a2a3aa",
            400: "#b5b5bb",
            300: "#c1c1c6",
            200: "#d4d5d8",
            100: "#e2e2e5",
            50: "#f6f6f7",
            white: "#ffffff",
            black: "#000000",
        },
        success: {
            900: "#0d403b",
            800: "#156a62",
            700: "#1e9489",
            600: "#27bfb1",
            500: "#40d8ca",
            400: "#6be1d6",
            300: "#95e9e2",
            200: "#bff2ed",
            100: "#ebfafa",
            50: "#f7fdfd",
        },
        warning: {
            900: "#4a3802",
            800: "#7c5d03",
            700: "#ae8205",
            600: "#f98820",
            500: "#f9c120",
            400: "#facf51",
            300: "#fcdc83",
            200: "#fdeab5",
            100: "#fef8e6",
            50: "#fffcf5",
        },
        error: {
            900: "#4d0000",
            800: "#800000",
            700: "#b30000",
            600: "#e60000",
            500: "#ff1a1a",
            400: "#ff4c4c",
            300: "#ff7f7f",
            200: "#ffb3b3",
            100: "#ffe5e5",
            50: "#fff5f5",
        },
    },
    fonts: {
        "loader-link": "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=swap",
        "family-primary": "Plus Jakarta Sans, serif",
        "family-secondary": "Plus Jakarta Sans, serif",
        "base-font-size": "16px",
        "base-font-size-sm": "12px",
        "base-line-height": "24px",
        "vertical-rhythm": 1,
    },
    spacing: {
        "size-00": "0px",
        "size-01": "2px",
        "size-02": "4px",
        "size-03": "8px",
        "size-04": "12px",
        "size-05": "16px",
        "size-06": "24px",
        "size-07": "32px",
        "size-08": "40px",
        "size-09": "48px",
        "size-10": "56px",
        "size-11": "64px",
        "size-12": "72px",
        "size-13": "80px",
        "size-14": "88px",
        "size-15": "96px",
        "size-16": "144px",
    },
    breakpoints: {
        mobile: "320px",
        tablet: "744px",
        desktop: "1280px",
    },
    border: {
        width: {
            default: "1px",
            small: "1px",
        },
        radius: {
            default: "4px",
            small: "4px",
        },
    },
};

const mojito = {
    colors: {
        primary: {
            900: "#051a2e",
            800: "#0a335c",
            700: "#0f4d8a",
            600: "#1466b8",
            500: "#307fe2",
            400: "#4799eb",
            300: "#75b2f0",
            200: "#76b3ef",
            100: "#e8f2fc",
            50: "#f6fafe",
        },
        ["primary-green"]: {
            900: "#0d2618",
            800: "#133924",
            700: "#1a4c30",
            600: "#205f3c",
            500: "#00af63",
            400: "#3aac6d",
            300: "#53c586",
            200: "#79d2a1",
            100: "#a0dfbc",
            50: "#ecf9f2",
        },
        secondary: {
            900: "#0d2618",
            800: "#133924",
            700: "#1a4c30",
            600: "#205f3c",
            500: "#00af63",
            400: "#3aac6d",
            300: "#53c586",
            200: "#79d2a1",
            100: "#a0dfbc",
            50: "#ecf9f2",
        },
        neutral: {
            900: "#262626",
            800: "#404040",
            700: "#595959",
            600: "#737373",
            500: "#8c8c8c",
            400: "#a6a6a6",
            300: "#bfbfbf",
            200: "#d9d9d9",
            100: "#f2f2f2",
            50: "#fcfcfc",
            white: "#ffffff",
            black: "#000000",
        },
        success: {
            900: "#0d403b",
            800: "#156a62",
            700: "#1e9489",
            600: "#27bfb1",
            500: "#40d8ca",
            400: "#6be1d6",
            300: "#95e9e2",
            200: "#bff2ed",
            100: "#ebfafa",
            50: "#f7fdfd",
        },
        warning: {
            900: "#4a3802",
            800: "#7c5d03",
            700: "#ae8205",
            600: "#f98820",
            500: "#f9c120",
            400: "#facf51",
            300: "#fcdc83",
            200: "#fdeab5",
            100: "#fef8e6",
            50: "#fffcf5",
        },
        error: {
            900: "#4d0000",
            800: "#800000",
            700: "#b30000",
            600: "#e60000",
            500: "#ff1a1a",
            400: "#ff4c4c",
            300: "#ff7f7f",
            200: "#ffb3b3",
            100: "#ffe5e5",
            50: "#fff5f5",
        },
    },
    fonts: {
        "loader-link": "https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,opsz,wght@0,6..12,200..1000;1,6..12,200..1000&family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap",
        "family-primary": "Nunito Sans, serif",
        "family-secondary": "Poppins, serif",
        "base-font-size": "16px",
        "base-font-size-sm": "12px",
        "base-line-height": "24px",
        "vertical-rhythm": 1,
    },
    spacing: {
        "size-00": "0px",
        "size-01": "2px",
        "size-02": "4px",
        "size-03": "8px",
        "size-04": "12px",
        "size-05": "16px",
        "size-06": "24px",
        "size-07": "32px",
        "size-08": "40px",
        "size-09": "48px",
        "size-10": "56px",
        "size-11": "64px",
        "size-12": "72px",
        "size-13": "80px",
        "size-14": "88px",
        "size-15": "96px",
        "size-16": "144px",
    },
    breakpoints: {
        mobile: "320px",
        tablet: "744px",
        desktop: "1280px",
    },
    border: {
        width: {
            default: "1px",
            small: "1px",
        },
        radius: {
            default: "4px",
            small: "4px",
        },
    },
};

const negroni = {
    colors: {
        primary: {
            900: "#002473",
            800: "#002E91",
            700: "#003AB5",
            600: "#0045D9",
            500: "#0051FF",
            400: "#2E70FF",
            300: "#5E91FF",
            200: "#91B4FF",
            100: "#C2D5FF",
            50: "#E6EEFF",
        },
        ["primary-green"]: {
            900: "#0B402A",
            800: "#0E5437",
            700: "#126D47",
            600: "#188B5B",
            500: "#1A9964",
            400: "#48AD83",
            300: "#66BB97",
            200: "#96D0B8",
            100: "#B8DFCF",
            50: "#E8F5F0",
        },
        secondary: {
            900: "#0B402A",
            800: "#0E5437",
            700: "#126D47",
            600: "#188B5B",
            500: "#1A9964",
            400: "#48AD83",
            300: "#66BB97",
            200: "#96D0B8",
            100: "#B8DFCF",
            50: "#E8F5F0",
        },
        neutral: {
            900: "#1D1D1D",
            800: "#595A5E",
            700: "#737479",
            600: "#8D8D8D",
            500: "#BBBBBB",
            400: "#CECECE",
            300: "#D9D9D9",
            200: "#E0E0E0",
            100: "#EEEEEE",
            50: "#F5F5F5",
            white: "#ffffff",
            black: "#000000",
        },
        success: {
            900: "#05512c",
            800: "#076b39",
            700: "#098a4a",
            600: "#0cb15f",
            500: "#0dc268",
            400: "#3dce86",
            300: "#5dd69a",
            200: "#90e3ba",
            100: "#b4ecd0",
            50: "#e7f9f0",
        },
        warning: {
            900: "#6B4800",
            800: "#8C5E00",
            700: "#B57900",
            600: "#E89C00",
            500: "#FFAB00",
            400: "#FFBC33",
            300: "#FFC754",
            200: "#FFD88A",
            100: "#FFE5B0",
            50: "#FFF7E6",
        },
        error: {
            900: "#5B1E19",
            800: "#772721",
            700: "#9A322B",
            600: "#C54137",
            500: "#D9473C",
            400: "#E16C63",
            300: "#E6847C",
            200: "#EEAAA5",
            100: "#F3C6C3",
            50: "#FBEDEC",
        },
    },
    fonts: {
        "loader-link": "https://fonts.googleapis.com/css2?family=Albert+Sans:ital,wght@0,100..900;1,100..900&display=swap",
        "family-primary": "Albert Sans, sans-serif",
        "family-secondary": "Albert Sans, sans-serif",
        "base-font-size": "16px",
        "base-font-size-sm": "12px", // TODO: Create a more flexible font structure to accept multiple font sizes or sizes
        "base-line-height": "24px",
    },
    spacing: {
        "size-00": "0px",
        "size-01": "2px",
        "size-02": "4px",
        "size-03": "8px",
        "size-04": "12px",
        "size-05": "16px",
        "size-06": "24px",
        "size-07": "32px",
        "size-08": "40px",
        "size-09": "48px",
        "size-10": "56px",
        "size-11": "64px",
        "size-12": "72px",
        "size-13": "80px",
        "size-14": "88px",
        "size-15": "96px",
        "size-16": "144px",
    },
    breakpoints: {
        mobile: "320px",
        tablet: "744px",
        desktop: "1280px",
    },
    border: {
        width: {
            default: "2px",
            small: "1px",
        },
        radius: {
            default: "100px",
            small: "100px",
        },
    },
};

const themes = {
    daikiri,
    kalimotxo,
    mojito,
    negroni,
};

var qrName = "Enter the name of your QR code";
var shared = {
	qrName: qrName
};

var urlLabel = "Type in the URL to link with your QR code *";
var url = {
	urlLabel: urlLabel
};

var button = {
	submit: "Submit",
	cancel: "Cancel",
	seeMore: "See more",
	seeLess: "See less"
};
var layout = {
	button: button
};

i18n.use(initReactI18next).init({
    resources: {
        en: {
            shared,
            url,
            layout,
        },
    },
    lng: "en",
    fallbackLng: "en",
    ns: ["shared", "url", "layout"],
    defaultNS: "translations",
    interpolation: {
        escapeValue: false,
    },
});

/* TODO: Not working when using '${getBreakpoint("tablet")}' as a value of the min-width */
const StyledQREditor = styled$1.div `
  display: flex;
  background-color: ${getColor("neutral", "white")};
  color: ${getColor("neutral", 900)};
  flex-direction: column;
  padding: ${getSpacing("size-05")};
  min-height: 480px;
  box-sizing: border-box;

  p {
    margin-bottom: unset;
    margin-top: unset;
    color: unset;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: row;
    padding: ${getSpacing("size-06")};
    gap: ${getSpacing("size-06")};
  }
`;
const StyledQRForm = styled$1.div `
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyleLoadingWrapper = ({ children, onStylesLoaded, }) => {
    const [isStylesLoaded, setIsStylesLoaded] = useState(false);
    useEffect(() => {
        const checkStyles = () => {
            const styledElements = document.querySelectorAll("style[data-styled]");
            return styledElements.length > 0;
        };
        if (checkStyles()) {
            setIsStylesLoaded(true);
            onStylesLoaded === null || onStylesLoaded === void 0 ? void 0 : onStylesLoaded();
            return;
        }
        const observer = new MutationObserver(() => {
            if (checkStyles()) {
                setIsStylesLoaded(true);
                onStylesLoaded === null || onStylesLoaded === void 0 ? void 0 : onStylesLoaded();
                observer.disconnect();
            }
        });
        observer.observe(document.head, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["data-styled"],
        });
        const timeoutId = setTimeout(() => {
            setIsStylesLoaded(true);
            observer.disconnect();
        }, 2000);
        return () => {
            observer.disconnect();
            clearTimeout(timeoutId);
        };
    }, [onStylesLoaded]);
    return (React__default.createElement("div", { style: {
            opacity: isStylesLoaded ? 1 : 0,
            transition: "opacity 0.3s ease-in",
        } }, children));
};
const QREditor = (props) => {
    const { mode = "compact", qrTypes: qrTypesProp, language = "en", theme = "daikiri", className, style, serviceConfig, navigateToSubdomain = true, onStylesLoaded, } = props;
    const themeObject = typeof theme === "string" ? themes[theme] : theme;
    if (themeObject.fonts["loader-link"]) {
        useFontLoader({ googleFontUrl: themeObject.fonts["loader-link"] });
    }
    const qrTypes = qrTypesProp || getDefaultQRTypeConfigData(mode);
    const selectedQRType = useQRStore((state) => state.selectedType);
    const setSelectedQRType = useQRStore((state) => state.setSelectedType);
    const setQRData = useQRStore((state) => state.setQRData);
    useEffect(() => {
        i18n.changeLanguage(language);
    }, [language]);
    useEffect(() => {
        if (selectedQRType) {
            setQRData(getQRDataByType(selectedQRType));
            setSelectedQRType(selectedQRType || DEFAULT_QR_TYPE);
        }
    }, [selectedQRType]);
    return (React__default.createElement(ThemeProvider, { theme: themeObject },
        React__default.createElement(I18nextProvider, { i18n: i18n },
            React__default.createElement(ServicesProvider, { config: serviceConfig },
                React__default.createElement(StyleLoadingWrapper, { onStylesLoaded: onStylesLoaded },
                    React__default.createElement(GlobalStyles, null),
                    React__default.createElement(StyledQREditor, { className: className, style: style },
                        React__default.createElement(StyledQRForm, null,
                            React__default.createElement(QRTypeSelector, { mode: mode, qrTypes: qrTypes, onSelect: setSelectedQRType }),
                            React__default.createElement(QRForm, { qrTypes: qrTypes, mode: mode, navigateToSubdomain: navigateToSubdomain })),
                        React__default.createElement(QRRenderer, { navigateToSubdomain: navigateToSubdomain, theme: theme })))))));
};

export { QREditor, StyledQREditor };
//# sourceMappingURL=index.es.js.map
